// ─────────────────────────────────────────────────────────────────
// Phase I — Page Parcours Cliente refondue (Design System V1)
// Date : 2026-05-10
//
// Inspiration UI : Linear, Notion, Stripe Dashboard, Raycast.
// Charte Anissa : vert #1A2E1F en accent + fond ivoire + Playfair italic
// pour les titres premium + Lexend Deca pour body + Inter pour UI.
//
// Toutes les classes CSS vivent dans src/styles/journey.css. Aucun
// inline style metier — uniquement de la composition. Permet de
// retoucher le design sans toucher la logique.
// ─────────────────────────────────────────────────────────────────

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { supabase } from './supabaseClient';
import { useModalA11y } from './hooks/useModalA11y';
import {
  PACK_DEFINITIONS,
  buildPackFollowupSchedule,
  canSendPackReview,
  countConsultationsInPeriod,
  getActivePacks,
  getInitialAppointmentDate,
  getNextPendingStep,
  getPackAccess,
  getPackAppointmentProgress,
  isContinuousFollowupPack,
} from './services/packSystem';
import { isAnamneseFilled } from './services/anamneseAnalyzer';
import {
  hasPlannedSportCoordination,
  resolveClientCarePathway,
} from './services/clientCarePathway';
import {
  isClientAppChannelEnabled,
  isClientAppEffectivelyActive,
} from './services/clientAppAccess';
import {
  journeyStepsForState,
  STEP_META,
  DEFAULT_JOURNEY_STATE,
  getStepStatus,
  transitions,
  updateJourneyState,
  setPendingProtocolPhases,
  clearPendingProtocolPhases,
  isSameInstant,
  hasAnamnesisCompletionProof,
  canValidateAnamnesisStep,
  PlanValidationClinicalError,
  buildPlanValidationContent,
  evaluatePlanValidationReadiness,
  isStepConsultable,
  resolveJourneyCompletionPresentation,
  sha256Hex,
} from './services/journeyState';
import AnalysisSuggestionModal from './AnalysisSuggestionModal';
import AnalysisPlanCard from './AnalysisPlanCard';
import { canValidateResultsReview, preservePreviousPlanResults } from './services/resultsData';
import MgdReportUploadBlock from './components/MgdReportUploadBlock';
import ExternalPlanInputPanel from './components/ExternalPlanInputPanel';
import JourneyPlanEditor from './JourneyPlanEditor';
import { mgdReviewV2Client } from './services/mgdReviewV2Client';
import FollowupDocumentDeliveryModal from './components/FollowupDocumentDeliveryModal';
import { loadFollowupDocumentReference } from './services/followupDocumentDelivery';
import ClientAppPreviewModal from './ClientAppPreviewModal';
import JourneyMessagesPanel from './JourneyMessagesPanel';
import JourneyNotesPanel from './JourneyNotesPanel';
import SuiviTimeline from './components/SuiviTimeline';
import LongitudinalFollowupSummary from './components/LongitudinalFollowupSummary';
import JourneyPhasesCard from './components/JourneyPhasesCard';
// Refonte du suivi : nouvel écran derrière le drapeau suivi_v2 (vide par défaut).
import { SuiviAiguillage } from './suivi/SuiviPage';
import { messageQuitterSuivi } from './suivi/suiviEcran';
import CockpitErrorBoundary from './components/CockpitErrorBoundary';
import ClientAccessManager from './components/ClientAccessManager';
import ClientPulseSummary from './components/ClientPulseSummary';
import DeliveryAppContentSummary from './components/DeliveryAppContentSummary';
import PourquoiBloque from './components/PourquoiBloque';
import ClinicalAlertBanner from './components/ClinicalAlertBanner';
import SportHandoffReportModal from './components/SportHandoffReportModal';
// Chantier 9b : état du verrou du pré-questionnaire et autorisation de
// correction. Le bloc lit la base directement — le magasin local ne connaît
// pas ces colonnes, et un verrou affiché depuis un cache est un verrou faux.
import QuestionnaireVerrouBloc from './components/QuestionnaireVerrouBloc';
// Décision d'Anissa 2026-08-03 : l'antécédent familial signale, il ne bloque
// plus. La règle vit dans le résolveur clinique ; ici on ne fait que l'afficher.
import { signauxAntecedentsFamiliaux } from './services/clinical/resolveClinicalState';
import { formatCurrentTreatments } from './services/clinical/currentTreatments';
import FeedbacksTrendChart from './components/FeedbacksTrendChart';
import { getActivePhase, bakePendingProtocolPhases } from './services/protocolPhases';
// V97.40 (roadmap 1.2) — propage email + client_id sur les appels app cliente.
import { clientIdentityFields, hasClientIdentity } from './services/clientIdentity';
import {
  finishNextRdvSync,
  saveNextRdvInSaasOnly,
  shouldSyncNextRdvToClientApp,
  startNextRdvSync,
  syncNextRdvToClientApp,
  syncNextRdvWithServerVersion,
} from './services/nextRdvSync';
import { finalizeFollowupMilestones } from './services/followupMilestoneCompletion';
import {
  buildPhaseTransitionSafetySnapshot,
} from './services/riskProfileFlags';
// V97.23 (V97.18 Phase E) — Auto-generation brouillon IA apres transition phase.
// Pré-rédaction IA du BROUILLON de synthèse clinique globale (l'IA propose, Anissa décide).
import { suggestGlobalSynthesis, hasEnoughAnalysesForSynthesis } from './services/suggestGlobalSynthesis';
import {
  buildConsultationMeetingAuthorityFingerprint,
  buildProgramMeetingFacts,
  suggestConsultationMeeting,
  hasEnoughForBriefing,
} from './services/suggestConsultationBriefing';
import {
  buildMeetingRecord,
  buildMeetingReviewFingerprint,
  buildReviewedMeetingRecord,
  createManualMeetingQuestion,
  getMeetingWorkspaceReadiness,
  invalidateGeneratedMeetingWorkspace,
  isMeetingAiSnapshotReady,
  MEETING_WORKSPACE_LIMITS,
} from './services/consultationMeetingWorkspace';
import { buildBriefingData, resolveBriefingSince } from './services/buildConsultationBriefing';
import {
  fetchClientLongitudinal,
  resolveLongitudinalFetchState,
} from './services/fetchClientLongitudinal';
import {
  buildLongitudinalEvents,
  buildLongitudinalSummary,
  resolveExactSaasVersionForProgram,
  resolveLongitudinalAnchor,
} from './services/longitudinalFollowup';
// V97.4 V3.C — saisie dynamique des marqueurs attendus depuis le catalogue.
// Lecture seule du catalogue : la source de vérité reste journey_state.results_data.
import { getExpectedMarkersForTest } from './services/clinical/catalog/orthoAnalyticTests';
import { filterNonMgdResultsData, resolveResultLaboratory } from './services/clinical/buildClinicalContext';
import {
  buildExternalProfileSummary,
  isExternalPlanInput,
  sha256ExternalText,
} from './services/externalPlanWorkflow';
import { externalPlanAuthorityClient } from './services/externalPlanAuthority';
import { saveExternalDossierSynthesis } from './services/externalDossierSynthesisAuthority';
// P2.1 (remède sécurité clinique) — état réel (panne vs vide) propagé aux signaux.
import { signalDisplayState } from './services/signalDisplayState';
import {
  getNutritionConsultations,
  saveNutritionConsultationConfirmed,
} from './store';
import {
  cancelPhaseTransition,
  finalizePhaseTransition,
  getActivePhaseTransition,
  getPendingPhaseTransitionActivation,
  getPendingPhaseTransitionNotification,
  markPhaseTransitionAppActivated,
  markPhaseTransitionNotified,
  preparePhaseTransition,
} from './services/phaseTransitionService';
import { trackPlanValidated, trackPlanModification } from './services/observability';
// Refonte Suivi (Phase 2) — module de génération « prêt à publier ».
import ProgramGenerationPanel from './components/ProgramGenerationPanel';
import { assertPlanClinicallyCleared, formatClearanceForConfirm } from './services/clinicalClearance';
// Refonte Suivi (Phase 3) — bande miroir « ce que la cliente voit ».
import {
  computeProgramCompleteness,
  shouldShowProgramGeneration,
} from './services/programCompleteness';
import {
  buildPlanDeliveryContractV1,
  readCurrentPlanDeliveryEvidence,
} from './services/planDeliveryContract';
import ClientAppSyncAlert from './components/ClientAppSyncAlert';
// 2026-07-07 — Bloc « Questions du RDV » (briefing IA actionnable). Composant
// autonome contrôlé : seed/coche/réponse/ajout ; persistance branchée par le parent.
import RdvQuestionsBlock, { resolveRdvQuestions } from './components/RdvQuestionsBlock';
import { saveClient, PRATICIENNE_PAR_DEFAUT } from './store';
import {
  readClientAppOptions,
  withBothWeightOptions,
  withClientAppOption,
} from './services/clientAppOptions';
import { resolveClientMirror } from './services/resolveClientMirror';
import { fetchClientsStatus } from './services/fetchClientsStatus';
import { listPlanDrafts } from './services/planDraftsService';
// Refonte Suivi (Phase 4) — marqueurs de publication de la frise.
import { buildPublicationMarkers } from './services/buildPublicationMarkers';
import { resolveActivePlanPublication } from './services/resolveActivePlanPublication';
// 2026-07-26 — « Préparer l'appel résultats » (docs/SPEC-appel-resultats.md).
import ResultsCallModal from './components/ResultsCallModal';
import { hasEnoughForResultsCall } from './services/buildResultsCallData';
import {
  buildFollowupNutritionVersion,
  normalizeNutritionConsultationCloudRow,
} from './services/nutritionConsultationPersistence';
import { loadCanonicalFollowupOrdinal as loadCanonicalFollowupOrdinalService } from './services/followupVersionOrdinal';
import {
  persistReviewedFollowupAdaptation,
  proposeReviewedFollowupAdaptation,
} from './services/reviewedFollowupAdaptation';
import { persistManualProgramUpdateVersion } from './services/manualProgramUpdate';
import { FollowupSafetyPreflightError } from './services/followupSafetySnapshot';
import {
  buildCycleReviewDigest,
  resolveCycleReviewWorkflowIntent,
  selectCurrentSentCycleReview,
} from './services/cycleReviewSummary';
import './styles/journey.css';

// Le store local utilise quelques alias camelCase, tandis que Supabase renvoie
// la consultation complète en snake_case. L'aperçu doit conserver la ligne
// entière : lettre, recettes, compléments, corrections éditoriales et phases
// font partie de ce qu'Anissa vérifie avant publication.
export function normalizePreviewConsultation(row, fallbackClientId) {
  return normalizeNutritionConsultationCloudRow(row, fallbackClientId);
}

export async function assertMgdPlanAuthorizedForOutput(clientId, consultation,
  reviewClient = mgdReviewV2Client, knownStatus = null,
  externalAuthorityClient = externalPlanAuthorityClient, requireExternalValidation = true) {
  const status = knownStatus || await reviewClient.status(clientId);
  const consultationId = consultation?.id;
  const text = consultation?.nutritionPlan ?? consultation?.nutrition_plan ?? '';
  const externalInput = consultation?.editorial_overrides?.external_plan_input
    ?? consultation?.editorialOverrides?.external_plan_input;
  if (isExternalPlanInput(externalInput)) {
    if (!consultationId || !String(text).trim()) {
      throw new Error('Brouillon externe persisté introuvable. Publication bloquée.');
    }
    const currentHash = await sha256Hex(String(text).replace(/\r\n/g, '\n'));
    const authority = await externalAuthorityClient.status({
      clientId,
      consultationId,
      planHash: currentHash,
      profileSummaryHash: externalInput.profile_summary_sha256,
    });
    if (!authority.authorized || !authority.persisted
        || (requireExternalValidation && !authority.validated)) {
      throw new Error('L’autorité serveur du brouillon externe est absente. Validation bloquée.');
    }
    return Object.freeze({
      hasActiveMgd: false,
      externalInput: true,
      consultationId,
      currentPlanHash: currentHash,
      externalAuthority: authority,
    });
  }
  if (status.reports.length === 0) return Object.freeze({ hasActiveMgd: false });
  if (!consultationId || !String(text).trim()) {
    throw new Error('Plan MGD persisté introuvable. Publication bloquée.');
  }
  const proof = await reviewClient.planProofStatus({ clientId, consultationId });
  const currentHash = await sha256Hex(String(text).replace(/\r\n/g, '\n'));
  if (!proof.authorized || !proof.persisted || proof.planHash !== currentHash) {
    throw new Error('Le texte courant ne correspond pas au plan MGD autorisé et persisté. Publication bloquée.');
  }
  return Object.freeze({
    hasActiveMgd: true,
    consultationId,
    currentPlanHash: currentHash,
    proof,
  });
}

export async function loadConfirmedNutritionVersions(clientId, db = supabase) {
  if (!clientId) throw new Error('Cliente introuvable.');
  const { data, error } = await db
    .from('nutrition_consultations')
    .select('*')
    .eq('client_id', clientId)
    .eq('is_deleted', false)
    .order('created_at', { ascending: false })
    .order('id', { ascending: false });
  if (error) throw new Error(error.message || 'Impossible de recharger les versions cloud.');
  return (data || []).map((row) => normalizePreviewConsultation(row, clientId));
}

async function loadCanonicalFollowupOrdinal(
  clientId,
  versions,
  { journeyStateOverride = null, db = supabase } = {},
) {
  return loadCanonicalFollowupOrdinalService(clientId, versions, {
    journeyStateOverride,
    db,
  });
}

function isMutableFollowupDraft(canonical) {
  const publication = canonical?.client_app_publication ?? canonical?.clientAppPublication ?? null;
  const previousId = canonical?.previousConsultationId || canonical?.previous_consultation_id || '';
  return canonical?.isFollowup === true
    && /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(previousId)
    && canonical?.status === 'a_valider'
    && canonical?.isDeleted !== true
    && canonical?.is_deleted !== true
    && publication === null;
}

export const FOLLOWUP_PROGRAM_UPDATE_SCOPES = Object.freeze([
  Object.freeze({
    value: 'targeted',
    title: 'Ajustement ciblé',
    description: 'Corriger un élément précis du programme sans reprendre le reste.',
    initialTab: 'plan',
    workflowAction: 'adapt',
  }),
  Object.freeze({
    value: 'menus_recipes',
    title: 'Menus et recettes',
    description: 'Revoir la semaine, les repas et les recettes en conservant le reste du programme.',
    initialTab: 'recipes',
    workflowAction: 'adapt',
  }),
  Object.freeze({
    value: 'full',
    title: 'Nouvelle version complète',
    description: 'Reprendre l’ensemble du programme dans une nouvelle version complète.',
    initialTab: 'plan',
    workflowAction: 'newVersion',
  }),
]);

export function resolveFollowupProgramUpdateScope(value) {
  return FOLLOWUP_PROGRAM_UPDATE_SCOPES.find((scope) => scope.value === value) || null;
}

function readFollowupProgramUpdateScope(consultation) {
  const provenance = consultation?.followupData?._versionProvenance
    ?? consultation?.followup_data?._versionProvenance
    ?? null;
  if (!['reviewed_appointment_adaptation', 'manual_program_update'].includes(provenance?.trigger)) {
    return null;
  }
  const scope = resolveFollowupProgramUpdateScope(provenance.updateScope);
  const compatible = provenance.trigger === 'manual_program_update'
    ? provenance.workflowAction === 'manual'
    : scope?.workflowAction === provenance.workflowAction;
  if (!scope || !compatible) {
    throw new Error('Portée de mise à jour non prouvée — à valider Anissa.');
  }
  return scope;
}

export function assertManualProgramUpdateDraftScope(existingVersion, requestedScope) {
  const existingScope = readFollowupProgramUpdateScope(existingVersion);
  if (!existingScope) {
    throw new Error('Le brouillon existant a une provenance incompatible — à valider Anissa.');
  }
  if (!requestedScope || existingScope.value !== requestedScope.value) {
    throw new Error(
      `Ce brouillon existe déjà avec la portée « ${existingScope.title} ». Ouvre cette portée enregistrée au lieu d’en choisir une autre.`,
    );
  }
  return existingScope;
}

export function assertGeneratedPiecesSaveTarget(row, clientId, continuousFollowup) {
  const canonical = normalizePreviewConsultation(row, clientId);
  if (!canonical?.id || !canonical?.nutritionPlan) {
    throw new Error('La version active exacte est introuvable. Recharge la fiche.');
  }
  if (!continuousFollowup) return canonical;

  if (!isMutableFollowupDraft(canonical)) {
    throw new Error('Crée ou ouvre une nouvelle version de suivi avant de générer ces éléments.');
  }
  return canonical;
}

function stableJsonValue(value) {
  if (Array.isArray(value)) return value.map(stableJsonValue);
  if (value && typeof value === 'object') {
    return Object.keys(value)
      .sort()
      .reduce((result, key) => {
        result[key] = stableJsonValue(value[key]);
        return result;
      }, {});
  }
  return value;
}

export async function loadCanonicalPhaseTransitionConsultation(run, db = supabase) {
  const clientId = String(run?.clientId || '').trim();
  const consultationId = String(run?.targetConsultationId || '').trim();
  if (run?.status !== 'finalized' || !clientId || !consultationId) {
    throw new Error('Transition finalisée impossible à relire.');
  }
  const { data, error } = await db
    .from('nutrition_consultations')
    .select('*')
    .eq('id', consultationId)
    .eq('client_id', clientId)
    .eq('is_deleted', false)
    .maybeSingle();
  if (error || !data) {
    throw new Error(error?.message || 'La version finalisée est introuvable dans le SaaS.');
  }
  const canonical = normalizePreviewConsultation(data, clientId);
  const canonicalPhases = JSON.stringify(stableJsonValue(canonical?.protocol_phases));
  const finalizedPhases = JSON.stringify(stableJsonValue(run?.proposedProtocolPhases));
  if (canonical?.active_phase_id !== run.toPhaseId || canonicalPhases !== finalizedPhases) {
    throw new Error('La version canonique ne confirme pas la phase finalisée. Recharge la fiche.');
  }
  if (canonical?.followupData?.phaseTransitionDraft
      || canonical?.followup_data?.phaseTransitionDraft) {
    throw new Error('La version canonique contient encore un brouillon de transition. Recharge la fiche.');
  }
  return canonical;
}

export async function loadValidatedConsultationForDelivery(clientId, journeyState, db = supabase) {
  const validation = journeyState?.plan_validation;
  const consultationId = String(validation?.consultation_id || '').trim();
  const contentSha256 = String(validation?.content_sha256 || '').trim().toLowerCase();
  if (!clientId
      || !/^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(consultationId)
      || !/^[0-9a-f]{64}$/.test(contentSha256)) {
    throw new Error('Preuve du plan validé indisponible. Reviens à l étape Plan et valide à nouveau.');
  }
  const { data, error } = await db
    .from('nutrition_consultations')
    .select('*')
    .eq('id', consultationId)
    .eq('client_id', clientId)
    .eq('is_deleted', false)
    .maybeSingle();
  if (error || !data) {
    throw new Error(error?.message || 'Le plan validé est introuvable dans le SaaS.');
  }
  const normalized = normalizePreviewConsultation(data, clientId);
  const actualSha256 = await sha256Hex(buildPlanValidationContent(
    normalized?.nutritionPlan || '',
    data?.supplements || '',
  ));
  if (actualSha256 !== contentSha256) {
    throw new Error('Le plan a changé depuis sa validation. Valide à nouveau avant livraison.');
  }
  return {
    consultation: normalized,
    consultationId,
    contentSha256,
  };
}

export function applyDeliveredSnapshot(previous, delivered) {
  if (
    !delivered?.journeyState ||
    delivered.journeyState.current_step !== 'followup' ||
    delivered.journeyState.delivered !== true ||
    delivered.packStartedAtConfirmed !== true ||
    !Number.isFinite(Date.parse(delivered.packStartedAt || ''))
  ) {
    throw new Error('Etat de livraison incomplet');
  }
  return {
    ...previous,
    journey_state: delivered.journeyState,
    packStartedAt: delivered.packStartedAt,
    pack_started_at: delivered.packStartedAt,
    packStartedAtConfirmed: true,
    pack_started_at_confirmed: true,
  };
}

const PHASE_TRANSITION_DELIVERY_MODES = new Set(['app', 'document', 'app_and_document']);
const PHASE_TRANSITION_DOCUMENT_CHANNELS = new Set(['email', 'hand_delivery', 'post']);

export function resolvePhaseTransitionDelivery(journey) {
  const proof = journey?.delivery_proof;
  const mode = typeof proof?.mode === 'string' ? proof.mode.trim() : '';
  if (!PHASE_TRANSITION_DELIVERY_MODES.has(mode)) {
    throw new Error('Mode de remise du programme absent. Vérifie la livraison initiale avant de préparer la phase suivante.');
  }
  const documentChannel = proof?.document_channel ?? null;
  if (mode.includes('document') && !PHASE_TRANSITION_DOCUMENT_CHANNELS.has(documentChannel)) {
    throw new Error('Canal de remise du document absent. Vérifie la livraison initiale avant de continuer.');
  }
  if (mode === 'app' && documentChannel !== null) {
    throw new Error('Le mode Application contient un canal document inattendu.');
  }
  return { mode, documentChannel };
}

function copyProtocolPhases(protocolPhases) {
  if (!protocolPhases || typeof protocolPhases !== 'object') return null;
  return JSON.parse(JSON.stringify(protocolPhases));
}

export function buildPhaseTransitionTargetConsultation(
  sourceConsultation,
  clientId,
  versionNumber,
  followupWeek,
  { now = new Date(), idFactory, appointmentProof } = {},
) {
  if (!sourceConsultation?.id || !clientId) {
    throw new Error('Version source du programme indisponible.');
  }
  const normalized = normalizePreviewConsultation(sourceConsultation, clientId) || sourceConsultation;
  const createdAt = now instanceof Date ? now.toISOString() : String(now);
  if (
    appointmentProof?.workflowAction !== 'transition'
    || !/^[a-f0-9]{64}$/.test(String(appointmentProof?.appointmentSha256 || ''))
    || appointmentProof?.appointmentClientId !== clientId
    || appointmentProof?.consultationOrdinal !== followupWeek
  ) {
    throw new Error('La transition exige le dernier rendez-vous relu avec l’action « transition ».');
  }
  return buildFollowupNutritionVersion(normalized, {
    label: `V${versionNumber} — Phase suivante à valider`,
    protocol_phases: copyProtocolPhases(sourceConsultation.protocol_phases),
    active_phase_id: sourceConsultation.active_phase_id || null,
    followupData: {
      phaseTransitionDraft: {
        sourceConsultationId: sourceConsultation.id,
        fromPhaseId: sourceConsultation.active_phase_id || null,
        toPhaseId: null,
      },
    },
  }, {
    clientId,
    sourceConsultationId: sourceConsultation.id,
    followupWeek,
    idFactory,
    now: () => createdAt,
    provenance: {
      trigger: 'phase_transition',
      workflowAction: 'transition',
      appointmentSha256: appointmentProof.appointmentSha256,
      appointmentClientId: appointmentProof.appointmentClientId,
      consultationOrdinal: appointmentProof.consultationOrdinal,
    },
  });
}

export function getPhaseTransitionDraft(consultation) {
  const draft = consultation?.followupData?.phaseTransitionDraft
    || consultation?.followup_data?.phaseTransitionDraft
    || null;
  if (!draft || typeof draft !== 'object' || Array.isArray(draft)) return null;
  const sourceConsultationId = String(draft.sourceConsultationId || '').trim();
  const fromPhaseId = String(draft.fromPhaseId || '').trim();
  const toPhaseId = String(draft.toPhaseId || '').trim();
  if (!sourceConsultationId || !fromPhaseId || !toPhaseId) return null;
  return { sourceConsultationId, fromPhaseId, toPhaseId };
}

export async function createPhaseTransitionDraftWorkflow({
  clientId,
  sourceConsultation,
  fromPhase,
  toPhase,
  versionNumber,
  followupWeek,
  idFactory,
  now,
  appointmentProof,
  saveConfirmed = saveNutritionConsultationConfirmed,
} = {}) {
  const targetDraft = buildPhaseTransitionTargetConsultation(
    sourceConsultation,
    clientId,
    versionNumber,
    followupWeek,
    { idFactory, now, appointmentProof },
  );
  targetDraft.followupData.phaseTransitionDraft.fromPhaseId = fromPhase?.id || null;
  targetDraft.followupData.phaseTransitionDraft.toPhaseId = toPhase?.id || null;
  const targetConsultation = await saveConfirmed(targetDraft);
  return targetConsultation;
}

export async function preparePhaseTransitionWorkflow({
  clientId,
  sourceConsultation,
  targetConsultation,
  fromPhase,
  toPhase,
  delivery,
  safetyConfirmed = false,
  safetySnapshot,
  saveConfirmed = saveNutritionConsultationConfirmed,
  prepare = preparePhaseTransition,
} = {}) {
  const draft = getPhaseTransitionDraft(targetConsultation);
  if (!draft
      || draft.sourceConsultationId !== sourceConsultation?.id
      || draft.fromPhaseId !== fromPhase?.id
      || draft.toPhaseId !== toPhase?.id) {
    throw new Error('La version de la phase suivante doit être créée et relue avant sa remise.');
  }
  if (safetyConfirmed !== true) {
    throw new Error('La validation clinique explicite d’Anissa est requise avant la remise.');
  }
  const confirmedTarget = await saveConfirmed(targetConsultation);
  const run = await prepare({
    clientId,
    sourceConsultationId: sourceConsultation.id,
    targetConsultation: confirmedTarget,
    fromPhaseId: fromPhase?.id,
    toPhaseId: toPhase?.id,
    deliveryMode: delivery?.mode,
    documentChannel: delivery?.documentChannel ?? null,
    clinicalConfirmed: true,
    safetySnapshot,
  });
  return { targetConsultation: confirmedTarget, run };
}

export async function finalizePhaseTransitionProofWorkflow({
  run,
  proofType,
  proofSha256,
  appPlanId = null,
  finalize = finalizePhaseTransition,
  loadCanonical = loadCanonicalPhaseTransitionConsultation,
  activateApp,
  markAppActivated = markPhaseTransitionAppActivated,
  sendPush,
  markNotified = markPhaseTransitionNotified,
} = {}) {
  let updatedRun = await finalize({
    transitionId: run?.id,
    proofType,
    proofSha256,
    appPlanId,
  });
  let canonicalConsultation = null;
  let activationResult = null;
  let notificationPending = false;
  if (updatedRun.status === 'finalized') {
    canonicalConsultation = await loadCanonical(updatedRun);
    if (updatedRun.deliveryMode !== 'document') {
      if (typeof activateApp !== 'function') {
        throw new Error('Activation de la nouvelle phase dans l’app indisponible.');
      }
      activationResult = await activateApp(updatedRun, canonicalConsultation);
      updatedRun = await markAppActivated(
        updatedRun.id,
        activationResult?.plan_id,
        activationResult?.publication_artifact_sha256,
      );
    }
    if (updatedRun.deliveryMode !== 'document' && !updatedRun.notifiedAt && sendPush) {
      try {
        const sent = await sendPush(updatedRun);
        if (sent) updatedRun = await markNotified(updatedRun.id);
        else notificationPending = true;
      } catch (pushError) {
        // La remise et la transition sont déjà confirmées. Une panne push ne
        // doit pas les annuler ni inciter à republier; notifiedAt reste null.
        // eslint-disable-next-line no-console
        console.warn('[phase-transition-push] notification non envoyée :', pushError?.message || pushError);
        notificationPending = true;
      }
    }
  }
  return {
    run: updatedRun,
    canonicalConsultation,
    activationResult,
    notificationPending,
  };
}

export async function retryPhaseTransitionNotificationWorkflow({
  run,
  sendPush,
  markNotified = markPhaseTransitionNotified,
} = {}) {
  if (run?.status !== 'finalized'
      || run?.notifiedAt !== null
      || run?.appActivatedAt === null
      || !['app', 'app_and_document'].includes(run?.deliveryMode)
      || typeof sendPush !== 'function') {
    throw new Error('Aucune notification de phase finalisée ne peut être reprise.');
  }
  const sent = await sendPush(run);
  if (sent !== true) throw new Error('La notification n’a pas été envoyée.');
  return markNotified(run.id);
}

export async function retryPhaseTransitionActivationWorkflow({
  run,
  activateApp,
  markAppActivated = markPhaseTransitionAppActivated,
  sendPush,
  markNotified = markPhaseTransitionNotified,
} = {}) {
  if (run?.status !== 'finalized'
      || run?.appActivatedAt !== null
      || !run?.appPlanId
      || !/^[0-9a-f]{64}$/.test(String(run?.appArtifactSha256 || ''))
      || !['app', 'app_and_document'].includes(run?.deliveryMode)
      || typeof activateApp !== 'function') {
    throw new Error('Aucune activation de phase ne peut être reprise.');
  }
  const activationResult = await activateApp(run);
  let updatedRun = await markAppActivated(
    run.id,
    activationResult?.plan_id,
    activationResult?.publication_artifact_sha256,
  );
  let notificationPending = false;
  if (!updatedRun.notifiedAt && typeof sendPush === 'function') {
    try {
      const sent = await sendPush(updatedRun);
      if (sent === true) updatedRun = await markNotified(updatedRun.id);
      else notificationPending = true;
    } catch {
      notificationPending = true;
    }
  }
  return { run: updatedRun, activationResult, notificationPending };
}

export async function exportPhaseTransitionDocumentWorkflow({
  client,
  targetConsultation,
  run,
  exportPlan,
  exportOptions,
} = {}) {
  if (!run || !targetConsultation || targetConsultation.id !== run.targetConsultationId) {
    throw new Error('La version à exporter ne correspond pas à la transition préparée.');
  }
  const planText = targetConsultation.nutritionPlan
    || targetConsultation.nutrition_plan
    || targetConsultation.plan_text
    || '';
  const supplements = targetConsultation.supplements || '';
  const actualSha256 = await sha256Hex(buildPlanValidationContent(planText, supplements));
  if (actualSha256 !== run.contentSha256) {
    throw new Error('Le programme a changé depuis sa préparation. Prépare à nouveau la phase suivante.');
  }
  const exportResult = await exportPlan(client, targetConsultation, planText, exportOptions);
  const artifactSha256 = String(exportResult?.artifactSha256 || '').trim().toLowerCase();
  if (exportResult?.ok !== true || !/^[0-9a-f]{64}$/.test(artifactSha256)) {
    throw new Error('La preuve exacte du document Word est absente. La remise ne peut pas être confirmée.');
  }
  const consultationSha256 = await sha256Hex(
    JSON.stringify(stableJsonValue(targetConsultation)),
  );
  return Object.freeze({ transitionId: run.id, artifactSha256, consultationSha256 });
}

export function canConfirmPhaseTransitionDocument(run, exportedTransitionProof) {
  return !!run
    && !!run.id
    && run.id === exportedTransitionProof?.transitionId
    && /^[0-9a-f]{64}$/.test(String(exportedTransitionProof?.artifactSha256 || ''))
    && /^[0-9a-f]{64}$/.test(String(exportedTransitionProof?.consultationSha256 || ''))
    && (run.deliveryMode === 'document' || run.deliveryMode === 'app_and_document')
    && !run.documentConfirmedAt;
}

export function resolvePhaseTransitionArtifactSha256(publishResult, persistedRun, expectedTransitionId) {
  if (!persistedRun || persistedRun.id !== expectedTransitionId) {
    throw new Error('La transition préparée n’est plus active. Recharge la fiche avant de continuer.');
  }
  const artifactSha256 = String(
    publishResult?.publication_artifact_sha256
    || persistedRun.appArtifactSha256
    || '',
  ).trim().toLowerCase();
  if (!/^[0-9a-f]{64}$/.test(artifactSha256)) {
    throw new Error('La preuve de publication de la phase suivante est absente.');
  }
  return artifactSha256;
}

export async function persistPublishedAppActivation(clientId, publishResult, db = supabase) {
  const stagingClientId = publishResult?.client_id ? String(publishResult.client_id) : '';
  if (!clientId || !stagingClientId) {
    throw new Error('Identite de l espace cliente publie indisponible');
  }
  const { data, error } = await db
    .from('clients')
    .update({ app_enabled: true, staging_client_id: stagingClientId })
    .eq('id', clientId)
    .select('app_enabled, staging_client_id')
    .single();
  if (
    error
    || data?.app_enabled !== true
    || String(data?.staging_client_id || '') !== stagingClientId
  ) {
    throw new Error(error?.message || 'Activation de l espace cliente non confirmee');
  }
  return {
    app_enabled: true,
    appEnabled: true,
    staging_client_id: stagingClientId,
    stagingClientId,
  };
}

export default function ClientJourneyPage({
  clientId,
  justCreated = false,
  onDismissJustCreated,
  onExit,
  onEditProfile,
  onSendPackReview,
  onViewHistory,
  resumeProgramUpdateScope = null,
  onConsumeResumeProgramUpdateScope,
}) {
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // 2026-08-08 — La fiche affichée vient-elle de la base, ou de la copie
  // locale ? Le repli local (V97.95) est utile, mais il était SILENCIEUX :
  // le parcours s'affichait normalement alors qu'aucune écriture ne pouvait
  // aboutir, et Anissa ne le découvrait qu'au clic. On le dit maintenant.
  const [clientHorsLigne, setClientHorsLigne] = useState(false);
  // Phase T : aperçu app cliente (modal mockup téléphone)
  const [showAppPreview, setShowAppPreview] = useState(false);
  // La fenêtre reste consultable pendant tout le parcours, mais la publication
  // n'est ouverte que depuis la porte explicite de Livraison (ou depuis le
  // Suivi pour une nouvelle version). Un aperçu ne doit jamais publier par
  // accident un plan encore en génération ou en édition.
  const [appPreviewPublishAllowed, setAppPreviewPublishAllowed] = useState(false);
  const [appPreviewMgdActive, setAppPreviewMgdActive] = useState(false);
  const [appPreviewDeliveryContext, setAppPreviewDeliveryContext] = useState(null);
  // Section où ouvrir l'aperçu, quand Anissa entre par une pièce précise.
  const [appPreviewSection, setAppPreviewSection] = useState(null);
  const [previewConsultation, setPreviewConsultation] = useState(null);
  const appPreviewProofStaleRef = useRef(null);
  const [phaseTransitionRevision, setPhaseTransitionRevision] = useState(0);
  // Phase AC : panel latéral messagerie SaaS ↔ cliente
  const [showMessages, setShowMessages] = useState(false);
  // Phase AE : panel latéral notes internes Anissa
  const [showNotes, setShowNotes] = useState(false);
  const [showSportHandoff, setShowSportHandoff] = useState(false);
  // V97.28 — Navigation libre dans la timeline une fois le parcours terminé.
  // viewedStepOverride permet à Anissa de cliquer sur une étape passée pour la
  // consulter (sans repasser par les validations). null = on suit current_step.
  // Reset automatiquement quand current_step change côté DB.
  const [viewedStepOverride, setViewedStepOverride] = useState(null);
  // Nouveau suivi : tant qu'une modification n'est pas en base, changer
  // d'étape demande une confirmation (sinon elle partirait sans qu'on le voie).
  const suiviNonEnregistreRef = useRef({ nonEnregistre: false, menus: false, autres: false });
  const signalerSuiviNonEnregistre = useCallback((nonEnregistre, details = {}) => {
    suiviNonEnregistreRef.current = {
      nonEnregistre: nonEnregistre === true,
      menus: details.menus === true,
      autres: details.autres === true,
    };
  }, []);
  // Seules les modifications hors menus sont gardées dans ce navigateur ; les
  // menus non enregistrés de l'éditeur sont perdus : le message le dit.
  const quitterSuiviPossible = useCallback(() => {
    const etat = suiviNonEnregistreRef.current;
    if (!etat.nonEnregistre || typeof window === 'undefined') return true;
    return window.confirm(messageQuitterSuivi(etat));
  }, []);
  const consulterEtape = useCallback((etape) => {
    if (!quitterSuiviPossible()) return;
    setViewedStepOverride(etape);
  }, [quitterSuiviPossible]);
  // Un retour en arrière refusé était avalé par un catch muet : le bouton ne
  // faisait simplement rien, sans que personne sache pourquoi.
  const [retourErreur, setRetourErreur] = useState(null);
  const clientLoadRequestRef = useRef(0);

  const openAppPreview = useCallback(async (options = {}) => {
    if (!clientId) return;
    // A l'étape Livraison, l'aperçu doit porter sur la consultation exacte
    // validée en base. Les autres ouvertures restent consultatives et gardent
    // le repli local historique.
    let validatedReference = null;
    let consult = null;
    if (options.consultation) {
      consult = options.phaseTransitionRun
        ? { ...options.consultation, phaseTransitionRun: options.phaseTransitionRun }
        : options.consultation;
    } else if (options.deliveryMode) {
      validatedReference = await loadValidatedConsultationForDelivery(
        clientId,
        client?.journey_state,
      );
      consult = validatedReference.consultation;
    } else {
      consult = (getNutritionConsultations(clientId) || [])[0] || null;
    }
    if (!consult && !options.deliveryMode) {
      const { data } = await supabase
        .from('nutrition_consultations')
        .select('*')
        .eq('client_id', clientId)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      if (data) {
        consult = normalizePreviewConsultation(data, clientId);
      }
    }
    const mgdStatus = await mgdReviewV2Client.status(clientId);
    const hasActiveMgd = mgdStatus.reports.length > 0;
    // La préparation éditoriale depuis Livraison doit connaître la branche
    // d'autorité exacte (notamment externe vs anciens rapports), même lorsque
    // la publication reste volontairement masquée jusqu'à la remise du Word.
    const outputAuthority = options.allowPublish === true || options.deliveryMode
      ? await assertMgdPlanAuthorizedForOutput(clientId, consult, mgdReviewV2Client, mgdStatus)
      : { hasActiveMgd };
    setPreviewConsultation(consult || { clientId, nutritionPlan: '', date: new Date().toISOString() });
    setAppPreviewMgdActive(outputAuthority.hasActiveMgd);
    setAppPreviewPublishAllowed(options.allowPublish === true);
    appPreviewProofStaleRef.current = typeof options.onDeliveryProofStale === 'function'
      ? options.onDeliveryProofStale
      : null;
    setAppPreviewDeliveryContext(options.deliveryMode ? {
      mode: options.deliveryMode,
      documentChannel: options.documentChannel ?? null,
      consultationId: validatedReference?.consultationId || null,
      contentSha256: validatedReference?.contentSha256 || null,
    } : null);
    // Section d'entrée quand Anissa clique une pièce incomplète depuis
    // Livraison. Sans elle, l'aperçu s'ouvre toujours au même endroit et il
    // faut retrouver la section à la main.
    setAppPreviewSection(typeof options.section === 'string' ? options.section : null);
    setShowAppPreview(true);
  }, [client?.journey_state, clientId]);

  const loadClient = useCallback(async () => {
    const requestId = clientLoadRequestRef.current + 1;
    clientLoadRequestRef.current = requestId;
    const requestClientId = clientId;
    const requestIsCurrent = () => (
      clientLoadRequestRef.current === requestId && requestClientId === clientId
    );
    if (!requestClientId) { setClient(null); setLoading(false); return; }
    setLoading(true);
    setClient(null);
    setError(null);
    // V97.95 — garde-fou anti-gel. Avant : aucun timeout ni try/catch → si la
    // requête Supabase ne revenait jamais (incident/latence), la page restait
    // figée sur "Chargement du parcours…". Maintenant : on borne l'attente à
    // 15s et, en cas d'échec/latence OU de fiche pas encore synchronisée, on
    // charge depuis le store local (saveClient l'y écrit dès la création).
    const loadLocal = async () => {
      try { const m = await import('./store'); return m.getClient?.(requestClientId) || null; }
      catch { return null; }
    };
    try {
      const query = supabase
        .from('clients')
        .select('*')
        .eq('id', requestClientId)
        .maybeSingle();
      const timeout = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('__timeout__')), 15000),
      );
      const { data, error: err } = await Promise.race([query, timeout]);
      if (!requestIsCurrent()) return;
      if (err) throw new Error(err.message);
      // V97.11.5 — Normalize Supabase snake_case → camelCase (packType etc.,
      // sinon PACK_DEFINITIONS[undefined] = null → modal analyses à 0 CHF).
      const normalized = data ? {
        ...data,
        packType: data.pack_type || null,
        packStartedAt: data.pack_started_at || null,
        packStartedAtConfirmed: data.pack_started_at_confirmed ?? false,
        packSchedule: data.pack_schedule || null,
        stagingClientId: data.staging_client_id || null,
        createdBy: data.created_by || PRATICIENNE_PAR_DEFAUT,
        createdAt: data.created_at,
        updatedAt: data.updated_at,
        latestSections: data.latest_sections || null,
        interviewNotes: data.interview_notes || null,
      } : null;
      if (normalized) { setClient(normalized); setClientHorsLigne(false); return; }
      // Pas (encore) en base (sync en cours) → fallback local si dispo.
      const local = await loadLocal();
      if (!requestIsCurrent()) return;
      if (local) { setClient(local); setClientHorsLigne(true); } else setError('Cliente introuvable.');
    } catch (e) {
      // Supabase injoignable/lent → on bosse sur la copie locale de la fiche.
      const local = await loadLocal();
      if (!requestIsCurrent()) return;
      if (local) { setClient(local); setClientHorsLigne(true); return; }
      setError(e?.message === '__timeout__'
        ? 'Supabase met trop de temps à répondre (incident en cours ?). Réessaie dans un instant.'
        : (e?.message || 'Chargement impossible.'));
    } finally {
      if (requestIsCurrent()) setLoading(false);
    }
  }, [clientId]);

  useEffect(() => {
    loadClient();
    return () => { clientLoadRequestRef.current += 1; };
  }, [loadClient]);

  // V97.28 — Reset override quand current_step change côté DB (ex : Anissa
  // a validé une étape via flèche retour pendant qu'elle consultait, ou un
  // refresh a ramené un nouveau current_step). On suit la DB par défaut.
  useEffect(() => {
    setViewedStepOverride(null);
  }, [client?.journey_state?.current_step]);

  if (loading) {
    return (
      <div className="jrn-page">
        <div style={{ padding: 60, color: 'var(--jrn-text-muted)', textAlign: 'center', fontFamily: 'var(--jrn-font-body)' }}>
          Chargement du parcours…
        </div>
      </div>
    );
  }
  if (error) {
    return (
      <div className="jrn-page">
        <div style={{ padding: 60, textAlign: 'center', fontFamily: 'var(--jrn-font-body)' }}>
          <div style={{ color: 'var(--jrn-error)', marginBottom: 16 }}>{error}</div>
          <button type="button" onClick={loadClient} className="jrn-btn jrn-btn--primary">
            Réessayer
          </button>
        </div>
      </div>
    );
  }
  if (!client) {
    return (
      <div className="jrn-page">
        <div style={{ padding: 60, color: 'var(--jrn-text-muted)', textAlign: 'center', fontFamily: 'var(--jrn-font-body)' }}>
          Cliente introuvable.
        </div>
      </div>
    );
  }

  const journey = { ...DEFAULT_JOURNEY_STATE, ...(client.journey_state || {}) };
  const pack = PACK_DEFINITIONS[client.packType] || null;
  const packAccess = getPackAccess(client.packType);
  const carePathway = resolveClientCarePathway(client.form);
  const sportCoordinationPlanned = hasPlannedSportCoordination(client.form);
  const prenom = client.prenom || client.form?.prenom || 'Cliente';
  const nom = client.nom || client.form?.nom || '';
  const currentStep = journey.current_step || 'anamnesis';
  const clientAppChannelEnabled = isClientAppChannelEnabled(client, journey);
  // V97.28 — viewedStep = ce qu'Anissa regarde. Par défaut = currentStep, mais
  // si elle a cliqué sur une étape passée dans la timeline (parcours fini),
  // l'override prend la main. La DB n'est jamais modifiée par cette navigation.
  const viewedStep = viewedStepOverride || currentStep;
  const isViewingPast = viewedStepOverride !== null && viewedStepOverride !== currentStep;

  const journeySteps = journeyStepsForState(journey);
  const currentStepIndex = Math.max(1, journeySteps.indexOf(currentStep) + 1);
  // Nom de la destination du bouton retour, pour qu'il annonce où il mène.
  const etapePrecedenteLabel = currentStepIndex > 1
    ? (STEP_META[journeySteps[currentStepIndex - 2]]?.label || null)
    : null;
  // BC.5G.12 : la barre suit la position courante (currentStepIndex - 1) et
  // pas les flags _validated. Comme ça quand Anissa revient en arrière via
  // la flèche ←, la barre redescend pour refléter où elle est vraiment.
  // Cas particuliers à l'étape 8 : un suivi activé ou un accompagnement
  // ponctuel déjà livré sont des parcours terminés → 100%.
  // isParcoursComplete n'est plus lu ici : la consultation d'une étape passée
  // ne dépend plus de l'achèvement du parcours (voir le fil des étapes).
  // La fonction continue de le calculer pour en déduire progressPct.
  const {
    isOneShotTerminal,
    progressPct,
  } = resolveJourneyCompletionPresentation(currentStep, journey, packAccess);

  // Le quota d'écriture reste limité aux consultations de suivi. L'affichage
  // compte en revanche aussi le rendez-vous initial inclus dans le pack.
  const consultationsTotal = packAccess.includedFollowupConsultations;
  const consultationsLog = Array.isArray(journey?.consultations_log) ? journey.consultations_log : [];
  // Compteur PAR PÉRIODE : au renouvellement du suivi, le log est append-only
  // (historique conservé) mais le quota se calcule sur la période courante
  // depuis packStartedAt. Sans packStartedAt -> compte tout (retro-compat).
  const consultationsUsed = countConsultationsInPeriod(consultationsLog, client.packStartedAt);
  const appointmentProgress = getPackAppointmentProgress(client, consultationsLog);

  // BC.2 (2026-05-11) : helpers cockpit cliente
  // Initiales pour avatar (FB depuis "Farid Benyahia", F si pas de nom)
  const initials = (
    (prenom?.[0] || '') + (nom?.[0] || '')
  ).toUpperCase() || '?';
  // Jour J du pack — V97.8.1 : ne s'affiche QUE si le pack est confirmé démarré
  // (= markDelivered effectué). Avant ce fix, on calculait depuis packStartedAt
  // posé à la création → Anissa voyait 'Jour 1 du pack' avant le RDV.
  const daysSincePack = !isOneShotTerminal
    && client.packStartedAt && client.packStartedAtConfirmed === true
    ? Math.max(1, Math.floor((Date.now() - new Date(client.packStartedAt).getTime()) / 86400000) + 1)
    : null;
  // Dernière consultation effectuée (pour "dernier check-in")
  const lastConsultation = consultationsLog.length > 0 ? consultationsLog[consultationsLog.length - 1] : null;
  const daysSinceLastConsult = !isOneShotTerminal && lastConsultation
    ? Math.floor((Date.now() - new Date(lastConsultation.date).getTime()) / 86400000)
    : null;

  // BC.3C (2026-05-11) : status contextuel parcours pour pill premium header.
  // Donne un signal émotionnel ("Onboarding actif", "Plan en édition"...)
  // au lieu de juste afficher le step courant en bas.
  const statusContext = (() => {
    switch (currentStep) {
      case 'anamnesis':       return { label: 'Onboarding actif', tone: 'accent' };
      case 'analyses':        return { label: 'Phase analyses',   tone: 'accent' };
      case 'waiting_results': return { label: 'Réception des rapports', tone: 'neutral' };
      case 'results':         return { label: 'Revue des résultats',    tone: 'accent' };
      case 'plan_generation': return { label: 'Génération du plan', tone: 'gold' };
      case 'plan_editing':    return { label: 'Plan en édition',   tone: 'gold' };
      case 'delivery':        return { label: 'Livraison en cours', tone: 'gold' };
      case 'followup':        return isOneShotTerminal
        ? { label: 'Accompagnement terminé', tone: 'neutral' }
        : { label: 'Suivi en cours', tone: 'accent' };
      default:                return { label: 'Parcours',          tone: 'neutral' };
    }
  })();

  const refresh = () => loadClient();

  return (
    <div className="jrn-page">
      {/* 2026-08-08 — Fiche lue depuis la copie locale : la base ne la voit
          pas (session expirée, fiche pas encore synchronisée, ou incident
          Supabase). Aucune validation d'étape ne pourra être enregistrée,
          autant le dire avant le clic plutôt qu'après. */}
      {clientHorsLigne && (
        <div role="status" style={{
          display: 'flex', alignItems: 'flex-start', gap: 12,
          margin: '0 0 18px', padding: '14px 16px', borderRadius: 14,
          background: '#fdf6e7', border: '1px solid rgba(176,124,20,0.35)',
          fontFamily: 'var(--jrn-font-body)', fontSize: 14, lineHeight: 1.5,
        }}>
          <span aria-hidden="true" style={{ fontSize: 17, lineHeight: 1.3 }}>⚠</span>
          <div style={{ flex: 1 }}>
            <strong style={{ display: 'block', marginBottom: 3 }}>
              Fiche affichée depuis la copie locale
            </strong>
            La base ne renvoie pas cette fiche. Les validations d’étape ne
            seront pas enregistrées tant que ce n’est pas rétabli. Vérifie que
            tu es bien connectée, puis recharge.
            <button
              type="button"
              onClick={loadClient}
              className="jrn-btn"
              style={{ display: 'block', marginTop: 10 }}
            >
              Recharger la fiche
            </button>
          </div>
        </div>
      )}
      {/* V97.85 — Guidage post-création : le « truc super » qui manquait entre
          « créer la fiche » et « activer son espace ». Bandeau prochaine étape,
          persistant et dismissable, sur la fiche fraîchement créée. */}
      {justCreated && (
        <div style={{
          display: 'flex', alignItems: 'flex-start', gap: 14,
          margin: '0 0 18px', padding: '16px 18px', borderRadius: 14,
          background: 'linear-gradient(135deg, #eaf3ec 0%, #f4faf5 100%)',
          border: '1px solid rgba(46,94,62,0.25)',
          boxShadow: '0 6px 20px -12px rgba(46,94,62,0.35)',
        }}>
          <span style={{
            flexShrink: 0, width: 34, height: 34, borderRadius: '50%',
            background: '#2E5E3E', color: '#fff', display: 'inline-flex',
            alignItems: 'center', justifyContent: 'center', fontSize: 18, fontWeight: 700,
          }} aria-hidden="true">✓</span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <p style={{ margin: 0, fontSize: 15, fontWeight: 600, color: '#1f3a2a' }}>
              Fiche de {prenom} créée.
            </p>
            <p style={{ margin: '4px 0 0', fontSize: 13, lineHeight: 1.5, color: '#3a5244' }}>
              Prochaine étape&nbsp;: dans <strong>Onboarding</strong>, choisis soit d’activer l’application, soit d’envoyer le pré-questionnaire par lien sans compte.
            </p>
          </div>
          <button
            type="button"
            onClick={onDismissJustCreated}
            aria-label="Fermer"
            style={{ flexShrink: 0, border: 'none', background: 'transparent', color: '#3a5244', fontSize: 22, lineHeight: 1, cursor: 'pointer', padding: 2 }}
          >
            ×
          </button>
        </div>
      )}
      {/* ─── Header ─────────────────────────────────────────────── */}
      <header className="jrn-header">
        {/* BC.2 (2026-05-11) : header refondu en cockpit cliente.
            Avatar initiales + bloc identité (nom complet + eyebrow méta)
            + ligne stats pills (pack / consultations / jour pack / dernier check-in)
            + progression à droite + actions. */}
        <div className="jrn-header__identity">
          <div className="jrn-header__avatar" aria-hidden="true">{initials}</div>
          <div className="jrn-header__id">
            <p className="jrn-header__eyebrow">
              Parcours cliente{pack ? ` · ${pack.label}` : ''}
            </p>
            <h1 className="jrn-header__name">
              {prenom}{nom ? ` ${nom}` : ''}
            </h1>
            <div className="jrn-header__meta">
              {/* BC.3C : status contextuel premium en premier (pill avec dot pulse) */}
              <span className={`jrn-status-pill jrn-status-pill--${statusContext.tone}`}>
                <span className="jrn-status-pill__dot" aria-hidden="true" />
                {statusContext.label}
              </span>
              {appointmentProgress.total > 0 && (
                <span
                  className="jrn-meta-chip jrn-meta-chip--gold"
                  title={`${appointmentProgress.completed} rendez-vous réalisé${appointmentProgress.completed > 1 ? 's' : ''} sur ${appointmentProgress.total}, rendez-vous initial inclus`}
                >
                  <span className="jrn-meta-chip__icon">📅</span>
                  {appointmentProgress.completed} / {appointmentProgress.total} rendez-vous
                </span>
              )}
              {daysSincePack && (
                <span className="jrn-meta-chip jrn-meta-chip--neutral" title={`Pack démarré le ${new Date(client.packStartedAt).toLocaleDateString('fr-CH')}`}>
                  <span className="jrn-meta-chip__icon">📆</span>
                  Jour {daysSincePack} du pack
                </span>
              )}
              {daysSinceLastConsult !== null && (
                <span className="jrn-meta-chip jrn-meta-chip--accent" title={`Dernière consultation : ${new Date(lastConsultation.date).toLocaleDateString('fr-CH')}`}>
                  <span className="jrn-meta-chip__icon">✓</span>
                  Dernier RDV {daysSinceLastConsult === 0 ? 'aujourd\'hui' : `il y a ${daysSinceLastConsult}j`}
                </span>
              )}
            </div>
          </div>
        </div>

        <div className="jrn-header__progress">
          <div className="jrn-header__progress-meta">
            <span>Étape {currentStepIndex} / {journeySteps.length}</span>
            <span>{progressPct}%</span>
          </div>
          <div className="jrn-header__progress-track">
            <div className="jrn-header__progress-fill" style={{ width: `${progressPct}%` }} />
          </div>
        </div>

        {/* BC.3C : actions header groupées en 2 zones distinctes.
            Zone 1 (actions cliente — soft accent) : Messages / Notes / Aperçu app
            Séparateur visuel
            Zone 2 (navigation — ghost) : Étape précédente / Profil / Dashboard */}
        <div className="jrn-header__actions">
          <div className="jrn-header__actions-group">
            {clientAppChannelEnabled && (
              <button
                onClick={() => setShowMessages(true)}
                className="jrn-btn jrn-btn--soft"
                title="Messages avec la cliente + ressentis reçus"
              >
                Messages
              </button>
            )}
            <button
              onClick={() => setShowNotes(true)}
              className="jrn-btn jrn-btn--soft"
              title="Notes internes privées sur la cliente (jamais envoyées)"
            >
              Notes
            </button>
            {clientAppChannelEnabled && (
              <button
                onClick={openAppPreview}
                className="jrn-btn jrn-btn--soft"
                title="Aperçu de ce que la cliente voit dans l'app"
              >
                Aperçu app
              </button>
            )}
          </div>

          <span className="jrn-header__actions-sep" aria-hidden="true" />

          <div className="jrn-header__actions-group">
            {currentStepIndex > 1 && (
              <button
                onClick={async () => {
                  setRetourErreur(null);
                  try {
                    await transitions.goToPreviousStep(client.id, currentStep, journey);
                    refresh();
                  } catch (e) {
                    setRetourErreur(e?.message || 'Le retour à l’étape précédente n’a pas abouti.');
                  }
                }}
                className="jrn-btn jrn-btn--ghost"
                title={etapePrecedenteLabel
                  ? `Ramener le parcours à l’étape ${etapePrecedenteLabel}`
                  : 'Revenir à l’étape précédente'}
              >
                {/* Une flèche nue n'annonçait ni ce qu'elle faisait, ni où
                    elle menait : le seul indice était une infobulle. */}
                ← {etapePrecedenteLabel || 'Étape précédente'}
              </button>
            )}
            {onEditProfile && (
              <button onClick={(e) => { if (quitterSuiviPossible()) onEditProfile(e); }} className="jrn-btn jrn-btn--ghost" title="Éditer le profil cliente">
                Profil
              </button>
            )}
            <button onClick={(e) => { if (quitterSuiviPossible()) onExit?.(e); }} className="jrn-btn jrn-btn--ghost">
              Dashboard
            </button>
          </div>
        </div>
        {/* Le refus du retour se dit ici, contre le bouton qui l'a provoqué,
            et pas plusieurs écrans plus bas. */}
        {retourErreur && (
          <p className="jrn-header__retour-echec" role="alert">
            <strong>Le retour en arrière n’a pas abouti.</strong> {retourErreur}
          </p>
        )}
      </header>

      {/* V97.82 — Alerte fiabilité : la sync d'étape vers l'app cliente a
          échoué (après retries). Visible sur toutes les étapes, avec Réessayer.
          Se rend null s'il n'y a aucun marqueur (cas normal). */}
      <ClientAppSyncAlert
        journeyState={journey}
        clientId={clientId}
        prenom={prenom}
        onRetried={refresh}
      />

      {/* ─── Body : sidebar + main ──────────────────────────────── */}
      <div className="jrn-body">
        <aside className="jrn-sidebar">
          {/* ─── Étapes du parcours (timeline verticale) ─────────── */}
          <p className="jrn-sidebar__label">Parcours</p>
          {/* V97.87 — rail de progression : fraction parcourue (0 → 1)
              jusqu'à l'étape réellement courante, consommée par
              .jrn-steps::after dans journey.css (purement visuel). */}
          <div
            className="jrn-steps"
            style={{ '--jrn-rail-fill': (currentStepIndex - 1) / Math.max(1, journeySteps.length - 1) }}
          >
            {journeySteps.map((step, visibleIndex) => {
              const status = getStepStatus(journey, step);
              const meta = isOneShotTerminal && step === 'followup'
                ? { ...STEP_META[step], label: 'Terminé' }
                : STEP_META[step];
              // V97.28 — "active" reflète maintenant ce que regarde Anissa,
              // pas l'état DB. La step réellement courante (currentStep) garde
              // un style visuel distinct via la classe --current.
              const isViewed = step === viewedStep;
              const isCurrent = step === currentStep;
              // La règle vit dans journeyState.js, sous test.
              const isClickable = isStepConsultable(journey, step, currentStep);
              const cls = ['jrn-step'];
              if (isViewed) cls.push('jrn-step--active');
              else if (status === 'validated') cls.push('jrn-step--validated');
              else if (status === 'skipped') cls.push('jrn-step--skipped');
              if (isCurrent && !isViewed) cls.push('jrn-step--current-hint');
              if (isClickable) cls.push('jrn-step--clickable');
              // V97.8.1 (2026-05-12) : pastille notification sur l'étape
              // Onboarding si la cliente a soumis son pré-questionnaire mais
              // que l'anamnèse n'est pas encore validée. Anissa voit ainsi
              // immédiatement qu'il y a quelque chose à lire/gérer.
              const f = client.form || {};
              const preQReceived = !!(f.objectif_primaire || f.dureeProbleme || f.ressentiDigestion);
              const showPreQDot = step === 'anamnesis'
                && preQReceived
                && !journey.anamnesis_validated;
              const handleClick = isClickable ? () => consulterEtape(step) : undefined;
              const title = isClickable
                ? `Consulter cette étape (lecture seule — n'altère pas le suivi)`
                : isCurrent ? 'Étape actuelle' : undefined;
              return (
                <div
                  key={step}
                  className={cls.join(' ')}
                  onClick={handleClick}
                  role={isClickable ? 'button' : undefined}
                  tabIndex={isClickable ? 0 : undefined}
                  onKeyDown={isClickable ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); consulterEtape(step); } } : undefined}
                  title={title}
                  style={isClickable ? { cursor: 'pointer' } : undefined}
                >
                  <span className="jrn-step__num">
                    {status === 'validated' ? '✓' : status === 'skipped' ? '↷' : visibleIndex + 1}
                  </span>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                    {meta.label}
                    {showPreQDot && (
                      <span
                        title="Pré-questionnaire reçu — à lire avant le RDV"
                        style={{
                          display: 'inline-block',
                          width: 8, height: 8, borderRadius: '50%',
                          background: 'var(--jrn-accent)',
                          boxShadow: '0 0 0 3px rgba(46, 78, 56, 0.15)',
                        }}
                      />
                    )}
                    {isCurrent && !isViewed && (
                      <span
                        title="Étape actuelle"
                        style={{
                          fontSize: 9.5,
                          fontWeight: 700,
                          letterSpacing: '.08em',
                          textTransform: 'uppercase',
                          color: 'var(--jrn-accent)',
                          padding: '1px 6px',
                          border: '1px solid var(--jrn-accent)',
                          borderRadius: 999,
                          marginLeft: 2,
                        }}
                      >
                        ici
                      </span>
                    )}
                  </span>
                </div>
              );
            })}
          </div>

          {/* ─── BC.3B Cockpit clinique cliente ────────────────────
              Pack actif + suivi (consultations, jour pack, dernier RDV)
              + statut app cliente. Remplace le résumé minimal AR.3. */}
          <div className="jrn-cockpit">
            <div className="jrn-cockpit__section">
              <p className="jrn-cockpit__label">Contexte</p>
              <p className="jrn-cockpit__pack">{carePathway.shortLabel}</p>
              {!carePathway.key && (
                <p className="jrn-cockpit__hint">À compléter dans le profil.</p>
              )}
              <button
                type="button"
                className="jrn-cockpit__action"
                onClick={() => setShowSportHandoff(true)}
              >
                {sportCoordinationPlanned ? 'Préparer le relais sport' : 'Préparer un relais sport ponctuel'}
              </button>
            </div>
            {pack && (
              <div className="jrn-cockpit__section">
                <p className="jrn-cockpit__label">Pack actif</p>
                <p className="jrn-cockpit__pack">{pack.label}</p>
              </div>
            )}

            {(appointmentProgress.total > 0 || daysSincePack || daysSinceLastConsult !== null) && (
              <div className="jrn-cockpit__section">
                <p className="jrn-cockpit__label">Suivi</p>
                {appointmentProgress.total > 0 && (
                  <div className="jrn-cockpit__stat">
                    <div className="jrn-cockpit__stat-head">
                      <span className="jrn-cockpit__stat-key">Rendez-vous du pack</span>
                      <span className="jrn-cockpit__stat-val">{appointmentProgress.completed} <span className="jrn-cockpit__stat-total">/ {appointmentProgress.total}</span></span>
                    </div>
                    <div className="jrn-cockpit__bar">
                      <div
                        className="jrn-cockpit__bar-fill"
                        style={{ width: `${Math.min(100, Math.round((appointmentProgress.completed / appointmentProgress.total) * 100))}%` }}
                      />
                    </div>
                  </div>
                )}
                {daysSincePack && (
                  <div className="jrn-cockpit__row">
                    <span className="jrn-cockpit__row-key">📆 Pack démarré</span>
                    <span className="jrn-cockpit__row-val">Jour {daysSincePack}</span>
                  </div>
                )}
                {daysSinceLastConsult !== null && (
                  <div className="jrn-cockpit__row">
                    <span className="jrn-cockpit__row-key">✓ Dernier RDV</span>
                    <span className="jrn-cockpit__row-val">
                      {daysSinceLastConsult === 0 ? 'aujourd\'hui' : `il y a ${daysSinceLastConsult}j`}
                    </span>
                  </div>
                )}
              </div>
            )}

            <div className="jrn-cockpit__section">
              <p className="jrn-cockpit__label">Espace cliente</p>
              <div className="jrn-cockpit__row">
                <span className="jrn-cockpit__row-key">App cliente</span>
                <span className={`jrn-cockpit__pill ${clientAppChannelEnabled ? 'jrn-cockpit__pill--on' : 'jrn-cockpit__pill--off'}`}>
                  {clientAppChannelEnabled ? 'Activée' : 'Sans app'}
                </span>
              </div>
              <p className="jrn-cockpit__hint">
                {clientAppChannelEnabled
                  ? isOneShotTerminal
                    ? 'Le programme reste accessible dans l’application. Aucun suivi continu n’est inclus.'
                    : 'Programme et suivi accessibles dans l’application.'
                  : journey?.delivery_proof?.mode === 'document'
                    ? isOneShotTerminal
                      ? 'Programme remis par document. Aucun suivi continu n’est inclus.'
                      : 'Programme remis par document, suivi conservé dans le SaaS.'
                    : 'Le questionnaire peut être envoyé par lien, sans créer de compte.'}
              </p>
            </div>

            <div className="jrn-cockpit__section jrn-cockpit__section--next">
              <p className="jrn-cockpit__label">Prochaine action</p>
              <p className="jrn-cockpit__next">
                {isOneShotTerminal ? 'Accompagnement terminé' : (STEP_META[currentStep]?.label || '—')}
              </p>
              <p className="jrn-cockpit__next-meta">Étape {currentStepIndex} / {journeySteps.length} · {progressPct}%</p>
              {/* 23.08.2026 — le système sait déjà pourquoi il refuse, il le
                  calcule partout. Mais chaque refus vit là où il est calculé,
                  souvent loin du bouton qui l'a provoqué. Ce panneau rassemble
                  ce qui manque, avec une action par ligne, sous « Prochaine
                  action » : c'est là qu'on regarde quand on ne sait plus.
                  Il porte sur l'étape REGARDÉE, pas sur l'étape courante. */}
              <PourquoiBloque
                client={client}
                journey={journey}
                etape={viewedStep}
                onAller={(cible) => {
                  if (cible === 'profil') { onEditProfile?.(); return; }
                  if (isStepConsultable(journey, cible, currentStep)) consulterEtape(cible);
                }}
              />
            </div>
          </div>
        </aside>

        {/* Phase T : Modal Aperçu app cliente (mockup mobile + publish) */}
        {showAppPreview && previewConsultation && (
          <ClientAppPreviewModal
            client={client}
            consultation={previewConsultation}
            aiActionsAllowed={!appPreviewMgdActive}
            publishAllowed={appPreviewPublishAllowed}
            publicationCompletionRequired={journey?.current_step === 'delivery' && !journey?.delivered}
            initialSection={appPreviewSection}
            onClose={() => {
              appPreviewProofStaleRef.current = null;
              setShowAppPreview(false);
              setAppPreviewPublishAllowed(false);
              setAppPreviewMgdActive(false);
              setAppPreviewDeliveryContext(null);
              setAppPreviewSection(null);
            }}
            onDeliveryProofStale={() => {
              appPreviewProofStaleRef.current?.();
              appPreviewProofStaleRef.current = null;
              setShowAppPreview(false);
              setAppPreviewPublishAllowed(false);
              setAppPreviewMgdActive(false);
              setAppPreviewDeliveryContext(null);
            }}
            // V97.13.37 — Acces direct au panel Messages depuis la modal
            onOpenMessages={() => setShowMessages(true)}
            // V97.99 — Publication centralisée dans la modale. Après un publish
            // réussi, on avance le parcours si on est à l'étape Livraison et
            // pas encore livré (équivalent de l'ancien handleDelivered).
            // Garde-fou : markDelivered ré-arme packStartedAt, ne pas le rejouer.
            onPublished={async (publishResult) => {
              const isPhaseTransitionPublication = Boolean(previewConsultation?.phaseTransitionRun);
              try {
                const transitionRun = previewConsultation?.phaseTransitionRun || null;
                if (transitionRun) {
                  const persistedRun = await getActivePhaseTransition(client.id);
                  const runForFinalization = persistedRun || transitionRun;
                  const artifactSha256 = resolvePhaseTransitionArtifactSha256(
                    publishResult,
                    runForFinalization,
                    transitionRun.id,
                  );
                  const transitionResult = await finalizePhaseTransitionProofWorkflow({
                    run: runForFinalization,
                    proofType: 'app',
                    proofSha256: artifactSha256,
                    appPlanId: publishResult?.plan_id,
                    activateApp: async (finalizedRun) => {
                      const { activatePhaseTransitionClientAppPlan } = await import('./services/publishToClientApp');
                      return activatePhaseTransitionClientAppPlan({
                        client,
                        run: finalizedRun,
                        stagedPublishResult: publishResult,
                      });
                    },
                    sendPush: async (finalizedRun) => {
                      const identity = clientIdentityFields(client);
                      if (!identity.email && !identity.client_id) return false;
                      const nextPhase = finalizedRun.proposedProtocolPhases?.phases?.find(
                        (phase) => phase.id === finalizedRun.toPhaseId,
                      );
                      const { clientAppFetch } = await import('./services/clientAppFetch');
                      await clientAppFetch('/api/admin/push/send', {
                        method: 'POST',
                        payload: {
                          ...identity,
                          title: 'Votre parcours évolue',
                          body: nextPhase
                            ? `Votre nouvelle phase est disponible : Phase ${nextPhase.order} · ${nextPhase.client_name}.`
                            : 'Votre nouvelle phase est disponible dans votre programme.',
                          url: '/plan#strategie',
                          tag: `phase-transition-${finalizedRun.id}`,
                        },
                      });
                      return true;
                    },
                  });
                  const appActivation = await persistPublishedAppActivation(
                    client.id,
                    transitionResult.activationResult,
                  );
                  setClient((previous) => ({ ...previous, ...appActivation }));
                  if (transitionResult.canonicalConsultation) {
                    setPreviewConsultation(transitionResult.canonicalConsultation);
                  }
                  setPhaseTransitionRevision((value) => value + 1);
                  return transitionResult.activationResult;
                }
                const appActivation = await persistPublishedAppActivation(
                  client.id,
                  publishResult,
                );
                if (journey?.current_step === 'delivery' && !journey?.delivered) {
                  if (!appPreviewDeliveryContext) {
                    throw new Error('Mode de livraison absent : revenez a l etape Livraison.');
                  }
                  const artifactSha256 = String(
                    publishResult?.publication_artifact_sha256
                    || client?.journey_state?.plan_validation?.publication_artifact_sha256
                    || '',
                  );
                  if (!/^[0-9a-f]{64}$/.test(artifactSha256)) {
                    throw new Error('Preuve de publication absente : vérifiez le statut avant de continuer.');
                  }
                  // Dès que la publication externe a réussi, le canal devient
                  // irréversible dans cet écran, même si la finalisation SaaS
                  // doit être reprise. Un autre mode ne doit jamais reclasser
                  // cette remise réelle.
                  setClient((previous) => ({
                    ...previous,
                    ...appActivation,
                    journey_state: {
                      ...previous?.journey_state,
                      delivery_preference: {
                        ...(previous?.journey_state?.delivery_preference || {}),
                        mode: appPreviewDeliveryContext.mode,
                        document_channel: appPreviewDeliveryContext.documentChannel,
                        app_publication_confirmed: true,
                      },
                      plan_validation: {
                        ...(previous?.journey_state?.plan_validation || {}),
                        publication_artifact_sha256: artifactSha256,
                      },
                    },
                  }));
                  const delivered = await transitions.markDeliveredByChannel(
                    client.id,
                    appPreviewDeliveryContext,
                  );
                  const { grantPackClientAccess } = await import('./services/clientAccessManager');
                  await grantPackClientAccess(client, {
                    startsAt: delivered.packStartedAt,
                    sourceRef: `initial:${publishResult?.plan_id || previewConsultation?.id}`,
                  });
                  // La transition renvoie l'etat ET l'horloge relus apres
                  // ecriture. Le Suivi ne doit ni reafficher Livraison, ni
                  // demarrer avec une periode locale vide alors qu'elle est
                  // deja active en base.
                  setClient((previous) => ({
                    ...applyDeliveredSnapshot(previous, delivered),
                    ...appActivation,
                  }));
                } else {
                  setClient((previous) => ({ ...previous, ...appActivation }));
                }
              } catch (e) {
                // eslint-disable-next-line no-console
                console.warn('[onPublished] finalisation SaaS échouée après publication :', e?.message);
                throw new Error(isPhaseTransitionPublication
                  ? 'Le livrable de phase est préparé, mais sa finalisation ou son activation reste à reprendre. La cliente ne le voit pas encore.'
                  : 'Le programme est publié, mais sa finalisation reste à confirmer dans le SaaS. Réessayez sans modifier le plan.');
              }
            }}
          />
        )}

        {showSportHandoff && (
          <SportHandoffReportModal
            client={client}
            onClose={() => setShowSportHandoff(false)}
            onEditProfile={onEditProfile}
          />
        )}

        {/* Phase AC : Panel messagerie SaaS ↔ cliente (slide-in droite) */}
        {clientAppChannelEnabled && showMessages && (
          <JourneyMessagesPanel
            client={client}
            onClose={() => setShowMessages(false)}
          />
        )}

        {/* Phase AE : Panel notes internes Anissa (slide-in droite) */}
        {showNotes && (
          <JourneyNotesPanel
            client={client}
            onClose={() => setShowNotes(false)}
          />
        )}

        <main className="jrn-main">
          {/* V97.96 — Mode guidé : fil conducteur permanent. À chaque étape,
              une phrase en français simple dit à Anissa quoi faire maintenant
              (avec le prénom + où cliquer). Rien à mémoriser. Affiché
              uniquement sur l'étape courante (pas en mode consultation). */}
          {!isViewingPast && (() => {
            const guide = {
              anamnesis: `Choisis si ${prenom} utilise l’app ou reçoit le questionnaire par lien sans compte. Quand tu as ses réponses, valide l’étape.`,
              analyses: `Propose les analyses biologiques de ${prenom}, ou passe l'étape si elle n'en fait pas.`,
              waiting_results: `Utilise les rapports originaux de ${prenom} dans ta conversation de travail, puis reviens continuer vers la synthèse.`,
              results: `Vérifie les rapports reçus et ajoute, si besoin, les résultats que ${prenom} possédait déjà.`,
              plan_generation: `Génère le plan nutritionnel de ${prenom} (bouton ci-dessous).`,
              plan_editing: `Relis et ajuste le plan dans le SaaS, puis valide-le. Le document Word officiel sera créé dans Livraison.`,
              delivery: `Choisis si ${prenom} reçoit l'app, un document, ou les deux. Le pack démarre seulement après la confirmation finale.`,
              followup: isOneShotTerminal
                ? `Le programme de ${prenom} est livré. Aucun suivi continu n’est inclus dans cet accompagnement ponctuel.`
                : `Suis ${prenom} : lis ses ressentis, réponds à ses messages, et adapte son plan quand c'est le moment.`,
            };
            const text = guide[currentStep] || guide.anamnesis;
            return (
              <div style={{
                marginBottom: 18, padding: '14px 18px', borderRadius: 12,
                background: 'var(--jrn-accent-soft)', border: '1px solid var(--jrn-accent-tint)',
                display: 'flex', gap: 12, alignItems: 'flex-start',
              }}>
                <span aria-hidden style={{
                  flexShrink: 0, marginTop: 1, width: 22, height: 22, borderRadius: '50%',
                  background: 'var(--jrn-accent)', color: 'var(--jrn-surface)',
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 12, fontWeight: 700, fontVariantNumeric: 'tabular-nums',
                }}>{currentStepIndex}</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{
                    fontSize: 10.5, fontWeight: 700, letterSpacing: '.09em',
                    textTransform: 'uppercase', color: 'var(--jrn-accent)', marginBottom: 3,
                    fontFamily: 'var(--jrn-font-ui)',
                  }}>
                    Que faire maintenant · Étape {currentStepIndex}/{journeySteps.length}
                  </div>
                  <p style={{ margin: 0, fontSize: 14, lineHeight: 1.5, color: 'var(--jrn-text)', fontFamily: 'var(--jrn-font-body)' }}>
                    {text}
                  </p>
                </div>
              </div>
            );
          })()}
          {/* V97.28 — Bandeau divergence : Anissa consulte une étape passée
              pendant que le suivi continue. Évite toute confusion sur l'état
              réel + bouton de retour rapide à l'étape courante. */}
          {isViewingPast && (
            <div style={{
              marginBottom: 16,
              padding: '10px 14px',
              background: 'rgba(167, 139, 250, 0.08)',
              border: '1px solid rgba(167, 139, 250, 0.35)',
              borderRadius: 8,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 12,
              flexWrap: 'wrap',
            }}>
              <div style={{ fontSize: 12.5, color: '#5a4290', flex: 1, minWidth: 240 }}>
                {/* La phrase promettait qu'aucune modification n'altère le
                    suivi, ce que cet écran ne garantit pas : les étapes
                    restent éditables. Elle dit maintenant ce qui est vrai,
                    à savoir que consulter ne déplace pas le curseur. */}
                <strong>Mode consultation.</strong>{' '}
                Tu regardes <em>{STEP_META[viewedStep]?.label || viewedStep}</em>.
                {' '}L'étape en cours du parcours reste{' '}
                <strong>{STEP_META[currentStep]?.label || currentStep}</strong>{' '}
                : la consulter ne la déplace pas.
              </div>
              <button
                type="button"
                onClick={() => consulterEtape(null)}
                className="jrn-btn jrn-btn--soft"
                style={{ background: 'white', borderColor: 'rgba(167, 139, 250, 0.4)', color: '#5a4290' }}
              >
                ← Revenir à l'étape actuelle
              </button>
            </div>
          )}
          {viewedStep === 'anamnesis' && <StepAnamnesis client={client} onChange={refresh} onEditProfile={onEditProfile} />}
          {viewedStep === 'analyses' && <StepAnalyses client={client} journey={journey} onChange={refresh} />}
          {viewedStep === 'waiting_results' && <StepWaitingResults client={client} onChange={refresh} />}
          {viewedStep === 'results' && <StepResults key={client.id} client={client}
            onChange={refresh} onEditProfile={onEditProfile} />}
          {viewedStep === 'plan_generation' && <StepPlanGeneration client={client} journey={journey} onChange={refresh} />}
          {viewedStep === 'plan_editing' && (
            <StepPlanEditing
              client={client}
              journey={journey}
              onChange={refresh}
              onOpenClientPreview={() => openAppPreview({ allowPublish: false })}
            />
          )}
          {viewedStep === 'delivery' && (
            <StepDelivery
              client={client}
              onChange={refresh}
              onDelivered={(delivered) => {
                setClient((previous) => applyDeliveredSnapshot(previous, delivered));
              }}
              onOpenAppPreview={(options = {}) => openAppPreview({
                ...options,
                allowPublish: options.allowPublish !== false,
              })}
            />
          )}
          {viewedStep === 'followup' && <SuiviAiguillage client={client} onDirtyChange={signalerSuiviNonEnregistre} ancienEcran={<StepFollowup client={client} journey={journey} onChange={refresh} onExit={onExit} onEditProfile={onEditProfile} onSendPackReview={onSendPackReview} onViewHistory={onViewHistory} phaseTransitionRevision={phaseTransitionRevision} resumeProgramUpdateScope={resumeProgramUpdateScope} onConsumeResumeProgramUpdateScope={onConsumeResumeProgramUpdateScope} onOpenAppPreview={(options = {}) => openAppPreview({ ...options, allowPublish: options?.phaseTransitionRun?.deliveryMode ? options.phaseTransitionRun.deliveryMode !== 'document' : journey?.delivery_proof?.mode !== 'document' })} onOpenMessages={() => setShowMessages(true)} />} />}
        </main>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// HELPER : header d'étape (eyebrow + title + intro)
// ═══════════════════════════════════════════════════════════════════

function StepHead({ index, title, intro, total = 8 }) {
  return (
    <header style={{ marginBottom: 'var(--jrn-8)' }}>
      <p className="jrn-step-eyebrow">Étape {index} / {total}</p>
      <h2 className="jrn-step-title">{title}</h2>
      <p className="jrn-step-intro">{intro}</p>
    </header>
  );
}

function ErrorLine({ msg }) {
  if (!msg) return null;
  return <div className="jrn-error" role="alert" aria-live="assertive">⚠ {msg}</div>;
}

// V97.34 — petite liste titrée pour le rendu du briefing IA (sujets, questions,
// pistes d'analyses). Ne rend rien si la liste est vide.
function BriefingList({ title, items }) {
  if (!Array.isArray(items) || items.length === 0) return null;
  return (
    <div style={{ marginBottom: 'var(--jrn-4)' }}>
      <p style={{ fontSize: 'var(--jrn-text-xs)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.04em', color: 'var(--jrn-text-soft)', marginBottom: 6 }}>
        {title}
      </p>
      <ul style={{ margin: 0, paddingLeft: 18, fontSize: 'var(--jrn-text-sm)', color: 'var(--jrn-text)', lineHeight: 1.6 }}>
        {items.map((it, i) => <li key={i} style={{ marginBottom: 4 }}>{it}</li>)}
      </ul>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// ÉTAPE 1 — ANAMNÈSE
// ═══════════════════════════════════════════════════════════════════

// BB.2 (2026-05-11) : Étape 1 refondue en cockpit Onboarding (4 blocs).
// Reflète le workflow réel d'Anissa :
//   1. Configurer le mode d'accompagnement (app/papier/poids/notifs)
//   2. Envoyer le pré-questionnaire
//   3. Recevoir les réponses
//   4. Valider l'onboarding (= passer aux analyses)
// Exporte pour etre RENDU en test : c est le seul moyen de prouver que le
// signal des antecedents familiaux atteint vraiment cet ecran. Un test qui
// rend le composant isole ne prouve que son existence, pas son montage —
// verifie par mutation le 2026-08-03.
export function StepAnamnesis({ client, onChange, onEditProfile }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [sendingQuestionnaire, setSendingQuestionnaire] = useState(null); // 'app' | 'link' | null
  // V97.4 — état local pour le CTA "Activer l'espace cliente" intégré
  // directement dans la carte App cliente (anciennement renvoyait vers Profil).
  const [activatingApp, setActivatingApp] = useState(false);
  const [activationError, setActivationError] = useState(null);
  // V97.10 — RDV anamnèse : Anissa pose la date du RDV après réception du
  // pré-q. La cliente la verra sur son app + recevra une notif J-1.
  const [rdvBusy, setRdvBusy] = useState(false);
  const [rdvDraft, setRdvDraft] = useState('');
  const [rdvNoteDraft, setRdvNoteDraft] = useState('');
  const [rdvEditMode, setRdvEditMode] = useState(false);
  const [rdvSyncWarning, setRdvSyncWarning] = useState(null);
  // V97.11 — Anamnèse approfondie : Anissa marque la fin du remplissage
  // explicitement (apres ou pendant le RDV). Timestamp dans journey_state.
  const [anamneseBusy, setAnamneseBusy] = useState(false);
  // V97.34 — Briefing IA pré-RDV (aide à l'anamnèse, jamais un diagnostic).
  const [briefingBusy, setBriefingBusy] = useState(false);
  const [briefingError, setBriefingError] = useState(null);
  const form = client.form || {};
  const journey = client.journey_state || {};
  // V97.99 — état local des questions du RDV (variant journey/sélection). Évite
  // un rechargement complet de la page à chaque coche (qui renvoyait Anissa en
  // haut). La persistance se fait en arrière-plan via saveClient, sans refresh.
  const [rdvQuestions, setRdvQuestions] = useState(() => resolveRdvQuestions(client.form || {}));

  // V97.4 — Active l'app cliente in-place depuis l'onboarding étape 1.
  // Logique miroir de App.jsx ligne 1218 (création rapide) : POST
  // invite-client + update Supabase clients.app_enabled=true.
  // Best-effort : si l'invite échoue, on n'écrit pas app_enabled=true
  // et on affiche l'erreur à Anissa pour qu'elle puisse retry.
  const handleActivateApp = async () => {
    setActivatingApp(true);
    setActivationError(null);
    try {
      const email = client.form?.email || client.email;
      if (!email) throw new Error('Cliente sans email — impossible d\'activer l\'app');

      const { clientAppFetch } = await import('./services/clientAppFetch');
      const packType = client.packType || client.pack_type || '';
      const packAccess = getPackAccess(packType);
      if (!packAccess.known) {
        throw new Error('Pack absent ou inconnu : activation de l app bloquee');
      }
      const appMode = packAccess.appMode;
      const saasOrigin = window.location.origin;

      const inviteRes = await clientAppFetch('/api/admin/invite-client', {
        method: 'POST',
        payload: {
          email,
          first_name: client.form?.prenom || client.prenom || 'Cliente',
          mode: appMode,
          questionnaire_url: `${saasOrigin}/questionnaire/${client.id}`,
        },
      });

      if (!inviteRes?.ok) {
        throw new Error(inviteRes?.error || 'L\'app cliente n\'a pas répondu correctement');
      }

      // Update local Supabase clients.app_enabled
      const { error: updErr } = await supabase
        .from('clients')
        .update({ app_enabled: true })
        .eq('id', client.id);
      if (updErr) throw new Error(updErr.message);

      onChange?.();
    } catch (e) {
      setActivationError(e?.message || 'Erreur d\'activation');
    } finally {
      setActivatingApp(false);
    }
  };

  // ─── Détection statuts ──────────────────────────────────────────
  // Réponses reçues : le form a au moins un champ clé rempli (objectifs/symptômes/pathologies/activité)
  // V97.8.1 (2026-05-12) : détection mise à jour pour matcher les champs
  // du pré-questionnaire app cliente (5 sections / 15 champs essentiels).
  // Avant ce fix, on cherchait des champs legacy SaaS (`objectifs` avec s,
  // `symptomes`, `activite`) qui ne sont pas remplis par le pre-q in-app.
  // Maintenant on accepte aussi les champs réellement remplis par Camille.
  // P1.1 (remède sécurité clinique, 2026-06-10) — délègue à la source unique
  // isAnamneseFilled (anamneseAnalyzer). Même prédicat qu'avant (champs pré-q
  // V97.8.1 + legacy SaaS), désormais partagé avec le gate de génération IA
  // pour qu'aucun chemin ne juge la complétude différemment.
  const minimallyFilled = isAnamneseFilled(form);
  const anamnesisCompletionConfirmed = hasAnamnesisCompletionProof(journey);
  const canValidateAnamnesis = canValidateAnamnesisStep(form, journey);
  const questionnaireSentAt = journey.questionnaire_sent_at || null;
  const questionnaireMode = journey.questionnaire_mode || null; // 'app' | 'link'
  const questionnaireReceived = minimallyFilled;

  // V97.34 — briefing IA déjà généré (lu depuis clients.form.ia_briefing).
  const briefing = form.ia_briefing?.output || null;
  const briefingGeneratedAt = form.ia_briefing?.generated_at || null;

  // Déclenche la génération via l'endpoint SaaS-local protégé par session Anissa.
  const handleGenerateBriefing = async () => {
    setBriefingBusy(true);
    setBriefingError(null);
    try {
      const { generateBriefing } = await import('./services/generateBriefing');
      const res = await generateBriefing(client.id);
      // Refresh pour relire clients.form.ia_briefing fraîchement persisté.
      onChange?.();
      // Sauvegarde DB échouée mais briefing généré : on prévient sans bloquer.
      if (res?.warning) setBriefingError(res.warning);
    } catch (e) {
      setBriefingError(e?.message || 'Erreur lors de la génération du briefing');
    } finally {
      setBriefingBusy(false);
    }
  };

  const handleSendQuestionnaire = async (mode) => {
    setSendingQuestionnaire(mode);
    setErr(null);
    try {
      const { openClientWelcomeAppMail, openClientQuestionnaireMail } = await import('./services/sendClientQuestionnaire');
      if (mode === 'app') openClientWelcomeAppMail(client);
      else openClientQuestionnaireMail(client);
      // Track l'envoi dans journey_state
      await updateJourneyState(client.id, {
        questionnaire_sent_at: new Date().toISOString(),
        questionnaire_mode: mode,
      });
      onChange();
    } catch (e) {
      setErr(e?.message || 'Erreur envoi');
    } finally {
      setSendingQuestionnaire(null);
    }
  };

  const handleValidate = async () => {
    if (!minimallyFilled) {
      setErr('Complète d’abord les informations essentielles de l’anamnèse.');
      return;
    }
    if (!anamnesisCompletionConfirmed) {
      setErr('Marque d’abord l’anamnèse comme terminée.');
      return;
    }
    setBusy(true); setErr(null);
    try {
      await transitions.validateAnamnesis(client.id);
      onChange();
    } catch (e) { setErr(e?.message || 'Erreur transition'); }
    finally { setBusy(false); }
  };

  // V97.10 — Handlers RDV anamnèse
  const formatRdv = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    const datePart = d.toLocaleDateString('fr-CH', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' });
    const timePart = d.toLocaleTimeString('fr-CH', { hour: '2-digit', minute: '2-digit' });
    return `${datePart} à ${timePart}`;
  };

  const isoToDatetimeLocal = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };

  const handleSaveRdv = async () => {
    if (!rdvDraft) return;
    setRdvBusy(true); setErr(null);
    try {
      const isoAt = new Date(rdvDraft).toISOString();
      const noteOrNull = rdvNoteDraft.trim() || null;

      // 1. Sauvegarde dans le SaaS (journey_state JSONB)
      const { updateJourneyState } = await import('./services/journeyState');
      await updateJourneyState(client.id, {
        rdv_anamnesis_at: isoAt,
        rdv_anamnesis_note: noteOrNull,
      });

      // 2. V97.10 Phase B : push vers app cliente pour que la cliente voie
      //    la date sur sa timeline (/parcours rdv_scheduled).
      //    V97.10.2 : on enchaine avec une notif push immediate ("RDV fixe").
      //    Best-effort sur les 2 etapes — le SaaS reste source de verite si
      //    l'app cliente est down.
      setRdvSyncWarning(null);
      if (shouldSyncRdvToApp) try {
        if (!hasClientIdentity(client)) {
          throw new Error('Cliente sans identifiant app — sync impossible');
        }
        // 2a. Pousse la date avec une version explicite. L'app refuse toute
        // ecriture RDV non versionnee afin qu'un ancien onglet ne puisse pas
        // ecraser un rendez-vous plus recent.
        await syncNextRdvWithServerVersion(client, isoAt);
        // 2b. Envoie une notif push immediate (best-effort separe — si la
        //     cliente n'a pas active les push, on ignore silencieusement).
        try {
          const { clientAppFetch } = await import('./services/clientAppFetch');
          await clientAppFetch('/api/admin/push/send', {
            method: 'POST',
            payload: {
              ...clientIdentityFields(client),
              title: 'Votre RDV avec Anissa est fixé',
              body: `Rendez-vous d'anamnèse : ${formatRdv(isoAt)}.`,
              url: '/parcours',
              tag: 'rdv-anamnese',
            },
          });
        } catch (pushErr) {
          // eslint-disable-next-line no-console
          console.warn('[rdv-push] failed (non-bloquant):', pushErr?.message || pushErr);
        }
      } catch (syncErr) {
        const msg = syncErr?.message || String(syncErr);
        // eslint-disable-next-line no-console
        console.warn('[rdv-sync] failed:', msg, syncErr);
        setRdvSyncWarning(`RDV sauvegardé côté SaaS, mais sync app cliente échouée : ${msg}`);
      }

      setRdvEditMode(false);
      onChange();
    } catch (e) {
      setErr(e?.message || 'Erreur sauvegarde RDV');
    } finally {
      setRdvBusy(false);
    }
  };

  const handleEditRdv = () => {
    setRdvDraft(isoToDatetimeLocal(journey.rdv_anamnesis_at));
    setRdvNoteDraft(journey.rdv_anamnesis_note || '');
    setRdvEditMode(true);
  };

  // V97.11 — Handlers anamnèse approfondie
  const handleMarkAnamneseComplete = async () => {
    setAnamneseBusy(true); setErr(null);
    try {
      const { updateJourneyState } = await import('./services/journeyState');
      await updateJourneyState(client.id, {
        anamnesis_completed_at: new Date().toISOString(),
      });
      onChange();
    } catch (e) {
      setErr(e?.message || 'Erreur marquage anamnèse');
    } finally {
      setAnamneseBusy(false);
    }
  };

  const handleUnmarkAnamneseComplete = async () => {
    if (!window.confirm('Repasser l\'anamnèse en "à compléter" ?')) return;
    setAnamneseBusy(true); setErr(null);
    try {
      const { updateJourneyState } = await import('./services/journeyState');
      await updateJourneyState(client.id, {
        anamnesis_completed_at: null,
      });
      onChange();
    } catch (e) {
      setErr(e?.message || 'Erreur démarquage anamnèse');
    } finally {
      setAnamneseBusy(false);
    }
  };

  const handleClearRdv = async () => {
    const clearDetail = shouldSyncRdvToApp
      ? ' La cliente ne le verra plus sur son app.'
      : ' La date sera retirée du dossier SaaS.';
    if (!window.confirm(`Annuler ce RDV ?${clearDetail}`)) return;
    setRdvBusy(true); setErr(null);
    try {
      // 1. Clear dans le SaaS
      const { updateJourneyState } = await import('./services/journeyState');
      await updateJourneyState(client.id, {
        rdv_anamnesis_at: null,
        rdv_anamnesis_note: null,
      });

      // 2. Clear cote app cliente
      setRdvSyncWarning(null);
      if (shouldSyncRdvToApp) try {
        if (!hasClientIdentity(client)) {
          throw new Error('Cliente sans identifiant app — sync impossible');
        }
        await syncNextRdvWithServerVersion(client, null);
      } catch (syncErr) {
        const msg = syncErr?.message || String(syncErr);
        // eslint-disable-next-line no-console
        console.warn('[rdv-sync clear] failed:', msg, syncErr);
        setRdvSyncWarning(`RDV effacé côté SaaS, mais sync app cliente échouée : ${msg}`);
      }

      setRdvEditMode(false);
      setRdvDraft('');
      setRdvNoteDraft('');
      onChange();
    } catch (e) {
      setErr(e?.message || 'Erreur annulation RDV');
    } finally {
      setRdvBusy(false);
    }
  };

  const hasEmail = !!(client.form?.email || client.email);

  // Le pré-questionnaire peut aussi être rempli par lien sans compte. On ne
  // déduit donc l'activation de l'app que d'une preuve d'activation ou d'un
  // questionnaire explicitement envoyé via l'app.
  const appEffectivelyActive = isClientAppEffectivelyActive(client);
  const shouldSyncRdvToApp = shouldSyncNextRdvToClientApp(client, journey);

  return (
    <section className="jrn-stage jrn-stage--onboarding">
      <StepHead
        index={1}
        title="Onboarding cliente"
        intro="Configurez l'expérience cliente, envoyez le pré-questionnaire, recueillez les réponses, puis validez pour lancer le parcours."
      />

      {/* ═══ BLOC 1 — Mode d'accompagnement ════════════════════════
          V97.11.2 : mode compact quand pre-q recu. Les 3 cards lourdes
          ne servent qu'au tout debut (cliente fraichement creee). Une
          fois la cliente engagee, on resume sur une ligne. */}
      {questionnaireReceived ? (
        // ─── Mode compact (cliente deja active) ──────────────────
        <div style={{ marginBottom: 'var(--jrn-6)' }}>
          <p className="jrn-label" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <span style={{ color: 'var(--jrn-accent)', fontWeight: 700 }}>✓</span>
            1 · Mode d'accompagnement
          </p>
          <div className="jrn-surface" style={{
            padding: 'var(--jrn-4) var(--jrn-5)',
            opacity: 0.92,
          }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 'var(--jrn-3)', fontSize: 'var(--jrn-text-sm)', color: 'var(--jrn-text-soft)' }}>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <span>{appEffectivelyActive ? '📱' : '🔗'}</span>
                <strong style={{ color: 'var(--jrn-text)' }}>
                  {appEffectivelyActive ? 'App cliente' : 'Questionnaire par lien'}
                </strong>
                <span style={{ color: 'var(--jrn-accent)', fontWeight: 600 }}>
                  {appEffectivelyActive ? '· Active' : '· Sans compte'}
                </span>
              </span>
              <span style={{ color: 'var(--jrn-text-muted)' }}>·</span>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <span>⚖️</span>
                <strong style={{ color: 'var(--jrn-text)' }}>Suivi poids</strong>
                <span style={{ color: 'var(--jrn-text-muted)' }}>· à configurer étape 7</span>
              </span>
              <span style={{ color: 'var(--jrn-text-muted)' }}>·</span>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <span>📦</span>
                <strong style={{ color: 'var(--jrn-text)' }}>Plan papier</strong>
                <span style={{ color: 'var(--jrn-text-muted)' }}>· à confirmer étape 7</span>
              </span>
            </div>
          </div>
        </div>
      ) : (
      // ─── Mode complet (cliente fraichement creee, rien de fait) ──
      <div style={{ marginBottom: 'var(--jrn-6)' }}>
        <p className="jrn-label">1 · Mode d'accompagnement</p>
        <p style={{ fontSize: 'var(--jrn-text-sm)', color: 'var(--jrn-text-soft)', marginTop: 4, marginBottom: 'var(--jrn-3)', lineHeight: 1.55 }}>
          Décide ici comment la cliente va vivre son parcours : app permanente, livraison papier, tracking poids. Ces choix peuvent être ajustés plus tard.
        </p>
        <div className="jrn-surface" style={{ padding: 'var(--jrn-6)' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--jrn-4)' }}>
            <OnboardingOption
              icon="📱"
              title="App cliente"
              description="Compte permanent avec timeline, notifications, suivi continu. Recommandé pour les suivis longs."
              status={appEffectivelyActive ? 'active' : 'inactive'}
              hint={appEffectivelyActive
                ? 'Activée — la cliente peut se connecter via son lien reçu par email.'
                : 'Inactive — clique sur "Activer" pour créer son espace et lui envoyer le magic link.'}
              ctaLabel={appEffectivelyActive ? null : 'Activer l\'espace cliente'}
              onCtaClick={appEffectivelyActive ? null : handleActivateApp}
              ctaBusy={activatingApp}
              ctaError={activationError}
            />
            <OnboardingOption
              icon="⚖️"
              title="Suivi du poids"
              description="La cliente saisit son poids dans son ressenti quotidien. Configurable étape 7 Livraison."
              status="config-elsewhere"
              hint="Configurer dans étape 7 Livraison (toggles tracking + visible)"
            />
            <OnboardingOption
              icon="📦"
              title="Plan papier"
              description="Livret fondateur premium imprimé envoyé par poste. À confirmer étape 7 Livraison."
              status="config-elsewhere"
              hint="Configurer dans étape 7 Livraison (toggle papier)"
            />
          </div>
        </div>
      </div>
      )}

      {/* ═══ BLOC 2 — Pré-questionnaire ════════════════════════════
          V97.10 : on masque ce bloc une fois les réponses reçues
          (les boutons d'envoi n'ont plus de sens). Le bloc Réponses
          reçues (BLOC 3) prend le relais. */}
      {!questionnaireReceived && (
      <div style={{ marginBottom: 'var(--jrn-6)' }}>
        <p className="jrn-label">2 · Pré-questionnaire</p>
        <p style={{ fontSize: 'var(--jrn-text-sm)', color: 'var(--jrn-text-soft)', marginTop: 4, marginBottom: 'var(--jrn-3)', lineHeight: 1.55 }}>
          Si {client.form?.prenom || client.prenom || 'la cliente'} est activée sur l&apos;app, son pré-questionnaire y est déjà proposé (c&apos;est son étape active dès la connexion) — rien à faire ici. Le bouton ci-dessous ne sert que si elle n&apos;utilise pas l&apos;app.
        </p>
        <div className="jrn-surface" style={{ padding: 'var(--jrn-6)' }}>
          {!hasEmail && (
            <p style={{ margin: 0, fontSize: 'var(--jrn-text-sm)', color: 'var(--jrn-warn)' }}>
              ⚠ Cliente sans email — impossible d'envoyer. Renseignez l'email via Profil.
            </p>
          )}
          {hasEmail && !questionnaireSentAt && (
            <>
              <div className="jrn-actions" style={{ marginTop: 0 }}>
                <button
                  onClick={() => handleSendQuestionnaire('link')}
                  disabled={sendingQuestionnaire !== null}
                  className="jrn-btn jrn-btn--soft"
                  title="Cliente sans app : mail Gmail avec un lien direct vers le questionnaire. Pas besoin de compte."
                >
                  {sendingQuestionnaire === 'link' ? 'Envoi…' : '📩 Envoyer par lien email (sans app)'}
                </button>
              </div>
              <p style={{ marginTop: 'var(--jrn-3)', fontSize: 'var(--jrn-text-xs)', color: 'var(--jrn-text-muted)', lineHeight: 1.55 }}>
                Réservé aux clientes <strong>sans app</strong> : un seul questionnaire à remplir, sans compte. Les clientes activées sur l&apos;app le voient automatiquement.
              </p>
            </>
          )}
          {hasEmail && questionnaireSentAt && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--jrn-3)', flexWrap: 'wrap' }}>
              <span style={{
                fontSize: 11,
                fontWeight: 600,
                padding: '4px 10px',
                borderRadius: 999,
                background: 'var(--jrn-accent-soft)',
                color: 'var(--jrn-accent)',
                textTransform: 'uppercase',
                letterSpacing: '.04em',
              }}>
                ✓ Envoyé {questionnaireMode === 'app' ? '— mode app' : '— mode lien'}
              </span>
              <span style={{ fontSize: 'var(--jrn-text-sm)', color: 'var(--jrn-text-muted)' }}>
                le {new Date(questionnaireSentAt).toLocaleDateString('fr-CH', { day: '2-digit', month: 'long', year: 'numeric' })}
              </span>
              <button
                onClick={() => handleSendQuestionnaire(questionnaireMode || 'link')}
                disabled={sendingQuestionnaire !== null}
                className="jrn-btn jrn-btn--ghost"
                style={{ marginLeft: 'auto', fontSize: 12, padding: '6px 12px' }}
              >
                ↻ Renvoyer
              </button>
            </div>
          )}
        </div>
      </div>
      )}

      {/* ═══ BLOC 3 — Réponses reçues ══════════════════════════════
          V97.10 : renumérotation dynamique — quand BLOC 2 est masqué
          (réponses reçues), ce bloc devient logiquement le n°2.
          V97.11.1 : opacity légère + check inline quand reçues pour
          signaler "déjà fait" sans le rendre criard. */}
      <div style={{ marginBottom: 'var(--jrn-6)', opacity: questionnaireReceived ? 0.92 : 1 }}>
        <p className="jrn-label" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          {questionnaireReceived && <span style={{ color: 'var(--jrn-accent)', fontWeight: 700 }}>✓</span>}
          {questionnaireReceived ? '2' : '3'} · Réponses reçues
        </p>
        <p style={{ fontSize: 'var(--jrn-text-sm)', color: 'var(--jrn-text-soft)', marginTop: 4, marginBottom: 'var(--jrn-3)', lineHeight: 1.55 }}>
          Les réponses arrivent quand la cliente soumet le formulaire (ou pendant le RDV si tu remplis manuellement).
        </p>
        <div className="jrn-surface" style={{ padding: 'var(--jrn-6)' }}>
          {questionnaireReceived ? (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--jrn-3)', marginBottom: 'var(--jrn-3)' }}>
                <span style={{
                  fontSize: 11,
                  fontWeight: 600,
                  padding: '4px 10px',
                  borderRadius: 999,
                  background: 'var(--jrn-accent-soft)',
                  color: 'var(--jrn-accent)',
                  textTransform: 'uppercase',
                  letterSpacing: '.04em',
                }}>
                  🟢 Reçu
                </span>
              </div>
              {/* V97.8.1 (2026-05-12) : affichage des champs réellement remplis
                  par le pré-questionnaire app cliente. On affiche le résumé
                  clinique essentiel — pas la totalité (Anissa creusera au RDV).
                  Champs legacy gardés en fallback si Anissa avait saisi
                  manuellement. */}
              <div className="jrn-kv">
                <div className="jrn-kv__k">Objectif principal</div>
                <div className={`jrn-kv__v ${form.objectif_primaire || form.objectifs ? '' : 'jrn-kv__v--empty'}`}>
                  {form.objectif_primaire || form.objectifs || 'non renseigné'}
                </div>
                <div className="jrn-kv__k">Depuis combien de temps</div>
                <div className={`jrn-kv__v ${form.dureeProbleme ? '' : 'jrn-kv__v--empty'}`}>
                  {form.dureeProbleme || 'non renseigné'}
                </div>
                <div className="jrn-kv__k">Urgence</div>
                <div className={`jrn-kv__v ${form.objectif_urgency ? '' : 'jrn-kv__v--empty'}`}>
                  {form.objectif_urgency === 'urgent_moins_1m' ? 'Urgent (< 1 mois)'
                    : form.objectif_urgency === 'moyen_3_6m' ? 'Moyen terme (3–6 mois)'
                    : form.objectif_urgency === 'long_terme' ? 'Long terme (transformation durable)'
                    : 'non renseigné'}
                </div>
                <div className="jrn-kv__k">Digestion ressentie</div>
                <div className={`jrn-kv__v ${form.ressentiDigestion ? '' : 'jrn-kv__v--empty'}`}>
                  {form.ressentiDigestion === 'Confortable' ? 'Confortable'
                    : form.ressentiDigestion === 'Inconfort_occasionnel' ? 'Inconfort occasionnel'
                    : form.ressentiDigestion === 'Inconfort_frequent' ? 'Inconfort fréquent'
                    : form.ressentiDigestion === 'Inconfort_quotidien' ? 'Inconfort quotidien'
                    : 'non renseigné'}
                </div>
                <div className="jrn-kv__k">Énergie au quotidien</div>
                <div className={`jrn-kv__v ${form.energieJournee ? '' : 'jrn-kv__v--empty'}`}>
                  {form.energieJournee || 'non renseigné'}
                </div>
                <div className="jrn-kv__k">Pathologies</div>
                <div className={`jrn-kv__v ${form.pathologies ? '' : 'jrn-kv__v--empty'}`}>
                  {form.pathologies || 'aucune signalée'}
                </div>
                <div className="jrn-kv__k">Traitements</div>
                <div className={`jrn-kv__v ${formatCurrentTreatments(form, { includeMoment: true }) ? '' : 'jrn-kv__v--empty'}`}>
                  {formatCurrentTreatments(form, { includeMoment: true }) || 'aucun signalé'}
                </div>
                <div className="jrn-kv__k">Allergies</div>
                <div className={`jrn-kv__v ${form.allergies ? '' : 'jrn-kv__v--empty'}`}>
                  {form.allergies || 'aucune signalée'}
                </div>
                <div className="jrn-kv__k">Cycle hormonal</div>
                <div className={`jrn-kv__v ${form.grossesseActuelle || form.contraception ? '' : 'jrn-kv__v--empty'}`}>
                  {(() => {
                    const parts = [];
                    if (form.grossesseActuelle && form.grossesseActuelle !== 'Non') parts.push(form.grossesseActuelle);
                    if (form.contraception) parts.push(`Contraception : ${form.contraception}`);
                    return parts.length > 0 ? parts.join(' · ') : 'non renseigné';
                  })()}
                </div>
              </div>
            </>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--jrn-3)' }}>
              <span style={{
                fontSize: 11,
                fontWeight: 600,
                padding: '4px 10px',
                borderRadius: 999,
                background: 'rgba(184, 134, 38, 0.10)',
                color: '#8a6722',
                textTransform: 'uppercase',
                letterSpacing: '.04em',
              }}>
                🟡 En attente
              </span>
              <span style={{ fontSize: 'var(--jrn-text-sm)', color: 'var(--jrn-text-muted)' }}>
                Aucune réponse pour l'instant. La cliente n'a pas encore rempli le questionnaire.
              </span>
            </div>
          )}
        </div>
      </div>

      {/* ═══ Antécédents familiaux — signal, jamais blocage ════════
          Décision d'Anissa du 2026-08-03. Placé juste sous « Réponses
          reçues », parce que c'est là qu'on lit ce que la cliente a déclaré.

          ⚠️ POURQUOI ICI ET PAS SEULEMENT DANS LE PANNEAU CLINIQUE. Le signal
          avait d'abord été posé dans `ClinicalVerificationPanel`, qui n'est
          monté que dans le formulaire de fiche — pas dans cette étape. Il
          était donc dans le bundle et sur aucun écran que Benoit regardait :
          `Ctrl+F` ne le trouvait pas. Deuxième fois en deux jours qu'un
          composant construit n'atteint pas l'écran visé. */}
      <SignalAntecedentsFamiliaux form={form} />

      {/* ═══ Verrouillage du pré-questionnaire (chantier 9b) ═══════
          Décision d'Anissa du 2026-08-02, arbitrage C. Non numéroté :
          ce n'est pas une étape du parcours mais l'état d'un verrou, et
          la seule commande qui peut le rouvrir. Placé juste sous les
          réponses reçues, parce que c'est là qu'on se demande si elles
          sont bien les dernières. */}
      <QuestionnaireVerrouBloc
        clientId={client.id}
        clientPrenom={client.form?.prenom || client.prenom || ''}
      />

      {/* ═══ Briefing IA — préparation du RDV (V97.34) ═════════════
          Aide à l'anamnèse dérivée du pré-questionnaire. NON numérotée :
          c'est un assistant de préparation, pas une étape du parcours.
          Visible une fois les réponses reçues (le briefing dérive du form).
          Endpoint SaaS-local /api/generate-briefing protégé par
          requireSaaSAdmin (session Supabase Anissa) — pas de secret au front. */}
      {questionnaireReceived && (
        <div style={{ marginBottom: 'var(--jrn-6)' }}>
          <p className="jrn-label" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            Briefing · préparation du RDV
          </p>
          <p style={{ fontSize: 'var(--jrn-text-sm)', color: 'var(--jrn-text-soft)', marginTop: 4, marginBottom: 'var(--jrn-3)', lineHeight: 1.55 }}>
            Synthèse préparatoire générée depuis le pré-questionnaire : pistes à creuser et questions à poser. Une aide à l'anamnèse, jamais une analyse clinique définitive.
          </p>
          <div className="jrn-surface" style={{
            padding: 'var(--jrn-6)',
            ...(briefing ? {
              borderLeft: '3px solid var(--jrn-accent)',
              background: 'linear-gradient(90deg, rgba(46, 78, 56, 0.04) 0%, transparent 60%)',
            } : {}),
          }}>
            {briefing ? (
              <>
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--jrn-3)', flexWrap: 'wrap', marginBottom: 'var(--jrn-3)' }}>
                  <span style={{
                    fontSize: 11,
                    fontWeight: 600,
                    padding: '4px 10px',
                    borderRadius: 999,
                    textTransform: 'uppercase',
                    letterSpacing: '.04em',
                    background: briefing.confidence === 'high' ? 'var(--jrn-accent-soft)' : 'rgba(184, 134, 38, 0.10)',
                    color: briefing.confidence === 'high' ? 'var(--jrn-accent)' : '#8a6722',
                  }}>
                    Fiabilité : {briefing.confidence === 'high' ? 'élevée' : briefing.confidence === 'moderate' ? 'modérée' : 'faible'}
                  </span>
                  {briefingGeneratedAt && (
                    <span style={{ fontSize: 'var(--jrn-text-xs)', color: 'var(--jrn-text-muted)' }}>
                      généré le {new Date(briefingGeneratedAt).toLocaleDateString('fr-CH', { day: '2-digit', month: 'long', year: 'numeric' })}
                    </span>
                  )}
                </div>

                {briefing.executive_summary && (
                  <p style={{ fontSize: 'var(--jrn-text-sm)', color: 'var(--jrn-text)', lineHeight: 1.6, marginBottom: 'var(--jrn-4)' }}>
                    {briefing.executive_summary}
                  </p>
                )}

                {Array.isArray(briefing.red_flags) && briefing.red_flags.length > 0 && (() => {
                  const noFlag = briefing.red_flags.length === 1 && /aucun red flag/i.test(briefing.red_flags[0]);
                  return (
                    <div style={{ marginBottom: 'var(--jrn-4)' }}>
                      <p style={{ fontSize: 'var(--jrn-text-xs)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.04em', color: noFlag ? 'var(--jrn-text-soft)' : '#a33', marginBottom: 6 }}>
                        {noFlag ? 'Vigilance' : '⚠ Points de vigilance'}
                      </p>
                      <ul style={{ margin: 0, paddingLeft: 18, fontSize: 'var(--jrn-text-sm)', color: noFlag ? 'var(--jrn-text-muted)' : '#a33', lineHeight: 1.6 }}>
                        {briefing.red_flags.map((f, i) => <li key={i} style={{ marginBottom: 4 }}>{f}</li>)}
                      </ul>
                    </div>
                  );
                })()}

                <BriefingList title="Sujets prioritaires à creuser" items={briefing.priority_topics} />
                <BriefingList title="Questions à explorer au RDV" items={briefing.questions_to_explore} />
                <BriefingList title="Pistes d'analyses (à valider cliniquement)" items={briefing.suggested_analyses} />

                <div className="jrn-actions" style={{ marginTop: 'var(--jrn-4)' }}>
                  <button
                    onClick={handleGenerateBriefing}
                    disabled={briefingBusy}
                    className="jrn-btn jrn-btn--ghost"
                    style={{ fontSize: 12, padding: '6px 12px' }}
                  >
                    {briefingBusy ? '✨ Régénération…' : '✨ Régénérer'}
                  </button>
                </div>
              </>
            ) : (
              <>
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--jrn-3)', marginBottom: 'var(--jrn-3)' }}>
                  <span style={{ fontSize: 'var(--jrn-text-sm)', color: 'var(--jrn-text-muted)' }}>
                    Aucun briefing généré pour l'instant.
                  </span>
                </div>
                <div className="jrn-actions" style={{ marginTop: 0 }}>
                  <button
                    onClick={handleGenerateBriefing}
                    disabled={briefingBusy}
                    className="jrn-btn jrn-btn--soft"
                  >
                    {briefingBusy ? 'Préparation…' : 'Préparer le briefing'}
                  </button>
                </div>
              </>
            )}
            {briefingError && (
              <p style={{ marginTop: 'var(--jrn-3)', fontSize: 'var(--jrn-text-sm)', color: '#c25050', background: 'rgba(220, 80, 80, 0.08)', padding: '8px 12px', borderRadius: 6, lineHeight: 1.5 }}>
                ⚠ {briefingError}
              </p>
            )}
          </div>
        </div>
      )}

      {/* ═══ Questions du RDV (2026-07-07) ═════════════════════════════
          Rend le briefing IA actionnable : questions cochables + réponses
          saisies au RDV, jointes à l'anamnèse (form.rdv_questions) et
          injectées dans le plan (buildRdvNotesBlockFr). Composant autonome
          contrôlé : ici la persistance est immédiate (saveClient + refresh) ;
          il est aussi monté dans le formulaire d'anamnèse (AnissaClientForm,
          persistance au submit). Visible une fois le pré-q reçu, comme le briefing. */}
      {questionnaireReceived && (
        <RdvQuestionsBlock
          questions={rdvQuestions}
          onChange={(next) => {
            // V97.99 — état local + persistance en arrière-plan (saveClient),
            // SANS onChange?.() : plus de rechargement de page, donc plus de
            // retour en haut à chaque coche. La donnée est bien sauvegardée.
            setRdvQuestions(next);
            saveClient({ ...client, form: { ...client.form, rdv_questions: next } });
          }}
          variant="journey"
        />
      )}

      {/* ═══ BLOC RDV anamnèse (V97.10) ════════════════════════════
          Visible une fois les réponses du pré-q reçues. Anissa fixe la
          date du RDV, la cliente la verra sur son app + recevra une
          notif J-1 (extension cron V97.11.2, à wirer Phase B).
          V97.11.1 : surface visuellement accent quand RDV fixe — devient
          le "point chaud" de l'etape Onboarding. */}
      {questionnaireReceived && (
        <div style={{ marginBottom: 'var(--jrn-6)' }}>
          <p className="jrn-label">3 · RDV anamnèse</p>
          <p style={{ fontSize: 'var(--jrn-text-sm)', color: 'var(--jrn-text-soft)', marginTop: 4, marginBottom: 'var(--jrn-3)', lineHeight: 1.55 }}>
            Fixez la date du rendez-vous. La cliente le verra sur son app.
          </p>
          <div className="jrn-surface" style={{
            padding: 'var(--jrn-6)',
            ...(journey.rdv_anamnesis_at && !rdvEditMode ? {
              borderLeft: '3px solid var(--jrn-accent)',
              background: 'linear-gradient(90deg, rgba(46, 78, 56, 0.04) 0%, transparent 60%)',
            } : {}),
          }}>
            {(!journey.rdv_anamnesis_at || rdvEditMode) ? (
              <>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--jrn-3)', marginBottom: 'var(--jrn-3)' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: 'var(--jrn-text-sm)', color: 'var(--jrn-text-soft)', marginBottom: 4 }}>
                      Date et heure
                    </label>
                    <input
                      type="datetime-local"
                      value={rdvDraft}
                      onChange={(e) => setRdvDraft(e.target.value)}
                      style={{
                        width: '100%',
                        padding: '8px 10px',
                        border: '1px solid var(--jrn-border)',
                        borderRadius: 6,
                        background: 'var(--jrn-surface-2, transparent)',
                        color: 'inherit',
                        fontSize: 'var(--jrn-text-sm)',
                      }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: 'var(--jrn-text-sm)', color: 'var(--jrn-text-soft)', marginBottom: 4 }}>
                      Note (optionnel)
                    </label>
                    <input
                      type="text"
                      value={rdvNoteDraft}
                      onChange={(e) => setRdvNoteDraft(e.target.value)}
                      placeholder="Cabinet, visio Zoom, téléphone…"
                      style={{
                        width: '100%',
                        padding: '8px 10px',
                        border: '1px solid var(--jrn-border)',
                        borderRadius: 6,
                        background: 'var(--jrn-surface-2, transparent)',
                        color: 'inherit',
                        fontSize: 'var(--jrn-text-sm)',
                      }}
                    />
                  </div>
                </div>
                <div className="jrn-actions" style={{ marginTop: 0 }}>
                  <button
                    onClick={handleSaveRdv}
                    disabled={rdvBusy || !rdvDraft}
                    className="jrn-btn jrn-btn--primary"
                  >
                    {rdvBusy ? '…' : 'Confirmer le RDV'}
                  </button>
                  {rdvEditMode && (
                    <button
                      onClick={() => setRdvEditMode(false)}
                      disabled={rdvBusy}
                      className="jrn-btn jrn-btn--ghost"
                    >
                      Annuler
                    </button>
                  )}
                </div>
              </>
            ) : (
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--jrn-3)', flexWrap: 'wrap' }}>
                  <span style={{
                    fontSize: 11,
                    fontWeight: 600,
                    padding: '4px 10px',
                    borderRadius: 999,
                    background: 'var(--jrn-accent-soft)',
                    color: 'var(--jrn-accent)',
                    textTransform: 'uppercase',
                    letterSpacing: '.04em',
                  }}>
                    📅 RDV prévu
                  </span>
                  <span style={{ fontSize: 'var(--jrn-text-md)', fontWeight: 600 }}>
                    {formatRdv(journey.rdv_anamnesis_at)}
                  </span>
                  {journey.rdv_anamnesis_note && (
                    <span style={{ fontSize: 'var(--jrn-text-sm)', color: 'var(--jrn-text-muted)' }}>
                      · {journey.rdv_anamnesis_note}
                    </span>
                  )}
                </div>
                <div className="jrn-actions" style={{ marginTop: 'var(--jrn-3)' }}>
                  <button
                    onClick={handleEditRdv}
                    disabled={rdvBusy}
                    className="jrn-btn jrn-btn--ghost"
                    style={{ fontSize: 12, padding: '6px 12px' }}
                  >
                    Modifier
                  </button>
                  <button
                    onClick={handleClearRdv}
                    disabled={rdvBusy}
                    className="jrn-btn jrn-btn--ghost"
                    style={{ fontSize: 12, padding: '6px 12px' }}
                  >
                    Annuler le RDV
                  </button>
                </div>
              </div>
            )}
            {rdvSyncWarning && (
              <p style={{
                marginTop: 'var(--jrn-3)',
                fontSize: 'var(--jrn-text-sm)',
                color: 'var(--jrn-warn, #b8861f)',
                background: 'rgba(184, 134, 38, 0.08)',
                padding: '8px 12px',
                borderRadius: 6,
                lineHeight: 1.5,
              }}>
                ⚠ {rdvSyncWarning}
              </p>
            )}
          </div>
        </div>
      )}

      {/* ═══ BLOC Anamnèse approfondie (V97.11) ═══════════════════
          Visible une fois les réponses du pré-q reçues (typiquement
          remplie pendant ou après le RDV anamnèse). Timestamp explicite
          journey_state.anamnesis_completed_at marque la fin par Anissa. */}
      {questionnaireReceived && (
        <div style={{ marginBottom: 'var(--jrn-6)' }}>
          <p className="jrn-label">4 · Anamnèse approfondie</p>
          <p style={{ fontSize: 'var(--jrn-text-sm)', color: 'var(--jrn-text-soft)', marginTop: 4, marginBottom: 'var(--jrn-3)', lineHeight: 1.55 }}>
            Pendant ou après le RDV, complète l'anamnèse approfondie : sommeil, sport, antibiotiques, transit, glycémie, mode de vie…
          </p>
          <div className="jrn-surface" style={{
            padding: 'var(--jrn-6)',
            ...(anamnesisCompletionConfirmed ? {
              borderLeft: '3px solid var(--jrn-accent)',
              background: 'linear-gradient(90deg, rgba(46, 78, 56, 0.04) 0%, transparent 60%)',
            } : {}),
          }}>
            {anamnesisCompletionConfirmed ? (
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--jrn-3)', flexWrap: 'wrap' }}>
                  <span style={{
                    fontSize: 13,
                    fontWeight: 700,
                    padding: '6px 14px',
                    borderRadius: 999,
                    background: 'var(--jrn-accent)',
                    color: 'var(--jrn-ivory, #f5f0e8)',
                    letterSpacing: '.02em',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                  }}>
                    ✓ Anamnèse complétée
                  </span>
                  <span style={{ fontSize: 'var(--jrn-text-sm)', color: 'var(--jrn-text-soft)', fontWeight: 500 }}>
                    le {new Date(journey.anamnesis_completed_at).toLocaleDateString('fr-CH', { day: '2-digit', month: 'long', year: 'numeric' })}
                  </span>
                </div>
                <div className="jrn-actions" style={{ marginTop: 'var(--jrn-3)' }}>
                  {onEditProfile && (
                    <button
                      onClick={onEditProfile}
                      disabled={anamneseBusy}
                      className="jrn-btn jrn-btn--ghost"
                      style={{ fontSize: 12, padding: '6px 12px' }}
                    >
                      Modifier
                    </button>
                  )}
                  <button
                    onClick={handleUnmarkAnamneseComplete}
                    disabled={anamneseBusy}
                    className="jrn-btn jrn-btn--ghost"
                    style={{ fontSize: 12, padding: '6px 12px' }}
                  >
                    Repasser en cours
                  </button>
                </div>
              </div>
            ) : (
              <>
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--jrn-3)', marginBottom: 'var(--jrn-3)' }}>
                  <span style={{
                    fontSize: 11,
                    fontWeight: 600,
                    padding: '4px 10px',
                    borderRadius: 999,
                    background: 'rgba(184, 134, 38, 0.10)',
                    color: '#8a6722',
                    textTransform: 'uppercase',
                    letterSpacing: '.04em',
                  }}>
                    🟡 À compléter
                  </span>
                  <span style={{ fontSize: 'var(--jrn-text-sm)', color: 'var(--jrn-text-muted)' }}>
                    Marque comme complétée une fois que tu as fini de remplir les champs.
                  </span>
                </div>
                <div className="jrn-actions" style={{ marginTop: 0 }}>
                  {onEditProfile && (
                    <button onClick={onEditProfile} className="jrn-btn jrn-btn--primary">
                      Compléter l'anamnèse
                    </button>
                  )}
                  <button
                    onClick={handleMarkAnamneseComplete}
                    disabled={anamneseBusy}
                    className="jrn-btn jrn-btn--soft"
                  >
                    {anamneseBusy ? '…' : 'Marquer comme complétée'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* ═══ BLOC Validation onboarding (CTA final) ═══════════ */}
      <div style={{ marginBottom: 'var(--jrn-6)' }}>
        <p className="jrn-label">{questionnaireReceived ? '5' : '4'} · Validation</p>
        <p style={{ fontSize: 'var(--jrn-text-sm)', color: 'var(--jrn-text-soft)', marginTop: 4, marginBottom: 'var(--jrn-3)', lineHeight: 1.55 }}>
          Une fois le RDV anamnèse fait et les informations clés vérifiées, validez pour passer à l'étape Analyses.
        </p>
        {!canValidateAnamnesis && (
          <p style={{
            marginBottom: 'var(--jrn-3)',
            fontSize: 'var(--jrn-text-sm)',
            color: '#6b5018',
            background: 'rgba(184, 134, 38, 0.06)',
            border: '1px solid rgba(184, 134, 38, 0.18)',
            padding: '10px 14px',
            borderRadius: 8,
            lineHeight: 1.55,
            fontStyle: 'italic',
          }}>
            {questionnaireReceived
              ? 'Termine l’entretien, puis marque l’anamnèse comme complétée avant de passer aux analyses.'
              : 'Renseigne d’abord les informations essentielles de l’anamnèse. Le passage aux analyses restera fermé jusque-là.'}
          </p>
        )}
        <div className="jrn-actions" style={{ marginTop: 0 }}>
          <button onClick={handleValidate} disabled={busy || !canValidateAnamnesis} className="jrn-btn jrn-btn--primary">
            {busy ? '…' : (questionnaireReceived ? 'Valider l\'onboarding et passer aux analyses' : 'Continuer le parcours →')}
          </button>
        </div>
        <ErrorLine msg={err} />
      </div>

      <p style={{ fontSize: 'var(--jrn-text-xs)', color: 'var(--jrn-text-muted)', marginTop: 'var(--jrn-4)' }}>
        L'édition complète de l'anamnèse se fait via le bouton <strong>Profil</strong> en haut.
      </p>
    </section>
  );
}

// Bloc OnboardingOption — option avec icône, titre, description, statut
// V97.4 (2026-05-12) : ajout props optionnelles ctaLabel + onCtaClick +
// ctaBusy + ctaError pour transformer la carte d'un affichage passif en
// CTA opérationnel. Utilisé pour activer l'app cliente directement depuis
// l'onboarding étape 1 sans passer par le bouton Profil (= ancien doublon UX).
function OnboardingOption({ icon, title, description, status, hint, ctaLabel, onCtaClick, ctaBusy, ctaError }) {
  // status: 'active' | 'inactive' | 'config-elsewhere'
  const badgeProps = {
    active:           { label: 'Activé',         bg: 'var(--jrn-accent-soft)',     color: 'var(--jrn-accent)' },
    inactive:         { label: 'Inactif',        bg: 'rgba(40,32,20,0.06)',         color: 'var(--jrn-text-muted)' },
    'config-elsewhere':{ label: 'À configurer',  bg: 'rgba(184,134,38,0.10)',       color: '#8a6722' },
  }[status] || { label: status, bg: 'rgba(0,0,0,0.05)', color: 'var(--jrn-text-muted)' };

  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--jrn-4)' }}>
      <span style={{ fontSize: 22, flexShrink: 0, marginTop: 2 }}>{icon}</span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--jrn-2)', marginBottom: 4, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 15, fontWeight: 600, color: 'var(--jrn-text)' }}>{title}</span>
          <span style={{
            fontSize: 10,
            fontWeight: 600,
            padding: '3px 8px',
            borderRadius: 999,
            background: badgeProps.bg,
            color: badgeProps.color,
            textTransform: 'uppercase',
            letterSpacing: '.04em',
            whiteSpace: 'nowrap',
          }}>
            {badgeProps.label}
          </span>
        </div>
        <div style={{ fontSize: 13, color: 'var(--jrn-text-soft)', lineHeight: 1.55 }}>{description}</div>
        {hint && (
          <div style={{ fontSize: 11, color: 'var(--jrn-text-muted)', marginTop: 4, fontStyle: 'italic' }}>{hint}</div>
        )}
        {/* V97.4 — CTA fonctionnel quand on a un callback (typiquement
            "Activer l'espace cliente" pour App cliente inactive). */}
        {ctaLabel && onCtaClick && (
          <div style={{ marginTop: 'var(--jrn-3)' }}>
            <button
              type="button"
              onClick={onCtaClick}
              disabled={ctaBusy}
              className="jrn-btn jrn-btn--soft"
              style={{ fontSize: 13 }}
            >
              {ctaBusy ? '…' : ctaLabel}
            </button>
            {ctaError && (
              <div style={{ marginTop: 'var(--jrn-2)', fontSize: 12, color: 'var(--jrn-error, #b22222)' }}>
                {ctaError}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// ÉTAPE 2 — ANALYSES
// ═══════════════════════════════════════════════════════════════════

function StepAnalyses({ client, journey, onChange }) {
  const [showSuggest, setShowSuggest] = useState(false);
  const [savePlanError, setSavePlanError] = useState(null);
  const [savingTransition, setSavingTransition] = useState(false);
  const [hasPlan, setHasPlan] = useState(null);
  const [planLoadError, setPlanLoadError] = useState(null);
  // V97.141 : incremente a chaque plan valide pour remonter AnalysisPlanCard.
  const [planVersion, setPlanVersion] = useState(0);

  useEffect(() => {
    let cancelled = false;
    async function check() {
      setHasPlan(null);
      setPlanLoadError(null);
      const { data, error } = await supabase
        .from('analysis_plans')
        .select('id')
        .eq('client_id', client.id)
        .limit(1)
        .maybeSingle();
      if (cancelled) return;
      if (error) {
        setPlanLoadError('Impossible de vérifier les plans d’analyses. Recharge la page avant de continuer.');
        return;
      }
      setHasPlan(!!data);
    }
    check();
    return () => { cancelled = true; };
  }, [client.id]);

  const handleSavePlan = async (plan) => {
    setSavePlanError(null);
    const packAccess = getPackAccess(client.packType);
    if (!packAccess.known || plan?.pack_type !== client.packType) {
      const message = 'Pack à vérifier avant d’enregistrer un plan d’analyses';
      setSavePlanError(message);
      throw new Error(message);
    }
    // V97.141 : un seul brouillon a la fois. « Refaire la suggestion » doit
    // REMPLACER le brouillon en cours, pas en empiler un second : la carte
    // n'affiche que le plus recent, et deux brouillons concurrents pour une
    // meme cliente rendraient les couts ambigus.
    //
    // On insere AVANT de nettoyer, jamais l'inverse : Supabase ne nous donne
    // pas de transaction ici, et supprimer d'abord ferait perdre son plan a
    // Anissa si l'insert echouait derriere. Dans le pire cas on garde deux
    // brouillons — la carte affiche le plus recent, donc le bon.
    const { data: created, error } = await supabase
      .from('analysis_plans')
      .insert(plan)
      .select('id')
      .single();
    if (error) {
      setSavePlanError(error.message);
      throw new Error(error.message);
    }
    // On ne supprime QUE les brouillons de CETTE cliente, et jamais celui
    // qu'on vient de creer : un plan envoye / en cours / termine est un
    // document reel (la cliente a ses kits), il n'est jamais ecrase.
    // Un echec ici n'annule pas la sauvegarde : le plan est deja en base.
    const { error: cleanErr } = await supabase
      .from('analysis_plans')
      .delete()
      .eq('client_id', plan.client_id)
      .eq('status', 'draft')
      .neq('id', created.id);
    if (cleanErr) setSavePlanError(cleanErr.message);

    setHasPlan(true);
    // Force le remount de la carte : son chargement ne depend que de
    // clientId, sans ca elle continuerait d'afficher le plan precedent.
    setPlanVersion((v) => v + 1);
  };

  const handleValidate = async () => {
    setSavingTransition(true);
    try {
      await transitions.validateAnalyses(client.id);
      onChange();
    } catch (e) { setSavePlanError(e?.message || 'Erreur'); }
    finally { setSavingTransition(false); }
  };

  const handleSkip = async () => {
    if (hasPlan !== false || planLoadError) {
      setSavePlanError('Impossible de vérifier les plans d’analyses. Recharge la page avant de continuer.');
      return;
    }
    if (!window.confirm('Continuer sans analyse ni résultat à ajouter ?\n\nLe parcours passera directement à la génération du plan.')) return;
    setSavingTransition(true);
    try {
      await transitions.skipAnalyses(client.id);
      onChange();
    } catch (e) { setSavePlanError(e?.message || 'Erreur'); }
    finally { setSavingTransition(false); }
  };

  const handleExistingMgdReport = async () => {
    if (hasPlan !== false || planLoadError) {
      setSavePlanError('Impossible de vérifier les plans d’analyses. Recharge la page avant de continuer.');
      return;
    }
    setSavingTransition(true);
    setSavePlanError(null);
    try {
      // Aucun plan n'est inventé : on ouvre seulement l'étape de réception,
      // où le dépôt conservera l'origine distincte `historical_initial`.
      await transitions.validateAnalyses(client.id);
      onChange();
    } catch (e) { setSavePlanError(e?.message || 'Erreur'); }
    finally { setSavingTransition(false); }
  };

  return (
    <section className="jrn-stage jrn-stage--analyses">
      <StepHead
        index={2}
        title="Analyses biologiques"
        intro="Décide ici si Anissa prescrit des analyses, utilise des résultats déjà disponibles ou continue sans analyse."
      />

      {/* BC.5B : structure en blocs numérotés style étape 1 (Onboarding) */}

      {/* ─── Bloc 1 : Plan d'analyses ────────────────────────────── */}
      <div className="jrn-block">
        <div className="jrn-block__head">
          <span className="jrn-block__num">1</span>
          <h3 className="jrn-block__title">Plan d'analyses</h3>
        </div>
        <p className="jrn-block__intro">
          {hasPlan === false
            ? 'Le pack ne décide pas à ta place : les tests peuvent être inclus, réglés séparément par la cliente ou ne pas être nécessaires. Choisis simplement le bon scénario.'
            : hasPlan === true
            ? 'Le plan d\'analyses est en place. Vérifie les tests sélectionnés ci-dessous avant validation.'
            : 'Chargement du statut…'}
        </p>

        {hasPlan === null && !planLoadError && (
          <div className="jrn-surface jrn-surface--quiet">
            <div className="jrn-empty">
              <div className="jrn-empty__icon">⏳</div>
              <p className="jrn-empty__title">Vérification en cours</p>
              <p className="jrn-empty__hint">Chargement du plan d'analyses de la cliente…</p>
            </div>
          </div>
        )}

        {planLoadError && (
          <div className="jrn-surface jrn-surface--warn" role="alert">
            {planLoadError}
          </div>
        )}

        {hasPlan === false && (() => {
          // V97.8.1 (2026-05-13) : 3 sous-états selon l'avancement de
          // l'onboarding. Avant ce fix, on proposait 'Lancer la suggestion IA'
          // dès l'étape 2, même si la cliente n'avait pas rempli son
          // pré-questionnaire — l'IA aurait alors rien à exploiter.
          const f = client.form || {};
          const preQReceived = isAnamneseFilled(f);
          const anamnesisValidated = journey?.anamnesis_validated === true;

          // État A : pré-q pas encore rempli par la cliente
          if (!preQReceived) {
            return (
              <div className="jrn-surface jrn-surface--quiet">
                <div className="jrn-empty">
                  <div className="jrn-empty__icon">⏳</div>
                  <p className="jrn-empty__title">En attente du pré-questionnaire</p>
                  <p className="jrn-empty__hint">
                    La cliente n&apos;a pas encore rempli son pré-questionnaire dans l&apos;app.
                    Une fois reçu, tu pourras valider l&apos;anamnèse à l&apos;étape 1, puis
                    choisir les analyses.
                  </p>
                  <div className="jrn-actions" style={{ marginTop: 'var(--jrn-2)' }}>
                    <button onClick={handleSkip} disabled={savingTransition} className="jrn-btn jrn-btn--ghost">
                      Passer cette étape (cliente sans analyses)
                    </button>
                  </div>
                </div>
              </div>
            );
          }

          // État B : pré-q reçu mais anamnèse pas encore validée par Anissa
          if (!anamnesisValidated) {
            return (
              <div className="jrn-surface jrn-surface--quiet">
                <div className="jrn-empty">
                  <div className="jrn-empty__icon">📋</div>
                  <p className="jrn-empty__title">Pré-questionnaire reçu</p>
                  <p className="jrn-empty__hint">
                    Avant de proposer les analyses, retourne sur l&apos;étape <strong>Onboarding</strong> pour
                    relire les réponses, faire le RDV anamnèse et valider. Le catalogue et la suggestion
                    éventuelle seront ensuite disponibles, sans présélection automatique.
                  </p>
                  <div className="jrn-actions" style={{ marginTop: 'var(--jrn-2)' }}>
                    <button onClick={handleSkip} disabled={savingTransition} className="jrn-btn jrn-btn--ghost">
                      Passer cette étape
                    </button>
                  </div>
                </div>
              </div>
            );
          }

          // État C : anamnèse validée — on peut proposer les analyses (comportement original)
          return (
            <div className="jrn-surface jrn-surface--quiet">
              <div className="jrn-empty">
                <div className="jrn-empty__icon">🧪</div>
                <p className="jrn-empty__title">Quel est le scénario pour cette cliente ?</p>
                <p className="jrn-empty__hint">
                  Une prescription peut être créée même si la cliente règle les tests séparément. Aucun choix n&apos;est fait automatiquement.
                </p>
                <div className="jrn-actions" style={{ marginTop: 'var(--jrn-2)' }}>
                  <button onClick={() => setShowSuggest(true)} className="jrn-btn jrn-btn--primary">
                    Prescrire des analyses
                  </button>
                  <button onClick={handleExistingMgdReport} disabled={savingTransition} className="jrn-btn jrn-btn--soft">
                    Utiliser des résultats déjà disponibles →
                  </button>
                  <button onClick={handleSkip} disabled={savingTransition} className="jrn-btn jrn-btn--ghost">
                    Aucune analyse pour cette cliente
                  </button>
                </div>
              </div>
            </div>
          );
        })()}

        {hasPlan === true && (
          <AnalysisPlanCard
            key={planVersion}
            clientId={client.id}
            onRelaunch={() => setShowSuggest(true)}
          />
        )}
      </div>

      {/* ─── Bloc 2 : Validation (si plan en place) ─────────────── */}
      {hasPlan === true && (
        <div className="jrn-block">
          <div className="jrn-block__head">
            <span className="jrn-block__num">2</span>
            <h3 className="jrn-block__title">Validation</h3>
          </div>
          <p className="jrn-block__intro">
            Une fois les tests vérifiés, valide pour passer en attente des résultats.
          </p>
          <div className="jrn-actions" style={{ marginTop: 0 }}>
            <button onClick={handleValidate} disabled={savingTransition} className="jrn-btn jrn-btn--hero">
              {savingTransition ? '…' : 'Valider et passer en attente résultats →'}
            </button>
          </div>
        </div>
      )}

      <ErrorLine msg={savePlanError} />

      <AnalysisSuggestionModal
        isOpen={showSuggest}
        client={client}
        packType={client.packType}
        onClose={() => setShowSuggest(false)}
        onValidate={async (plan) => {
          await handleSavePlan(plan);
          setShowSuggest(false);
        }}
      />
    </section>
  );
}

// ═══════════════════════════════════════════════════════════════════
// ÉTAPE 3 — ATTENTE RÉSULTATS
// ═══════════════════════════════════════════════════════════════════

export function StepWaitingResults({ client, onChange }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const externalFlow = client?.journey_state?.external_dossier_flow === true;
  const resultsReceived = client?.journey_state?.results_received === true;

  const handleReceived = async () => {
    setBusy(true); setErr(null);
    try {
      if (externalFlow) {
        await transitions.markResultsReceivedToExternalSynthesis(
          client.id,
          client.journey_state,
        );
      } else {
        await transitions.markResultsReceived(client.id);
      }
      onChange();
    } catch (e) { setErr(e?.message || 'Erreur'); }
    finally { setBusy(false); }
  };

  return (
    <section className="jrn-stage jrn-stage--waiting-results">
      <StepHead
        index={3}
        total={externalFlow ? 7 : 8}
        title="Recevoir les résultats"
        intro="Les rapports ne sont ni déposés ni transformés dans le SaaS. Utilise les PDF originaux dans ta conversation de travail."
      />

      <div className="jrn-block">
          <div className="jrn-block__head">
            <span className="jrn-block__num">1</span>
            <h3 className="jrn-block__title">Réception du rapport</h3>
          </div>
          <p className="jrn-block__intro">
            Garde les PDF originaux dans ton espace professionnel, utilise-les dans ta conversation de travail, puis reviens ici pour poursuivre.
          </p>
          <MgdReportUploadBlock clientId={client.id} />
          <div className="jrn-actions" style={{ marginTop: 0 }}>
            <button
              type="button"
              onClick={handleReceived}
              disabled={busy}
              className="jrn-btn jrn-btn--hero"
              aria-busy={busy}
            >
              {busy ? 'Enregistrement…' : externalFlow
                ? 'PDF reçus — continuer vers la synthèse'
                : resultsReceived
                  ? 'Continuer vers la revue des résultats'
                  : 'Marquer le rapport comme reçu'}
            </button>
          </div>
        </div>

      <details className="jrn-block">
        <summary className="jrn-block__title">Voir la prescription</summary>
        <div style={{ marginTop: 18 }}>
          <h3 className="jrn-block__title">Plan d’analyses prescrit</h3>
          <AnalysisPlanCard clientId={client.id} />
        </div>
      </details>

      <ErrorLine msg={err} />
    </section>
  );
}

// ═══════════════════════════════════════════════════════════════════
// ÉTAPE 4 — SAISIE RÉSULTATS
// ═══════════════════════════════════════════════════════════════════

const resultRowKey = (row) => row?.test_code || row?.code || row?.name || row?.test_name || null;

export function mergeEditableOrthoRowsPreservingRestricted(originalRows, editedRows) {
  const original = Array.isArray(originalRows) ? originalRows : [];
  const editedOrtho = (Array.isArray(editedRows) ? editedRows : [])
    .filter((row) => resolveResultLaboratory(row) === 'ortho_analytic');
  const editedByKey = new Map(editedOrtho.map((row) => [resultRowKey(row), row])
    .filter(([key]) => key));
  const consumed = new Set();
  const merged = [];
  for (const row of original) {
    if (resolveResultLaboratory(row) !== 'ortho_analytic') {
      merged.push(row);
      continue;
    }
    const key = resultRowKey(row);
    if (key && editedByKey.has(key)) {
      merged.push(editedByKey.get(key));
      consumed.add(key);
    }
  }
  for (const row of editedOrtho) {
    const key = resultRowKey(row);
    if (!key || !consumed.has(key)) merged.push(row);
  }
  return merged;
}

export function hydrateOrthoPlanResultsPreservingRestricted(planTests, previousRows) {
  const previous = Array.isArray(previousRows) ? previousRows : [];
  const restricted = previous.filter((row) => resolveResultLaboratory(row) !== 'ortho_analytic');
  const previousOrtho = previous.filter((row) => resolveResultLaboratory(row) === 'ortho_analytic');
  const previousOrthoByKey = new Map(previousOrtho.map((row) => [resultRowKey(row), row])
    .filter(([key]) => key));
  const editableTests = (Array.isArray(planTests) ? planTests : [])
    .filter((test) => resolveResultLaboratory(test) === 'ortho_analytic');
  const hydratedOrtho = editableTests.map((test) => {
    const key = test.code || test.name;
    const existing = previousOrthoByKey.get(key);
    if (existing) return { ...existing, source_lab: 'ortho_analytic' };
    return {
      test_code: test.code,
      test_name: test.name || test.code,
      source_lab: 'ortho_analytic',
      value: '',
      synthesis: '',
      category: null,
      status: null,
    };
  });
  return [...preservePreviousPlanResults(hydratedOrtho, previousOrtho), ...restricted];
}

export function classifyExplicitPlanLaboratories(planTests) {
  const tests = Array.isArray(planTests) ? planTests : [];
  const laboratories = tests.map((test) => resolveResultLaboratory(test));
  return {
    hasMgdPlan: laboratories.includes('mgd'),
    hasOrthoPlan: laboratories.includes('ortho_analytic'),
    hasUnknownPlan: laboratories.some((laboratory) => laboratory === null),
    unknownPlanCount: laboratories.filter((laboratory) => laboratory === null).length,
    allMgdPlan: laboratories.length > 0
      && laboratories.every((laboratory) => laboratory === 'mgd'),
  };
}

export function buildDownstreamOrthoResultContext(journey) {
  return filterNonMgdResultsData(journey?.results_data);
}

export function countHistoricalResultsWithUnknownLaboratory(resultsData) {
  const data = resultsData && typeof resultsData === 'object' ? resultsData : {};
  return [...(Array.isArray(data.from_plan) ? data.from_plan : []),
    ...(Array.isArray(data.external) ? data.external : [])]
    .filter((row) => resolveResultLaboratory(row) === null).length;
}

export function StepResults({ client, onChange }) {
  // Init data depuis journey_state.results_data, fallback sur l'ancien field results_synthesis
  const initialData = client.journey_state?.results_data || {
    from_plan: [],
    external: [],
    global_synthesis: client.journey_state?.results_synthesis || '',
  };
  const [resultsByTest, setResultsByTest] = useState(initialData.from_plan || []);
  const [externalAnalyses, setExternalAnalyses] = useState(initialData.external || []);
  const [globalSynthesis, setGlobalSynthesis] = useState(initialData.global_synthesis || '');

  const [planTests, setPlanTests] = useState(null);
  const [planLoadState, setPlanLoadState] = useState('loading');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  // Pré-rédaction IA du BROUILLON de synthèse globale (l'IA propose, Anissa décide).
  const [synthesisAiBusy, setSynthesisAiBusy] = useState(false);
  const [synthesisAiErr, setSynthesisAiErr] = useState(null);
  // 2026-07-26 — modal « Préparer l'appel résultats ».
  const [callPrepOpen, setCallPrepOpen] = useState(false);

  // Charge le analysis_plan de la cliente pour pre-remplir les cartes par test
  useEffect(() => {
    let cancelled = false;
    async function load() {
      setPlanLoadState('loading');
      const { data, error } = await supabase
        .from('analysis_plans')
        .select('id, created_at, status, selected_tests')
        .eq('client_id', client.id)
        .in('status', ['sent', 'in_progress', 'completed'])
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      if (cancelled) return;
      if (error) {
        setPlanTests(null);
        setPlanLoadState('error');
        return;
      }
      const tests = Array.isArray(data?.selected_tests) ? data.selected_tests : [];
      setPlanTests(tests);
      setPlanLoadState('ready');
      // Merge : on garde les valeurs deja saisies, on ajoute les tests du plan absents.
      setResultsByTest((prev) => {
        return hydrateOrthoPlanResultsPreservingRestricted(tests, prev);
      });
    }
    load();
    return () => { cancelled = true; };
  }, [client.id]);

  const updateTestField = (idx, field, value) => {
    setResultsByTest((prev) => prev.map((r, i) => i === idx ? { ...r, [field]: value } : r));
  };

  // V97.4 V3.C — upsert d'un champ marker sur le test idx.
  // Structure cible : from_plan[idx].markers = [{ marker_code, label, unit, value, synthesis, status }]
  // - Si le marker n'existait pas encore : on l'ajoute en récupérant label/unit depuis le catalogue.
  // - Si déjà présent : on patch le champ demandé.
  // - Ne touche jamais value/synthesis/status au niveau test (legacy preservé).
  const updateTestMarker = (testIdx, markerCode, field, value) => {
    setResultsByTest((prev) => prev.map((r, i) => {
      if (i !== testIdx) return r;
      const existing = Array.isArray(r.markers) ? r.markers : [];
      const mIdx = existing.findIndex((m) => m && m.marker_code === markerCode);
      let nextMarkers;
      if (mIdx === -1) {
        const catalogMarkers = r.test_code ? getExpectedMarkersForTest(r.test_code) : [];
        const cat = catalogMarkers.find((cm) => cm && cm.code === markerCode);
        nextMarkers = [
          ...existing,
          {
            marker_code: markerCode,
            label: cat?.label || markerCode,
            unit: cat?.unit || null,
            value: '',
            synthesis: '',
            status: null,
            [field]: value,
          },
        ];
      } else {
        nextMarkers = existing.map((m, k) => k === mIdx ? { ...m, [field]: value } : m);
      }
      return { ...r, markers: nextMarkers };
    }));
  };
  const { hasMgdPlan, hasOrthoPlan, hasUnknownPlan, unknownPlanCount }
    = classifyExplicitPlanLaboratories(planTests);
  const hiddenHistoricalUnknownCount = countHistoricalResultsWithUnknownLaboratory(initialData);
  const orthoPlanResults = resultsByTest
    .map((row, index) => ({ row, index }))
    .filter(({ row }) => resolveResultLaboratory(row) === 'ortho_analytic');
  const orthoExternalResults = externalAnalyses
    .map((row, index) => ({ row, index }))
    .filter(({ row }) => resolveResultLaboratory(row) === 'ortho_analytic');
  const addExternal = () => {
    if (!hasOrthoPlan) return;
    setExternalAnalyses((prev) => [...prev, {
      name: '', value: '', synthesis: '', category: null, status: null, source_lab: 'ortho_analytic',
    }]);
  };
  const updateExternal = (idx, field, value) => {
    setExternalAnalyses((prev) => prev.map((r, i) => i === idx ? { ...r, [field]: value } : r));
  };
  const removeExternal = (idx) => {
    setExternalAnalyses((prev) => prev.filter((_, i) => i !== idx));
  };

  // L'IA PROPOSE un brouillon de synthèse à partir des analyses saisies ;
  // Anissa relit/édite/valide. Jamais appliqué ni sauvegardé automatiquement :
  // on pré-remplit uniquement le textarea.
  const resultsForAi = filterNonMgdResultsData({
    from_plan: orthoPlanResults.map(({ row }) => row),
    external: orthoExternalResults.map(({ row }) => row),
  });
  const canSuggestSynthesis = hasEnoughAnalysesForSynthesis({
    fromPlan: resultsForAi.from_plan,
    external: resultsForAi.external,
  });
  const handleSuggestSynthesis = async () => {
    if (synthesisAiBusy) return;
    if (globalSynthesis.trim() &&
        !window.confirm('Remplacer la synthèse actuelle par la nouvelle proposition ?')) {
      return;
    }
    setSynthesisAiBusy(true);
    setSynthesisAiErr(null);
    try {
      const draft = await suggestGlobalSynthesis({
        fromPlan: resultsForAi.from_plan,
        external: resultsForAi.external,
      });
      setGlobalSynthesis(draft);
    } catch (e) {
      setSynthesisAiErr(e?.message || "La préparation de la proposition a échoué.");
    } finally {
      setSynthesisAiBusy(false);
    }
  };

  const handleValidate = async () => {
    if (!canValidateResults) {
      setErr('Renseigne au moins un résultat avant de continuer.');
      return;
    }
    setBusy(true); setErr(null);
    try {
      const { updateJourneyState } = await import('./services/journeyState');
      await updateJourneyState(client.id, {
        results_data: {
          ...initialData,
          from_plan: mergeEditableOrthoRowsPreservingRestricted(initialData.from_plan, resultsByTest),
          external: mergeEditableOrthoRowsPreservingRestricted(initialData.external, externalAnalyses),
          ...(hasOrthoPlan && !hasMgdPlan && !hasUnknownPlan
            ? { global_synthesis: globalSynthesis } : {}),
        },
        // La synthèse historique reste intacte dès que MGD est présent ou que
        // l'origine du plan ne permet pas de prouver un contexte 100 % Ortho.
        ...(hasOrthoPlan && !hasMgdPlan && !hasUnknownPlan
          ? { results_synthesis: globalSynthesis } : {}),
      });
      await transitions.validateResults(client.id);

      // Jalon push best-effort : previent la cliente que ses analyses sont
      // arrivees. Ne doit JAMAIS bloquer la validation d'etape ni afficher
      // d'erreur a Anissa si le push echoue.
      try {
        const email = client?.form?.email || client?.email;
        if (email) {
          const { clientAppFetch } = await import('./services/clientAppFetch');
          clientAppFetch('/api/admin/notify-milestone', {
            method: 'POST',
            payload: { email, milestone: 'analyses_received' },
          }).catch(() => {});
        }
      } catch { /* best-effort, ne bloque jamais la validation */ }

      onChange();
    } catch (e) { setErr(e?.message || 'Erreur validation'); }
    finally { setBusy(false); }
  };

  const handleContinueExternalDossier = async () => {
    setBusy(true); setErr(null);
    try {
      await transitions.validateResults(client.id);
      onChange();
    } catch (e) { setErr(e?.message || 'Erreur validation'); }
    finally { setBusy(false); }
  };

  const allResults = [...resultsForAi.from_plan, ...resultsForAi.external];
  const canValidateResults = canValidateResultsReview({
    planLoadState,
    hasReviewedLabReport: false,
    results: allResults,
  });

  if (planLoadState === 'ready' && hasMgdPlan) {
    return (
      <section className="jrn-stage jrn-stage--results">
        <StepHead
          index={4}
          title="Préparer le dossier externe"
          intro="Utilise les PDF originaux dans ta conversation de travail, puis poursuis vers la synthèse du profil."
        />
        <div className="jrn-block">
          <div className="jrn-block__head">
            <span className="jrn-block__num">✓</span>
            <h3 className="jrn-block__title">Rapports prêts à utiliser</h3>
          </div>
          <p className="jrn-block__intro">
            Aucun rapport médical n’est envoyé ni analysé par le SaaS. Utilise les PDF originaux dans ta conversation de travail avec la synthèse du profil proposée à l’étape suivante.
          </p>
          <div className="jrn-actions" style={{ marginTop: 0 }}>
            <button type="button" onClick={handleContinueExternalDossier}
              disabled={busy} className="jrn-btn jrn-btn--hero" aria-busy={busy}>
              {busy ? 'Ouverture…' : 'Continuer vers la synthèse et le plan'}
            </button>
          </div>
          <ErrorLine msg={err} />
        </div>
      </section>
    );
  }

  return (
    <section className="jrn-stage jrn-stage--results">
      <StepHead
        index={4}
        title="Revoir les résultats"
        intro="Vérifie ici les éventuels résultats Ortho-Analytic déjà disponibles avant de préparer le plan."
      />

      {client?.journey_state?.results_received === true ? (
        <div className="jrn-surface jrn-surface--quiet" role="status">
          Rapport reçu — vérification en attente
        </div>
      ) : (
        <div className="jrn-surface jrn-surface--warn" role="alert">
          Réception du rapport non confirmée. Reviens à l’étape précédente.
        </div>
      )}

      {planLoadState === 'loading' && <div className="jrn-surface jrn-surface--quiet" role="status">Vérification du plan d’analyses…</div>}
      {planLoadState === 'error' && <div className="jrn-surface jrn-surface--warn" role="alert">Le plan d’analyses n’a pas pu être vérifié. Recharge la page avant de poursuivre.</div>}

      {hiddenHistoricalUnknownCount > 0 && (
        <div className="jrn-surface jrn-surface--warn" role="alert">
          {hiddenHistoricalUnknownCount} ligne{hiddenHistoricalUnknownCount > 1 ? 's' : ''} historique{hiddenHistoricalUnknownCount > 1 ? 's' : ''} masquée{hiddenHistoricalUnknownCount > 1 ? 's' : ''} : provenance du laboratoire à confirmer.
        </div>
      )}

      {planLoadState === 'ready' && (hasUnknownPlan
        || !Array.isArray(planTests) || planTests.length === 0) && (
        <div className="jrn-surface jrn-surface--warn" role="alert">
          {unknownPlanCount > 0
            ? `${unknownPlanCount} ligne${unknownPlanCount > 1 ? 's' : ''} masquée${unknownPlanCount > 1 ? 's' : ''} : provenance du laboratoire à confirmer.`
            : 'Aucune ligne de plan confirmée. La progression reste bloquée.'}
        </div>
      )}

      {hasOrthoPlan && (
      <div className="jrn-block">
        <div className="jrn-block__head">
          <span className="jrn-block__num">2</span>
          <h3 className="jrn-block__title">Résultats Ortho-Analytic déjà disponibles ({orthoExternalResults.length})</h3>
          <button onClick={addExternal} className="jrn-btn jrn-btn--soft" style={{ marginLeft: 'auto' }}>
            + Ajouter un résultat Ortho-Analytic
          </button>
        </div>
        <p className="jrn-block__intro">
          Pour les anciens bilans ou les résultats que la cliente possédait avant ce suivi. Ils restent séparés des rapports prescrits.
        </p>
        {orthoExternalResults.length === 0 && (
          <div className="jrn-surface jrn-surface--quiet">
            <div className="jrn-empty">
              <div className="jrn-empty__icon">📋</div>
              <p className="jrn-empty__title">Aucun résultat antérieur ajouté</p>
              <p className="jrn-empty__hint">Si la cliente en possède déjà, ajoute-les ici. Sinon, continue simplement.</p>
            </div>
          </div>
        )}
        {orthoExternalResults.length > 0 && (
          <div className="jrn-result-cards">
            {orthoExternalResults.map(({ row: r, index: i }) => (
              <ResultCard
                key={i}
                editable
                title={r.name}
                badge="Déjà disponible"
                badgeColor="muted"
                value={r.value}
                synthesis={r.synthesis}
                category={r.category}
                onTitleChange={(v) => updateExternal(i, 'name', v)}
                onValueChange={(v) => updateExternal(i, 'value', v)}
                onSynthesisChange={(v) => updateExternal(i, 'synthesis', v)}
                onCategoryChange={(v) => updateExternal(i, 'category', v)}
                onDelete={() => removeExternal(i)}
              />
            ))}
          </div>
        )}
      </div>
      )}

      {hasOrthoPlan && (
      <details className="jrn-block">
        <summary className="jrn-block__title">Outils complémentaires Ortho-Analytic</summary>
      {/* ─── Préparer l'appel résultats (2026-07-26) ─────────────
          Cadrage = réponses écrites d'Anissa (docs/SPEC-appel-resultats.md).
          Visible dès qu'une ligne d'analyse est exploitable (fail-closed). */}
      {hasEnoughForResultsCall(resultsForAi) && (
        <div className="jrn-block" style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: 220 }}>
            <p style={{ margin: 0, fontSize: '.82rem', fontWeight: 600, color: '#cfe0d2' }}>
              Appel résultats
            </p>
            <p style={{ margin: '2px 0 0', fontSize: '.72rem', color: 'rgba(255,255,255,.5)', lineHeight: 1.4 }}>
              Prépare ton appel à {client.prenom || client.form?.prenom || 'la cliente'} : ta lecture par thème, des points d'appel, et ta note après coup.
            </p>
          </div>
          <button type="button" className="jrn-btn" onClick={() => setCallPrepOpen(true)}>
            Préparer l'appel
          </button>
        </div>
      )}
      {callPrepOpen && (
        <ResultsCallModal
          prenom={client.prenom || client.form?.prenom || null}
          form={client.form || null}
          resultsData={resultsForAi}
          clientId={client.id}
          initialNotes={client.journey_state?.results_call_notes || []}
          onClose={() => setCallPrepOpen(false)}
        />
      )}
      {!hasEnoughForResultsCall(resultsForAi) && (
        <p className="jrn-block__intro" style={{ marginTop: 12 }}>Ces outils s’activeront lorsque des résultats auront été renseignés.</p>
      )}
      </details>
      )}

      {/* ─── Bloc 1 : Analyses prescrites ──────────────────────── */}
      {planLoadState === 'ready' && orthoPlanResults.length > 0 && (
        <div className="jrn-block">
          <div className="jrn-block__head">
            <span className="jrn-block__num">3</span>
            <h3 className="jrn-block__title">Saisie manuelle Ortho-Analytic ({orthoPlanResults.length})</h3>
          </div>
          <p className="jrn-block__intro">
            Pour chaque analyse : saisis les valeurs brutes, catégorise (hormonal, microbiote, etc), puis note ta lecture clinique. Le statut prioritaire/à surveiller/optimal aide à orienter le plan.
          </p>
          <div className="jrn-result-cards">
            {orthoPlanResults.map(({ row: r, index: i }) => {
              // V97.4 V3.C — marqueurs attendus depuis catalog (peut être []).
              // Si le test n'est pas dans le catalogue, expectedMarkers === [] et
              // ResultCard ne rend pas la section dynamique : mode résultat libre uniquement.
              const expectedMarkers = r.test_code ? getExpectedMarkersForTest(r.test_code) : [];
              return (
                <ResultCard
                  key={r.test_code || i}
                  title={r.test_name}
                  badge="Plan d'analyses"
                  badgeColor="accent"
                  value={r.value}
                  synthesis={r.synthesis}
                  category={r.category}
                  onValueChange={(v) => updateTestField(i, 'value', v)}
                  onSynthesisChange={(v) => updateTestField(i, 'synthesis', v)}
                  onCategoryChange={(v) => updateTestField(i, 'category', v)}
                  expectedMarkers={expectedMarkers}
                  markers={Array.isArray(r.markers) ? r.markers : []}
                  onMarkerChange={(markerCode, field, value) => updateTestMarker(i, markerCode, field, value)}
                />
              );
            })}
          </div>
        </div>
      )}

      {planLoadState === 'ready' && hasOrthoPlan && orthoPlanResults.length === 0 && (
        <div className="jrn-block">
          <div className="jrn-surface jrn-surface--quiet">
            <div className="jrn-empty">
              <div className="jrn-empty__icon">🧪</div>
              <p className="jrn-empty__title">Aucune analyse prescrite</p>
              <p className="jrn-empty__hint">
                Cette cliente n'a pas de plan d'analyses validé. Les résultats déjà disponibles restent accessibles dans la section précédente.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* ─── Bloc 3 : note interne Ortho-Analytic (jamais transmise en aval) ─ */}
      {hasOrthoPlan && !hasMgdPlan && !hasUnknownPlan && (
      <div className="jrn-block">
        <div className="jrn-block__head">
          <span className="jrn-block__num">{orthoPlanResults.length > 0 ? '4' : '3'}</span>
          <h3 className="jrn-block__title">Synthèse interne Ortho-Analytic</h3>
          <button
            onClick={handleSuggestSynthesis}
            disabled={synthesisAiBusy || !canSuggestSynthesis}
            className="jrn-btn jrn-btn--soft"
            style={{ marginLeft: 'auto' }}
            title={!canSuggestSynthesis ? "Saisis d'abord au moins une analyse (valeur ou lecture clinique)." : undefined}
          >
            {synthesisAiBusy ? 'Rédaction…' : 'Préparer un brouillon'}
          </button>
        </div>
        <p className="jrn-block__intro">
          Note de travail interne d’Anissa. Elle n’est utilisée ni pour composer le programme ni dans les documents générés.
        </p>
        {!canSuggestSynthesis && (
          <p className="jrn-block__intro" style={{ opacity: 0.75 }}>
            Saisis d'abord au moins une analyse (valeur ou lecture clinique) pour préparer un brouillon.
          </p>
        )}
        <div className="jrn-synthesis">
          <textarea
            value={globalSynthesis}
            onChange={(e) => setGlobalSynthesis(e.target.value)}
            rows={9}
            placeholder="Synthèse rédigée et validée par Anissa à partir des résultats vérifiés."
            className="jrn-synthesis__textarea"
          />
          <p className="jrn-block__intro" style={{ marginTop: 6, marginBottom: 0 }}>
            Proposition interne — à relire et ajuster. Rien n'est validé tant que tu n'as pas enregistré.
          </p>
          <ErrorLine msg={synthesisAiErr} />
          <div className="jrn-synthesis__hints">
            <span className="jrn-synthesis__hint-pill">Priorités identifiées</span>
            <span className="jrn-synthesis__hint-pill">Axes nutritionnels</span>
            <span className="jrn-synthesis__hint-pill">Points inflammatoires</span>
            <span className="jrn-synthesis__hint-pill">Signaux croisés</span>
          </div>
        </div>
      </div>
      )}

      {/* ─── Bloc 4 : Validation ──────────────────────────────── */}
      {hasOrthoPlan && (
      <div className="jrn-block">
        <div className="jrn-actions" style={{ marginTop: 0 }}>
          <button onClick={handleValidate} disabled={busy || !canValidateResults} className="jrn-btn jrn-btn--hero">
            {busy ? '…' : '✓ Valider et débloquer le plan nutritionnel →'}
          </button>
        </div>
        {!canValidateResults && planLoadState === 'ready' && (
          <p className="jrn-block__intro" role="status">Finalise la revue d’un rapport ou renseigne un résultat déjà disponible pour continuer.</p>
        )}
      </div>
      )}
      <ErrorLine msg={err} />
    </section>
  );
}

// BC.5D : Carte d'analyse refondue en cockpit d'interprétation clinique.
// Header : titre + badge type + catégorie auto-détectée (cliquable) +
// 2 sections séparées : valeurs du laboratoire / lecture rédigée par Anissa.
const CATEGORIES = [
  { value: 'hormonal',    label: 'Hormonal',     icon: '⚭' },
  { value: 'microbiote',  label: 'Microbiote',   icon: '⚗' },
  { value: 'inflammation',label: 'Inflammation', icon: '✦' },
  { value: 'carence',     label: 'Carence',      icon: '◆' },
  { value: 'metabolique', label: 'Métabolique',  icon: '◈' },
  { value: 'autre',       label: 'Autre',        icon: '○' },
];

// V97.13.7 : templates par categorie pour aide cognitive a la saisie.
// Pas un formulaire rigide — juste une structure prefillable que Anissa
// peut completer librement. Objectif : construire un "langage clinique
// interne" coherent entre dossiers, sans perdre la flexibilite du textarea.
const VALUES_TEMPLATES = {
  microbiote: `Diversité (Shannon) :
Dysbiose (1-10) :
Perméabilité (zonuline) :
Inflammation (calprotectine, histamine) :
Flore protectrice (butyrate, Akkermansia) :
Levures / opportunistes :
sIgA :`,
  hormonal: `Cortisol / DHEA :
Thyroïde (TSH, T3l, T4l, anti-TPO) :
Hormones sexuelles :
Métabolites :`,
  sang: `Vitamines (D, B12, B9, folates) :
Minéraux (Mg, Zn, Fer, Ferritine, Sat. transferrine) :
Oligo-éléments (Sélénium, Cuivre) :
Acides gras (Index oméga-3) :`,
  inflammation: `CRP us :
Calprotectine :
Marqueurs Th1/Th2 :
Histamine :`,
  // Catégorie générique pour analyses non typées
  default: `Valeurs principales :
Marqueurs hors normes :
Observations labo :`,
};

const SYNTHESIS_TEMPLATE = `Axes principaux :

Priorité nutritionnelle :

Points de vigilance :`;

// V97.4 V3.C — exporté pour permettre les tests unitaires (vitest) +
// le mount dans la route dev /preview-v3c. Pas de consommation externe en prod.
/**
 * Le signal des antécédents familiaux — décision d'Anissa du 2026-08-03.
 *
 * Il INFORME et ne bloque rien : d'où un filet discret plutôt qu'un cadre
 * d'alerte. Une note qui a l'air d'un blocage finit par être traitée comme un
 * blocage.
 *
 * ⚠️ POURQUOI IL EST EXPORTÉ. Sa première version vivait dans
 * `ClinicalVerificationPanel`, monté uniquement dans le formulaire de fiche —
 * pas dans l'étape d'onboarding. Il partait donc dans le bundle et
 * n'apparaissait sur aucun écran regardé : `Ctrl+F` ne le trouvait pas.
 * Exporté, il se rend en test comme `ResultCard`, et la question « est-ce que
 * ça s'affiche vraiment ? » cesse de dépendre d'une lecture de code.
 */
export function SignalAntecedentsFamiliaux({ form }) {
  const signal = signauxAntecedentsFamiliaux(form);
  if (!signal.present) return null;

  const LIBELLES_DOMAINE = { diabete: 'diabète', digestif: 'digestif', thyroide: 'thyroïde' };

  return (
    <div style={{
      borderLeft: '2px solid rgba(130,195,158,.5)',
      paddingLeft: 12,
      margin: '0 0 var(--jrn-6)',
      fontSize: 'var(--jrn-text-sm)',
      color: 'var(--jrn-text-soft)',
      lineHeight: 1.55,
    }}>
      <strong style={{ color: 'var(--jrn-text)', fontWeight: 600 }}>
        Antécédents familiaux déclarés
      </strong>
      {' — '}
      {signal.domaines.map((d) => LIBELLES_DOMAINE[d] || d).join(', ')}.
      <div style={{ marginTop: 4, color: 'var(--jrn-text-muted)' }}>
        Information de contexte : elle ne bloque pas la génération et ne
        qualifie aucun diagnostic. Le détail est dans le champ
        « antécédents familiaux » de la fiche.
      </div>
    </div>
  );
}

export function ResultCard({
  title, badge, badgeColor,
  value, synthesis,
  category,
  onValueChange, onSynthesisChange,
  onCategoryChange,
  editable, onTitleChange, onDelete,
  // V97.4 V3.C — props optionnelles. Si expectedMarkers absent/vide,
  // la carte se comporte exactement comme avant (résultat libre uniquement).
  expectedMarkers, markers, onMarkerChange,
}) {
  // BC.5D.2 : badge catégorie cliquable (toggle dropdown sur clic)
  const [editingCategory, setEditingCategory] = useState(false);
  const categoryMeta = category ? CATEGORIES.find((c) => c.value === category) : null;

  return (
    <div className="jrn-result-card">
      {/* Header : titre + badges source/category + status select */}
      <header className="jrn-result-card__head">
        <div className="jrn-result-card__head-left">
          {editable ? (
            <input
              type="text"
              value={title}
              onChange={(e) => onTitleChange(e.target.value)}
              className="jrn-result-card__title-input"
              placeholder="Nom de l'analyse"
            />
          ) : (
            <h4 className="jrn-result-card__title">{title}</h4>
          )}
          <div className="jrn-result-card__head-badges">
            <span className={`jrn-result-card__source jrn-result-card__source--${badgeColor || 'neutral'}`}>{badge}</span>
            {/* BC.5D.2 : badge catégorie cliquable.
                Si pas en édition : badge coloré (ou placeholder cliquable si null).
                Si en édition : select dropdown. */}
            {editingCategory ? (
              <select
                value={category || ''}
                onChange={(e) => { onCategoryChange?.(e.target.value || null); setEditingCategory(false); }}
                onBlur={() => setEditingCategory(false)}
                className="jrn-result-card__select"
                autoFocus
              >
                <option value="">Aucune catégorie</option>
                {CATEGORIES.map((c) => (
                  <option key={c.value} value={c.value}>{c.icon} {c.label}</option>
                ))}
              </select>
            ) : categoryMeta ? (
              <button
                type="button"
                onClick={() => setEditingCategory(true)}
                className={`jrn-cat-pill jrn-cat-pill--${category}`}
                title="Modifier la catégorie"
              >
                <span className="jrn-cat-pill__icon">{categoryMeta.icon}</span>
                {categoryMeta.label}
              </button>
            ) : (
              <button
                type="button"
                onClick={() => setEditingCategory(true)}
                className="jrn-cat-pill jrn-cat-pill--empty"
                title="Choisir une catégorie"
              >
                + Catégorie
              </button>
            )}
          </div>
        </div>
        <div className="jrn-result-card__head-right">
          {onDelete && (
            <button onClick={onDelete} className="jrn-result-card__delete" title="Supprimer">🗑</button>
          )}
        </div>
      </header>

      {/* Section 1 : Valeurs laboratoire (compact, monospace, fond teinté) */}
      <div className="jrn-result-card__values">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 4 }}>
          <label className="jrn-result-card__section-label" style={{ marginBottom: 0 }}>Valeurs laboratoire</label>
          {/* V97.13.7 : bouton "Insérer modèle" — pre-remplit le textarea
              avec une mini-structure guidee selon la categorie du test.
              Visible uniquement si le textarea est vide pour ne pas ecraser
              une saisie en cours. */}
          {!value && (
            <button
              type="button"
              onClick={() => onValueChange(VALUES_TEMPLATES[category] || VALUES_TEMPLATES.default)}
              style={{
                fontSize: 11,
                color: 'var(--jrn-accent, #2d5a3d)',
                background: 'transparent',
                border: 'none',
                padding: 0,
                cursor: 'pointer',
                textDecoration: 'underline',
                fontWeight: 500,
              }}
              title="Insérer une structure guidée pour la saisie"
            >
              + Insérer modèle
            </button>
          )}
        </div>
        <textarea
          value={value || ''}
          onChange={(e) => onValueChange(e.target.value)}
          rows={2}
          className="jrn-result-card__values-textarea"
          placeholder="Marqueur · valeur · unité · référence indiquée par le laboratoire"
        />
      </div>

      {/* V97.4 V3.C — Section Marqueurs attendus (conditionnelle).
          Apparaît uniquement si le test a des expectedMarkers dans le catalogue
          ET qu'un handler onMarkerChange est fourni. Sinon la carte reste en
          mode résultat libre comme avant. Aucun champ n'est obligatoire — Anissa
          peut tout laisser vide ("non renseigné"). */}
      {Array.isArray(expectedMarkers) && expectedMarkers.length > 0 && onMarkerChange && (
        <div className="jrn-result-card__markers">
          <label className="jrn-result-card__section-label">Marqueurs attendus</label>
          <div className="jrn-marker-grid">
            {expectedMarkers.map((em) => {
              const saved = Array.isArray(markers)
                ? markers.find((m) => m && m.marker_code === em.code)
                : null;
              const mValue = saved?.value || '';
              const mNote = saved?.synthesis || '';
              return (
                <div key={em.code} className="jrn-marker-row">
                  <div className="jrn-marker-row__head">
                    <span className="jrn-marker-row__label">{em.label}</span>
                    {em.unit && <span className="jrn-marker-row__unit">{em.unit}</span>}
                  </div>
                  <input
                    type="text"
                    value={mValue}
                    onChange={(e) => onMarkerChange(em.code, 'value', e.target.value)}
                    placeholder="Valeur (non renseigné si vide)"
                    className="jrn-marker-row__value"
                  />
                  <input
                    type="text"
                    value={mNote}
                    onChange={(e) => onMarkerChange(em.code, 'synthesis', e.target.value)}
                    placeholder="Note Anissa (optionnel)"
                    className="jrn-marker-row__note"
                  />
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Section 2 : Lecture clinique Anissa (éditorial, plus lisible) */}
      <div className="jrn-result-card__interpretation">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 4 }}>
          <label className="jrn-result-card__section-label jrn-result-card__section-label--accent" style={{ marginBottom: 0 }}>Lecture clinique</label>
          {/* V97.13.7 : bouton "Insérer modèle synthese" */}
          {!synthesis && (
            <button
              type="button"
              onClick={() => onSynthesisChange(SYNTHESIS_TEMPLATE)}
              style={{
                fontSize: 11,
                color: 'var(--jrn-accent, #2d5a3d)',
                background: 'transparent',
                border: 'none',
                padding: 0,
                cursor: 'pointer',
                textDecoration: 'underline',
                fontWeight: 500,
              }}
              title="Insérer une structure axes / priorité / vigilance"
            >
              + Insérer modèle
            </button>
          )}
        </div>
        <textarea
          value={synthesis || ''}
          onChange={(e) => onSynthesisChange(e.target.value)}
          rows={3}
          className="jrn-result-card__interpretation-textarea"
          placeholder="Profil compatible avec… Pistes nutritionnelles envisagées : magnésium, gestion glycémique, soutien circadien."
        />
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// ÉTAPE 5 — GÉNÉRATION DU PLAN
// ═══════════════════════════════════════════════════════════════════

export function StepPlanGeneration({ client, onChange }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const form = useMemo(() => client.form || {}, [client.form]);
  const anamnesisAvailable = isAnamneseFilled(form);
  const anamnesisGateMessage = "Complète l'anamnèse depuis Profil avant de préparer le dossier.";
  const profileSummary = useMemo(
    () => buildExternalProfileSummary(form, {
      prenom: client?.prenom,
      nom: client?.nom,
    }),
    [client?.nom, client?.prenom, form],
  );
  const handleExternalDraft = async (synthesisText, reviewedProfileSummary) => {
    if (!anamnesisAvailable) throw new Error(anamnesisGateMessage);
    setBusy(true);
    setErr(null);
    try {
      const now = new Date().toISOString();
      const normalizedSynthesis = String(synthesisText || '').replace(/\r\n/g, '\n').trim();
      const synthesisSha256 = await sha256ExternalText(normalizedSynthesis);
      const profileSummarySha256 = await sha256ExternalText(reviewedProfileSummary.trim());
      const requestId = globalThis.crypto?.randomUUID?.();
      if (!requestId) throw new Error('Identifiant de sauvegarde indisponible.');
      const receipt = await saveExternalDossierSynthesis({
        clientId: client.id,
        synthesisText: normalizedSynthesis,
        synthesisSha256,
        profileSummarySha256,
        requestId,
        reviewedAt: now,
      });
      await transitions.openPlanGeneratorFromExternalSynthesis(client.id, {
          ...receipt,
          privacy_review_confirmed: true,
          review_status: 'reviewed',
          reviewed_at: now,
      });
      onChange();
    } catch (caught) {
      setErr(caught?.message || 'La sauvegarde du brouillon n’a pas pu être confirmée.');
      throw caught;
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="jrn-stage jrn-stage--generation">
      <StepHead
        index={client?.journey_state?.external_dossier_flow === true ? 4 : 5}
        total={client?.journey_state?.external_dossier_flow === true ? 7 : 8}
        title="Importer la synthèse du dossier"
        intro={anamnesisAvailable
          ? 'Utilise les PDF originaux dans ta conversation de travail avec la synthèse du profil, puis colle ici la réponse obtenue.'
          : "L'anamnèse est requise pour construire la synthèse du profil. Complète-la depuis Profil, puis reviens ici."}
      />
      <ExternalPlanInputPanel
        profileSummary={profileSummary}
        initialText=""
        disabled={busy || !anamnesisAvailable}
        onSave={handleExternalDraft}
      />
      <ErrorLine msg={err || (!anamnesisAvailable ? anamnesisGateMessage : null)} />
    </section>
  );
}

// ═══════════════════════════════════════════════════════════════════
// ÉTAPE 6 — ÉDITION DU PLAN (composer embed)
// ═══════════════════════════════════════════════════════════════════

export function StepPlanEditing({ client, journey, onChange, onOpenClientPreview }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const atelierHeadingRef = useRef(null);
  const [versions, setVersions] = useState([]);
  // Distingue « aucune version enregistrée » de « la liste n'a pas pu être
  // lue ». Sans ça, une panne de lecture se raconte comme un plan vide.
  const [versionsIllisibles, setVersionsIllisibles] = useState(false);
  const [editorSaveState, setEditorSaveState] = useState('loading');
  const versionsCount = versions.length;
  const latestVersion = versions[0] || null;
  const latestPlanText = typeof latestVersion?.nutritionPlan === 'string'
    ? latestVersion.nutritionPlan
    : '';
  const latestSupplementsText = typeof latestVersion?.supplements === 'string'
    ? latestVersion.supplements
    : '';
  const [validationAuthority, setValidationAuthority] = useState({ state: 'checking' });
  const validationVerdict = useMemo(
    () => (validationAuthority.state === 'standard'
      ? evaluatePlanValidationReadiness({
        planText: latestPlanText,
        supplementsText: latestSupplementsText,
        form: client?.form || {},
        journeyState: journey,
      })
      : { cleared: false, violations: [], warnings: [] }),
    [latestPlanText, latestSupplementsText, client?.form, journey, validationAuthority.state],
  );
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!client?.id || !latestVersion?.id || !latestPlanText.trim()) {
        if (!cancelled) setValidationAuthority({ state: 'blocked', message: 'Enregistre une version complète du plan.' });
        return;
      }
      if (!cancelled) setValidationAuthority({ state: 'checking' });
      try {
        const authority = await assertMgdPlanAuthorizedForOutput(
          client.id, latestVersion, mgdReviewV2Client, null, externalPlanAuthorityClient, false,
        );
        if (!cancelled) {
          setValidationAuthority(authority.hasActiveMgd
            ? { state: 'server_authorized', authority }
            : { state: 'standard' });
        }
      } catch (error) {
        if (!cancelled) {
          setValidationAuthority({
            state: 'blocked',
            message: error?.message || 'Preuve serveur du plan indisponible.',
          });
        }
      }
    })();
    return () => { cancelled = true; };
  }, [client?.id, latestVersion?.id, latestPlanText]);
  const usesServerMgdAuthority = validationAuthority.state === 'server_authorized';
  const validationBlocked = versionsCount === 0
    || !latestVersion?.id
    || !latestPlanText.trim()
    || !['idle', 'saved'].includes(editorSaveState)
    || ['checking', 'blocked'].includes(validationAuthority.state);
  const [deliveryContractState, setDeliveryContractState] = useState({
    state: 'checking', contract: null, message: null,
  });
  useEffect(() => {
    let cancelled = false;
    if (!latestVersion?.id || !latestPlanText.trim()) {
      setDeliveryContractState({ state: 'blocked', contract: null,
        message: 'Enregistre une version complète du plan.' });
      return () => { cancelled = true; };
    }
    setDeliveryContractState({ state: 'checking', contract: null, message: null });
    buildPlanDeliveryContractV1(client, latestVersion)
      .then((contract) => {
        if (cancelled) return;
        setDeliveryContractState(contract.readiness.ready
          ? { state: 'ready', contract, message: null }
          : { state: 'blocked', contract,
            message: contract.readiness.blockers.join(' ') });
      })
      .catch((error) => {
        if (!cancelled) setDeliveryContractState({ state: 'blocked', contract: null,
          message: error?.message || 'Le contenu application ne peut pas être préparé.' });
      });
    return () => { cancelled = true; };
  }, [client, latestVersion, latestPlanText]);
  const finalValidationBlocked = validationBlocked || deliveryContractState.state !== 'ready';

  // BC.5G.1 : Directive IA visible en permanence dans la sidebar.
  // State + autosave debounced + suggestion auto-fill basée contexte cliente.
  const [aiDirectives, setAiDirectives] = useState('');
  const [aiDirectivesLoaded, setAiDirectivesLoaded] = useState(false);
  const [savingDirectives, setSavingDirectives] = useState('idle'); // 'idle' | 'saving' | 'saved'

  // BC.5G : fetch la liste complète des versions (pas juste count) pour
  // afficher l'historique dans la sidebar contextuelle.
  useEffect(() => {
    if (!client?.id) return;
    let cancelled = false;
    (async () => {
      try {
        const { getNutritionConsultations } = await import('./store');
        const list = getNutritionConsultations(client.id) || [];
        if (!cancelled) setVersions(list);
        // Charge la directive IA depuis la dernière consultation
        const last = list[0];
        if (!cancelled && !aiDirectivesLoaded) {
          setAiDirectives(last?.aiDirectives || '');
          setAiDirectivesLoaded(true);
        }
        if (!cancelled) setVersionsIllisibles(false);
      } catch (e) {
        // Cet échec était muet, et une liste vide se lisait comme « aucune
        // version enregistrée ». Anissa se voyait alors demander de sauvegarder
        // un plan qu'elle avait peut-être déjà sauvegardé dix fois.
        console.warn('[atelier] liste des versions illisible :', e?.message);
        if (!cancelled) setVersionsIllisibles(true);
      }
    })();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client?.id, journey]);

  // Autosave debounced 250ms — persiste la directive sur la dernière consultation.
  // V97.13.x : debounce réduit (1500→250) pour éviter race condition avec ouverture
  // du modal Génération qui re-fetch DB. Anissa colle + clique Générer dans la foulée.
  useEffect(() => {
    if (!aiDirectivesLoaded || !client?.id) return;
    const handle = setTimeout(async () => {
      try {
        setSavingDirectives('saving');
        const { getNutritionConsultations, saveNutritionConsultation } = await import('./store');
        const list = getNutritionConsultations(client.id) || [];
        const last = list[0];
        if (last) {
          await saveNutritionConsultation({ ...last, aiDirectives });
          setSavingDirectives('saved');
          setTimeout(() => setSavingDirectives('idle'), 1500);
        } else {
          // Pas encore de consultation : on garde la directive en mémoire,
          // elle sera injectée à la première génération.
          setSavingDirectives('idle');
        }
      } catch {
        setSavingDirectives('idle');
      }
    }, 250);
    return () => clearTimeout(handle);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aiDirectives]);

  const handlePlanSaved = async (confirmedSavedPlan) => {
    // Refresh des versions après une sauvegarde dans l'éditeur
    try {
      const { getNutritionConsultations } = await import('./store');
      const list = getNutritionConsultations(client.id) || [];
      setVersions(list);
      if (client?.journey_state?.plan_generated !== true
          && confirmedSavedPlan?.nutritionPlan?.trim()) {
        await updateJourneyState(client.id, { plan_generated: true });
      }
    } catch (saveRefreshError) {
      setErr(saveRefreshError?.message
        ? `Plan enregistré, mais son statut n’a pas pu être actualisé : ${saveRefreshError.message}`
        : 'Plan enregistré, mais son statut n’a pas pu être actualisé. Recharge la fiche.');
    }
  };

  const handleValidate = async () => {
    if (!['idle', 'saved'].includes(editorSaveState)) {
      setErr('Attends la confirmation de sauvegarde du plan avant de le valider.');
      return;
    }
    if (!latestVersion?.id || !latestPlanText.trim()) {
      setErr('Enregistre une version complète du plan avant de la valider.');
      return;
    }
    setBusy(true); setErr(null);
    try {
      const mgdAuthority = await assertMgdPlanAuthorizedForOutput(
        client.id, latestVersion, mgdReviewV2Client, null, externalPlanAuthorityClient, false,
      );
      let clinicalOverrideConfirmed = false;
      if (!mgdAuthority.hasActiveMgd && !validationVerdict.cleared) {
        if (!window.confirm(formatClearanceForConfirm(validationVerdict))) {
          setBusy(false);
          return;
        }
        clinicalOverrideConfirmed = true;
      }
      if (!clinicalOverrideConfirmed && !window.confirm('Valider le plan ?\n\nLe plan passe en étape Livraison.')) {
        setBusy(false);
        return;
      }
      const expectedContentSha256 = await sha256Hex(
        buildPlanValidationContent(latestPlanText, latestSupplementsText),
      );
      const exactDeliveryContract = await buildPlanDeliveryContractV1(client, latestVersion);
      if (!exactDeliveryContract.readiness.ready) {
        throw new Error(exactDeliveryContract.readiness.blockers.join(' '));
      }
      await transitions.validatePlan(
        client.id,
        latestVersion.id,
        expectedContentSha256,
        { clinicalOverride: clinicalOverrideConfirmed },
      );
      // V97.3 Phase C1 : tracking plan_validated (best-effort, post-update)
      const latest = versions[0];
      const oldest = versions[versions.length - 1];
      // ai_to_practitioner_delta_ratio : écart entre V1 (initiale IA) et la version validée.
      // Approximation par longueur — 0 = pure IA non modifiée, ratio croissant = + de réécriture.
      let aiToPractitionerDeltaRatio = null;
      if (latest?.nutritionPlan && oldest?.nutritionPlan && oldest !== latest) {
        const oldLen = oldest.nutritionPlan.length;
        const newLen = latest.nutritionPlan.length;
        if (oldLen > 0) {
          aiToPractitionerDeltaRatio = Number(Math.abs((newLen - oldLen) / oldLen).toFixed(3));
        }
      }
      trackPlanValidated({
        clientId: client.id,
        consultationId: latest?.id,
        versionId: `V${versions.length}`,
        totalLength: latest?.nutritionPlan?.length,
        aiToPractitionerDeltaRatio,
        versionsCount: versions.length,
      });
      onChange();
    } catch (e) {
      if (e instanceof PlanValidationClinicalError && Array.isArray(e.verdict?.violations)) {
        const details = e.verdict.violations.map((item) => item.label).filter(Boolean);
        setErr(details.length > 0
          ? `Plan à corriger avant validation : ${details.join(' · ')}`
          : e.message);
      } else {
        setErr(e?.message || 'Erreur transition');
      }
      setBusy(false);
    }
  };

  // BC.5F : contexte clinique mobilisé pour la composition (reuse de step 5)
  const resultsData = buildDownstreamOrthoResultContext(journey);
  const allResults = [
    ...(resultsData.from_plan || []),
    ...(resultsData.external || []),
  ];
  const statusCounts = allResults.reduce((acc, r) => {
    if (r.status) acc[r.status] = (acc[r.status] || 0) + 1;
    return acc;
  }, {});
  const categoryCounts = allResults.reduce((acc, r) => {
    if (r.category) acc[r.category] = (acc[r.category] || 0) + 1;
    return acc;
  }, {});
  const topCategories = Object.entries(categoryCounts)
    .sort(([, a], [, b]) => b - a)
    .slice(0, 5)
    .map(([key, count]) => {
      const meta = CATEGORIES.find((c) => c.value === key);
      return { value: key, label: meta?.label || key, icon: meta?.icon, count };
    });
  const hasClinicalContext = allResults.length > 0 || topCategories.length > 0;

  // BC.5G.1 → V97.105 : suggestion de directive IA.
  // AVANT : recopiait les données cliente (pack, objectifs, contraintes,
  // allergies, axes, marqueurs) dans la directive. Problème : (1) redondant —
  // l'IA a déjà tout ça via les sources structurées (anamnèse, analyses,
  // synthèse) ; (2) DANGEREUX — réécrire les infos sécurité (allergies /
  // contraintes) dans un bloc "priorité maximale" en texte libre crée un risque
  // de contradiction avec l'anamnèse (source de vérité). La sécurité reste
  // pilotée par l'anamnèse structurée + la clairance clinique à la publication.
  // La directive ne porte donc QUE le style/approche maison ; Anissa y ajoute
  // ensuite ses consignes propres à la cliente (préférences, exclusions, focus).
  const buildSuggestedDirective = () => {
    return [
      'Approche : progressive, pédagogique, sans frustration.',
      'Style : clinique premium, ton chaleureux mais rigoureux.',
      'Inclure : structure journée, équivalences, conseils lifestyle, supplémentation suggérée à valider.',
    ].join('\n');
  };

  const handleSuggestDirective = () => {
    const suggested = buildSuggestedDirective();
    if (aiDirectives && !window.confirm('Remplacer la directive actuelle par une suggestion construite depuis le contexte cliente ?')) return;
    setAiDirectives(suggested);
  };

  return (
    <section className="jrn-stage jrn-stage--editing">
      <StepHead
        index={client?.journey_state?.external_dossier_flow === true ? 5 : 6}
        total={client?.journey_state?.external_dossier_flow === true ? 7 : 8}
        title="Atelier de composition"
        intro="Prépare un brouillon, ré-écris librement et sauvegarde plusieurs versions. Une fois le protocole finalisé, valide pour passer à la livraison."
      />

      {/* BC.5G : layout 2 colonnes — atelier à gauche, cockpit contextuel à droite */}
      <div className="jrn-atelier-grid">
        {/* ─── COLONNE GAUCHE : Atelier + Validation ───────────── */}
        <div className="jrn-atelier-grid__main">

          {/* Atelier d'édition (JourneyPlanEditor — empty state intégré) */}
          <div className="jrn-block" id="journey-plan-editor-workspace">
            <div className="jrn-block__head">
              <span className="jrn-block__num">1</span>
              <h3
                ref={atelierHeadingRef}
                className="jrn-block__title"
                tabIndex={-1}
              >
                Atelier d'édition
              </h3>
              {versionsCount > 0 && (
                <div className="jrn-block__head-meta">
                  <span className="jrn-result-pill jrn-result-pill--optimal">{versionsCount} version{versionsCount > 1 ? 's' : ''}</span>
                </div>
              )}
            </div>
            <p className="jrn-block__intro">
              Prépare un brouillon, ré-écris à la main et vérifie la cohérence clinique. Chaque sauvegarde crée une version.
            </p>
            <JourneyPlanEditor
              client={client}
              onPlanSaved={handlePlanSaved}
              onSaveStateChange={setEditorSaveState}
              onOpenClientPreview={onOpenClientPreview}
              controlledAiDirectives={aiDirectives}
            />
          </div>

          {/* Validation conditionnelle */}
          <div className="jrn-block">
            <div className="jrn-block__head">
              <span className="jrn-block__num">2</span>
              <h3 className="jrn-block__title">Validation</h3>
            </div>
            <p className="jrn-block__intro">
              {versionsCount === 0 && versionsIllisibles
                ? 'Les versions du plan n\'ont pas pu être lues. Recharge la page avant de valider : tant que ce message est là, on ne sait pas ce qui est enregistré.'
                : versionsCount === 0
                ? 'Pour ouvrir la validation : 1. choisis la structure des repas ; 2. génère le premier plan ; 3. relis-le ; 4. adopte-le pour l’enregistrer. Le bloc 2 · Validation s’ouvrira ensuite.'
                : !['idle', 'saved'].includes(editorSaveState)
                  ? 'La validation attend la confirmation de sauvegarde de la dernière modification.'
                : validationAuthority.state === 'checking'
                  ? 'Vérification de la preuve serveur du plan…'
                : usesServerMgdAuthority
                  ? 'Le texte enregistré correspond au plan MGD autorisé et persisté par le serveur.'
                : validationAuthority.state === 'blocked'
                  ? validationAuthority.message
                : deliveryContractState.state === 'checking'
                  ? 'Préparation du contenu exact destiné à l’application…'
                : deliveryContractState.state === 'blocked'
                  ? deliveryContractState.message
                : validationVerdict.cleared
                  ? `Version prête : ${deliveryContractState.contract?.readiness?.summary?.meals_per_day || 0} repères repas par jour. Les recettes et finitions restent éditables dans Livraison.`
                  : 'La dernière version doit être corrigée avant de pouvoir passer à la livraison.'}
            </p>
            {versionsCount > 0 && validationAuthority.state === 'standard' && !validationVerdict.cleared && (
              <div className="jrn-error" role="alert" style={{ marginBottom: 'var(--jrn-4)' }}>
                <strong>Validation à confirmer par Anissa</strong>
                <p style={{ margin: '8px 0 0' }}>
                  Relis le passage signalé. Si l’allergène est seulement cité pour être exclu, Anissa peut confirmer et poursuivre sans modifier le plan.
                </p>
                <ul style={{ margin: '8px 0 0', paddingLeft: 18 }}>
                  {(validationVerdict.violations || []).map((item, index) => (
                    <li key={`${item.type || 'controle'}-${index}`}>{item.label}</li>
                  ))}
                </ul>
                <button
                  type="button"
                  className="jrn-btn jrn-btn--ghost"
                  style={{ marginTop: 'var(--jrn-3)' }}
                  onClick={() => {
                    const atelier = document.getElementById('journey-plan-editor-workspace');
                    const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
                    atelier?.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
                    atelierHeadingRef.current?.focus({ preventScroll: true });
                  }}
                >
                  Corriger dans l’atelier ↑
                </button>
              </div>
            )}
            <div className="jrn-actions" style={{ marginTop: 0 }}>
              <button
                onClick={handleValidate}
                disabled={busy || finalValidationBlocked}
                className={`jrn-btn jrn-btn--hero ${finalValidationBlocked ? 'jrn-btn--hero-locked' : ''}`}
                title={finalValidationBlocked ? 'La dernière version et son contenu application doivent être prêts' : 'Valider le plan et préparer la livraison'}
              >
                {busy ? '…' : finalValidationBlocked
                  ? '🔒 Attendre ou corriger le plan avant validation'
                  : validationAuthority.state === 'standard' && !validationVerdict.cleared
                    ? 'Faire confirmer par Anissa et continuer →'
                  : '✓ Valider et préparer le contenu de l’application →'}
              </button>
            </div>
          </div>

          <ErrorLine msg={err} />
        </div>

        {/* ─── COLONNE DROITE : Cockpit contextuel sticky ───── */}
        <aside className="jrn-atelier-grid__side">
          {/* BC.5G.1 : Directive IA — cerveau du protocole, toujours visible */}
          <div className="jrn-side-card jrn-side-card--directive">
            <div className="jrn-side-card__head">
              <p className="jrn-side-card__label">
                Directives de rédaction
              </p>
              <span className={`jrn-side-card__save-status jrn-side-card__save-status--${savingDirectives}`}>
                {savingDirectives === 'saving' && '⏳ Enregistrement…'}
                {savingDirectives === 'saved' && '✓ Enregistré'}
              </span>
            </div>
            <p className="jrn-side-card__sub-hint">
              Le cadre du protocole : ton, philosophie, contraintes et exclusions. Il sera appliqué à toutes les propositions.
            </p>
            <textarea
              value={aiDirectives}
              onChange={(e) => setAiDirectives(e.target.value)}
              placeholder="Ex : approche douce, sans gluten, focus microbiote + fatigue, éviter restriction agressive, style pédagogique…"
              rows={9}
              className="jrn-side-directive__textarea"
            />
            <button
              type="button"
              onClick={handleSuggestDirective}
              className="jrn-side-directive__suggest"
              title="Construire une suggestion depuis l'anamnèse et les analyses"
            >
              ✨ Suggérer depuis le contexte cliente
            </button>
          </div>

          {/* Contexte clinique */}
          {hasClinicalContext && (
            <div className="jrn-side-card">
              <p className="jrn-side-card__label">Contexte clinique</p>
              {Object.keys(statusCounts).length > 0 && (
                <div className="jrn-side-card__stats">
                  {statusCounts.prioritaire > 0 && (
                    <div className="jrn-side-stat jrn-side-stat--prioritaire">
                      <span className="jrn-side-stat__num">{statusCounts.prioritaire}</span>
                      <span className="jrn-side-stat__label">🔴 prio</span>
                    </div>
                  )}
                  {statusCounts.surveiller > 0 && (
                    <div className="jrn-side-stat jrn-side-stat--surveiller">
                      <span className="jrn-side-stat__num">{statusCounts.surveiller}</span>
                      <span className="jrn-side-stat__label">🟡 surv.</span>
                    </div>
                  )}
                  {statusCounts.optimal > 0 && (
                    <div className="jrn-side-stat jrn-side-stat--optimal">
                      <span className="jrn-side-stat__num">{statusCounts.optimal}</span>
                      <span className="jrn-side-stat__label">🟢 opt.</span>
                    </div>
                  )}
                </div>
              )}
              {topCategories.length > 0 && (
                <div className="jrn-side-card__axes">
                  <span className="jrn-side-card__sub-label">Axes</span>
                  <div className="jrn-side-card__axes-list">
                    {topCategories.map((cat) => (
                      <span key={cat.value} className={`jrn-cat-pill jrn-cat-pill--${cat.value}`}>
                        <span className="jrn-cat-pill__icon">{cat.icon}</span>
                        {cat.label}
                        <span className="jrn-cat-pill__count">·&nbsp;{cat.count}</span>
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Versions du protocole */}
          <div className="jrn-side-card">
            <p className="jrn-side-card__label">Versions du protocole</p>
            {versions.length === 0 ? (
              <p className="jrn-side-card__empty">Aucune version sauvegardée pour l'instant.</p>
            ) : (
              <ul className="jrn-side-versions">
                {versions.slice(0, 5).map((v, i) => {
                  const isLatest = i === 0;
                  const dateStr = v.createdAt
                    ? new Date(v.createdAt).toLocaleDateString('fr-CH', { day: '2-digit', month: 'short' })
                    : '—';
                  return (
                    <li key={v.id || i} className={`jrn-side-version ${isLatest ? 'jrn-side-version--latest' : ''}`}>
                      <span className="jrn-side-version__num">V{versions.length - i}</span>
                      <div className="jrn-side-version__body">
                        <span className="jrn-side-version__label">{v.label || (isLatest ? 'Version courante' : `Version V${versions.length - i}`)}</span>
                        <span className="jrn-side-version__date">{dateStr}</span>
                      </div>
                      {isLatest && <span className="jrn-side-version__badge">Active</span>}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </aside>
      </div>
    </section>
  );
}

// ═══════════════════════════════════════════════════════════════════
// ÉTAPE 7 — LIVRAISON
// ═══════════════════════════════════════════════════════════════════

function StepDelivery({ client, onDelivered, onOpenAppPreview, onChange }) {
  const savedDeliveryPreference = client?.journey_state?.delivery_preference || {};
  const savedDeliveryCommitment = client?.journey_state?.delivery_commitment || {};
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [versionsCount, setVersionsCount] = useState(0);
  const [deliveryMode, setDeliveryMode] = useState(
    savedDeliveryCommitment.mode || savedDeliveryPreference.mode || null,
  );
  const [documentChannel, setDocumentChannel] = useState(
    savedDeliveryCommitment.document_channel || savedDeliveryPreference.document_channel || '',
  );
  const [documentConfirmed, setDocumentConfirmed] = useState(
    ['document', 'app_and_document'].includes(savedDeliveryCommitment.mode)
      || savedDeliveryPreference.document_confirmed === true,
  );
  const [paperExported, setPaperExported] = useState(
    /^[0-9a-f-]{36}$/i.test(String(
      savedDeliveryCommitment.consultation_id
      || savedDeliveryPreference.document_consultation_id
      || '',
    ))
      && /^[0-9a-f]{64}$/.test(String(
        savedDeliveryCommitment.content_sha256
        || savedDeliveryPreference.document_content_sha256
        || '',
      )),
  );
  const [documentPlanProof, setDocumentPlanProof] = useState(() => ({
    consultationId: savedDeliveryCommitment.consultation_id
      || savedDeliveryPreference.document_consultation_id
      || null,
    contentSha256: savedDeliveryCommitment.content_sha256
      || savedDeliveryPreference.document_content_sha256
      || null,
  }));
  const [localModeLocked, setLocalModeLocked] = useState(
    Boolean(savedDeliveryCommitment.mode) || savedDeliveryPreference.document_confirmed === true,
  );
  const [lastConsultDate, setLastConsultDate] = useState(null);
  const [appContentState, setAppContentState] = useState({ state: 'loading', contract: null });
  const [revalidatingAppContent, setRevalidatingAppContent] = useState(false);
  const [revalidationError, setRevalidationError] = useState(null);
  // V97.13.12 : modification de la date de remise (migré du menu Plus dashboard)

  const prenom = (client?.form?.prenom || client?.prenom || 'la cliente').trim();
  const pack = PACK_DEFINITIONS[client.packType] || null;
  const packLabel = pack?.label || 'Accompagnement';

  // Charge le nombre de versions + date dernière consultation
  useEffect(() => {
    if (!client?.id) return;
    let cancelled = false;
    (async () => {
      try {
        const { getNutritionConsultations } = await import('./store');
        const list = getNutritionConsultations(client.id) || [];
        if (cancelled) return;
        setVersionsCount(list.length);
        if (list[0]?.createdAt) setLastConsultDate(list[0].createdAt);
      } catch (e) {
        // Compteur d'en-tête : l'écran reste utilisable sans lui, mais un
        // échec muet n'a jamais aidé personne à comprendre un compte à zéro.
        console.warn('[en-tête] compte des versions indisponible :', e?.message);
      }
    })();
    return () => { cancelled = true; };
  }, [client?.id]);

  useEffect(() => {
    let cancelled = false;
    if (!client?.id) return () => { cancelled = true; };
    setAppContentState({ state: 'loading', contract: null });
    (async () => {
      try {
        const validated = await loadValidatedConsultationForDelivery(
          client.id,
          client?.journey_state,
        );
        const contract = await buildPlanDeliveryContractV1(client, validated.consultation);
        try {
          const verified = await readCurrentPlanDeliveryEvidence({
            client, consultation: validated.consultation, db: supabase,
          });
          if (!cancelled) setAppContentState({ state: 'ready', contract: verified.contract || contract });
        } catch (error) {
          if (!cancelled && error?.message === 'Preuve du livrable application invalide.') {
            setAppContentState({ state: 'stale', contract, message: error.message });
            return;
          }
          throw error;
        }
      } catch (error) {
        if (!cancelled) setAppContentState({ state: 'error', contract: null,
          message: error?.message || 'Le contenu application validé est indisponible.' });
      }
    })();
    return () => { cancelled = true; };
  }, [client]);

  const revalidateAppContent = async () => {
    if (revalidatingAppContent) return;
    setRevalidationError(null);
    if (!window.confirm(
      'Revalider le contenu actuel de l’application ?\n\n'
      + 'Cette action confirme les menus, recettes, compléments et le Meal Prep enregistrés. Rien ne sera publié.',
    )) return;
    setRevalidatingAppContent(true);
    setErr(null);
    // La validation du plan n'est autorisée que depuis l'étape « plan ». Une
    // fois à la livraison, un scellé périmé — une anamnèse corrigée après coup,
    // par exemple — ne pouvait plus être refait : « Parcours ou anamnese non
    // confirmes », sans issue. On replace donc le curseur avant de revalider,
    // et on le remet où il était si la revalidation échoue.
    const etapeInitiale = client?.journey_state?.current_step || null;
    let curseurDeplace = false;
    try {
      if (etapeInitiale && etapeInitiale !== 'plan_editing') {
        await transitions.restartPlanEditing(client.id);
        curseurDeplace = true;
      }
      const validated = await loadValidatedConsultationForDelivery(
        client.id,
        client?.journey_state,
      );
      let persisted;
      try {
        persisted = await transitions.validatePlan(
          client.id,
          validated.consultationId,
          validated.contentSha256,
        );
      } catch (validationError) {
        // Même porte que la validation initiale : une alerte restante se
        // confirme consciemment par Anissa (scellée, auditée), sinon on bloque.
        if (!(validationError instanceof PlanValidationClinicalError)
            || !window.confirm(formatClearanceForConfirm(validationError.verdict))) {
          throw validationError;
        }
        persisted = await transitions.validatePlan(
          client.id,
          validated.consultationId,
          validated.contentSha256,
          { clinicalOverride: true },
        );
      }
      const refreshedClient = { ...client, journey_state: persisted };
      const refreshed = await loadValidatedConsultationForDelivery(
        client.id,
        persisted,
      );
      const verified = await readCurrentPlanDeliveryEvidence({
        client: refreshedClient,
        consultation: refreshed.consultation,
        db: supabase,
      });
      setAppContentState({ state: 'ready', contract: verified.contract });
      await onChange?.();
    } catch (error) {
      // Ne jamais laisser le parcours reculé après un échec : Anissa
      // retrouverait sa cliente à l'étape plan sans avoir rien demandé.
      if (curseurDeplace) {
        try {
          await updateJourneyState(client.id, { current_step: etapeInitiale });
        } catch {
          /* le message d'origine reste plus utile que celui du repli */
        }
      }
      const message = error?.message || 'La revalidation du contenu de l’application a échoué.';
      setRevalidationError(message);
      setErr(message);
    } finally {
      setRevalidatingAppContent(false);
    }
  };

  const markAppContentProofStale = () => {
    setAppContentState((previous) => ({
      state: 'stale',
      contract: previous?.contract || null,
      message: 'Preuve du livrable application invalide.',
      // On arrive ici uniquement depuis une tentative de publication refusée.
      stoppedPublication: true,
    }));
  };

  const isFirstVersion = versionsCount <= 1;

  // Détection automatique du nombre d'analyses retenues (best-effort)
  const analysesCount = useMemo(() => {
    const plan = client?.analysisPlan || client?.analysis_plan;
    const tests = plan?.selectedTests || plan?.selected_tests;
    if (Array.isArray(tests) && tests.length > 0) return tests.length;
    return null;
  }, [client?.analysisPlan, client?.analysis_plan]);

  const validationDateLabel = lastConsultDate
    ? new Date(lastConsultDate).toLocaleDateString('fr-CH', { day: '2-digit', month: 'long', year: 'numeric' })
    : null;

  // Pesée — toggle local (lecture seule pour le miroir)
  const weightTrackingEnabled = !!(client?.weightTrackingEnabled ?? client?.weight_tracking_enabled);

  // V97.13.22 — workflow hybride physique + digital (3 états)
  const includesApp = deliveryMode === 'app' || deliveryMode === 'app_and_document';
  const includesDocument = deliveryMode === 'document' || deliveryMode === 'app_and_document';
  const appAlreadyActive = isClientAppEffectivelyActive(client);
  const approvedArtifactSha256 = String(
    client?.journey_state?.plan_validation?.publication_artifact_sha256 || '',
  );
  const savedAppPublication = ['app', 'app_and_document'].includes(savedDeliveryPreference.mode)
    && /^[0-9a-f]{64}$/.test(approvedArtifactSha256);
  const deliveryModeLocked = localModeLocked
    || Boolean(savedDeliveryCommitment.mode)
    || savedDeliveryPreference.document_confirmed === true
    || savedAppPublication;
  // Compatibilite visuelle de l'ancien bloc papier pendant la transition vers
  // les trois canaux explicites. La confirmation remplace les anciens jalons
  // expedition/reception qui supposaient toujours une app cliente.
  const includePaper = includesDocument;

  const handleExportWord = async () => {
    setExporting(true);
    setErr(null);
    try {
      const [{ exportPlanToWord }, { ExportClinicalError, formatClearanceForConfirm }] = await Promise.all([
        import('./services/exportToWord'),
        import('./services/clinicalClearance'),
      ]);
      const validated = await loadValidatedConsultationForDelivery(
        client.id,
        client?.journey_state,
      );
      const last = validated.consultation;
      const mgdOutput = await assertMgdPlanAuthorizedForOutput(client.id, last);
      // P1.2 — le gate vit dans exportPlanToWord (service). Override conscient.
      try {
        await exportPlanToWord(client, last, last.nutritionPlan || '');
      } catch (ce) {
        if (ce instanceof ExportClinicalError) {
          if (mgdOutput.hasActiveMgd) {
            throw new Error('Export MGD bloqué : aucune dérogation clinique navigateur n’est autorisée.');
          }
          if (!window.confirm(formatClearanceForConfirm(ce.verdict))) {
            setExporting(false);
            return;
          }
          // V97.28 — override confirmé : on trace (fire-and-forget, non bloquant).
          const { traceClinicalOverride, CLINICAL_OVERRIDE_DOORS } = await import('./services/clinicalOverrideAudit');
          void traceClinicalOverride(ce.verdict, CLINICAL_OVERRIDE_DOORS.EXPORT_WORD, {
            clientId: client?.id,
            consultationId: last?.id,
          });
          await exportPlanToWord(client, last, last.nutritionPlan || '', { clinicalOverride: true });
        } else {
          throw ce;
        }
      }
      setDocumentPlanProof({
        consultationId: validated.consultationId,
        contentSha256: validated.contentSha256,
      });
      await updateJourneyState(client.id, {
        delivery_preference: {
          ...savedDeliveryPreference,
          mode: deliveryMode,
          document_channel: documentChannel || null,
          document_confirmed: documentConfirmed,
          document_consultation_id: validated.consultationId,
          document_content_sha256: validated.contentSha256,
          document_exported_at: new Date().toISOString(),
        },
      });
      setPaperExported(true);
    } catch (e) {
      setErr(e?.message || 'Erreur export Word');
    } finally {
      setExporting(false);
    }
  };

  // V97.13.22 — étape 1 : Anissa marque le protocole comme expédié
  const finalizeDocumentOnly = async () => {
    setBusy(true); setErr(null);
    try {
      if (deliveryMode !== 'document' || !documentChannel || !documentConfirmed) {
        throw new Error('Exporte le document, choisis son canal et confirme sa remise.');
      }
      if (!documentPlanProof.consultationId || !documentPlanProof.contentSha256) {
        throw new Error('La preuve du document exporté est absente. Exporte-le à nouveau.');
      }
      const delivered = await transitions.markDeliveredByChannel(client.id, {
        mode: 'document',
        documentChannel,
        consultationId: documentPlanProof.consultationId,
        contentSha256: documentPlanProof.contentSha256,
      });
      onDelivered(delivered);
    } catch (e) { setErr(e?.message || 'Erreur'); }
    finally { setBusy(false); }
  };

  const chooseMode = (mode) => {
    if (deliveryModeLocked && mode !== deliveryMode) {
      setErr('Une remise est déjà engagée. Termine sa finalisation sans changer de canal.');
      return;
    }
    setDeliveryMode(mode);
    setDocumentConfirmed(false);
    setErr(null);
    if (mode === 'app') setDocumentChannel('');
  };

  const changeDocumentConfirmation = async (checked) => {
    if (!checked) {
      if (!deliveryModeLocked) setDocumentConfirmed(false);
      return;
    }
    if (!documentChannel) {
      setErr('Choisis le canal utilisé pour remettre le document.');
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      let proof = documentPlanProof;
      if (!proof.consultationId || !proof.contentSha256) {
        const validated = await loadValidatedConsultationForDelivery(
          client.id,
          client?.journey_state,
        );
        proof = {
          consultationId: validated.consultationId,
          contentSha256: validated.contentSha256,
        };
      }
      await transitions.confirmDocumentDelivery(client.id, {
        mode: deliveryMode,
        documentChannel,
        consultationId: proof.consultationId,
        contentSha256: proof.contentSha256,
      });
      setDocumentPlanProof(proof);
      setPaperExported(true);
      setDocumentConfirmed(true);
      setLocalModeLocked(true);
    } catch (error) {
      setDocumentConfirmed(false);
      setErr(error?.message || 'La confirmation de remise n a pas été enregistrée.');
    } finally {
      setBusy(false);
    }
  };

  const openPublicationPreview = async () => {
    if (!includesApp) return;
    if (deliveryMode === 'app_and_document' && (!documentChannel || !documentConfirmed
        || !documentPlanProof.consultationId || !documentPlanProof.contentSha256)) {
      setErr('Choisis le canal puis confirme la remise du document avant de publier dans l app.');
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      await updateJourneyState(client.id, {
        delivery_preference: {
          mode: deliveryMode,
          document_channel: includesDocument ? documentChannel : null,
          document_confirmed: includesDocument ? documentConfirmed : false,
          document_consultation_id: includesDocument ? documentPlanProof.consultationId : null,
          document_content_sha256: includesDocument ? documentPlanProof.contentSha256 : null,
        },
      });
      await onOpenAppPreview({
        deliveryMode,
        documentChannel: includesDocument ? documentChannel : null,
        consultationId: client?.journey_state?.plan_validation?.consultation_id || null,
        contentSha256: client?.journey_state?.plan_validation?.content_sha256 || null,
        onDeliveryProofStale: markAppContentProofStale,
      });
    } catch (e) {
      setErr(e?.message || 'Le mode de livraison n a pas ete enregistre.');
    } finally {
      setBusy(false);
    }
  };

  // `section` ouvre l'aperçu directement au bon endroit quand Anissa clique
  // sur une pièce incomplète. Les boutons génériques n'en passent pas et
  // gardent l'ouverture par défaut (« Préparer le dossier »).
  const openPreparationPreview = async (section = null) => {
    if (!includesApp) return;
    setBusy(true);
    setErr(null);
    try {
      await onOpenAppPreview({
        deliveryMode,
        documentChannel: includesDocument ? documentChannel : null,
        allowPublish: false,
        onDeliveryProofStale: markAppContentProofStale,
        section: typeof section === 'string' ? section : null,
      });
    } catch (e) {
      setErr(e?.message || 'L’aperçu de préparation est indisponible.');
    } finally {
      setBusy(false);
    }
  };

  const deploymentSteps = [
    {
      key: 'plan-validated',
      label: 'Protocole validé',
      hint: `Version V${versionsCount || 1} sauvegardée dans l'atelier`,
      status: 'done',
    },
    ...(includesDocument ? [{
      key: 'paper-export',
      label: 'Document exporté',
      hint: paperExported ? 'Le document validé a été téléchargé.' : 'Exporte le document Word ci-dessous.',
      status: paperExported ? 'done' : 'pending',
    }, {
      key: 'document-confirmed',
      label: 'Remise du document confirmée',
      hint: documentConfirmed ? 'Canal choisi et remise confirmée par Anissa.' : 'Choisis le canal puis confirme la remise.',
      status: documentConfirmed ? 'done' : 'pending',
    }] : []),
    ...(includesApp ? [{
      key: 'app-publication',
      label: 'Programme vérifié dans l’app',
      hint: 'La lettre, les menus, les recettes et la fiche frigo seront relus avant publication.',
      status: 'pending',
    }] : []),
    ...(!deliveryMode ? [{
      key: 'delivery-choice',
      label: 'Mode de livraison à choisir',
      hint: 'Application, document, ou les deux.',
      status: 'pending',
    }] : []),
  ];

  // V97.13.13 — Miroir cliente repense : 1 carte FEATURED (app cliente,
  // coeur du suivi long terme) + 2-3 cartes secondaires plus discretes.
  // App centrale, livret en extension premium (pas l'inverse).
  const featuredAppCard = {
    icon: '📱',
    title: 'Application Anissa Nutrition',
    subtitle: `${prenom} accède à son espace privé sécurisé, disponible mobile & desktop.`,
    contents: [
      { label: 'Protocole personnalisé', desc: `Version V${versionsCount || 1} consultable et lisible chaque jour` },
      { label: 'Fiche frigo plastifiable', desc: 'Matrice repas + suppléments toujours sous la main' },
      { label: 'Suppléments structurés', desc: 'Routine répartie sur 5 moments de la journée' },
      { label: 'Ressentis quotidiens', desc: weightTrackingEnabled ? 'Énergie, digestion, sommeil + poids' : 'Énergie, digestion, sommeil' },
      { label: 'Messagerie directe', desc: 'Réponses Anissa sous 24h en jours ouvrables' },
      { label: 'Adaptations évolutives', desc: `Les versions suivantes du protocole arrivent dans l'app sans rupture` },
    ],
  };

  const secondaryMirrorItems = [];
  if (includePaper) {
    secondaryMirrorItems.push({
      icon: '📄',
      label: 'Document complet validé',
      sub: documentChannel === 'email'
        ? 'Remis par e-mail au format Word.'
        : documentChannel === 'hand_delivery'
          ? 'Remis en main propre.'
          : documentChannel === 'post'
            ? 'Remis par envoi postal.'
            : 'Canal de remise à choisir.',
    });
  }

  return (
    <section className="jrn-stage jrn-stage--delivery jrn-activation-step">
      <StepHead
        index={client?.journey_state?.external_dossier_flow === true ? 6 : 7}
        total={client?.journey_state?.external_dossier_flow === true ? 7 : 8}
        title="Livraison du programme"
        intro={`Le protocole de ${prenom} est prêt. Choisis l'application, un document, ou les deux, puis confirme la remise.`}
      />

      {/* ════════ HERO — Protocole prêt pour activation ════════ */}
      <div className="jrn-activation-hero">
        <div className="jrn-activation-hero__top">
          <span className="jrn-activation-hero__badge">
            <span className="jrn-activation-hero__badge-dot" aria-hidden>✓</span>
            Protocole prêt pour activation
          </span>
        </div>
        <h2 className="jrn-activation-hero__title">
          Le protocole personnalisé de <em>{prenom}</em> est prêt.
        </h2>
        <p className="jrn-activation-hero__lede">
          Toutes les validations cliniques ont été réalisées.
          <span className="jrn-activation-hero__lede-break" /> Le suivi peut maintenant commencer.
        </p>
        <dl className="jrn-activation-hero__meta">
          <div className="jrn-activation-hero__meta-cell">
            <dt>Anissa</dt>
            <dd>Anissa Deroubaix</dd>
          </div>
          <div className="jrn-activation-hero__meta-cell">
            <dt>Pack</dt>
            <dd>{packLabel}</dd>
          </div>
          <div className="jrn-activation-hero__meta-cell">
            <dt>Version protocole</dt>
            <dd>V{versionsCount || 1}</dd>
          </div>
          {analysesCount !== null && (
            <div className="jrn-activation-hero__meta-cell">
              <dt>Analyses retenues</dt>
              <dd>{analysesCount}</dd>
            </div>
          )}
          {validationDateLabel && (
            <div className="jrn-activation-hero__meta-cell">
              <dt>Validé le</dt>
              <dd>{validationDateLabel}</dd>
            </div>
          )}
        </dl>
      </div>

      {/* ════════ Bloc 1 — Options de remise ════════
          V97.13.36 : ordre logique repense (Options → Miroir → Deploiement).
          Configurer AVANT de visualiser : Anissa decide d abord ce qu elle
          inclut (papier oui/non, suivi poids), puis le miroir REFLETE ces
          options, puis la timeline execute. Avant : Options arrivait apres
          le miroir, ce qui demandait a Anissa de re-visualiser. */}
      <div className="jrn-block">
        <div className="jrn-block__head">
          <span className="jrn-block__num">1</span>
          <h3 className="jrn-block__title">Options de remise</h3>
        </div>
        <div role="radiogroup" aria-label="Mode de livraison" style={{
          display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: 12, marginBottom: 18,
        }}>
          {[
            { value: 'app', icon: '📱', title: 'Application', text: 'Programme publié dans l espace cliente.' },
            { value: 'document', icon: '📄', title: 'Document Word', text: 'Document Word remis sans activer l application.' },
            { value: 'app_and_document', icon: '✨', title: 'Application + document', text: 'Les deux formats, avec une seule date de départ.' },
          ].map((option) => {
            const selected = deliveryMode === option.value;
            return (
              <button
                key={option.value}
                type="button"
                role="radio"
                aria-checked={selected}
                onClick={() => chooseMode(option.value)}
                disabled={deliveryModeLocked && !selected}
                style={{
                  padding: 18,
                  borderRadius: 14,
                  border: selected ? '2px solid var(--jrn-accent)' : '1px solid var(--jrn-border)',
                  background: selected ? 'var(--jrn-accent-soft)' : 'var(--jrn-surface)',
                  color: 'var(--jrn-text)',
                  textAlign: 'left',
                  cursor: 'pointer',
                }}
              >
                <span aria-hidden style={{ fontSize: 22 }}>{option.icon}</span>
                <strong style={{ display: 'block', marginTop: 8, fontSize: 14 }}>{option.title}</strong>
                <span style={{ display: 'block', marginTop: 5, fontSize: 12.5, lineHeight: 1.45, color: 'var(--jrn-text-soft)' }}>
                  {option.text}
                </span>
              </button>
            );
          })}
        </div>

        {!deliveryMode && (
          <div className="jrn-surface jrn-surface--quiet" style={{ marginBottom: 16 }}>
            Sélectionne un mode de livraison pour afficher les actions correspondantes.
          </div>
        )}
        {deliveryMode === 'document' && appAlreadyActive && (
          <div className="jrn-surface jrn-surface--quiet" style={{ marginBottom: 16 }}>
            <strong>Espace cliente déjà existant.</strong>
            <p style={{ margin: '5px 0 0' }}>
              Ce mode ne publiera rien de nouveau dans l’app, mais il ne supprime pas un accès déjà actif.
            </p>
          </div>
        )}
        <p className="jrn-block__intro">
          Le choix est administratif : il ne modifie pas le contenu clinique validé.
        </p>

        {includesApp && (
          <DeliveryAppContentSummary
            state={appContentState}
            client={client}
            error={revalidationError}
            onOpenPreview={openPreparationPreview}
            onRevalidate={revalidateAppContent}
            revalidating={revalidatingAppContent}
          />
        )}

        {/* Toggle livret papier */}
        {includesDocument && (<div className="jrn-paper-card">
          <div className="jrn-paper-card__head">
            <div className="jrn-paper-card__head-text">
              <div className="jrn-paper-card__title">
                <span aria-hidden>📄</span> Document Word
              </div>
              <div className="jrn-paper-card__hint">
                {isFirstVersion
                  ? `À remettre par e-mail, en main propre ou par courrier.`
                  : `Une version actualisée peut être remise par le même canal.`}
              </div>
            </div>
          </div>
          {includePaper && (
            <div className="jrn-paper-card__steps">
              <label style={{ display: 'block', marginBottom: 14, fontSize: 12.5, fontWeight: 650 }}>
                Comment le document sera-t-il remis ?
                <select
                  value={documentChannel}
                  disabled={deliveryModeLocked}
                  onChange={(event) => {
                    setDocumentChannel(event.target.value);
                    setDocumentConfirmed(false);
                  }}
                  style={{ display: 'block', width: '100%', marginTop: 7, padding: '10px 12px', borderRadius: 9 }}
                >
                  <option value="">Choisir le canal</option>
                  <option value="email">E-mail (Word)</option>
                  <option value="hand_delivery">Remis en main propre</option>
                  <option value="post">Envoi postal</option>
                </select>
              </label>
              <label style={{ display: 'flex', gap: 9, alignItems: 'flex-start', marginBottom: 14, fontSize: 12.5, lineHeight: 1.45 }}>
                <input
                  type="checkbox"
                  checked={documentConfirmed}
                  disabled={busy || !documentChannel || deliveryModeLocked}
                  onChange={(event) => void changeDocumentConfirmation(event.target.checked)}
                  style={{ marginTop: 2 }}
                />
                Je confirme que le document validé a bien été remis ou envoyé à {prenom} par ce canal.
              </label>
              <div className="jrn-paper-card__steps-label">Marche à suivre</div>
              <ol className="jrn-paper-card__steps-list">
                <li>Exporter le document Word ci-dessous</li>
                <li>
                  {documentChannel === 'email' && 'Envoyer le document Word par e-mail'}
                  {documentChannel === 'hand_delivery' && 'Imprimer si souhaité, puis remettre en main propre'}
                  {documentChannel === 'post' && 'Imprimer, préparer le pli, puis expédier'}
                  {!documentChannel && 'Choisir le canal de remise'}
                </li>
                <li>Cocher la confirmation uniquement après la remise réelle</li>
              </ol>
              <button
                onClick={handleExportWord}
                disabled={exporting}
                className="jrn-btn jrn-btn--soft jrn-paper-card__btn"
              >
                {exporting ? 'Export en cours…' : (paperExported ? '✓ Word téléchargé — Re-télécharger' : '📥 Exporter le Word')}
              </button>
            </div>
          )}
        </div>)}

        {/* Toggle suivi poids — option fine */}
        {includesApp && (<>
        <div className="jrn-inline-card jrn-inline-card--accent">
          <div className="jrn-inline-card__row">
            <div className="jrn-inline-card__body">
              <div className="jrn-inline-card__title">
                <span aria-hidden>⚖️</span> Suivi du poids
              </div>
              <div className="jrn-inline-card__hint">
                Activer si le poids est un axe du protocole. {prenom} saisira sa pesée dans le ressenti quotidien.
              </div>
            </div>
            <WeightTogglesInline client={client} compact />
          </div>
        </div>

        {/* 2026-07-21 — Photos de repas (opt-in par cliente, flux Suivi).
            Placé ici, à côté du poids : c'est le moment où Anissa décide ce
            que l'app demandera à la cliente au quotidien. */}
        <div className="jrn-inline-card jrn-inline-card--accent">
          <div className="jrn-inline-card__row">
            <div className="jrn-inline-card__body">
              <div className="jrn-inline-card__title">
                Photos de repas
              </div>
              <div className="jrn-inline-card__hint">
                {prenom} pourra photographier ses repas (collations comprises) depuis son app — les photos arrivent dans ton flux Suivi. À réserver aux profils à l&apos;aise avec l&apos;image de leurs assiettes.
              </div>
            </div>
            <MealPhotosToggleInline client={client} />
          </div>
        </div>
        </>)}
      </div>

      {/* ════════ Bloc 2 — Miroir cliente (app cliente centrale) ════════ */}
      <div className="jrn-block">
        <div className="jrn-block__head">
          <span className="jrn-block__num">2</span>
          <h3 className="jrn-block__title">Ce que reçoit {prenom}</h3>
        </div>
        <p className="jrn-block__intro">
          {deliveryMode === 'document'
            ? `${prenom} recevra uniquement le document validé. Le suivi reste piloté par Anissa dans le SaaS.`
            : deliveryMode === 'app_and_document'
              ? `${prenom} recevra l'espace cliente et une copie du document validé.`
              : deliveryMode === 'app'
                ? `${prenom} recevra le programme dans son espace cliente sécurisé.`
                : 'Le contenu final apparaîtra ici après le choix du mode de livraison.'}
        </p>

        {/* Carte FEATURED — App cliente, centre de l'expérience.
            V97.13.14 : mockup téléphone à gauche pour incarner visuellement
            l'app comme cœur émotionnel du parcours (vs simple carte texte). */}
        {includesApp && (<article className="jrn-mirror-featured jrn-mirror-featured--with-phone">
          <div className="jrn-mirror-featured__phone" aria-hidden="true">
            <div className="jrn-phone-mockup">
              <div className="jrn-phone-mockup__screen">
                {/* iOS-like status bar */}
                <div className="jrn-phone-mockup__statusbar">
                  <span>9:24</span>
                  <span className="jrn-phone-mockup__statusbar-right">
                    <span className="jrn-phone-mockup__bar jrn-phone-mockup__bar--s" />
                    <span className="jrn-phone-mockup__bar jrn-phone-mockup__bar--m" />
                    <span className="jrn-phone-mockup__bar jrn-phone-mockup__bar--l" />
                    <span className="jrn-phone-mockup__battery" />
                  </span>
                </div>

                {/* Header — avatar Anissa + greeting */}
                <div className="jrn-phone-mockup__header">
                  <div className="jrn-phone-mockup__avatar">A</div>
                  <div className="jrn-phone-mockup__header-text">
                    <div className="jrn-phone-mockup__hi">Bonjour {prenom}</div>
                    <div className="jrn-phone-mockup__sub">Jour 1 · semaine 1</div>
                  </div>
                </div>

                {/* Mini courbe ressentis 7j */}
                <div className="jrn-phone-mockup__chart">
                  <div className="jrn-phone-mockup__chart-head">
                    <span className="jrn-phone-mockup__chart-label">Énergie · 7 jours</span>
                    {/* P3.1 — aucun chiffre clinique inventé, même en décoration :
                        le mockup est un exemple, pas un vrai résultat cliente. */}
                    <span className="jrn-phone-mockup__chart-trend">exemple</span>
                  </div>
                  <svg className="jrn-phone-mockup__chart-svg" viewBox="0 0 200 44" preserveAspectRatio="none">
                    <defs>
                      <linearGradient id="jrnPhoneArea" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#1A2E1F" stopOpacity="0.22" />
                        <stop offset="100%" stopColor="#1A2E1F" stopOpacity="0" />
                      </linearGradient>
                    </defs>
                    <path d="M5,36 Q35,32 60,26 T120,16 T195,7 L195,44 L5,44 Z" fill="url(#jrnPhoneArea)" />
                    <path d="M5,36 Q35,32 60,26 T120,16 T195,7" stroke="#1A2E1F" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
                    <circle cx="5" cy="36" r="2.2" fill="#1A2E1F" />
                    <circle cx="42" cy="30" r="2.2" fill="#1A2E1F" />
                    <circle cx="80" cy="22" r="2.2" fill="#1A2E1F" />
                    <circle cx="118" cy="16" r="2.2" fill="#1A2E1F" />
                    <circle cx="156" cy="11" r="2.2" fill="#1A2E1F" />
                    <circle cx="195" cy="7" r="3" fill="#fff" stroke="#1A2E1F" strokeWidth="2" />
                  </svg>
                </div>

                {/* Card repas */}
                <div className="jrn-phone-mockup__card">
                  <span className="jrn-phone-mockup__card-icon">🥑</span>
                  <div className="jrn-phone-mockup__card-body">
                    <div className="jrn-phone-mockup__card-label">Petit-déjeuner</div>
                    <div className="jrn-phone-mockup__card-text">Œufs · avocat · pain complet</div>
                  </div>
                  <span className="jrn-phone-mockup__check">✓</span>
                </div>

                {/* Message Anissa */}
                <div className="jrn-phone-mockup__msg">
                  <div className="jrn-phone-mockup__msg-avatar">A</div>
                  <div className="jrn-phone-mockup__msg-bubble">
                    Comment se passe ta semaine ?
                  </div>
                </div>

                {/* Tab bar */}
                <div className="jrn-phone-mockup__tabbar">
                  <span className="jrn-phone-mockup__tab jrn-phone-mockup__tab--active" />
                  <span className="jrn-phone-mockup__tab" />
                  <span className="jrn-phone-mockup__tab" />
                  <span className="jrn-phone-mockup__tab" />
                </div>
              </div>
            </div>
          </div>
          <div className="jrn-mirror-featured__body">
            <header className="jrn-mirror-featured__head jrn-mirror-featured__head--no-icon">
              <div className="jrn-mirror-featured__head-text">
                <div className="jrn-mirror-featured__eyebrow">Espace cliente — cœur du suivi</div>
                <h4 className="jrn-mirror-featured__title">{featuredAppCard.title}</h4>
                <p className="jrn-mirror-featured__subtitle">{featuredAppCard.subtitle}</p>
              </div>
            </header>
            <ul className="jrn-mirror-featured__contents">
              {featuredAppCard.contents.map((c, i) => (
                <li key={i} className="jrn-mirror-featured__content">
                  <span className="jrn-mirror-featured__content-check" aria-hidden>✓</span>
                  <div>
                    <div className="jrn-mirror-featured__content-label">{c.label}</div>
                    <div className="jrn-mirror-featured__content-desc">{c.desc}</div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </article>)}

        {/* Cartes secondaires : extensions premium */}
        {secondaryMirrorItems.length > 0 && (
          <>
            <div className="jrn-mirror-secondary-label">
              {includesApp ? 'Document complémentaire' : 'Format remis'}
            </div>
            <ul className="jrn-mirror-secondary">
              {secondaryMirrorItems.map((item, i) => (
                <li key={i} className="jrn-mirror-item">
                  <span className="jrn-mirror-icon" aria-hidden>{item.icon}</span>
                  <div className="jrn-mirror-text">
                    <div className="jrn-mirror-label">{item.label}</div>
                    <div className="jrn-mirror-sub">{item.sub}</div>
                  </div>
                </li>
              ))}
            </ul>
          </>
        )}

        {includesApp && (<div className="jrn-surface jrn-surface--quiet" style={{ marginTop: 18 }}>
          <strong>Une dernière vérification avant publication</strong>
          <p style={{ margin: '6px 0 0' }}>
            La fenêtre d’aperçu montre la lettre, le plan et les menus, les recettes,
            la fiche frigo et les suppléments tels qu’ils seront envoyés. Les
            contenus proposés restent modifiables et rien n’est envoyé
            avant ton clic final sur « Publier le programme ».
          </p>
        </div>)}
      </div>

      {/* ════════ Bloc 3 — Déploiement de l'accompagnement ════════
          V97.13.36 : remonte ici (etait Bloc 1) pour que la timeline arrive
          APRES la config et le miroir. Ordre narratif : configure → visualise
          → execute. */}
      <div className="jrn-block">
        <div className="jrn-block__head">
          <span className="jrn-block__num">3</span>
          <h3 className="jrn-block__title">Déploiement de l'accompagnement</h3>
        </div>
        <p className="jrn-block__intro">
          Cinq jalons s'enchaînent. Le premier est validé. La confirmation finale ci-dessous déclenche les suivants.
        </p>
        <ol className="jrn-deploy-list">
          {deploymentSteps.map((step, i) => (
            <li key={step.key} className={`jrn-deploy-item jrn-deploy-item--${step.status}`}>
              <span className="jrn-deploy-marker" aria-hidden>
                {step.status === 'done' ? '✓' : step.status === 'skipped' ? '—' : i + 1}
              </span>
              <div className="jrn-deploy-body">
                <div className="jrn-deploy-label">{step.label}</div>
                <div className="jrn-deploy-hint">{step.hint}</div>
              </div>
              {step.status === 'pending' && (
                <span className="jrn-deploy-status">À l'activation</span>
              )}
              {step.status === 'done' && (
                <span className="jrn-deploy-status jrn-deploy-status--done">Terminé</span>
              )}
            </li>
          ))}
        </ol>
      </div>

      {/* ════════ CTA — V97.13.22 machine à 3 états (workflow hybride) ════════
          État 1 : protocole prêt → bouton "Marquer comme expédié"
          État 2 : expédié, attente cliente → statut + bouton secondaire manuel
          État 3 : reçu (cliente confirmée) → bouton "Activer l'espace cliente"
          Workflow premium physique + digital. */}

      {/* V97.86 — App-only (pas de courrier) : un seul geste. On évite le rituel
          postal fantôme (expédié → en acheminement → reçu) quand il n'y a rien
          à expédier. Les 3 états ci-dessous ne servent QUE pour l'envoi papier. */}
      {deliveryMode === 'app' && (
        <div className="jrn-activation-cta jrn-activation-cta--ready">
          <div className="jrn-activation-cta__copy">
            <div className="jrn-activation-cta__eyebrow">
              <span className="jrn-activation-cta__status-check" aria-hidden>✓</span> Prêt à vérifier
            </div>
            <p className="jrn-activation-cta__poetic">
              Le protocole de <em>{prenom}</em> est prêt dans son app.
            </p>
            <p className="jrn-activation-cta__sub">
              Ouvre l’aperçu, vérifie chaque section, puis publie. Le Suivi ne
              commencera qu’après une publication réussie.
            </p>
          </div>
          <button
            onClick={openPublicationPreview}
            disabled={busy}
            className="jrn-btn jrn-btn--hero jrn-activation-cta__btn"
          >
            Vérifier puis publier dans l’app →
          </button>
        </div>
      )}

      {/* État 1 — Protocole prêt à expédier (parcours papier uniquement) */}
      {deliveryMode === 'document' && (
        <div className="jrn-activation-cta jrn-activation-cta--ship">
          <div className="jrn-activation-cta__copy">
            <div className="jrn-activation-cta__eyebrow">Document prêt</div>
            <p className="jrn-activation-cta__poetic">
              Confirme la livraison du programme de <em>{prenom}</em>.
            </p>
            <p className="jrn-activation-cta__sub">
              Cette confirmation enregistre le canal, clôt la livraison et démarre le pack.
              {appAlreadyActive ? ' Aucune nouvelle version ne sera publiée dans l’app.' : ' Aucun espace cliente ne sera activé.'}
            </p>
          </div>
          <button
            onClick={finalizeDocumentOnly}
            disabled={busy || !documentChannel || !documentConfirmed
              || !documentPlanProof.consultationId || !documentPlanProof.contentSha256}
            className="jrn-btn jrn-btn--hero jrn-activation-cta__btn"
          >
            {busy ? 'Enregistrement…' : 'Confirmer la remise et démarrer le suivi →'}
          </button>
        </div>
      )}

      {/* État 3 — Reçu, espace cliente à activer */}
      {deliveryMode === 'app_and_document' && (
        <div className="jrn-activation-cta jrn-activation-cta--ready">
          <div className="jrn-activation-cta__copy">
            <div className="jrn-activation-cta__eyebrow">
              <span className="jrn-activation-cta__status-check" aria-hidden>✓</span> Deux formats prêts
            </div>
            <p className="jrn-activation-cta__poetic">
              Le document de <em>{prenom}</em> est confirmé. Il reste à publier l’application.
            </p>
            <p className="jrn-activation-cta__sub">
              Ouvre l’aperçu, vérifie chaque section, puis publie le programme.
              Le Suivi ne commencera qu’après une publication réussie.
            </p>
          </div>
          <button
            onClick={openPublicationPreview}
            disabled={busy || !documentChannel || !documentConfirmed
              || !documentPlanProof.consultationId || !documentPlanProof.contentSha256}
            className="jrn-btn jrn-btn--hero jrn-activation-cta__btn"
          >
            Vérifier puis publier dans l’app →
          </button>
        </div>
      )}

      <ErrorLine msg={err} />
    </section>
  );
}

// ═══════════════════════════════════════════════════════════════════
// ÉTAPE 8 — SUIVI
// ═══════════════════════════════════════════════════════════════════

// V97.41 (roadmap 1.4) — styles du badge de synchro app cliente (page Suivi).
const syncBadgeRowStyle = {
  marginTop: 6,
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  fontSize: 11,
};
const syncBadgeMutedStyle = {
  color: 'var(--jrn-text-muted, #6b6f6b)',
  letterSpacing: '.01em',
};
const syncBadgeErrorStyle = {
  color: '#e08a8a',
  letterSpacing: '.01em',
};
const syncRetryBtnStyle = {
  background: 'rgba(255,255,255,.04)',
  border: '1px solid rgba(255,255,255,.12)',
  borderRadius: 6,
  color: 'var(--jrn-text-muted, #8a8f8a)',
  fontSize: 10.5,
  fontFamily: 'inherit',
  padding: '2px 8px',
  cursor: 'pointer',
};

// V97.122 — Helpers RDV. `rdvIsoToLocalInput` convertit un instant ISO stocké
// vers la valeur locale attendue par <input type="datetime-local">.
// `formatRdvLong` produit un libellé humain (jour + heure) en fuseau local.
function rdvIsoToLocalInput(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
}
function formatRdvLong(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const hasTime = d.getHours() !== 0 || d.getMinutes() !== 0;
  return d.toLocaleString('fr-CH', {
    weekday: 'long', day: 'numeric', month: 'long',
    ...(hasTime ? { hour: '2-digit', minute: '2-digit' } : {}),
  });
}

// V97.123 — Résumé lisible d'un ressenti cliente (objet JSONB venu de l'app :
// { date, digestion, fatigue, energie, faim, note, weight_kg }). Le tiroir
// « Signes vitaux » ne doit JAMAIS afficher le JSON brut. Retourne la liste
// des dimensions renseignées avec leur libellé humain.
const FEEDBACK_SUMMARY_DIMS = [
  { key: 'digestion', label: 'Digestion', values: { better: 'Mieux', same: 'Pareil', worse: 'Pire' } },
  { key: 'fatigue',   label: 'Fatigue',   values: { better: 'Mieux', same: 'Pareil', worse: 'Pire' } },
  { key: 'energie',   label: 'Énergie',   values: { good: 'Bonne', ok: 'OK', low: 'Basse' } },
  { key: 'faim',      label: 'Faim',      values: { low: 'Pas assez', ok: 'OK', high: 'Trop' } },
];
function formatFeedbackSummary(f) {
  if (!f || typeof f !== 'object') return [];
  return FEEDBACK_SUMMARY_DIMS
    .filter((d) => f[d.key])
    .map((d) => ({ label: d.label, value: d.values[f[d.key]] || String(f[d.key]) }));
}

export function OneShotFollowupTerminal({
  client,
  prenom,
  packLabel,
  clientAppChannelEnabled = false,
  onExit,
}) {
  return (
    <section className="jrn-followup-step jrn-cockpit" aria-labelledby="oneshot-terminal-title">
      <div className="jrn-cockpit-card jrn-cockpit-card--priority jrn-cockpit-card--tone-ok">
        <div className="jrn-cockpit-card__head">
          <span className="jrn-cockpit-card__eyebrow">Programme livré</span>
          <span className="jrn-cockpit-card__tone jrn-cockpit-card__tone--ok" aria-hidden="true">✓</span>
        </div>
        <h2 id="oneshot-terminal-title" className="jrn-cockpit-card__priority-label">
          Accompagnement ponctuel terminé
        </h2>
        {packLabel && (
          <p className="jrn-cockpit-card__priority-reason">{packLabel}</p>
        )}
        <p className="jrn-cockpit-card__priority-reason">
          Aucun suivi continu n’est inclus. Un pack 3 ou 6 mois est nécessaire pour continuer l’accompagnement.
        </p>
        <p className="jrn-cockpit-card__priority-reason">
          {clientAppChannelEnabled
            ? `Le programme reste accessible à ${prenom} pendant le mois d’application autonome inclus, puis elle peut choisir l’abonnement mensuel.`
            : `Le programme de ${prenom} reste disponible dans son dossier.`}
        </p>
      </div>
      {clientAppChannelEnabled && client && <ClientAccessManager client={client} />}
      {onExit && (
        <div className="jrn-actions" style={{ marginTop: 'var(--jrn-6)' }}>
          <button type="button" onClick={onExit} className="jrn-btn jrn-btn--ghost">
            ← Retour dashboard
          </button>
        </div>
      )}
    </section>
  );
}

function StepFollowup({
  client,
  journey,
  onChange,
  onExit,
  onEditProfile,
  onSendPackReview,
  onViewHistory,
  onOpenAppPreview,
  onOpenMessages,
  phaseTransitionRevision = 0,
  resumeProgramUpdateScope = null,
  onConsumeResumeProgramUpdateScope,
}) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [feedbacks, setFeedbacks] = useState([]);
  const [loadingFb, setLoadingFb] = useState(true);
  const [adapting, setAdapting] = useState(false);
  // V97.41 (roadmap 1.4) — badge de synchro app cliente + retry.
  // lastSyncAt = horodatage du dernier fetch reussi des ressentis/pesees.
  // syncError = true si le dernier fetch a echoue (reseau/proxy/app cliente).
  // syncReloadKey = bump pour re-declencher l'effet de fetch (bouton Reessayer).
  const [lastSyncAt, setLastSyncAt] = useState(null);
  const [syncError, setSyncError] = useState(false);
  const [feedbackSnapshotStatus, setFeedbackSnapshotStatus] = useState('loading');
  const [syncReloadKey, setSyncReloadKey] = useState(0);
  const [cycleReviews, setCycleReviews] = useState([]);
  const [cycleReviewsStatus, setCycleReviewsStatus] = useState('loading');
  const [cycleReviewsError, setCycleReviewsError] = useState(null);
  const [cycleReviewsReloadKey, setCycleReviewsReloadKey] = useState(0);
  const [longitudinalSnapshot, setLongitudinalSnapshot] = useState(null);
  const [longitudinalStatus, setLongitudinalStatus] = useState('idle');
  const [longitudinalError, setLongitudinalError] = useState(null);
  const [longitudinalReloadKey, setLongitudinalReloadKey] = useState(0);
  const [briefingCycleReview, setBriefingCycleReview] = useState(null);
  const [briefingWorkflowIntent, setBriefingWorkflowIntent] = useState(null);
  const clientAppChannelEnabled = isClientAppChannelEnabled(client, journey);
  const documentDeliveryChannelEnabled = !clientAppChannelEnabled
    && journey?.delivery_proof?.mode === 'document';
  const shouldSyncNextRdvToApp = shouldSyncNextRdvToClientApp(client, journey);
  const handleRetrySync = useCallback(() => setSyncReloadKey((k) => k + 1), []);
  const [versions, setVersions] = useState([]);
  const [previewVersion, setPreviewVersion] = useState(null);
  // V97.28 — Modal éditeur inline pour cycle de suivi. Anissa peut éditer
  // la V courante (ou créer une suite) sans repasser par l'étape 6 du parcours.
  // current_step reste sur 'followup'.
  const [showInlineEditor, setShowInlineEditor] = useState(false);
  const [inlineEditorContext, setInlineEditorContext] = useState(null);
  const [inlineSavedConsultation, setInlineSavedConsultation] = useState(null);
  const [showProgramUpdateScope, setShowProgramUpdateScope] = useState(false);
  const [pendingProgramUpdateContext, setPendingProgramUpdateContext] = useState(null);
  const [safetyPreflightBlock, setSafetyPreflightBlock] = useState(null);
  const [consultationOutcome, setConsultationOutcome] = useState(null);
  const [showDocumentDelivery, setShowDocumentDelivery] = useState(false);
  const [confirmedDocumentVersionIds, setConfirmedDocumentVersionIds] = useState(() => new Set());
  // « Renouveler le suivi » : nouvelle période sur la même fiche (nouveau pack,
  // planning recalculé, compteur consultations remis à zéro). L'historique
  // (versions, consultations, poids, ressentis) est conservé intact.
  const [showRenewModal, setShowRenewModal] = useState(false);
  const [renewing, setRenewing] = useState(false);
  const [phaseTransitionRun, setPhaseTransitionRun] = useState(null);
  const [pendingTransitionActivation, setPendingTransitionActivation] = useState(null);
  const [pendingTransitionNotification, setPendingTransitionNotification] = useState(null);
  const [retryingTransitionActivation, setRetryingTransitionActivation] = useState(false);
  const [retryingTransitionNotification, setRetryingTransitionNotification] = useState(false);
  const [preparingTransition, setPreparingTransition] = useState(false);
  const [confirmingDocumentTransition, setConfirmingDocumentTransition] = useState(false);
  const [cancellingPhaseTransition, setCancellingPhaseTransition] = useState(false);
  const [exportingTransitionDocument, setExportingTransitionDocument] = useState(false);
  const [exportedTransitionProof, setExportedTransitionProof] = useState(null);
  const started = !!journey?.followup_started;
  const pack = PACK_DEFINITIONS[client.packType] || null;
  const packAccess = getPackAccess(client.packType);
  useEffect(() => {
    if (!resumeProgramUpdateScope) return;
    const resumedScope = resolveFollowupProgramUpdateScope(resumeProgramUpdateScope);
    if (resumedScope) {
      setPendingProgramUpdateContext({
        mode: 'manual',
        resumeScope: resumedScope.value,
      });
      setShowProgramUpdateScope(true);
    }
    onConsumeResumeProgramUpdateScope?.();
  }, [onConsumeResumeProgramUpdateScope, resumeProgramUpdateScope]);
  const phaseTransitionSafetyState = useMemo(() => {
    try {
      return {
        snapshot: buildPhaseTransitionSafetySnapshot(client?.form),
        issue: null,
      };
    } catch (error) {
      return {
        snapshot: null,
        issue: error?.message || 'Compléter la fiche cliente.',
      };
    }
  }, [client?.form]);

  // V97.13.16 — variables d'identité partagées avec étape 7
  const prenom = (client?.form?.prenom || client?.prenom || 'la cliente').trim();

  // V97.17.1 — Consultation active (pour protocol_phases dans le cockpit timeline)
  // Pattern identique a handleAdaptFromFeedback ligne 3865.
  const [activeConsult, setActiveConsult] = useState(() =>
    client?.id ? ((getNutritionConsultations(client.id) || [])[0] || null) : null
  );
  const activeDocumentConfirmed = Boolean(
    activeConsult?.id && confirmedDocumentVersionIds.has(activeConsult.id),
  );
  // Une preuve Word vaut uniquement pour la révision exacte exportée. Toute
  // nouvelle instance canonique/éditoriale impose un nouvel export.
  useEffect(() => {
    setExportedTransitionProof(null);
  }, [activeConsult]);
  // Recharge si client change (changement de cliente).
  // V97.68 — fallback Supabase quand le store local est vide (cliente chargée
  // depuis la DB, ex. Melissa) : sinon activeConsult reste null et le module de
  // génération croit à tort qu'il n'y a pas de plan. Même pattern que
  // openAppPreview. Fiabilise aussi la frise (qui lit la même activeConsult).
  useEffect(() => {
    let cancelled = false;
    if (!client?.id) {
      setActiveConsult(null);
      return undefined;
    }
    const local = (getNutritionConsultations(client.id) || [])[0] || null;
    if (local && phaseTransitionRevision === 0) {
      setActiveConsult(local);
      return undefined;
    }
    (async () => {
      try {
        const { data } = await supabase
          .from('nutrition_consultations')
          .select('*')
          .eq('client_id', client.id)
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle();
        if (!cancelled && data) setActiveConsult(data);
      } catch {
        // silencieux : sans consultation, le module affiche « plan à rédiger »
      }
    })();
    return () => { cancelled = true; };
  }, [client?.id, phaseTransitionRevision]);

  const refreshConfirmedVersions = useCallback(async (expectedId = null) => {
    const list = await loadConfirmedNutritionVersions(client.id);
    const expected = expectedId ? list.find((version) => version.id === expectedId) : null;
    if (expectedId && !expected) {
      throw new Error('La version sauvegardée n’a pas été retrouvée dans le cloud.');
    }
    setVersions(list);
    setActiveConsult(expected || list[0] || null);
    return { list, active: expected || list[0] || null };
  }, [client.id]);

  const assertDocumentVersionEditable = useCallback(async (consultation) => {
    if (!consultation?.id) return;
    readFollowupProgramUpdateScope(consultation);
    const appPublication = consultation?.client_app_publication
      ?? consultation?.clientAppPublication
      ?? null;
    if (appPublication !== null) {
      throw new Error('Cette version est déjà publiée. Crée une nouvelle version pour la modifier.');
    }
    if (!documentDeliveryChannelEnabled || consultation?.isFollowup !== true) {
      return;
    }
    if (confirmedDocumentVersionIds.has(consultation.id)) {
      throw new Error('Cette version a déjà été remise. Crée une nouvelle version pour la modifier.');
    }
    const loadedReference = await loadFollowupDocumentReference({
      clientId: client.id,
      consultationId: consultation.id,
    });
    if (loadedReference.reference.status === 'confirmed') {
      throw new Error('Cette version a déjà été remise. Crée une nouvelle version pour la modifier.');
    }
  }, [client.id, confirmedDocumentVersionIds, documentDeliveryChannelEnabled]);

  useEffect(() => {
    let cancelled = false;
    const consultationId = activeConsult?.id || null;
    const isFollowup = activeConsult?.isFollowup === true || activeConsult?.is_followup === true;
    if (!documentDeliveryChannelEnabled || !client?.id || !consultationId || !isFollowup) {
      return undefined;
    }
    (async () => {
      try {
        const loaded = await loadFollowupDocumentReference({
          clientId: client.id,
          consultationId,
        });
        if (cancelled) return;
        setConfirmedDocumentVersionIds((current) => {
          const next = new Set(current);
          if (loaded.reference.status === 'confirmed') next.add(consultationId);
          else next.delete(consultationId);
          return next;
        });
      } catch {
        // Fail-closed : aucune preuve confirmée n'est annoncée sans relecture
        // canonique. Le clic sur la porte documentaire réessaiera explicitement.
      }
    })();
    return () => { cancelled = true; };
  }, [activeConsult?.id, activeConsult?.isFollowup, activeConsult?.is_followup, client?.id, documentDeliveryChannelEnabled]);

  useEffect(() => {
    let cancelled = false;
    if (!client?.id) {
      setPhaseTransitionRun(null);
      setPendingTransitionActivation(null);
      setPendingTransitionNotification(null);
      return undefined;
    }
    (async () => {
      try {
        const [run, pendingActivation, pendingNotification] = await Promise.all([
          getActivePhaseTransition(client.id),
          getPendingPhaseTransitionActivation(client.id),
          getPendingPhaseTransitionNotification(client.id),
        ]);
        if (cancelled) return;
        setPhaseTransitionRun(run);
        setPendingTransitionActivation(pendingActivation);
        setPendingTransitionNotification(pendingNotification);
      } catch (error) {
        if (!cancelled) {
          setPhaseTransitionRun(null);
          setPendingTransitionActivation(null);
          setPendingTransitionNotification(null);
          setErr(error?.message || 'Impossible de charger la phase suivante.');
        }
      }
    })();
    return () => { cancelled = true; };
  }, [client?.id, phaseTransitionRevision]);

  // Refonte Suivi (Phase 2) — module de génération « prêt à publier ».
  // Sauvegarde les pièces générées (lettre d'intro, recettes) sur la
  // consultation active, puis rafraîchit l'état local.
  const handleSaveGenerated = useCallback(async (generated) => {
    if (!activeConsult || !generated) return;
    const cloudVersions = await loadConfirmedNutritionVersions(client.id);
    const selectedId = activeConsult.id;
    const cloudTarget = cloudVersions.find((version) => version.id === selectedId);
    if (!cloudTarget) {
      throw new Error('La version active exacte est introuvable. Recharge la fiche.');
    }
    if (packAccess.continuousFollowup && cloudVersions[0]?.id !== selectedId) {
      throw new Error('Une version plus récente existe. Recharge ou ouvre la dernière version avant de générer.');
    }
    const canonical = assertGeneratedPiecesSaveTarget(
      cloudTarget,
      client.id,
      packAccess.continuousFollowup,
    );
    await assertDocumentVersionEditable(canonical);
    const next = { ...canonical, ...generated };
    const saved = await saveNutritionConsultationConfirmed(next);
    await refreshConfirmedVersions(saved.id);
    return saved;
  }, [
    activeConsult,
    assertDocumentVersionEditable,
    client.id,
    packAccess.continuousFollowup,
    refreshConfirmedVersions,
  ]);

  // Refonte Suivi (Phase 3b) — données de l'alerte de la bande miroir :
  // statut de publication (fetchClientsStatus) + brouillon en attente
  // (listPlanDrafts). Fail-safe : null si config app absente ou erreur → la
  // bande affiche juste l'état visible, sans alerte.
  const [mirrorAlert, setMirrorAlert] = useState({ statusEntry: null, pendingDraft: null, draftsCheckFailed: false });
  useEffect(() => {
    let cancelled = false;
    if (!client?.id || !clientAppChannelEnabled) {
      setMirrorAlert({ statusEntry: null, pendingDraft: null, draftsCheckFailed: false });
      return undefined;
    }
    (async () => {
      const email = client?.form?.email || client?.email || null;
      let statusEntry = null;
      let pendingDraft = null;
      try {
        if (email) {
          const map = await fetchClientsStatus([{ stagingClientId: client.id, email }]);
          statusEntry = (map && Object.values(map)[0]) || null;
        }
      } catch (e) {
        // Sans statusEntry, resolveActivePlanPublication affiche déjà
        // « Statut de publication inconnu » : l'écran ne ment pas.
        console.warn('[bande miroir] statut cliente indisponible :', e?.message);
      }
      // L'échec de cette recherche était avalé : `pendingDraft` restait null
      // et l'absence d'alerte se lisait comme « rien en attente ». Un
      // brouillon qui expire pouvait passer inaperçu.
      let draftsCheckFailed = false;
      try {
        const res = await listPlanDrafts({ clientId: client.id, status: 'pending' });
        if (!res?.ok) draftsCheckFailed = true;
        else if (Array.isArray(res.data) && res.data.length > 0) {
          pendingDraft = [...res.data].sort(
            (a, b) => (Date.parse(b.created_at) || 0) - (Date.parse(a.created_at) || 0),
          )[0];
        }
      } catch (e) {
        draftsCheckFailed = true;
        console.warn('[bande miroir] brouillons en attente illisibles :', e?.message);
      }
      if (!cancelled) setMirrorAlert({ statusEntry, pendingDraft, draftsCheckFailed });
    })();
    return () => { cancelled = true; };
  }, [client?.id, clientAppChannelEnabled]);

  // Descripteur bande miroir : état visible (journey_state) + alerte (fetchée).
  const clientMirror = useMemo(
    () => resolveClientMirror(client, {
      statusEntry: mirrorAlert.statusEntry,
      pendingDraft: mirrorAlert.pendingDraft,
      draftsCheckFailed: mirrorAlert.draftsCheckFailed,
    }),
    [client, mirrorAlert],
  );

  // Refonte Suivi (Phase 4) — marqueurs de publication pour la frise (mêmes
  // données que l'alerte de la bande miroir).
  const publicationMarkers = useMemo(
    () => buildPublicationMarkers({
      pendingDraft: mirrorAlert.pendingDraft,
      statusEntry: mirrorAlert.statusEntry,
    }),
    [mirrorAlert],
  );

  // Statut de publication du PLAN ACTIF (version minimale — chip sur la frise).
  // Dérivé du statut live clients-status déjà fetché. Fail-closed.
  const activePlanPublication = useMemo(
    () => resolveActivePlanPublication(mirrorAlert.statusEntry),
    [mirrorAlert.statusEntry],
  );

  // Verdict de clairance clinique du plan actif (badge du module + gate publier).
  const programClearance = useMemo(() => {
    const planText = activeConsult?.nutrition_plan
      || activeConsult?.nutritionPlan
      || activeConsult?.plan_text
      || '';
    if (!planText.trim()) return null;
    try {
      return assertPlanClinicallyCleared(planText, { form: client?.form });
    } catch {
      return null;
    }
  }, [activeConsult, client]);

  // Refonte Suivi (frise unique) — complétude du programme : MÊME fonction
  // pure que le module de génération (computeProgramCompleteness). Sert
  // uniquement à décider si « générer / publier » est l'action du moment
  // (carte « Maintenant »). Aucune règle nouvelle, aucun fetch.
  const completeness = useMemo(
    () => computeProgramCompleteness(client, activeConsult),
    [client, activeConsult],
  );
  const phaseTransitionDraftReady = Boolean(getPhaseTransitionDraft(activeConsult));
  // « Générer / publier » est l'action du moment si des pièces manquent OU si
  // une version préparée attend encore sa validation côté app (alerte miroir).
  // Pur agencement d'affichage : les gates réels (complétude + clairance)
  // restent DANS ProgramGenerationPanel — la seule porte « Publier ».
  const generationCurrent = shouldShowProgramGeneration({
    phaseTransitionRunning: Boolean(phaseTransitionRun),
    phaseTransitionDraftReady,
    programIsLive: Boolean(started && clientMirror?.visible?.isLive),
    allReady: completeness.allReady,
    clientAppPending: Boolean(clientAppChannelEnabled && clientMirror?.alert?.kind === 'pending'),
    documentRequired: Boolean(documentDeliveryChannelEnabled && packAccess.continuousFollowup),
    documentConfirmed: activeDocumentConfirmed,
  });

  // V97.39.8 (roadmap 1.1) — phases acceptees mais en attente d'une
  // consultation hote (journey_state.pending_protocol_phases).
  const pendingPhases = journey?.pending_protocol_phases || null;

  // Handler save phases — passe par store + reflete local optimiste
  const handleSavePhases = useCallback(
    async (newProtocolPhases) => {
      // V97.39.8 (roadmap 1.1) — Cas "sans consultation hote" (pack Bilan :
      // page Suivi atteinte sans plan, cf. melissa). Anissa accepte le parcours
      // alors qu'aucune consultation n'existe pour le porter. On NE cree PAS de
      // consultation (ca consommerait le compteur de pack) : on range les
      // phases dans journey_state.pending_protocol_phases. Elles seront
      // greffees sur la 1ere consultation creee (effet de greffe ci-dessous).
      // Pas de push : il n'y a pas encore de transition de phase cote cliente.
      if (!activeConsult) {
        if (!client?.id) return;
        await setPendingProtocolPhases(client.id, newProtocolPhases);
        onChange?.();
        return;
      }
      const newActivePhaseId = newProtocolPhases?.phases?.find(
        (phase) => phase.status === 'active',
      )?.id || null;
      const next = {
        ...activeConsult,
        protocol_phases: newProtocolPhases,
        active_phase_id: newActivePhaseId,
      };
      const saved = await saveNutritionConsultationConfirmed(next);
      setActiveConsult(saved);
    },
    [activeConsult, client, onChange]
  );

  // V97.39.8 (roadmap 1.1) — Greffe des phases en attente sur la 1ere
  // consultation qui apparait. CENTRALISE le transfert : peu importe le
  // chemin de creation de la consultation (editeur de plan, "Creer la suite",
  // import…), des qu'une consultation existe et qu'il y a des phases en
  // attente, on les greffe via bakePendingProtocolPhases + on nettoie le
  // champ pending. Pas de push : c'est une INITIALISATION du parcours, pas une
  // transition de phase (aucune phase precedente cote cliente).
  useEffect(() => {
    if (!packAccess.known) return undefined;
    if (!activeConsult || activeConsult.protocol_phases) return;
    if (!pendingPhases || !client?.id) return;
    let cancelled = false;
    (async () => {
      const { consultation: next, baked } = bakePendingProtocolPhases(activeConsult, pendingPhases);
      if (!baked) return;
      await saveNutritionConsultationConfirmed(next);
      if (cancelled) return;
      setActiveConsult(next);
      try {
        await clearPendingProtocolPhases(client.id);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn('[pending-phases] clear failed (non-bloquant):', e?.message || e);
      }
      onChange?.();
    })();
    return () => { cancelled = true; };
  }, [activeConsult, pendingPhases, client, onChange, packAccess.known]);

  const sendPhaseTransitionPush = useCallback(async (finalizedRun) => {
    const identity = clientIdentityFields(client);
    if (!identity.email && !identity.client_id) return false;
    const nextPhase = finalizedRun.proposedProtocolPhases?.phases?.find(
      (phase) => phase.id === finalizedRun.toPhaseId,
    );
    const { clientAppFetch } = await import('./services/clientAppFetch');
    await clientAppFetch('/api/admin/push/send', {
      method: 'POST',
      payload: {
        ...identity,
        title: 'Votre parcours évolue',
        body: nextPhase
          ? `Votre nouvelle phase est disponible : Phase ${nextPhase.order} · ${nextPhase.client_name}.`
          : 'Votre nouvelle phase est disponible dans votre programme.',
        url: '/plan#strategie',
        tag: `phase-transition-${finalizedRun.id}`,
      },
    });
    return true;
  }, [client]);

  const handleRetryTransitionNotification = useCallback(async () => {
    if (!pendingTransitionNotification || retryingTransitionNotification) return;
    setRetryingTransitionNotification(true);
    setErr(null);
    try {
      await retryPhaseTransitionNotificationWorkflow({
        run: pendingTransitionNotification,
        sendPush: sendPhaseTransitionPush,
      });
      setPendingTransitionNotification(null);
      await onChange?.();
    } catch (error) {
      setErr(error?.message || 'La notification de phase n’a pas pu être envoyée.');
    } finally {
      setRetryingTransitionNotification(false);
    }
  }, [onChange, pendingTransitionNotification, retryingTransitionNotification, sendPhaseTransitionPush]);

  const handleRetryTransitionActivation = useCallback(async () => {
    if (!pendingTransitionActivation || retryingTransitionActivation) return;
    setRetryingTransitionActivation(true);
    setErr(null);
    try {
      const result = await retryPhaseTransitionActivationWorkflow({
        run: pendingTransitionActivation,
        activateApp: async (finalizedRun) => {
          const { activatePhaseTransitionClientAppPlan } = await import('./services/publishToClientApp');
          return activatePhaseTransitionClientAppPlan({
            client,
            run: finalizedRun,
            stagedPublishResult: {
              ok: true,
              staged: true,
              status: 'draft',
              plan_id: finalizedRun.appPlanId,
              source_consultation_id: finalizedRun.targetConsultationId,
              publication_artifact_sha256: finalizedRun.appArtifactSha256,
            },
          });
        },
        sendPush: sendPhaseTransitionPush,
      });
      setPendingTransitionActivation(null);
      setPendingTransitionNotification(result.notificationPending ? result.run : null);
      await onChange?.();
    } catch (error) {
      setErr(error?.message || 'L’activation de la phase dans l’app n’a pas pu être reprise.');
    } finally {
      setRetryingTransitionActivation(false);
    }
  }, [client, onChange, pendingTransitionActivation, retryingTransitionActivation, sendPhaseTransitionPush]);

  const handlePrepareNextPhase = useCallback(async ({
    fromPhase,
    toPhase,
    safetyConfirmed = false,
    safetySnapshot,
    journeyStateOverride = null,
  }) => {
    if (preparingTransition || !activeConsult || !fromPhase || !toPhase) return;
    setPreparingTransition(true);
    setErr(null);
    try {
      const existingRun = await getActivePhaseTransition(client.id);
      if (existingRun) {
        let targetConsultation = (getNutritionConsultations(client.id) || []).find(
          (consultation) => consultation.id === existingRun.targetConsultationId,
        ) || null;
        if (!targetConsultation) {
          const { data, error } = await supabase
            .from('nutrition_consultations')
            .select('*')
            .eq('id', existingRun.targetConsultationId)
            .eq('client_id', client.id)
            .eq('is_deleted', false)
            .maybeSingle();
          if (error || !data) {
            throw new Error(error?.message || 'La version préparée est introuvable.');
          }
          targetConsultation = normalizePreviewConsultation(data, client.id);
        }
        setActiveConsult(targetConsultation);
        setPhaseTransitionRun(existingRun);
        return;
      }

      const existingDraft = getPhaseTransitionDraft(activeConsult);
      if (!existingDraft) {
        const confirmedVersions = await loadConfirmedNutritionVersions(client.id);
        const sourceConsultation = confirmedVersions[0] || null;
        if (!sourceConsultation || sourceConsultation.id !== activeConsult.id) {
          throw new Error('Une version plus récente existe. Recharge la fiche avant de préparer la phase suivante.');
        }
        const { ordinal, existingVersion } = await loadCanonicalFollowupOrdinal(
          client.id,
          confirmedVersions,
          { journeyStateOverride },
        );
        if (existingVersion) {
          throw new Error(`La version du rendez-vous de suivi ${ordinal} existe déjà. Relis-la avant de préparer une transition.`);
        }
        const canonicalJourneyState = journeyStateOverride || journey;
        const canonicalLog = canonicalJourneyState?.consultations_log;
        const latestReviewedEntry = Array.isArray(canonicalLog) ? canonicalLog.at(-1) : null;
        const latestAppointment = latestReviewedEntry?.clinical?.appointment;
        if (
          latestReviewedEntry?.clinical?.workflow_action !== 'transition'
          || latestAppointment?.client_id !== client.id
          || latestAppointment?.source_consultation_id !== sourceConsultation.id
          || latestAppointment?.consultation_ordinal !== ordinal
          || !/^[a-f0-9]{64}$/.test(String(latestAppointment?.review_content_sha256 || ''))
        ) {
          throw new Error('Le dernier rendez-vous relu ne confirme pas cette transition — à valider Anissa.');
        }
        const targetConsultation = await createPhaseTransitionDraftWorkflow({
          clientId: client.id,
          sourceConsultation,
          fromPhase,
          toPhase,
          versionNumber: Math.max(2, (versions?.length || 0) + 1),
          followupWeek: ordinal,
          appointmentProof: {
            workflowAction: 'transition',
            appointmentSha256: latestAppointment.review_content_sha256,
            appointmentClientId: latestAppointment.client_id,
            consultationOrdinal: latestAppointment.consultation_ordinal,
          },
        });
        setActiveConsult(targetConsultation);
        setVersions((current) => [
          targetConsultation,
          ...current.filter((version) => version.id !== targetConsultation.id),
        ]);
        setInlineEditorContext({
          kind: 'phase-transition-draft',
          sourceConsultation: targetConsultation,
        });
        setShowInlineEditor(true);
        return;
      }
      if (safetyConfirmed !== true) {
        throw new Error('Anissa doit valider le programme et le contexte de sécurité actuel avant de préparer la remise.');
      }
      const currentSafetySnapshot = buildPhaseTransitionSafetySnapshot(client?.form);
      if (JSON.stringify(currentSafetySnapshot) !== JSON.stringify(safetySnapshot)) {
        throw new Error('Le contexte de sécurité a changé. Relis puis confirme à nouveau les informations affichées.');
      }
      if (existingDraft.fromPhaseId !== fromPhase.id || existingDraft.toPhaseId !== toPhase.id) {
        throw new Error('Cette version a été préparée pour une autre transition. Recharge la fiche avant de continuer.');
      }
      let sourceConsultation = (versions || []).find(
        (consultation) => consultation.id === existingDraft.sourceConsultationId,
      ) || null;
      if (!sourceConsultation) {
        const { data, error } = await supabase
          .from('nutrition_consultations')
          .select('*')
          .eq('id', existingDraft.sourceConsultationId)
          .eq('client_id', client.id)
          .eq('is_deleted', false)
          .maybeSingle();
        if (error || !data) {
          throw new Error(error?.message || 'La version source de la transition est introuvable.');
        }
        sourceConsultation = normalizePreviewConsultation(data, client.id);
      }
      const delivery = resolvePhaseTransitionDelivery(journey);
      const result = await preparePhaseTransitionWorkflow({
        clientId: client.id,
        sourceConsultation,
        targetConsultation: activeConsult,
        fromPhase,
        toPhase,
        delivery,
        safetyConfirmed,
        safetySnapshot: currentSafetySnapshot,
      });
      setActiveConsult(result.targetConsultation);
      setVersions((current) => [
        result.targetConsultation,
        ...current.filter((version) => version.id !== result.targetConsultation.id),
      ]);
      setPhaseTransitionRun(result.run);
      setExportedTransitionProof(null);
      await onChange?.();
    } catch (error) {
      setErr(error?.message || 'La phase suivante n’a pas pu être préparée.');
      throw error;
    } finally {
      setPreparingTransition(false);
    }
  }, [activeConsult, client, journey, onChange, preparingTransition, versions]);

  const handleExportTransitionDocument = useCallback(async () => {
    if (!phaseTransitionRun || !activeConsult || exportingTransitionDocument) return;
    setExportingTransitionDocument(true);
    setErr(null);
    try {
      const [{ exportPlanToWord }, { ExportClinicalError, formatClearanceForConfirm }] = await Promise.all([
        import('./services/exportToWord'),
        import('./services/clinicalClearance'),
      ]);
      const mgdOutput = await assertMgdPlanAuthorizedForOutput(client.id, activeConsult);
      try {
        const exportedProof = await exportPhaseTransitionDocumentWorkflow({
          client,
          targetConsultation: activeConsult,
          run: phaseTransitionRun,
          exportPlan: exportPlanToWord,
        });
        setExportedTransitionProof(exportedProof);
      } catch (clinicalError) {
        if (!(clinicalError instanceof ExportClinicalError)) throw clinicalError;
        if (mgdOutput.hasActiveMgd) {
          throw new Error('Export MGD bloqué : aucune dérogation clinique navigateur n’est autorisée.');
        }
        if (!window.confirm(formatClearanceForConfirm(clinicalError.verdict))) return;
        const { traceClinicalOverride, CLINICAL_OVERRIDE_DOORS } = await import('./services/clinicalOverrideAudit');
        await traceClinicalOverride(clinicalError.verdict, CLINICAL_OVERRIDE_DOORS.EXPORT_WORD, {
          clientId: client?.id,
          consultationId: activeConsult?.id,
        });
        const exportedProof = await exportPhaseTransitionDocumentWorkflow({
          client,
          targetConsultation: activeConsult,
          run: phaseTransitionRun,
          exportPlan: exportPlanToWord,
          exportOptions: { clinicalOverride: true },
        });
        setExportedTransitionProof(exportedProof);
      }
    } catch (error) {
      setExportedTransitionProof(null);
      setErr(error?.message || 'Le document Word n’a pas pu être exporté.');
    } finally {
      setExportingTransitionDocument(false);
    }
  }, [activeConsult, client, exportingTransitionDocument, phaseTransitionRun]);

  const handleConfirmTransitionDocument = useCallback(async () => {
    if (!phaseTransitionRun || !activeConsult || confirmingDocumentTransition) return;
    setConfirmingDocumentTransition(true);
    setErr(null);
    try {
      if (!canConfirmPhaseTransitionDocument(phaseTransitionRun, exportedTransitionProof)) {
        throw new Error('Exporte le document Word de cette transition avant de confirmer sa remise.');
      }
      const currentConsultationSha256 = await sha256Hex(
        JSON.stringify(stableJsonValue(activeConsult)),
      );
      if (currentConsultationSha256 !== exportedTransitionProof.consultationSha256) {
        throw new Error('Le programme a changé depuis l’export Word. Exporte à nouveau le document exact.');
      }
      const result = await finalizePhaseTransitionProofWorkflow({
        run: phaseTransitionRun,
        proofType: 'document',
        proofSha256: exportedTransitionProof.artifactSha256,
        sendPush: sendPhaseTransitionPush,
      });
      setPhaseTransitionRun(result.run.status === 'finalized' ? null : result.run);
      if (result.canonicalConsultation) {
        setActiveConsult(result.canonicalConsultation);
        setVersions((current) => current.map((version) => (
          version.id === result.canonicalConsultation.id
            ? result.canonicalConsultation
            : version
        )));
      }
      await onChange?.();
    } catch (error) {
      setErr(error?.message || 'La remise du document n’a pas pu être confirmée.');
    } finally {
      setConfirmingDocumentTransition(false);
    }
  }, [activeConsult, confirmingDocumentTransition, exportedTransitionProof, onChange, phaseTransitionRun, sendPhaseTransitionPush]);

  const handleCancelPhaseTransition = useCallback(async () => {
    if (!phaseTransitionRun || cancellingPhaseTransition) return;
    if (!window.confirm('Annuler cette préparation ? La version de travail reste disponible et pourra être modifiée puis préparée à nouveau.')) return;
    setCancellingPhaseTransition(true);
    setErr(null);
    try {
      await cancelPhaseTransition(phaseTransitionRun.id);
      setPhaseTransitionRun(null);
      setExportedTransitionProof(null);
      await onChange?.();
    } catch (error) {
      setErr(error?.message || 'La préparation ne peut pas être annulée après une preuve de remise.');
    } finally {
      setCancellingPhaseTransition(false);
    }
  }, [cancellingPhaseTransition, onChange, phaseTransitionRun]);

  // Phase AJ : log des consultations effectuees
  const packLabel = pack?.label || 'Accompagnement';
  // V97.57 — Pack ponctuel = pack SANS étapes d'accompagnement (steps: []).
  // Couvre consultation_initiale_220 + les legacy oneshot_*. Critère métier
  // (structure du pack) plutôt que durationWeeks (durée). Un pack inconnu
  // reste fermé et reçoit l'état explicite « Suivi indisponible » ci-dessous.
  const isOneShotPack = !packAccess.continuousFollowup;
  const consultationsTotal = packAccess.includedFollowupConsultations;
  const consultationsLog = useMemo(
    () => (Array.isArray(journey?.consultations_log) ? journey.consultations_log : []),
    [journey?.consultations_log],
  );
  const latestCanonicalConsultation = consultationsLog.at(-1) || null;
  const hasReviewedAppointment = latestCanonicalConsultation?.clinical?.appointment?.reviewed_by_anissa === true;
  const reviewedProgramAction = latestCanonicalConsultation?.clinical?.workflow_action || null;
  const currentPackStep = useMemo(() => getNextPendingStep(client), [client]);
  const closableReviewStep = currentPackStep?.type === 'review'
    && (currentPackStep.isLate || currentPackStep.isDueSoon)
    ? currentPackStep
    : null;
  // Compteur PAR PÉRIODE (cf. renouvellement du suivi) : ne compte que les
  // consultations loggées depuis packStartedAt. Sans packStartedAt -> tout.
  const consultationsUsed = countConsultationsInPeriod(consultationsLog, client.packStartedAt);
  const appointmentProgress = getPackAppointmentProgress(client, consultationsLog);
  const initialAppointmentAt = getInitialAppointmentDate(client);
  const daysSincePack = client.packStartedAt && client.packStartedAtConfirmed === true
    ? Math.max(1, Math.floor((Date.now() - new Date(client.packStartedAt).getTime()) / (1000 * 60 * 60 * 24)))
    : null;
  const activeVersion = versions[0] || null;

  // Phase AK : extraction des pesees depuis les feedbacks deja charges.
  // Une pesee = un feedback avec weight_kg non null (cf. migration weight_tracking).
  //
  // V97.39.8 (roadmap 1.5) — Mutualisation du double-fetch. Avant : 2 appels
  // reseau au chargement (90 j pour le poids + 14 j pour les ressentis). Ce
  // seul effet fait UN appel 90 j, derive le poids (90 j) ET les ressentis
  // (fenetre 14 j filtree localement). Comportement identique, moitie moins de
  // requetes.
  const [weightEntries, setWeightEntries] = useState([]);
  const [loadingWeight, setLoadingWeight] = useState(true);
  // « Préparer le rendez-vous » : le briefing couvre la période DEPUIS la
  // dernière consultation, qui peut dépasser 14 j. On conserve donc la fenêtre
  // 90 j COMPLÈTE (déjà fetchée pour le poids) sans recharger le réseau ; le
  // helper pur borne ensuite à la période exacte.
  const [allFeedbacks, setAllFeedbacks] = useState([]);
  useEffect(() => {
    if (!client?.id) return;
    let cancelled = false;
    setLoadingWeight(true);
    setLoadingFb(true);
    setFeedbackSnapshotStatus('loading');
    setSyncError(false);
    setWeightEntries([]);
    setFeedbacks([]);
    setAllFeedbacks([]);
    setMeetingWorkspace(null);
    if (!clientAppChannelEnabled) {
      setLoadingWeight(false);
      setLoadingFb(false);
      setFeedbackSnapshotStatus('unavailable');
      return () => { cancelled = true; };
    }
    (async () => {
      try {
        const { fetchClientFeedbacks } = await import('./services/fetchClientFeedbacks');
        const list = await fetchClientFeedbacks(client, 90);
        if (cancelled) return;
        // fetchClientFeedbacks renvoie { ok, error } : un ok:false (reseau /
        // proxy / app cliente) doit lever le badge "echec", pas passer pour
        // une synchro vide reussie.
        if (list && typeof list === 'object' && list.ok === false) {
          throw new Error(list.error || 'Synchro app cliente echouee');
        }
        const fbs = Array.isArray(list) ? list : (list?.feedbacks || []);
        // Fenetre 90 j COMPLETE conservee pour le briefing pre-consultation
        // (periode depuis la derniere consult, potentiellement > 14 j).
        setAllFeedbacks(fbs);

        // Pesees : fenetre 90 j, weight_kg numerique, tri decroissant.
        const entries = fbs
          .filter((f) => typeof f.weight_kg === 'number')
          .map((f) => ({ date: f.date || f.created_at, weight_kg: Number(f.weight_kg) }))
          .sort((a, b) => new Date(b.date) - new Date(a.date));
        setWeightEntries(entries);

        // Ressentis : meme source, fenetre 14 j (preserve la semantique
        // d'avant pour adherence / tendances / alertes cliniques).
        const cutoff14 = Date.now() - 14 * 86400000;
        const recent = fbs.filter((f) => {
          const ts = new Date(f.date || f.created_at).getTime();
          return Number.isFinite(ts) && ts >= cutoff14;
        });
        setFeedbacks(recent);
        setLastSyncAt(new Date());
        setSyncError(false);
        setFeedbackSnapshotStatus('ok');
      } catch {
        if (!cancelled) {
          setWeightEntries([]);
          setFeedbacks([]);
          setAllFeedbacks([]);
          setSyncError(true);
          setFeedbackSnapshotStatus('error');
        }
      } finally {
        if (!cancelled) {
          setLoadingWeight(false);
          setLoadingFb(false);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [client, client?.id, clientAppChannelEnabled, syncReloadKey]);
  useEffect(() => {
    if (!client?.id) return undefined;
    let cancelled = false;
    setCycleReviewsStatus('loading');
    setCycleReviewsError(null);
    (async () => {
      try {
        const { data: rows, error } = await supabase
          .from('cycle_reviews')
          .select('*')
          .eq('client_id', client.id)
          .order('created_at', { ascending: false });
        if (error) throw error;
        if (cancelled) return;
        setCycleReviews(Array.isArray(rows) ? rows : []);
        setCycleReviewsStatus('ready');
      } catch (error) {
        if (cancelled) return;
        setCycleReviews([]);
        setCycleReviewsStatus('error');
        setCycleReviewsError(error?.message || 'Les bilans de cycle sont indisponibles.');
      }
    })();
    return () => { cancelled = true; };
  }, [client?.id, cycleReviewsReloadKey]);
  const [showLogModal, setShowLogModal] = useState(false);
  const [logNote, setLogNote] = useState('');
  const [savingLog, setSavingLog] = useState(false);
  const [meetingWorkspace, setMeetingWorkspace] = useState(null);
  const [adaptationProposal, setAdaptationProposal] = useState(null);
  const [adaptationDraft, setAdaptationDraft] = useState('');
  const [adaptationConfirmation, setAdaptationConfirmation] = useState(null);
  const [adaptationReviewError, setAdaptationReviewError] = useState(null);
  const [savingAdaptation, setSavingAdaptation] = useState(false);
  const adaptationRequestRef = useRef(0);
  const activeClientIdRef = useRef(client?.id || null);
  activeClientIdRef.current = client?.id || null;
  useEffect(() => {
    adaptationRequestRef.current += 1;
    setShowBriefing(false);
    setShowLogModal(false);
    setMeetingWorkspace(null);
    setBriefingCycleReview(null);
    setBriefingWorkflowIntent(null);
    setAdaptationProposal(null);
    setAdaptationDraft('');
    setAdaptationConfirmation(null);
    setAdaptationReviewError(null);
    setShowProgramUpdateScope(false);
    setPendingProgramUpdateContext(null);
    setInlineSavedConsultation(null);
    setConsultationOutcome(null);
    setAdapting(false);
    return () => { adaptationRequestRef.current += 1; };
  }, [client?.id]);
  // V97.122 — Ligne « Prochain RDV » dédiée (indépendante des consultations).
  const [showRdvEdit, setShowRdvEdit] = useState(false);
  const [rdvDraft, setRdvDraft] = useState('');
  const [savingRdv, setSavingRdv] = useState(false);
  const [finalizingMilestones, setFinalizingMilestones] = useState(false);
  const [rdvSyncRuntimeError, setRdvSyncRuntimeError] = useState(null);
  const [rdvSyncRuntimeTarget, setRdvSyncRuntimeTarget] = useState(undefined);
  // « Préparer le rendez-vous » — panneau briefing pré-consultation.
  const [showBriefing, setShowBriefing] = useState(false);

  // 2026-07-19 (V2 espace Suivi) — Intention posée par SuiviCockpit :
  // « Préparer le rendez-vous » depuis le flux ouvre la fiche AVEC le
  // briefing déjà ouvert. Consommée une seule fois (retirée à la lecture),
  // fraîcheur 60 s, et uniquement pour LA cliente visée : une intention
  // périmée ou d'une autre fiche ne déclenche jamais rien (fail-closed).
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem('anissa_suivi_intent');
      if (!raw) return;
      const intent = JSON.parse(raw);
      if (intent?.localClientId !== client?.id) return;
      sessionStorage.removeItem('anissa_suivi_intent');
      if (Date.now() - (intent.at || 0) > 60000) return;
      if (intent.action === 'briefing') setShowBriefing(true);
    } catch {
      /* jamais bloquant */
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 2026-07-19 (revue Benoit) — un RDV passé ne doit plus s'afficher comme
  // « Prochain rendez-vous » avec promesse de rappel : on le requalifie et
  // on invite à reprogrammer. Tolérance 12 h : le jour même, il reste
  // « prochain » (suivi en cours de journée).
  const rdvIsPast = !!journey?.next_rdv_at &&
    new Date(journey.next_rdv_at).getTime() < Date.now() - 12 * 3600000;
  const latestConsultationAt = Date.parse(latestCanonicalConsultation?.date || '');
  const scheduledRdvAt = Date.parse(journey?.next_rdv_at || '');
  const pastAppointmentHasReviewedConsultation = rdvIsPast
    && hasReviewedAppointment
    && Number.isFinite(latestConsultationAt)
    && Number.isFinite(scheduledRdvAt)
    && latestConsultationAt >= scheduledRdvAt - 12 * 3600000;
  const rdvSyncMarker = journey?.next_rdv_app_sync;
  const rdvSyncNeedsRetry = shouldSyncNextRdvToApp && rdvSyncMarker?.version === 1
    && (rdvSyncMarker.status === 'pending' || rdvSyncMarker.status === 'error');
  const rdvConfirmedInApp = shouldSyncNextRdvToApp && rdvSyncMarker?.version === 1
    && rdvSyncMarker.status === 'synced'
    && (
      (journey?.next_rdv_at === null && rdvSyncMarker.target_at === null)
      || isSameInstant(journey?.next_rdv_at, rdvSyncMarker.target_at)
    );
  useEffect(() => {
    if (rdvConfirmedInApp) {
      setRdvSyncRuntimeError(null);
      setRdvSyncRuntimeTarget(undefined);
    }
  }, [rdvConfirmedInApp]);

  // V97.122 — Pose / modifie / annule le prochain RDV. Le SaaS persiste
  // d'abord une reprise explicite, puis n'annonce la synchronisation qu'après
  // confirmation exacte de l'app cliente. Une panne reste visible et rejouable.
  const handleSaveNextRdv = async (isoOrNull) => {
    setSavingRdv(true);
    setErr(null);
    setRdvSyncRuntimeError(null);
    setRdvSyncRuntimeTarget(undefined);
    try {
      if (!shouldSyncNextRdvToApp) {
        await saveNextRdvInSaasOnly(client.id, isoOrNull, {
          expectedCurrentAt: journey?.next_rdv_at ?? null,
        });
        setShowRdvEdit(false);
        await onChange?.();
        return;
      }
      const pendingMarker = await startNextRdvSync(client.id, isoOrNull);

      try {
        await syncNextRdvToClientApp(client, isoOrNull, {
          requestId: pendingMarker.request_id,
          requestedAt: pendingMarker.requested_at,
        });
        await finishNextRdvSync(client.id, pendingMarker, 'synced');
      } catch (syncErrorCaught) {
        const errorCode = syncErrorCaught?.code || 'client_app_sync_failed';
        try {
          await finishNextRdvSync(client.id, pendingMarker, 'error', errorCode);
        } catch {
          // Le marqueur pending reste persistant et rejouable. L'erreur locale
          // évite d'annoncer un succès complet si l'écriture d'erreur échoue.
        }
        setRdvSyncRuntimeError(
          "Le rendez-vous est enregistré dans le SaaS, mais l'app cliente ne l'a pas encore confirmé. Relance la synchronisation.",
        );
        setRdvSyncRuntimeTarget(isoOrNull);
        setShowRdvEdit(false);
        await onChange?.();
        return;
      }

      if (isoOrNull) {
        const dateLabel = new Date(isoOrNull).toLocaleString('fr-CH', {
          weekday: 'long', day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit',
        });
        try {
          const { clientAppFetch } = await import('./services/clientAppFetch');
          await clientAppFetch('/api/admin/push/send', {
            method: 'POST',
            payload: {
              ...clientIdentityFields(client),
              title: 'Ton prochain rendez-vous est fixé',
              body: `Prochain RDV avec Anissa : ${dateLabel}.`,
              url: '/parcours',
              tag: 'rdv-followup',
            },
          });
        } catch { /* notification best-effort après confirmation de la date */ }
      }
      setShowRdvEdit(false);
      await onChange?.();
    } catch (e) {
      setErr(e?.message || 'Erreur lors de l\'enregistrement du rendez-vous.');
    } finally {
      setSavingRdv(false);
    }
  };

  const finalizeRecordedMilestones = async ({
    consultationOrdinal,
    completePastAppointment,
    expectedNextRdvAt,
    reviewStepNumber,
  }) => {
    const result = await finalizeFollowupMilestones({
      clientId: client.id,
      consultationOrdinal,
      completePastAppointment,
      expectedNextRdvAt,
      reviewStepNumber,
    });

    // La RPC clôture d'abord le dossier SaaS. Si la cliente dispose du canal
    // app, on retire ensuite la date de son accueil avec le protocole de
    // confirmation serveur déjà utilisé pour les rendez-vous.
    if (completePastAppointment && shouldSyncNextRdvToApp) {
      try {
        await syncNextRdvWithServerVersion(client, null);
        setRdvSyncRuntimeError(null);
        setRdvSyncRuntimeTarget(undefined);
      } catch {
        setRdvSyncRuntimeError(
          "Le rendez-vous est clôturé dans le SaaS, mais l'app cliente n'a pas encore confirmé le retrait de la date. Relance la synchronisation.",
        );
        setRdvSyncRuntimeTarget(null);
      }
    }
    await onChange?.();
    return result;
  };

  const handleFinalizeRecordedPastAppointment = async () => {
    const consultationOrdinal = latestCanonicalConsultation?.clinical?.appointment?.consultation_ordinal;
    if (!Number.isInteger(consultationOrdinal) || !journey?.next_rdv_at) {
      setErr('La consultation relue ou le rendez-vous passé est introuvable.');
      return;
    }
    const reviewLabel = closableReviewStep?.label || null;
    const confirmation = reviewLabel
      ? `Confirmer que le rendez-vous a été effectué et que ${reviewLabel} a bien été traité pendant cette consultation ?`
      : 'Confirmer que ce rendez-vous a bien été effectué ?';
    if (!window.confirm(confirmation)) return;

    setFinalizingMilestones(true);
    setErr(null);
    try {
      await finalizeRecordedMilestones({
        consultationOrdinal,
        completePastAppointment: true,
        expectedNextRdvAt: journey.next_rdv_at,
        reviewStepNumber: closableReviewStep?.stepNumber ?? null,
      });
      setConsultationOutcome({
        title: reviewLabel ? 'Rendez-vous et point de suivi clôturés' : 'Rendez-vous clôturé',
        message: reviewLabel
          ? `${reviewLabel} est maintenant traité et n’apparaîtra plus dans les actions en retard.`
          : 'Le rendez-vous effectué est archivé et la prochaine date peut maintenant être planifiée.',
      });
    } catch (error) {
      setErr(error?.message || 'Le rendez-vous n’a pas pu être finalisé.');
    } finally {
      setFinalizingMilestones(false);
    }
  };

  // « Renouveler le suivi » — démarre une NOUVELLE PÉRIODE sur la même fiche.
  // On écrit uniquement les champs de pack (snake_case = colonnes Supabase,
  // même convention que store.js et le mapping de lecture). On NE touche pas
  // journey_state (la cliente reste en followup) ni l'historique
  // consultations/versions. packSchedule remis à [] → buildPackFollowupSchedule
  // recalcule un planning frais depuis aujourd'hui. Le compteur se réinitialise
  // via countConsultationsInPeriod (borné au nouveau packStartedAt).
  const handleRenewFollowup = async (newPackKey) => {
    if (!newPackKey) return;
    setRenewing(true);
    setErr(null);
    try {
      if (phaseTransitionRun || getPhaseTransitionDraft(activeConsult)) {
        throw new Error('Termine ou annule la transition de phase avant de renouveler le suivi.');
      }
      const renewalAccess = getPackAccess(newPackKey);
      if (!renewalAccess.known || !renewalAccess.continuousFollowup) {
        throw new Error('Pack de renouvellement invalide');
      }
      const startedAt = new Date().toISOString();
      const { data: renewed, error: updErr } = await supabase
        .from('clients')
        .update({
          pack_type: newPackKey,
          pack_started_at: startedAt,
          pack_started_at_confirmed: true,
          pack_schedule: [],
        })
        .eq('id', client.id)
        .select('pack_type, pack_started_at, pack_started_at_confirmed, pack_schedule')
        .maybeSingle();
      if (updErr) throw new Error(updErr.message);
      if (
        renewed?.pack_type !== newPackKey ||
        !isSameInstant(renewed?.pack_started_at, startedAt) ||
        renewed?.pack_started_at_confirmed !== true ||
        !Array.isArray(renewed?.pack_schedule) ||
        renewed.pack_schedule.length !== 0
      ) {
        throw new Error('Le renouvellement n a pas ete enregistre');
      }
      const { grantPackClientAccess } = await import('./services/clientAccessManager');
      await grantPackClientAccess({ ...client, packType: newPackKey, pack_type: newPackKey }, {
        startsAt: startedAt,
        sourceRef: `renewal:${newPackKey}:${startedAt}`,
      });
      setShowRenewModal(false);
      await onChange?.();
    } catch (e) {
      setErr(e?.message || 'Erreur lors du renouvellement du suivi');
    } finally {
      setRenewing(false);
    }
  };

  const handleLogConsultation = async (payload = {}) => {
    // V97.17.6 — payload structure : { note?: string, clinical?: object|null }
    // Backward compat : si appele sans argument, fallback sur logNote local.
    const finalNote = typeof payload.note === 'string' ? payload.note : logNote;
    const finalClinical = payload.clinical || null;
    const followupCompletion = payload.followupCompletion || null;
    const workflowAction = finalClinical?.workflow_action;
    let transitionTarget = null;
    let consultationPersisted = false;
    setSavingLog(true);
    setErr(null);
    setConsultationOutcome(null);
    try {
      if (phaseTransitionRun || getPhaseTransitionDraft(activeConsult)) {
        throw new Error('Termine ou annule la transition de phase avant d’enregistrer une autre consultation.');
      }
      if (!packAccess.known || consultationsTotal <= 0) {
        throw new Error('Aucune consultation autorisee pour ce pack');
      }
      if (consultationsUsed >= consultationsTotal) {
        throw new Error('Quota de consultations atteint pour ce pack');
      }
      // Une décision de transition n'entre dans le journal qu'après preuve
      // qu'une phase active et une phase suivante existent réellement.
      if (workflowAction === 'transition') {
        const phases = activeConsult?.protocol_phases;
        const active = getActivePhase(phases);
        if (!phases || !active) {
          throw new Error('Aucune phase active pour cette cliente. Configurez d’abord le parcours thérapeutique.');
        }
        const phaseList = phases.phases || [];
        const nextPhase = phaseList[phaseList.findIndex((phase) => phase.id === active.id) + 1];
        if (!nextPhase) {
          throw new Error(`${prenom} est déjà sur la dernière phase du parcours (${active.client_name}).`);
        }
        transitionTarget = { fromPhase: active, toPhase: nextPhase };
      }
      const loggedJourneyState = await transitions.logConsultation(
        client.id,
        { notes: finalNote, clinical: finalClinical },
      );
      consultationPersisted = true;
      setLogNote('');
      setMeetingWorkspace(null);
      setShowLogModal(false);
      setBriefingCycleReview(null);
      setBriefingWorkflowIntent(null);
      await onChange?.();

      if (followupCompletion?.completePastAppointment || followupCompletion?.reviewStepNumber) {
        await finalizeRecordedMilestones({
          consultationOrdinal: finalClinical?.appointment?.consultation_ordinal,
          completePastAppointment: followupCompletion.completePastAppointment === true,
          expectedNextRdvAt: followupCompletion.expectedNextRdvAt || null,
          reviewStepNumber: followupCompletion.reviewStepNumber ?? null,
        });
      }

      // V97.17.6.1 — chaine de la decision clinique vers le workflow versioning.
      // Apres save de la consultation, si la decision implique une action
      // (adapter / nouvelle version / transition phase), on enchaine direct
      // pour fermer la boucle clinique.
      if (workflowAction === 'adapt' || workflowAction === 'newVersion') {
        setPendingProgramUpdateContext({
          clinical: finalClinical,
          note: finalNote,
          journeyState: loggedJourneyState,
        });
        setShowProgramUpdateScope(true);
      } else if (workflowAction === 'transition') {
        await handlePrepareNextPhase({
          ...transitionTarget,
          journeyStateOverride: loggedJourneyState,
        });
      }
      if (workflowAction === 'continue') {
        setConsultationOutcome({
          title: 'Consultation enregistrée',
          message: `La version actuelle reste active dans l’app de ${prenom}. Aucune mise à jour n’était nécessaire.`,
        });
      }
    } catch (e) {
      const message = consultationPersisted
        ? `Consultation enregistrée. La préparation de la suite n’a pas abouti : ${e?.message || 'réessaie depuis les actions du suivi.'}`
        : (e?.message || 'Erreur enregistrement consultation');
      setErr(message);
      if (!consultationPersisted) throw new Error(message);
    } finally {
      setSavingLog(false);
    }
  };

  const handleRemoveLastConsultation = async () => {
    if (!window.confirm('Annuler la dernière consultation enregistrée ?\n\nCette action retire la dernière entrée du journal des consultations (utile en cas de clic accidentel).')) return;
    setBusy(true);
    setErr(null);
    try {
      await transitions.removeLastConsultation(client.id);
      onChange();
    } catch (e) {
      setErr(e?.message || 'Erreur annulation');
    } finally {
      setBusy(false);
    }
  };

  // Charge l'historique des versions du plan
  useEffect(() => {
    if (!client?.id) return;
    let cancelled = false;
    (async () => {
      try {
        let list;
        if (phaseTransitionRevision > 0) {
          const { data, error } = await supabase
            .from('nutrition_consultations')
            .select('*')
            .eq('client_id', client.id)
            .eq('is_deleted', false)
            .order('created_at', { ascending: false });
          if (error) throw error;
          list = (data || []).map((row) => normalizePreviewConsultation(row, client.id));
        } else {
          const { getNutritionConsultations } = await import('./store');
          list = getNutritionConsultations(client.id) || [];
        }
        if (cancelled) return;
        setVersions(list);
      } catch {
        if (!cancelled) setVersions([]);
      }
    })();
    return () => { cancelled = true; };
  }, [client?.id, journey, phaseTransitionRevision]);

  // Ressentis 14 j : derives de l'effet 90 j unifie ci-dessus (plus de double fetch).

  const handleStart = async () => {
    setBusy(true); setErr(null);
    try {
      await transitions.startFollowup(client.id);
      onChange();
    } catch (e) { setErr(e?.message || 'Erreur'); }
    finally { setBusy(false); }
  };

  // V97.28 — Ouverture modal inline (ne change PLUS current_step).
  // Avant : restartPlanEditing rebasculait l'étape sur 'plan_editing' et
  // forçait Anissa à re-traverser tout le parcours pour revalider.
  // Maintenant : la modale ouvre l'éditeur direct sur la V courante,
  // current_step reste 'followup'. Anissa édite, sauvegarde une nouvelle
  // version, ferme la modale, et continue le suivi.
  const openExistingInlineDraft = useCallback(async (
    sourceConsultation,
    kind = 'existing-draft',
    initialTab = 'plan',
  ) => {
    if (!sourceConsultation?.id) {
      setErr('Le brouillon exact est introuvable. Recharge la fiche.');
      return false;
    }
    if (phaseTransitionRun || getPhaseTransitionDraft(activeConsult)) {
      setErr('Termine ou annule la transition de phase avant d’ouvrir une autre version.');
      return false;
    }
    try {
      await assertDocumentVersionEditable(sourceConsultation);
    } catch (editError) {
      setErr(editError?.message || 'Cette version ne peut plus être modifiée.');
      return false;
    }
    if (initialTab === 'plan') setInlineEditorContext({ kind, sourceConsultation });
    else setInlineEditorContext({ kind, sourceConsultation, initialTab });
    setInlineSavedConsultation(sourceConsultation);
    setShowInlineEditor(true);
    return true;
  }, [activeConsult, assertDocumentVersionEditable, phaseTransitionRun]);

  // Refresh versions après save dans la modal inline
  const handleInlineEditorPlanSaved = async (savedConsultation) => {
    try {
      const expectedId = savedConsultation?.id
        || inlineEditorContext?.sourceConsultation?.id
        || null;
      const { active } = await refreshConfirmedVersions(expectedId);
      setInlineSavedConsultation(active || savedConsultation || null);
    } catch (refreshError) {
      const error = refreshError instanceof Error
        ? refreshError
        : new Error('La version cloud sauvegardée ne peut pas être rechargée.');
      setErr(error.message);
      throw error;
    }
  };

  // V97.247 — une V2 de suivi doit être validée par Anissa (même contrôle
  // clinique et même preuve serveur que le plan initial) avant de pouvoir être
  // publiée. La cliente reste en étape Suivi. Si une alerte bloquante subsiste,
  // Anissa peut la confirmer consciemment : la décision est scellée et auditée.
  const validateFollowupVersionForApp = async (version) => {
    const planText = version?.nutritionPlan || version?.nutrition_plan || '';
    const supplementsText = version?.supplements || '';
    if (!version?.id || !planText.trim()) {
      throw new Error('Enregistre une version complète avant de la publier.');
    }
    const expectedContentSha256 = await sha256Hex(
      buildPlanValidationContent(planText, supplementsText),
    );
    // Pas de raccourci local : une retouche des menus ou de l'app change la
    // preuve serveur sans changer le texte du plan. Le serveur reconnaît
    // lui-même un rejeu exact.
    if (!window.confirm(`Valider cette version du programme de ${prenom} ?\n\nElle reste en brouillon tant que tu n’as pas cliqué sur « Publier le programme ».`)) {
      throw new Error('Validation annulée : la version reste en brouillon.');
    }
    try {
      await transitions.validatePlan(client.id, version.id, expectedContentSha256);
    } catch (validationError) {
      if (!(validationError instanceof PlanValidationClinicalError)) throw validationError;
      if (!window.confirm(formatClearanceForConfirm(validationError.verdict))) {
        throw new Error('Version à corriger avant publication : '
          + (validationError.verdict?.violations || [])
            .map((item) => item.label).filter(Boolean).join(' · '));
      }
      await transitions.validatePlan(client.id, version.id, expectedContentSha256, {
        clinicalOverride: true,
      });
    }
    onChange?.();
  };

  const handlePrepareSavedFollowupForApp = async () => {
    const target = inlineSavedConsultation;
    if (!target?.id) {
      throw new Error('Enregistre d’abord la version avant de préparer sa mise à jour dans l’app.');
    }
    const { active } = await refreshConfirmedVersions(target.id);
    const exactVersion = active || target;
    await validateFollowupVersionForApp(exactVersion);
    await onOpenAppPreview?.({ consultation: exactVersion, allowPublish: true });
    setShowInlineEditor(false);
    setInlineEditorContext(null);
    setInlineSavedConsultation(null);
    setConsultationOutcome({
      title: 'Version enregistrée',
      message: `La mise à jour de l’app de ${prenom} est ouverte pour vérification. Elle ne sera visible qu’après le clic final d’Anissa sur « Publier le programme ».`,
    });
  };

  // V97.248 — un brouillon de V2 manuelle devient insauvegardable si la fiche
  // (anamnèse) change après son ouverture : sa preuve de sécurité est figée.
  // Anissa peut l'abandonner (mise à la corbeille côté serveur, rien n'est
  // effacé) puis repartir d'une nouvelle V2 sur la fiche à jour.
  const handleAbandonManualDraft = async () => {
    setAdapting(true);
    setErr(null);
    try {
      const consultations = await loadConfirmedNutritionVersions(client.id);
      const head = consultations[0] || null;
      const provenance = head?.followupData?._versionProvenance
        ?? head?.followup_data?._versionProvenance;
      if (!isMutableFollowupDraft(head) || provenance?.trigger !== 'manual_program_update') {
        throw new Error('Aucun brouillon de mise à jour manuelle à abandonner.');
      }
      if (!window.confirm(`Abandonner ce brouillon du programme de ${prenom} ?\n\nIl n’a jamais été publié. Il est mis de côté (rien n’est effacé) et tu pourras préparer une nouvelle version à partir de la fiche à jour.`)) {
        return;
      }
      const revision = Number.isInteger(head.followupEditRevision)
        ? head.followupEditRevision
        : head.followup_edit_revision;
      const { data, error } = await supabase.rpc('abandonner_brouillon_programme_manuel', {
        p_client_id: client.id,
        p_consultation_id: head.id,
        p_expected_revision: revision,
      });
      if (error) throw new Error(error.message);
      if (data?.abandoned !== true || data?.consultation_id !== head.id) {
        throw new Error('L’abandon du brouillon n’a pas été confirmé. Recharge la fiche.');
      }
      await refreshConfirmedVersions();
      setConsultationOutcome({
        title: 'Brouillon abandonné',
        message: `Le brouillon a été mis de côté. Le programme publié de ${prenom} n’a pas changé.`,
      });
    } catch (abandonError) {
      setErr(abandonError?.message || 'Le brouillon n’a pas pu être abandonné.');
    } finally {
      setAdapting(false);
    }
  };

  const handleManualProgramUpdate = async (requestedScopeValue) => {
    const requestedScope = resolveFollowupProgramUpdateScope(requestedScopeValue);
    if (!requestedScope) {
      setErr('Portée de mise à jour absente — à valider Anissa.');
      return;
    }
    if (phaseTransitionRun || getPhaseTransitionDraft(activeConsult)) {
      setErr('Termine ou annule la transition de phase avant de modifier le programme.');
      return;
    }
    setAdapting(true);
    setErr(null);
    try {
      const consultations = await loadConfirmedNutritionVersions(client.id);
      const sourceConsultation = consultations[0] || null;
      if (!sourceConsultation?.id) throw new Error('Le programme actuel est introuvable.');

      // Une seule tête modifiable à la fois : si une version est déjà en
      // attente, on la reprend au lieu de fabriquer une branche concurrente.
      if (isMutableFollowupDraft(sourceConsultation)) {
        const existingScope = assertManualProgramUpdateDraftScope(
          sourceConsultation,
          requestedScope,
        );
        const opened = await openExistingInlineDraft(
          sourceConsultation,
          'existing-followup-draft',
          existingScope.initialTab,
        );
        if (!opened) throw new Error('Le brouillon existant n’a pas pu être ouvert.');
        return;
      }

      const { data: freshClient, error: freshClientError } = await supabase
        .from('clients')
        .select('form')
        .eq('id', client.id)
        .maybeSingle();
      if (freshClientError) throw new Error(freshClientError.message);
      if (!freshClient) throw new Error('La fiche cliente actuelle ne peut pas être relue.');

      const saved = await persistManualProgramUpdateVersion({
        currentForm: freshClient.form,
        sourceConsultation,
        clientId: client.id,
        updateScope: requestedScope.value,
        versionNumber: consultations.length + 1,
        persist: saveNutritionConsultationConfirmed,
      });
      const { active: confirmedVersion } = await refreshConfirmedVersions(saved.id);
      const opened = await openExistingInlineDraft(
        confirmedVersion,
        'manual-program-update',
        requestedScope.initialTab,
      );
      if (!opened) throw new Error('La version est sauvegardée, mais son éditeur ne peut pas être ouvert.');
      setConsultationOutcome({
        title: 'Brouillon de mise à jour créé',
        message: `La version actuelle de ${prenom} reste visible. Cette nouvelle version ne sera publiée qu’après la validation finale d’Anissa.`,
      });
      await onChange?.();
    } catch (error) {
      if (error instanceof FollowupSafetyPreflightError) {
        setSafetyPreflightBlock({
          issues: error.issues,
          requestedScope: requestedScope.value,
        });
        return;
      }
      setErr(error?.message || 'La mise à jour du programme ne peut pas être ouverte.');
    } finally {
      setAdapting(false);
    }
  };

  const handleAdaptFromFeedback = async (consultationCtx = null) => {
    const requestId = adaptationRequestRef.current + 1;
    adaptationRequestRef.current = requestId;
    const requestClientId = client.id;
    const requestIsCurrent = () => (
      adaptationRequestRef.current === requestId
      && activeClientIdRef.current === requestClientId
    );
    const requestedScopeValue = consultationCtx?.requestedScopeValue || null;
    const providedConsultationContext = consultationCtx?.clinical ? consultationCtx : null;
    const latestReviewedConsultation = consultationsLog.at(-1) || null;
    const resolvedConsultationContext = providedConsultationContext || (latestReviewedConsultation ? {
      clinical: latestReviewedConsultation.clinical,
      note: latestReviewedConsultation.notes || '',
    } : null);
    if (phaseTransitionRun || getPhaseTransitionDraft(activeConsult)) {
      const transitionError = new Error(
        'Termine ou annule la transition de phase avant de créer une autre version.',
      );
      setErr(transitionError.message);
      if (resolvedConsultationContext?.journeyState) throw transitionError;
      return;
    }
    if (resolvedConsultationContext?.clinical?.appointment?.reviewed_by_anissa !== true) {
      const missingMaterialError = new Error(
        'Prépare, complète et valide le compte rendu du rendez-vous avant de demander une adaptation.',
      );
      setErr(missingMaterialError.message);
      if (resolvedConsultationContext?.journeyState) throw missingMaterialError;
      return;
    }
    const workflowAction = resolvedConsultationContext?.clinical?.workflow_action;
    const requestedScope = resolveFollowupProgramUpdateScope(
      requestedScopeValue,
    );
    if (!requestedScope) {
      const missingScopeError = new Error('Portée de mise à jour absente — à valider Anissa.');
      setErr(missingScopeError.message);
      if (resolvedConsultationContext?.journeyState) throw missingScopeError;
      return;
    }
    if (requestedScope.workflowAction !== workflowAction) {
      const scopeError = new Error(
        workflowAction === 'newVersion'
          ? 'Le compte rendu relu prévoit une nouvelle version complète. Choisis cette portée.'
          : 'Le compte rendu relu ne prévoit pas une nouvelle version complète. Choisis un ajustement ciblé ou Menus et recettes.',
      );
      setErr(scopeError.message);
      if (resolvedConsultationContext?.journeyState) throw scopeError;
      return;
    }
    {
      const nextVersionNum = (versions?.length || 0) + 1;
      const previousVersion = versions?.length || 0;
      const isFullVersion = resolvedConsultationContext?.clinical?.workflow_action === 'newVersion';
      const actionLabel = isFullVersion ? 'une nouvelle version complète' : 'une adaptation ciblée';
      const msg = `Préparer ${actionLabel} — V${nextVersionNum} ?\n\nLe SaaS va produire un brouillon en mémoire à partir du plan complet et du compte rendu relu. Rien ne sera sauvegardé ni publié avant ta relecture et ta confirmation du texte exact. La V${previousVersion || 1} actuelle restera intacte.`;
      if (!window.confirm(msg)) {
        if (resolvedConsultationContext?.journeyState) {
          throw new Error('La consultation est enregistrée, mais la création de la version a été annulée.');
        }
        return;
      }
    }
    setAdapting(true);
    setErr(null);
    try {
      const consultations = await loadConfirmedNutritionVersions(client.id);
      if (!requestIsCurrent()) return;
      const lastConsult = consultations[0] || null;
      if (activeConsult?.id && lastConsult?.id !== activeConsult.id) {
        throw new Error('Une version plus récente existe. Recharge ou ouvre la dernière version avant de créer cette adaptation.');
      }
      const currentPlan = lastConsult?.nutritionPlan
        || lastConsult?.nutrition_plan
        || lastConsult?.plan_text
        || '';
      if (!currentPlan) throw new Error('Pas de plan actuel à adapter');
      const ordinalContext = await loadCanonicalFollowupOrdinal(
        client.id,
        consultations,
        { journeyStateOverride: resolvedConsultationContext?.journeyState || null },
      );
      if (!requestIsCurrent()) return;
      const { ordinal, existingVersion } = ordinalContext;
      if (existingVersion) {
        const existingScope = readFollowupProgramUpdateScope(existingVersion);
        if (!existingScope) {
          throw new Error('La portée du brouillon existant n’est pas prouvée — à valider Anissa.');
        }
        if (existingScope.value !== requestedScope.value) {
          throw new Error(
            `Ce brouillon existe déjà avec la portée « ${existingScope.title} ». Ouvre cette portée enregistrée au lieu d’en choisir une autre.`,
          );
        }
        if (existingVersion.id === lastConsult.id && isMutableFollowupDraft(existingVersion)) {
          const opened = await openExistingInlineDraft(
            existingVersion,
            'existing-followup-draft',
            existingScope.initialTab,
          );
          if (!opened) throw new Error('Le brouillon existant n’a pas pu être ouvert.');
          return;
        }
        throw new Error(`La version du rendez-vous de suivi ${ordinal} existe déjà. Ouvre cette version au lieu d’en créer une autre.`);
      }

      const { data: freshClient, error: freshClientError } = await supabase
        .from('clients')
        .select('*')
        .eq('id', client.id)
        .maybeSingle();
      if (!requestIsCurrent()) return;
      if (freshClientError) throw new Error(freshClientError.message);
      if (!freshClient) throw new Error('La fiche cliente actuelle ne peut pas être relue.');

      const proposal = await proposeReviewedFollowupAdaptation({
        currentPlan,
        currentForm: freshClient.form,
        // La whitelist du service collecte chaque alias séparément. Passer la
        // ligne fraîche entière évite qu'un `telephone || phone` masque une
        // seconde identité encore présente dans le plan ou le compte rendu.
        identityContext: freshClient,
        appointment: resolvedConsultationContext.clinical.appointment,
        clinical: resolvedConsultationContext.clinical,
        note: resolvedConsultationContext.note,
        clientId: client.id,
        sourceConsultationId: lastConsult.id,
        consultationOrdinal: ordinal,
        updateScope: requestedScope.value,
      });
      if (!requestIsCurrent()) return;
      const confirmedAfterAi = await loadConfirmedNutritionVersions(requestClientId);
      if (!requestIsCurrent()) return;
      if (confirmedAfterAi[0]?.id !== lastConsult.id) {
        throw new Error('Le plan source a changé pendant la proposition. Recharge avant de continuer.');
      }
      setAdaptationProposal({ ...proposal, ordinal, editorScope: requestedScope.value });
      setAdaptationDraft(proposal.content);
      setAdaptationConfirmation(null);
      setAdaptationReviewError(null);
    } catch (e) {
      if (!requestIsCurrent()) return;
      setErr(e?.message || 'Erreur lors de la préparation de l’adaptation');
      if (resolvedConsultationContext?.journeyState) throw e;
    } finally {
      if (requestIsCurrent()) setAdapting(false);
    }
  };

  const handleConfirmAdaptation = async () => {
    if (!adaptationProposal || savingAdaptation) return;
    setSavingAdaptation(true);
    setAdaptationReviewError(null);
    try {
      const consultations = await loadConfirmedNutritionVersions(client.id);
      const sourceConsultation = consultations[0] || null;
      if (!sourceConsultation || sourceConsultation.id !== adaptationProposal.source_consultation_id) {
        throw new Error('Une version plus récente existe. Recharge avant de sauvegarder.');
      }
      const currentPlan = sourceConsultation.nutritionPlan
        || sourceConsultation.nutrition_plan
        || sourceConsultation.plan_text
        || '';
      const { data: freshClient, error: freshClientError } = await supabase
        .from('clients')
        .select('form, journey_state')
        .eq('id', client.id)
        .maybeSingle();
      if (freshClientError) throw new Error(freshClientError.message);
      if (!freshClient) throw new Error('La fiche cliente actuelle ne peut pas être relue.');

      const freshLog = Array.isArray(freshClient.journey_state?.consultations_log)
        ? freshClient.journey_state.consultations_log
        : [];
      const latestEntry = freshLog.at(-1) || null;
      const latestAppointment = latestEntry?.clinical?.appointment || null;
      if (latestAppointment?.reviewed_by_anissa !== true) {
        throw new Error('Le dernier rendez-vous canonique n’est pas le compte rendu relu utilisé — à valider Anissa.');
      }

      const ordinalContext = await loadCanonicalFollowupOrdinal(client.id, consultations, {
        journeyStateOverride: freshClient.journey_state,
      });
      if (ordinalContext.existingVersion || ordinalContext.ordinal !== adaptationProposal.ordinal) {
        throw new Error('La version de ce rendez-vous existe déjà ou le suivi a changé. Recharge la fiche.');
      }
      const versionNum = versions.length + 1;
      const saved = await persistReviewedFollowupAdaptation({
        proposal: adaptationProposal,
        editedContent: adaptationDraft,
        confirmedContent: adaptationConfirmation,
        currentForm: freshClient.form,
        sourceConsultation,
        latestAppointment,
        clientId: client.id,
        followupWeek: ordinalContext.ordinal,
        versionNumber: versionNum,
        persist: saveNutritionConsultationConfirmed,
      });
      const { active: confirmedVersion } = await refreshConfirmedVersions(saved.id);
      trackPlanModification({
        clientId: client.id,
        consultationId: sourceConsultation.id,
        section: 'global',
        source: 'hybrid',
        versionId: `V${versionNum}`,
        beforeLength: currentPlan.length,
        afterLength: adaptationDraft.length,
      });
      setAdaptationProposal(null);
      setAdaptationDraft('');
      setAdaptationConfirmation(null);
      const editorScope = resolveFollowupProgramUpdateScope(adaptationProposal.editorScope);
      const opened = await openExistingInlineDraft(
        confirmedVersion,
        'reviewed-appointment-adaptation',
        editorScope?.initialTab || 'plan',
      );
      if (!opened) throw new Error('La version est sauvegardée, mais son éditeur ne peut pas être ouvert.');
      setConsultationOutcome({
        title: 'Nouvelle version créée',
        message: 'Relis et enregistre le plan. Le bouton « Préparer la mise à jour de l’app » ouvrira ensuite cette version exacte avant publication.',
      });
      await onChange?.();
    } catch (error) {
      setAdaptationReviewError(error?.message || 'La version ne peut pas être sauvegardée.');
    } finally {
      setSavingAdaptation(false);
    }
  };

  // Calculs factuels du cockpit. Aucune cadence, variation biologique ou
  // interpretation de ressenti ne declenche une decision automatique.
  const lastVersion = versions[0] || null;
  const cycleReviewDigest = useMemo(() => buildCycleReviewDigest({
    client,
    reviews: cycleReviews,
    feedbacks: allFeedbacks,
    feedbackStatus: feedbackSnapshotStatus,
  }), [allFeedbacks, client, cycleReviews, feedbackSnapshotStatus]);

  const cycleReviewTimelineRows = useMemo(() => [
    ...cycleReviewDigest.reviews,
    cycleReviewDigest.sent,
  ].filter(Boolean), [cycleReviewDigest]);

  const longitudinalReviewRows = useMemo(() => {
    const knownIds = new Set(cycleReviewTimelineRows.map((review) => review?.id).filter(Boolean));
    const upcoming = buildPackFollowupSchedule(client)
      .filter((step) => step?.type === 'review' && step?.status !== 'done')
      .map((step) => ({
        id: step.reviewId || `scheduled-${step.stepNumber}`,
        status: step.status,
        step_number: step.stepNumber,
        cycle_label: step.label || `Bilan S${step.weekOffset}`,
        scheduled_at: step.dueDate,
      }))
      .filter((review) => !knownIds.has(review.id));
    return [...cycleReviewTimelineRows, ...upcoming];
  }, [client, cycleReviewTimelineRows]);

  const firstLongitudinalDataAt = useMemo(() => {
    const timestamps = allFeedbacks
      .map((row) => Date.parse(row?.updated_at || row?.created_at || row?.date))
      .filter(Number.isFinite);
    return timestamps.length > 0 ? new Date(Math.min(...timestamps)).toISOString() : null;
  }, [allFeedbacks]);

  const longitudinalAnchor = useMemo(() => resolveLongitudinalAnchor({
    consultationsLog,
    cycleReviews: cycleReviewTimelineRows,
    versions,
    packStartedAt: client?.packStartedAt || null,
    firstDataAt: firstLongitudinalDataAt,
  }), [client?.packStartedAt, consultationsLog, cycleReviewTimelineRows, firstLongitudinalDataAt, versions]);

  useEffect(() => {
    let cancelled = false;
    if (!clientAppChannelEnabled || !hasClientIdentity(client)) {
      setLongitudinalSnapshot(null);
      setLongitudinalStatus('unavailable');
      setLongitudinalError(null);
      return () => { cancelled = true; };
    }
    setLongitudinalStatus('loading');
    setLongitudinalError(null);
    (async () => {
      const result = await fetchClientLongitudinal(client, {
        since: longitudinalAnchor.at,
        to: new Date().toISOString(),
      });
      if (cancelled) return;
      const state = resolveLongitudinalFetchState(result);
      setLongitudinalSnapshot(state.snapshot);
      setLongitudinalStatus(state.status);
      setLongitudinalError(state.error);
    })();
    return () => { cancelled = true; };
  }, [client, clientAppChannelEnabled, longitudinalAnchor.at, longitudinalReloadKey, syncReloadKey]);

  const longitudinalSummary = useMemo(() => {
    if (longitudinalStatus !== 'ready') return null;
    const snapshot = longitudinalSnapshot;
    return buildLongitudinalSummary({
      anchor: longitudinalAnchor,
      nowISO: snapshot?.period?.to || new Date().toISOString(),
      feedbacks: snapshot?.feedbacks || [],
      feedbackStatus: snapshot ? 'ok' : 'unavailable',
      meals: snapshot?.meals || { events: [] },
      mealsStatus: snapshot ? 'ok' : 'unavailable',
      messages: snapshot?.messages || [],
      messagesStatus: snapshot ? 'ok' : 'unavailable',
      activeProgram: snapshot?.activeProgram || null,
      cycleReviews: longitudinalReviewRows,
    });
  }, [longitudinalAnchor, longitudinalReviewRows, longitudinalSnapshot, longitudinalStatus]);

  const longitudinalEvents = useMemo(() => {
    if (longitudinalStatus !== 'ready' || !longitudinalSnapshot) return [];
    return buildLongitudinalEvents({
      anchor: longitudinalAnchor,
      nowISO: longitudinalSnapshot.period.to,
      consultationsLog,
      cycleReviews: cycleReviewTimelineRows,
      versions,
      feedbacks: longitudinalSnapshot.feedbacks,
      meals: longitudinalSnapshot.meals,
      messages: longitudinalSnapshot.messages,
    });
  }, [consultationsLog, cycleReviewTimelineRows, longitudinalAnchor, longitudinalSnapshot, longitudinalStatus, versions]);

  const longitudinalActiveVersion = useMemo(() => {
    return resolveExactSaasVersionForProgram(versions, longitudinalSnapshot?.activeProgram);
  }, [longitudinalSnapshot?.activeProgram, versions]);

  // Une seule priorité peut etre proposee automatiquement : relire un
  // brouillon deja genere. Le reste demeure un choix explicite d'Anissa.
  let nextAction = {
    key: 'default',
    label: 'Suivi ouvert',
    reason: `${feedbacks.length} ressenti${feedbacks.length > 1 ? 's' : ''} recu${feedbacks.length > 1 ? 's' : ''} · ${appointmentProgress.completed}/${appointmentProgress.total} rendez-vous realise${appointmentProgress.completed > 1 ? 's' : ''}. A valider par Anissa.`,
    tone: 'ok',
    actionLabel: null,
    actionHandler: null,
  };
  if (activeDocumentConfirmed) {
    nextAction = {
      key: 'document_delivered',
      label: 'Programme remis par document',
      reason: 'Cette version exacte est confirmée et verrouillée. Toute adaptation doit créer une nouvelle version.',
      tone: 'ok',
      actionLabel: null,
      actionHandler: null,
    };
  } else if (!phaseTransitionRun && !phaseTransitionDraftReady
      && started && isMutableFollowupDraft(lastVersion) && versions.length >= 2) {
    nextAction = {
      key: 'draft_review',
      label: `Version V${versions.length} a relire`,
      reason: `Le brouillon V${versions.length} attend une validation explicite d'Anissa.`,
      tone: 'go',
      actionLabel: `Relire la V${versions.length}`,
      actionHandler: () => openExistingInlineDraft(lastVersion, 'existing-followup-draft'),
    };
  } else if (
    rdvIsPast
    && pastAppointmentHasReviewedConsultation
    && !phaseTransitionRun
    && !phaseTransitionDraftReady
  ) {
    nextAction = {
      key: 'past_appointment_recorded',
      label: 'Rendez-vous effectué à finaliser',
      reason: closableReviewStep
        ? `La consultation est bien enregistrée. Confirme que ${closableReviewStep.label} a été traité pendant ce rendez-vous pour fermer les deux actions.`
        : 'La consultation est bien enregistrée. Confirme le rendez-vous effectué pour archiver cette date.',
      tone: 'go',
      actionLabel: finalizingMilestones
        ? 'Finalisation…'
        : (closableReviewStep
          ? `Finaliser le rendez-vous et ${closableReviewStep.label}`
          : 'Marquer le rendez-vous comme effectué'),
      actionHandler: finalizingMilestones ? null : handleFinalizeRecordedPastAppointment,
    };
  } else if (
    rdvIsPast
    && consultationsUsed < consultationsTotal
    && !phaseTransitionRun
    && !phaseTransitionDraftReady
  ) {
    nextAction = {
      key: 'past_appointment',
      label: 'Compte rendu du rendez-vous à enregistrer',
      reason: `${formatRdvLong(journey.next_rdv_at)} est passé. Enregistre les faits et la décision d’Anissa avant de reprogrammer la suite.`,
      tone: 'go',
      actionLabel: 'Saisir le compte rendu',
      actionHandler: () => {
        setBriefingCycleReview(null);
        setBriefingWorkflowIntent(null);
        setMeetingWorkspace(null);
        setShowBriefing(true);
      },
    };
  }

  // Refonte Suivi — la carte « Pilotage » (jalons Questionnaire / Consultation
  // / Plan livré) est retirée pour les packs de suivi : ces jalons vivent déjà
  // dans la sidebar 8 étapes (zéro redondance). On garde uniquement le statut
  // honnête des packs ponctuels, qui n'existe nulle part ailleurs.
  const pilotDelivered = getStepStatus(journey, 'delivery') === 'validated';

  // Livraison confirmée = même condition que daysSincePack (packStartedAt
  // confirmé). Sert d'événement « Programme livré » dans la frise verticale.
  const deliveredAt = client.packStartedAt && client.packStartedAtConfirmed === true
    ? client.packStartedAt
    : null;

  // Phases affichées : consultation hôte sinon phases en attente (même
  // fallback que l'ancienne frise).
  const displayPhases = activeConsult?.protocol_phases || pendingPhases || null;
  const activePhaseName = getActivePhase(displayPhases)?.client_name || null;
  const activePhaseForCycleReview = getActivePhase(displayPhases);
  const cycleReviewPhaseChangeAvailable = Boolean(
    activePhaseForCycleReview
    && (displayPhases?.phases || []).some(
      (phase, index, phases) => phase.id === activePhaseForCycleReview.id && Boolean(phases[index + 1]),
    ),
  );
  const handlePrepareCycleReviewDecision = useCallback((intent = null) => {
    if (!cycleReviewDigest.current) return;
    const resolved = resolveCycleReviewWorkflowIntent(intent, {
      phaseChangeAvailable: cycleReviewPhaseChangeAvailable,
    });
    if (!resolved.allowed) return;
    setBriefingCycleReview(cycleReviewDigest);
    setBriefingWorkflowIntent(resolved.workflowAction);
    setMeetingWorkspace(null);
    setShowBriefing(true);
  }, [cycleReviewDigest, cycleReviewPhaseChangeAvailable]);
  const transitionFromPhase = phaseTransitionRun
    ? displayPhases?.phases?.find((phase) => phase.id === phaseTransitionRun.fromPhaseId)
    : null;
  const transitionToPhase = phaseTransitionRun
    ? phaseTransitionRun.proposedProtocolPhases?.phases?.find(
      (phase) => phase.id === phaseTransitionRun.toPhaseId,
    )
    : null;
  const transitionNeedsApp = clientAppChannelEnabled && (
    phaseTransitionRun?.deliveryMode === 'app'
      || phaseTransitionRun?.deliveryMode === 'app_and_document'
  );
  const transitionNeedsDocument = phaseTransitionRun?.deliveryMode === 'document'
    || phaseTransitionRun?.deliveryMode === 'app_and_document';
  const transitionCanPublishApp = transitionNeedsApp
    && (phaseTransitionRun?.deliveryMode !== 'app_and_document'
      || Boolean(phaseTransitionRun?.documentConfirmedAt));
  useEffect(() => {
    if (phaseTransitionRun || phaseTransitionDraftReady) setShowDocumentDelivery(false);
  }, [phaseTransitionDraftReady, phaseTransitionRun]);

  if (!packAccess.known) {
    return (
      <section className="jrn-followup-step jrn-cockpit" aria-labelledby="unknown-pack-title">
        <div className="jrn-cockpit-empty">
          <p id="unknown-pack-title" className="jrn-cockpit-empty__title">Suivi indisponible</p>
          <p className="jrn-cockpit-empty__hint">
            Le pack de cette fiche est absent ou inconnu. Aucune consultation,
            adaptation ou publication de suivi n est autorisee tant qu un pack
            valide n a pas ete choisi.
          </p>
          {onExit && (
            <button type="button" onClick={onExit} className="jrn-btn jrn-btn--soft">
              Revenir a la fiche
            </button>
          )}
        </div>
      </section>
    );
  }

  if (packAccess.appMode === 'oneshot' && !packAccess.continuousFollowup) {
    return (
      <OneShotFollowupTerminal
        client={client}
        prenom={prenom}
        packLabel={pack?.label || 'Accompagnement ponctuel'}
        clientAppChannelEnabled={clientAppChannelEnabled}
        onExit={onExit}
      />
    );
  }

  // Refonte Suivi — les « signaux temporels » du header (dernier ressenti /
  // dernière activité / consultations) sont retirés : ces infos vivent
  // désormais UNIQUEMENT dans la frise verticale « La vie du suivi » et dans
  // la raison de la carte « Maintenant » (moteur P1–P7). Zéro redondance.

  return (
    <section className="jrn-followup-step jrn-cockpit">
      {/* ════════ HERO COCKPIT — V97.13.21 (signaux temporels axe 4) ════════ */}
      <header className="jrn-cockpit-hero">
        <div className="jrn-cockpit-hero__id">
          <h2 className="jrn-cockpit-hero__title">
            Suivi · {prenom}
          </h2>
          <p className="jrn-cockpit-hero__sub">
            {packLabel}
            {daysSincePack ? ` · jour ${daysSincePack}` : ''}
            {started ? ` · cycle actif` : ' · à démarrer'}
            {started && lastVersion ? ` · protocole V${versions.length} active` : ''}
          </p>
          {/* V97.41 (roadmap 1.4) — badge synchro app cliente + retry.
              Donne a Anissa la confiance que les ressentis/pesees affiches sont
              a jour (ou signale une synchro echouee avec moyen de relancer). */}
          <div style={syncBadgeRowStyle}>
            {!clientAppChannelEnabled ? (
              <span style={syncBadgeMutedStyle}>Suivi dans le SaaS · remise par document</span>
            ) : (loadingFb || loadingWeight) ? (
              <span style={syncBadgeMutedStyle}>Synchro app cliente…</span>
            ) : syncError ? (
              <>
                <span style={syncBadgeErrorStyle}>⚠ Synchro app cliente échouée</span>
                <button type="button" onClick={handleRetrySync} style={syncRetryBtnStyle}>
                  Réessayer
                </button>
              </>
            ) : (
              <>
                <span style={syncBadgeMutedStyle}>
                  Synchronisé
                  {lastSyncAt
                    ? ` à ${lastSyncAt.toLocaleTimeString('fr-CH', { hour: '2-digit', minute: '2-digit' })}`
                    : ''}
                </span>
                <button
                  type="button"
                  onClick={handleRetrySync}
                  style={syncRetryBtnStyle}
                  title="Rafraîchir les ressentis & pesées depuis l'app cliente"
                >
                  ↻ Rafraîchir
                </button>
              </>
            )}
          </div>
          {/* Refonte Suivi — ce que la cliente voit, en UNE ligne discrète
              (remplace la bande miroir ; l'alerte « version en attente » est
              portée par la carte « Maintenant »). */}
          {started && !clientAppChannelEnabled && (
            <p className="jrn-cockpit-hero__mirror">
              Suivi sans app — le programme est remis à {prenom} par document.
            </p>
          )}
          {started && clientAppChannelEnabled && clientMirror?.visible && (
            <p className="jrn-cockpit-hero__mirror">
              App de {prenom} — elle voit : {clientMirror.visible.label}
              {clientMirror.activePhase ? ` · phase ${clientMirror.activePhase}` : ''}
              {clientMirror.visible.isLive ? ' · en direct' : ''}
            </p>
          )}
        </div>
        {/* Refonte Suivi — le header ne porte que l'identité, la synchro et le
            miroir cliente. Toutes les actions vivent dans « Maintenant » et
            dans le bloc « Actions ». */}
      </header>

      {clientAppChannelEnabled && <ClientAccessManager client={client} />}

      {!started && (
        <div className="jrn-cockpit-empty">
          <p className="jrn-cockpit-empty__title">Cockpit de suivi non activé.</p>
          <p className="jrn-cockpit-empty__hint">
            Active le cockpit pour commencer à logger consultations, ressentis et adaptations.
            Le parcours initial est complet (livraison validée étape 7).
          </p>
          <button
            onClick={handleStart}
            disabled={busy}
            className="jrn-btn jrn-btn--primary"
          >
            {busy ? 'Activation…' : 'Activer le suivi'}
          </button>
        </div>
      )}

      {started && (
        <>
          {/* ════ CARTE « MAINTENANT » — le foyer unique ═══════════════════
              Réutilise le moteur P1–P7 (nextAction, INCHANGÉ). Quand des
              pièces du programme manquent ou qu'une version attend sa
              publication, le module de génération (checklist 7 pièces + UNE
              porte « Publier » gated complétude + clairance) EST l'action du
              moment et s'affiche ici. Sinon, l'action clinique P1–P7.
              Une seule action mise en avant à la fois. Masquée pour les packs
              ponctuels (pas de suivi long). */}
          {!isOneShotPack && (
            <div className={`jrn-cockpit-card jrn-cockpit-card--priority jrn-now-card jrn-cockpit-card--tone-${generationCurrent ? 'go' : nextAction.tone}`}>
              <div className="jrn-cockpit-card__head">
                <span className="jrn-cockpit-card__eyebrow">Maintenant</span>
                {!generationCurrent && (
                  <span className={`jrn-cockpit-card__tone jrn-cockpit-card__tone--${nextAction.tone}`}>
                    {nextAction.tone === 'warn' ? '⚠' : nextAction.tone === 'go' ? '→' : '✓'}
                  </span>
                )}
              </div>

              {generationCurrent ? (
                <>
                  <p className="jrn-cockpit-card__priority-label">
                    {completeness.allReady
                      ? (clientAppChannelEnabled
                        ? `Publier le programme de ${prenom}`
                        : `Programme de ${prenom} prêt à remettre`)
                      : `Générer le programme de ${prenom}`}
                  </p>
                  {/* Alerte miroir « version préparée » — info unique, portée
                      ici (la porte Publier est juste en dessous). */}
                  {clientAppChannelEnabled && clientMirror?.alert?.kind === 'pending' && (
                    <p className="jrn-cockpit-card__priority-reason">
                      Version préparée, pas encore chez {prenom}
                      {typeof clientMirror.alert.sinceDays === 'number' ? ` — en attente depuis ${clientMirror.alert.sinceDays} j` : ''}
                      {typeof clientMirror.alert.expiresInDays === 'number'
                        ? (clientMirror.alert.expiresInDays >= 0 ? ` · expire dans ${clientMirror.alert.expiresInDays} j` : ' · expirée')
                        : ''}.
                    </p>
                  )}
                  <div className="jrn-now-card__module">
                    <CockpitErrorBoundary>
                      <ProgramGenerationPanel
                        client={client}
                        consultation={activeConsult}
                        clearanceVerdict={programClearance}
                        deliveryChannel={clientAppChannelEnabled ? 'app' : 'document'}
                        onSaveGenerated={handleSaveGenerated}
                        onOpenPreview={onOpenAppPreview}
                        onPublish={onOpenAppPreview}
                        onDeliverDocument={documentDeliveryChannelEnabled
                          && !phaseTransitionRun
                          && !phaseTransitionDraftReady
                          ? () => setShowDocumentDelivery(true)
                          : undefined}
                      />
                    </CockpitErrorBoundary>
                  </div>
                  {nextAction.tone !== 'ok' && (
                    <p className="jrn-now-card__later">Ensuite : {nextAction.label}</p>
                  )}
                </>
              ) : (
                <>
                  <p className="jrn-cockpit-card__priority-label">{nextAction.label}</p>
                  {nextAction.reason && (
                    <p className="jrn-cockpit-card__priority-reason">
                      <span className="jrn-cockpit-card__priority-reason-eyebrow">Pourquoi&nbsp;?</span>
                      {' '}{nextAction.reason}
                    </p>
                  )}
                  {nextAction.actionHandler && nextAction.actionLabel && (
                    <button
                      onClick={nextAction.actionHandler}
                      className={`jrn-btn ${nextAction.tone === 'go' ? 'jrn-btn--primary' : 'jrn-btn--soft'} jrn-cockpit-card__priority-btn`}
                    >
                      {nextAction.actionLabel}
                    </button>
                  )}
                </>
              )}
            </div>
          )}

          {/* Pack ponctuel : statut honnête (info unique, V97.57) + module de
              génération en clair — c'est leur seul flux « générer → publier ». */}
          {isOneShotPack && (
            <div className="jrn-cockpit-card jrn-pilot-card">
              <div className="jrn-cockpit-card__head">
                <span className="jrn-cockpit-card__eyebrow">Pilotage — {prenom}</span>
              </div>
              <div className="jrn-pilot-status">
                <span className="jrn-pilot-status__label">Statut</span>
                <span className="jrn-pilot-status__value">
                  {pilotDelivered
                    ? 'Plan remis · aucune consultation de suivi incluse'
                    : 'Anamnèse initiale · plan non livré'}
                </span>
              </div>
            </div>
          )}
          {isOneShotPack && (
            <CockpitErrorBoundary>
              <ProgramGenerationPanel
                client={client}
                consultation={activeConsult}
                clearanceVerdict={programClearance}
                deliveryChannel={clientAppChannelEnabled ? 'app' : 'document'}
                onSaveGenerated={handleSaveGenerated}
                onOpenPreview={onOpenAppPreview}
                onPublish={onOpenAppPreview}
              />
            </CockpitErrorBoundary>
          )}

          {/* V97.122 — Ligne « Prochain RDV » permanente et indépendante.
              Anissa pose / modifie / annule le rendez-vous suivant à tout
              moment, sans passer par l'enregistrement d'une consultation.
              Synchro app cliente (carte accueil) + rappel J-1. */}
          {!isOneShotPack && (
            <div className="jrn-cockpit-card" style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <div style={{ flex: '1 1 auto', minWidth: 200 }}>
                <span className="jrn-cockpit-card__eyebrow">{rdvIsPast ? 'Rendez-vous passé' : 'Prochain rendez-vous'}</span>
                {!showRdvEdit ? (
                  <div style={{ fontSize: 14, fontWeight: 600, color: rdvIsPast ? 'var(--jrn-text-muted, #6b6f6b)' : 'var(--jrn-text, #1a2e1f)', marginTop: 4 }}>
                    {journey.next_rdv_at
                      ? <>{formatRdvLong(journey.next_rdv_at)}{rdvIsPast && <span style={{ fontWeight: 400 }}> — à reprogrammer ?</span>}</>
                      : <span style={{ fontWeight: 400, color: 'var(--jrn-text-muted, #6b6f6b)' }}>Aucun rendez-vous programmé</span>}
                  </div>
                ) : (
                  <input
                    type="datetime-local"
                    value={rdvDraft}
                    onChange={(e) => setRdvDraft(e.target.value)}
                    disabled={savingRdv}
                    style={{ marginTop: 6, fontSize: 13, padding: '8px 10px', borderRadius: 8, border: '1px solid var(--jrn-border-strong, #e4dcc9)', background: 'var(--jrn-surface, #fff)', color: 'var(--jrn-text, #1a2e1f)', maxWidth: 260 }}
                  />
                )}
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                {!showRdvEdit ? (
                  <>
                    {!nextAction.key.startsWith('past_appointment') && (
                      <button
                        className="jrn-btn jrn-btn--primary"
                        onClick={() => { setBriefingCycleReview(null); setBriefingWorkflowIntent(null); setShowBriefing(true); }}
                        title="Résumé de la période depuis la dernière consultation, pour préparer le rendez-vous."
                      >
                        Préparer le rendez-vous
                      </button>
                    )}
                    <button
                      className="jrn-btn jrn-btn--soft"
                      onClick={() => { setRdvDraft(rdvIsoToLocalInput(journey.next_rdv_at)); setShowRdvEdit(true); }}
                    >
                      {journey.next_rdv_at ? 'Modifier' : 'Poser une date'}
                    </button>
                    {journey.next_rdv_at && !pastAppointmentHasReviewedConsultation && (
                      <button
                        className="jrn-btn jrn-btn--ghost"
                        disabled={savingRdv}
                        onClick={() => {
                          const detail = shouldSyncNextRdvToApp
                            ? ` ${prenom} ne le verra plus dans son app.`
                            : ' La date sera retirée du dossier SaaS.';
                          if (window.confirm(`Annuler le prochain rendez-vous ?${detail}`)) handleSaveNextRdv(null);
                        }}
                      >
                        Annuler le RDV
                      </button>
                    )}
                  </>
                ) : (
                  <>
                    <button
                      className="jrn-btn jrn-btn--primary"
                      disabled={savingRdv || !rdvDraft}
                      onClick={() => handleSaveNextRdv(new Date(rdvDraft).toISOString())}
                    >
                      {savingRdv ? 'Enregistrement…' : 'Enregistrer'}
                    </button>
                    <button className="jrn-btn jrn-btn--ghost" disabled={savingRdv} onClick={() => setShowRdvEdit(false)}>
                      Annuler
                    </button>
                  </>
                )}
              </div>
              {journey.next_rdv_at && !showRdvEdit && !rdvIsPast && rdvConfirmedInApp && (
                <div style={{ flexBasis: '100%', fontSize: 11, color: 'var(--jrn-text-muted, #6b6f6b)' }}>
                  {prenom} le voit dans son app · rappel automatique la veille.
                </div>
              )}
              {journey.next_rdv_at && !showRdvEdit && !rdvIsPast && !shouldSyncNextRdvToApp && (
                <div style={{ flexBasis: '100%', fontSize: 11, color: 'var(--jrn-text-muted, #6b6f6b)' }}>
                  Enregistré dans le SaaS · aucune synchronisation app.
                </div>
              )}
              {shouldSyncNextRdvToApp && (rdvSyncRuntimeError || rdvSyncNeedsRetry) && !showRdvEdit && (
                <div
                  role="alert"
                  style={{
                    flexBasis: '100%',
                    padding: '10px 12px',
                    borderRadius: 10,
                    border: '1px solid rgba(174, 113, 31, .3)',
                    background: 'rgba(239, 199, 112, .12)',
                    color: '#745016',
                    fontSize: 12,
                  }}
                >
                  <strong>Synchronisation app à reprendre.</strong>{' '}
                  Le rendez-vous est bien conservé dans le SaaS, mais sa date n'est pas encore confirmée dans l'app cliente.
                  <button
                    type="button"
                    className="jrn-btn jrn-btn--soft"
                    disabled={savingRdv}
                    onClick={() => handleSaveNextRdv(
                      rdvSyncRuntimeTarget !== undefined
                        ? rdvSyncRuntimeTarget
                        : (rdvSyncMarker?.target_at ?? null),
                    )}
                    style={{ marginLeft: 10 }}
                  >
                    {savingRdv ? 'Synchronisation…' : 'Réessayer maintenant'}
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Alerte clinique pattern-based (V97.17.20) — TOUJOURS visible,
              volontairement HORS du tiroir « Signes vitaux » : un signal
              clinique ne doit jamais être replié. Se masque seul si RAS. */}
          <CockpitErrorBoundary>
            <ClinicalAlertBanner feedbacks={feedbacks} syncError={syncError} />
          </CockpitErrorBoundary>

          {clientAppChannelEnabled && pendingTransitionActivation && (
            <section
              aria-labelledby="phase-transition-activation-title"
              style={{ margin: 'var(--jrn-5) 0', padding: '16px 18px', borderRadius: 12, background: 'rgba(229,200,120,.1)', border: '1px solid rgba(180,138,45,.28)' }}
            >
              <p style={{ margin: 0, fontSize: 10, letterSpacing: '.12em', textTransform: 'uppercase', color: '#795c18', fontWeight: 700 }}>
                Phase finalisée · activation app à reprendre
              </p>
              <h3 id="phase-transition-activation-title" style={{ margin: '5px 0 4px', color: '#1A2E1F', fontSize: 16 }}>
                Le brouillon exact est prêt, mais reste invisible
              </h3>
              <p style={{ margin: 0, color: '#4b5f50', fontSize: 12.5, lineHeight: 1.5 }}>
                Cette reprise active uniquement le brouillon déjà préparé. Elle ne republie pas le programme.
              </p>
              <button type="button" className="jrn-btn jrn-btn--primary" disabled={retryingTransitionActivation} onClick={handleRetryTransitionActivation} style={{ marginTop: 12 }}>
                {retryingTransitionActivation ? 'Activation…' : 'Reprendre uniquement l’activation'}
              </button>
            </section>
          )}

          {clientAppChannelEnabled && pendingTransitionNotification && !pendingTransitionActivation && (
            <section
              aria-labelledby="phase-transition-notification-title"
              style={{
                margin: 'var(--jrn-5) 0',
                padding: '16px 18px',
                borderRadius: 12,
                background: 'rgba(229,200,120,.1)',
                border: '1px solid rgba(180,138,45,.28)',
              }}
            >
              <p style={{ margin: 0, fontSize: 10, letterSpacing: '.12em', textTransform: 'uppercase', color: '#795c18', fontWeight: 700 }}>
                Phase active · notification à reprendre
              </p>
              <h3 id="phase-transition-notification-title" style={{ margin: '5px 0 4px', color: '#1A2E1F', fontSize: 16 }}>
                La nouvelle phase est déjà disponible dans l’app
              </h3>
              <p style={{ margin: 0, color: '#4b5f50', fontSize: 12.5, lineHeight: 1.5 }}>
                Seule la notification cliente a échoué. Cette action ne republie pas le programme et ne change plus la phase.
              </p>
              <button
                type="button"
                className="jrn-btn jrn-btn--primary"
                disabled={retryingTransitionNotification}
                onClick={handleRetryTransitionNotification}
                style={{ marginTop: 12 }}
              >
                {retryingTransitionNotification ? 'Envoi…' : 'Renvoyer uniquement la notification'}
              </button>
            </section>
          )}

          {phaseTransitionRun && (
            <section
              aria-labelledby="phase-transition-ready-title"
              style={{
                margin: 'var(--jrn-5) 0',
                padding: '18px 20px',
                borderRadius: 14,
                background: 'linear-gradient(135deg, rgba(234,243,236,.96), rgba(250,249,246,.98))',
                border: '1px solid rgba(46,94,62,.24)',
                boxShadow: '0 14px 34px -28px rgba(26,46,31,.7)',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
                <div style={{ flex: '1 1 360px' }}>
                  <p style={{ margin: 0, fontSize: 10, letterSpacing: '.12em', textTransform: 'uppercase', color: '#2E5E3E', fontWeight: 700 }}>
                    Programme préparé · en attente de remise
                  </p>
                  <h3 id="phase-transition-ready-title" style={{ margin: '5px 0 0', color: '#1A2E1F', fontSize: 18 }}>
                    Phase suivante prête
                  </h3>
                  <p style={{ margin: '7px 0 0', color: '#3a5244', fontSize: 13.5, lineHeight: 1.5 }}>
                    Phase {transitionFromPhase?.order || '—'} · {transitionFromPhase?.client_name || 'Phase actuelle'}
                    {' '}→ Phase {transitionToPhase?.order || '—'} · {transitionToPhase?.client_name || 'Phase suivante'}
                  </p>
                  <p style={{ margin: '6px 0 0', color: 'var(--jrn-text-muted, #6b6f6b)', fontSize: 12, lineHeight: 1.45 }}>
                    La phase actuelle reste active. La suivante ne le deviendra qu'après les preuves de remise ci-dessous.
                  </p>
                </div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                  {transitionNeedsApp && (
                    <span className={`jrn-cockpit__pill ${phaseTransitionRun.appPublishedAt ? 'jrn-cockpit__pill--on' : 'jrn-cockpit__pill--off'}`}>
                      {phaseTransitionRun.appPublishedAt ? '✓ App publiée' : 'App à publier'}
                    </span>
                  )}
                  {transitionNeedsDocument && (
                    <span className={`jrn-cockpit__pill ${phaseTransitionRun.documentConfirmedAt ? 'jrn-cockpit__pill--on' : 'jrn-cockpit__pill--off'}`}>
                      {phaseTransitionRun.documentConfirmedAt ? '✓ Document remis' : 'Document à remettre'}
                    </span>
                  )}
                </div>
              </div>

              <div className="jrn-actions" style={{ marginTop: 14 }}>
                {transitionCanPublishApp && !phaseTransitionRun.appPublishedAt && (
                  <button
                    type="button"
                    className="jrn-btn jrn-btn--primary"
                    disabled={!activeConsult || busy}
                    onClick={() => onOpenAppPreview?.({
                      consultation: activeConsult,
                      phaseTransitionRun,
                    })}
                  >
                    Vérifier puis publier dans l'app
                  </button>
                )}
                {transitionNeedsApp && !transitionCanPublishApp && (
                  <span style={{ fontSize: 12, color: 'var(--jrn-text-muted, #6b6f6b)' }}>
                    Confirme d'abord la remise du document; la publication app viendra en dernier.
                  </span>
                )}
                {transitionNeedsDocument && (
                  <button
                    type="button"
                    className="jrn-btn jrn-btn--ghost"
                    disabled={!activeConsult || exportingTransitionDocument}
                    onClick={handleExportTransitionDocument}
                  >
                    {exportingTransitionDocument
                      ? 'Export en cours…'
                      : (exportedTransitionProof?.transitionId === phaseTransitionRun.id
                        ? '✓ Word téléchargé — Re-télécharger'
                        : 'Exporter le Word')}
                  </button>
                )}
                {transitionNeedsDocument && !phaseTransitionRun.documentConfirmedAt && (
                  <button
                    type="button"
                    className="jrn-btn jrn-btn--primary"
                    disabled={
                      !activeConsult
                      || confirmingDocumentTransition
                      || !canConfirmPhaseTransitionDocument(phaseTransitionRun, exportedTransitionProof)
                    }
                    onClick={handleConfirmTransitionDocument}
                  >
                    {confirmingDocumentTransition ? 'Confirmation…' : 'Confirmer la remise du document'}
                  </button>
                )}
                {!phaseTransitionRun.appArtifactSha256
                  && !phaseTransitionRun.appPublishedAt
                  && !phaseTransitionRun.documentConfirmedAt && (
                    <button
                      type="button"
                      className="jrn-btn jrn-btn--ghost"
                      disabled={cancellingPhaseTransition}
                      onClick={handleCancelPhaseTransition}
                    >
                      {cancellingPhaseTransition ? 'Annulation…' : 'Annuler et reprendre la préparation'}
                    </button>
                )}
              </div>
            </section>
          )}

          {/* ════ FRISE VERTICALE « La vie du suivi » ══════════════════════
              Journal chronologique UNIFIÉ (récent en haut) : livraison,
              consultations, versions, publications, transitions de phase,
              ressentis par semaine, jalons poids. Remplace la frise
              horizontale ET les blocs Consultations / Ressentis / Poids /
              Historique des versions. */}
          <CockpitErrorBoundary>
            <LongitudinalFollowupSummary
              summary={longitudinalSummary}
              status={longitudinalStatus === 'idle' || longitudinalStatus === 'loading'
                ? 'loading'
                : (longitudinalStatus === 'ready' ? 'ok' : longitudinalStatus)}
              errorMessage={longitudinalError}
              onRetry={() => setLongitudinalReloadKey((value) => value + 1)}
              onOpenMessages={onOpenMessages}
              onOpenProgram={longitudinalActiveVersion
                ? () => setPreviewVersion(longitudinalActiveVersion)
                : null}
            />
          </CockpitErrorBoundary>

          <CockpitErrorBoundary>
            <SuiviTimeline
              deliveredAt={deliveredAt}
              initialAppointmentAt={initialAppointmentAt}
              initialAppointmentCompleted={appointmentProgress.initialCompleted > 0}
              consultationsLog={consultationsLog}
              feedbacks={longitudinalSnapshot?.feedbacks || feedbacks}
              cycleReviews={cycleReviewTimelineRows}
              versions={versions}
              weightEntries={weightEntries}
              protocolPhases={displayPhases}
              publicationMarkers={publicationMarkers}
              activePlanPublication={activePlanPublication}
              longitudinalEvents={longitudinalEvents}
              period={longitudinalSummary?.period || null}
              onPreviewVersion={setPreviewVersion}
              onRemoveLastConsultation={handleRemoveLastConsultation}
              busy={busy}
            />
          </CockpitErrorBoundary>

          {/* ════ TIROIR « SIGNES VITAUX » ═════════════════════════════════
              Secondaire, replié par défaut : sparklines des ressentis, texte
              des derniers ressentis, tendance poids, pouls clinique.
              Disponible sans crier. (L'alerte clinique reste HORS tiroir.) */}
          <details className="jrn-drawer jrn-vitals">
            <summary className="jrn-drawer__summary">
              Signes vitaux
              <span className="jrn-drawer__hint">tendances ressentis · poids · pouls clinique</span>
            </summary>
            <div className="jrn-drawer__body">
              <CockpitErrorBoundary>
                {/* V97.17.21 — sparklines Digestion / Fatigue / Énergie / Faim */}
                <FeedbacksTrendChart feedbacks={feedbacks} syncError={syncError} />

                {/* Texte des derniers ressentis (la frise n'en garde que le
                    pouls hebdomadaire — le contenu se lit ici). */}
                <div className="jrn-vitals__section">
                  <h4 className="jrn-vitals__title">
                    Derniers ressentis · {feedbacks.length}
                    <span className="jrn-vitals__title-meta">14 derniers jours</span>
                  </h4>
                  {/* P2.1 — état réel propagé : on ne dit « vient de démarrer »
                      que si la synchro a réussi et qu'il n'y a réellement rien. */}
                  {signalDisplayState({ loading: loadingFb, syncError, count: feedbacks.length }) === 'loading' && (
                    <p className="jrn-cockpit-empty-row">Chargement…</p>
                  )}
                  {signalDisplayState({ loading: loadingFb, syncError, count: feedbacks.length }) === 'sync_error' && (
                    <p className="jrn-cockpit-empty-row">
                      Ressentis indisponibles — synchro app cliente échouée. Donnée non récupérée (≠ absence de ressenti).
                    </p>
                  )}
                  {signalDisplayState({ loading: loadingFb, syncError, count: feedbacks.length }) === 'empty' && (
                    <p className="jrn-cockpit-empty-row">
                      Aucun ressenti reçu sur 14 jours — cliente vient de démarrer.
                    </p>
                  )}
                  {signalDisplayState({ loading: loadingFb, syncError, count: feedbacks.length }) === 'data' && (
                    <div className="jrn-surface" style={{ padding: 0, overflow: 'hidden' }}>
                      {feedbacks.slice(0, 5).map((f, i) => (
                        <div key={f.id || i} style={{ padding: '12px 16px', borderBottom: i < Math.min(4, feedbacks.length - 1) ? '1px solid var(--jrn-border)' : 'none' }}>
                          <div style={{ fontSize: 10, color: 'var(--jrn-text-muted)', textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 4 }}>
                            {f.created_at ? new Date(f.created_at).toLocaleDateString('fr-CH', { day: '2-digit', month: 'short' }) : ''}
                          </div>
                          <div style={{ fontSize: 13, color: 'var(--jrn-text)', lineHeight: 1.5 }}>
                            {(() => {
                              const dims = formatFeedbackSummary(f);
                              const hasWeight = typeof f.weight_kg === 'number';
                              const hasNote = typeof f.note === 'string' && f.note.trim();
                              if (dims.length === 0 && !hasWeight && !hasNote) {
                                return <span style={{ color: 'var(--jrn-text-muted)' }}>Ressenti enregistré (aucun détail).</span>;
                              }
                              return (
                                <>
                                  {(dims.length > 0 || hasWeight) && (
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 12px' }}>
                                      {dims.map((it) => (
                                        <span key={it.label} style={{ fontSize: 12.5 }}>
                                          <span style={{ color: 'var(--jrn-text-muted)' }}>{it.label} :</span>{' '}
                                          <span style={{ fontWeight: 600 }}>{it.value}</span>
                                        </span>
                                      ))}
                                      {hasWeight && (
                                        <span style={{ fontSize: 12.5 }}>
                                          <span style={{ color: 'var(--jrn-text-muted)' }}>Poids :</span>{' '}
                                          <span style={{ fontWeight: 600 }}>{f.weight_kg} kg</span>
                                        </span>
                                      )}
                                    </div>
                                  )}
                                  {hasNote && (
                                    <div style={{ marginTop: 6, fontStyle: 'italic', color: 'var(--jrn-text-soft)', whiteSpace: 'pre-wrap' }}>
                                      « {f.note.trim()} »
                                    </div>
                                  )}
                                </>
                              );
                            })()}
                          </div>
                        </div>
                      ))}
                      {feedbacks.length > 5 && (
                        <div style={{ padding: '8px 16px', fontSize: 11, color: 'var(--jrn-text-muted)', textAlign: 'center', background: 'var(--jrn-surface-alt)' }}>
                          + {feedbacks.length - 5} autres dans le panel Messages
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Tendance poids (Phase AK) */}
                <div className="jrn-vitals__section">
                  <h4 className="jrn-vitals__title">
                    Suivi du poids{weightEntries.length > 0 ? ` · ${weightEntries.length} pesée${weightEntries.length > 1 ? 's' : ''}` : ''}
                  </h4>
                  <WeightTrackingSection
                    client={client}
                    entries={weightEntries}
                    loading={loadingWeight}
                    syncError={syncError}
                  />
                </div>

                {/* V97.17.12 — Pouls clinique (chips des 3 dernières consults) */}
                <ClientPulseSummary consultationsLog={consultationsLog} />
              </CockpitErrorBoundary>
            </div>
          </details>

          {/* ════ ACTIONS SECONDAIRES — UN seul endroit ════════════════════
              Regroupe les boutons de routine jadis éparpillés (header, carte
              prioritaire, bloc « Cycle de suivi »). On saute l'action déjà
              promue par la carte « Maintenant » pour ne jamais doubler. */}
          <div className="jrn-block jrn-actions-block">
            <div className="jrn-block__head">
              <h3 className="jrn-block__title">Actions</h3>
              {appointmentProgress.total > 0 && (
                <div className="jrn-block__head-meta">
                  <span className="jrn-meta-chip jrn-meta-chip--neutral">
                    rendez-vous {appointmentProgress.completed} / {appointmentProgress.total} · initial inclus
                  </span>
                </div>
              )}
            </div>
            <div className="jrn-actions" style={{ marginTop: 0 }}>
              {consultationsTotal > 0 && !nextAction.key.startsWith('past_appointment') && (
                <button
                  onClick={() => { setBriefingCycleReview(null); setBriefingWorkflowIntent(null); setMeetingWorkspace(null); setShowBriefing(true); }}
                  disabled={consultationsUsed >= consultationsTotal || Boolean(phaseTransitionRun) || phaseTransitionDraftReady}
                  className="jrn-btn jrn-btn--soft"
                  title={consultationsUsed >= consultationsTotal ? 'Tous les rendez-vous de suivi inclus ont été réalisés' : 'Préparer le prochain rendez-vous de suivi'}
                >
                  Préparer + enregistrer le rendez-vous
                </button>
              )}
              {!isOneShotPack && (
                <div className="jrn-program-update-action">
                  <button
                    onClick={() => {
                      setPendingProgramUpdateContext({ mode: 'manual' });
                      setShowProgramUpdateScope(true);
                    }}
                    disabled={adapting || Boolean(phaseTransitionRun) || phaseTransitionDraftReady}
                    className="jrn-btn jrn-btn--primary"
                    title={`Modifier le programme de ${prenom}. Rien ne sera publié automatiquement.`}
                  >
                    {adapting ? 'Ouverture…' : 'Modifier le programme dans l’app'}
                  </button>
                  <small>À tout moment, après un rendez-vous ou une demande reçue.</small>
                  {isMutableFollowupDraft(activeConsult)
                    && (activeConsult?.followupData?._versionProvenance
                      ?? activeConsult?.followup_data?._versionProvenance)?.trigger === 'manual_program_update' && (
                    <button
                      onClick={handleAbandonManualDraft}
                      disabled={adapting}
                      className="jrn-btn jrn-btn--ghost"
                      title="Mettre de côté le brouillon non publié (rien n’est effacé), par exemple après une correction de l’anamnèse."
                    >
                      Abandonner ce brouillon
                    </button>
                  )}
                </div>
              )}
              {onViewHistory && (
                <button
                  onClick={() => onViewHistory(client.id)}
                  className="jrn-btn jrn-btn--ghost"
                  title="Voir l'historique complet des consultations passées"
                >
                  Historique complet
                </button>
              )}
              {/* Renouveler le suivi : nouvelle période sur la même fiche.
                  Réservé aux packs de suivi (avec planning), jamais aux
                  ponctuels / consultation_initiale. */}
              {!isOneShotPack && (
                <button
                  onClick={() => setShowRenewModal(true)}
                  disabled={Boolean(phaseTransitionRun) || phaseTransitionDraftReady}
                  className="jrn-btn jrn-btn--ghost"
                  title="Démarrer une nouvelle période de suivi sur cette fiche (garde tout l'historique)"
                >
                  Renouveler le suivi
                </button>
              )}
              {clientAppChannelEnabled && onOpenAppPreview && (
                <button
                  onClick={onOpenAppPreview}
                  className="jrn-btn jrn-btn--ghost"
                  title="Voir l'app telle que la cliente la voit"
                >
                  Aperçu app cliente
                </button>
              )}
              {/* V97.129 — Raccourci messagerie depuis le Suivi (ouvre le même
                  panneau que le bouton « Messages » du bandeau). Anissa écrit
                  à sa cliente là où elle suit son parcours. */}
              {clientAppChannelEnabled && onOpenMessages && (
                <button
                  onClick={onOpenMessages}
                  className="jrn-btn jrn-btn--ghost"
                  title="Écrire un message à la cliente (arrive dans son app + notification)"
                >
                  Message à la cliente
                </button>
              )}
            </div>
            {consultationsTotal > 0 && consultationsUsed >= consultationsTotal && (
              <p style={{ marginTop: 'var(--jrn-2)', fontSize: 'var(--jrn-text-xs)', color: 'var(--jrn-warn)' }}>
                ⓘ Tous les rendez-vous de suivi inclus ont été réalisés ({appointmentProgress.completed}/{appointmentProgress.total}, rendez-vous initial inclus). Pour ajouter un rendez-vous, créez un nouveau pack ou ajustez le pack actuel.
              </p>
            )}
          </div>

          {/* Parcours existant : affichage et gestes explicites uniquement.
              Aucun ressenti ne suggere automatiquement une transition. */}
          {!isOneShotPack && (
            <details className="jrn-drawer">
              <summary className="jrn-drawer__summary">
                Parcours thérapeutique — phases
                {activePhaseName && (
                  <span className="jrn-drawer__hint">phase active : {activePhaseName}</span>
                )}
              </summary>
              <div className="jrn-drawer__body">
                <CockpitErrorBoundary>
                  <JourneyPhasesCard
                    client={client}
                    consultation={activeConsult}
                    onSavePhases={handleSavePhases}
                    onPrepareNextPhase={phaseTransitionRun ? undefined : handlePrepareNextPhase}
                    nextPhaseDraftReady={phaseTransitionDraftReady}
                    safetySnapshot={phaseTransitionSafetyState.snapshot}
                    safetySnapshotIssue={phaseTransitionSafetyState.issue}
                    pendingPhases={pendingPhases}
                  />
                </CockpitErrorBoundary>
              </div>
            </details>
          )}

          {/* ════ BILAN DE CYCLE — action ponctuelle, rangée en tiroir ════ */}
          {!isOneShotPack && (
            <details
              key={`cycle-review-drawer-${cycleReviewDigest.current?.id || 'empty'}`}
              className="jrn-drawer"
              defaultOpen={Boolean(cycleReviewDigest.current)}
            >
              <summary className="jrn-drawer__summary">Bilan de cycle</summary>
              <div className="jrn-drawer__body">
                <PackReviewSection
                  client={client}
                  reviews={cycleReviews}
                  loadStatus={cycleReviewsStatus}
                  loadError={cycleReviewsError}
                  feedbacks={allFeedbacks}
                  feedbackStatus={feedbackSnapshotStatus}
                  onReload={() => setCycleReviewsReloadKey((value) => value + 1)}
                  onSendPackReview={onSendPackReview}
                  onPrepareDecision={handlePrepareCycleReviewDecision}
                  phaseChangeAvailable={cycleReviewPhaseChangeAvailable}
                  decisionDisabled={
                    consultationsUsed >= consultationsTotal
                    || Boolean(phaseTransitionRun)
                    || phaseTransitionDraftReady
                  }
                />
              </div>
            </details>
          )}

          {/* ─── Footer : Retour dashboard ─────────────────────── */}
          <div className="jrn-actions" style={{ marginTop: 'var(--jrn-6)' }}>
            <button onClick={onExit} className="jrn-btn jrn-btn--ghost">
              ← Retour dashboard
            </button>
          </div>
        </>
      )}

      <ErrorLine msg={err} />

      {consultationOutcome && (
        <div className="jrn-meeting-report__notice" role="status" aria-live="polite" style={{ marginTop: 14 }}>
          <strong>{consultationOutcome.title}</strong>
          <p style={{ margin: '5px 0 0' }}>{consultationOutcome.message}</p>
        </div>
      )}

      {/* Modale aperçu d'une version archivée */}
      {previewVersion && (
        <PlanVersionPreviewModal version={previewVersion} onClose={() => setPreviewVersion(null)} />
      )}

      {/* Modale « Renouveler le suivi » — nouvelle période sur la même fiche */}
      {showRenewModal && (
        <RenewFollowupModal
          currentPackType={client.packType}
          prenom={prenom}
          saving={renewing}
          onCancel={() => setShowRenewModal(false)}
          onConfirm={handleRenewFollowup}
        />
      )}

      {showProgramUpdateScope && (
        <FollowupProgramUpdateModal
          versionNumber={(versions?.length || 0) + 1}
          workflowAction={pendingProgramUpdateContext?.mode === 'manual'
            ? null
            : pendingProgramUpdateContext?.clinical?.workflow_action || reviewedProgramAction}
          manual={pendingProgramUpdateContext?.mode === 'manual'}
          initialSelection={pendingProgramUpdateContext?.resumeScope || null}
          busy={adapting}
          onCancel={() => {
            setShowProgramUpdateScope(false);
            setPendingProgramUpdateContext(null);
          }}
          onConfirm={async (scope) => {
            const deferredContext = pendingProgramUpdateContext;
            setShowProgramUpdateScope(false);
            setPendingProgramUpdateContext(null);
            if (deferredContext?.mode === 'manual') {
              await handleManualProgramUpdate(scope);
              return;
            }
            await handleAdaptFromFeedback({
              ...(deferredContext || {}),
              requestedScopeValue: scope,
            });
          }}
        />
      )}

      {safetyPreflightBlock && (
        <FollowupSafetyIssuesModal
          issues={safetyPreflightBlock.issues}
          onCancel={() => setSafetyPreflightBlock(null)}
          onEditProfile={() => {
            const requestedScope = safetyPreflightBlock.requestedScope;
            setSafetyPreflightBlock(null);
            onEditProfile?.({ resumeProgramUpdateScope: requestedScope });
          }}
        />
      )}

      {adaptationProposal && (
        <ReviewedAdaptationModal
          workflowAction={adaptationProposal.workflow_action}
          value={adaptationDraft}
          confirmed={adaptationConfirmation === adaptationDraft}
          saving={savingAdaptation}
          error={adaptationReviewError}
          onChange={(value) => {
            setAdaptationDraft(value);
            setAdaptationConfirmation(null);
            setAdaptationReviewError(null);
          }}
          onToggleConfirmation={(checked) => {
            setAdaptationConfirmation(checked ? adaptationDraft : null);
            setAdaptationReviewError(null);
          }}
          onCancel={() => {
            if (savingAdaptation) return;
            setAdaptationProposal(null);
            setAdaptationDraft('');
            setAdaptationConfirmation(null);
            setAdaptationReviewError(null);
          }}
          onConfirm={handleConfirmAdaptation}
        />
      )}

      {/* Modale suivi clinique structure (V97.17.6 + V97.17.23 contexte ressentis) */}
      {showLogModal && (
        <LogConsultationModal
          key={`consultation-log-${client.id}`}
          consultationNumber={appointmentProgress.completed + 1}
          totalIncluded={appointmentProgress.total}
          initialWorkflowAction={briefingWorkflowIntent}
          phaseChangeAvailable={cycleReviewPhaseChangeAvailable}
          requireAppointmentOccurredConfirmation={Boolean(briefingCycleReview?.current)}
          pastAppointmentAt={rdvIsPast ? journey.next_rdv_at : null}
          closableReviewStep={briefingCycleReview?.current ? null : closableReviewStep}
          cycleReviewProof={briefingCycleReview?.current ? {
            id: briefingCycleReview.current.id,
            step_number: briefingCycleReview.current.step_number,
            template_type: briefingCycleReview.current.template_type,
          } : null}
          onCancel={() => {
            setShowLogModal(false);
            setLogNote('');
            setMeetingWorkspace(null);
            setBriefingCycleReview(null);
            setBriefingWorkflowIntent(null);
          }}
          onConfirm={handleLogConsultation}
          saving={savingLog}
          recentFeedbacks={feedbacks?.slice(0, 7) || []}
          prenom={prenom}
          meetingWorkspace={meetingWorkspace}
          clientId={client.id}
          sourceConsultationId={activeConsult?.id || ''}
          consultationOrdinal={consultationsUsed + 1}
        />
      )}

      {/* V97.28 — Modal éditeur inline (Cycle de suivi → Éditer manuellement).
          Ne change pas current_step. Permet d'éditer la V courante ou de créer
          une nouvelle version directement depuis l'étape Suivi. */}
      {showInlineEditor && (
        <InlineEditorModal
          client={client}
          existingConsultationTarget={inlineEditorContext?.sourceConsultation || null}
          initialTab={inlineEditorContext?.initialTab || 'plan'}
          updateScope={readFollowupProgramUpdateScope(inlineEditorContext?.sourceConsultation)?.value || null}
          onBeforePersist={assertDocumentVersionEditable}
          onClose={() => {
            setShowInlineEditor(false);
            setInlineEditorContext(null);
            setInlineSavedConsultation(null);
          }}
          onPlanSaved={handleInlineEditorPlanSaved}
          savedConsultation={inlineSavedConsultation}
          canPrepareClientApp={
            clientAppChannelEnabled
            && inlineEditorContext?.kind !== 'phase-transition-draft'
          }
          onPrepareClientApp={handlePrepareSavedFollowupForApp}
        />
      )}

      {showDocumentDelivery && activeConsult?.id && (
        <FollowupDocumentDeliveryModal
          key={`${client.id}:${activeConsult.id}`}
          client={client}
          consultationId={activeConsult.id}
          onClose={() => setShowDocumentDelivery(false)}
          onConfirmed={async (result) => {
            setConfirmedDocumentVersionIds((current) => new Set(current).add(result.consultationId));
            await refreshConfirmedVersions(result?.consultationId);
            await onChange?.();
          }}
        />
      )}

      {/* « Préparer le rendez-vous » — briefing pré-consultation.
          Période = depuis la dernière consultation (fallback plan / pack / 35 j).
          Résumé pur (poids, tendances, commentaires) + brouillon IA éditable. */}
      {showBriefing && (
        <ConsultationBriefingModal
          key={`consultation-briefing-${client.id}`}
          clientId={client.id}
          prenom={prenom}
          feedbacks={allFeedbacks}
          weightEntries={weightEntries}
          consultationsLog={consultationsLog}
          planPublishedAt={activePlanPublication?.date || null}
          packStartedAt={client.packStartedAt || null}
          nextRdvAt={journey.next_rdv_at || null}
          resultsCallNotes={client.journey_state?.results_call_notes || []}
          syncError={syncError}
          syncStatus={feedbackSnapshotStatus}
          loading={loadingFb || loadingWeight}
          safetySnapshot={phaseTransitionSafetyState.snapshot}
          safetySnapshotIssue={phaseTransitionSafetyState.issue}
          programme={activeConsult}
          cycleReviewContext={briefingCycleReview}
          onClose={() => {
            setShowBriefing(false);
            setMeetingWorkspace(null);
            setBriefingCycleReview(null);
            setBriefingWorkflowIntent(null);
          }}
          onContinue={(workspace) => {
            setMeetingWorkspace(workspace);
            setShowBriefing(false);
            setShowLogModal(true);
          }}
        />
      )}
    </section>
  );
}

export function FollowupSafetyIssuesModal({ issues = [], onCancel, onEditProfile }) {
  const panelRef = useRef(null);
  useModalA11y({ onClose: onCancel, panelRef });
  return (
    <div
      className="jpe-modal-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="followup-safety-issues-title"
      onClick={(event) => { if (event.target === event.currentTarget) onCancel?.(); }}
    >
      <div className="jpe-modal jrn-program-update-modal" ref={panelRef}>
        <header className="jpe-modal__header">
          <div>
            <p className="jrn-step-eyebrow">Anamnèse à relire</p>
            <h3 id="followup-safety-issues-title" className="jpe-modal__title">
              Complète la fiche avant de préparer la version
            </h3>
          </div>
          <button type="button" className="jrn-btn jrn-btn--ghost" onClick={onCancel} aria-label="Fermer">
            Fermer
          </button>
        </header>
        <div className="jpe-modal__body">
          <p className="jrn-step-intro" style={{ marginTop: 0 }}>
            Aucun brouillon n’a été créé. Relis chaque information ci-dessous,
            sauvegarde l’anamnèse, puis reprends la préparation avec la même portée.
          </p>
          <ul className="jrn-safety-preflight-list">
            {issues.map((issue) => (
              <li key={issue.field} className="jrn-safety-preflight-list__item">
                <strong>{issue.label}</strong>
                <span>{issue.section}</span>
                <small>{issue.message}</small>
              </li>
            ))}
          </ul>
        </div>
        <div className="jrn-program-update-modal__footer">
          <button type="button" className="jrn-btn jrn-btn--ghost" onClick={onCancel}>
            Plus tard
          </button>
          <button
            type="button"
            className="jrn-btn jrn-btn--primary"
            onClick={onEditProfile}
            disabled={!onEditProfile}
          >
            Ouvrir la fiche et corriger l’anamnèse
          </button>
        </div>
      </div>
    </div>
  );
}

function FollowupProgramUpdateModal({
  versionNumber,
  workflowAction,
  manual = false,
  initialSelection = null,
  busy,
  onCancel,
  onConfirm,
}) {
  const [selected, setSelected] = useState(initialSelection);
  const panelRef = useRef(null);
  useModalA11y({ onClose: onCancel, panelRef });
  return (
    <div
      className="jpe-modal-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="program-update-scope-title"
      onClick={(event) => { if (event.target === event.currentTarget && !busy) onCancel(); }}
    >
      <div className="jpe-modal jrn-program-update-modal" ref={panelRef}>
        <header className="jpe-modal__header">
          <div>
            <p className="jrn-step-eyebrow">Programme · V{versionNumber}</p>
            <h3 id="program-update-scope-title" className="jpe-modal__title">Que veux-tu mettre à jour ?</h3>
          </div>
          <button type="button" className="jrn-btn jrn-btn--ghost" onClick={onCancel} aria-label="Fermer">Fermer</button>
        </header>
        <div className="jpe-modal__body">
          <p className="jrn-step-intro" style={{ marginTop: 0 }}>
            La version actuellement visible reste intacte. La nouvelle version sera un brouillon à relire et ne sera publiée qu’après ton clic final.
            {manual && ' Commence par choisir ce que tu veux modifier.'}
          </p>
          <div className="jrn-program-update-scopes" role="radiogroup" aria-label="Portée de la mise à jour">
            {FOLLOWUP_PROGRAM_UPDATE_SCOPES.map((scope) => {
              const unavailable = !manual && scope.workflowAction !== workflowAction;
              const checked = selected === scope.value;
              return (
                <label
                  key={scope.value}
                  className={`jrn-program-update-scope ${checked ? 'jrn-program-update-scope--selected' : ''} ${unavailable ? 'jrn-program-update-scope--disabled' : ''}`}
                >
                  <input
                    type="radio"
                    name="program-update-scope"
                    value={scope.value}
                    checked={checked}
                    disabled={unavailable || busy}
                    onChange={() => setSelected(scope.value)}
                  />
                  <span>
                    <strong>{scope.title}</strong>
                    <small>{scope.description}</small>
                    {unavailable && (
                      <small className="jrn-program-update-scope__reason">
                        Non prévu par le dernier compte rendu relu.
                      </small>
                    )}
                  </span>
                </label>
              );
            })}
          </div>
        </div>
        <div className="jrn-program-update-modal__footer">
          <button type="button" className="jrn-btn jrn-btn--ghost" onClick={onCancel} disabled={busy}>
            Annuler
          </button>
          <button
            type="button"
            className="jrn-btn jrn-btn--primary"
            onClick={() => selected && onConfirm(selected)}
            disabled={busy || !selected}
          >
            {busy ? 'Préparation…' : `Préparer la V${versionNumber}`}
          </button>
        </div>
      </div>
    </div>
  );
}

function ReviewedAdaptationModal({
  workflowAction,
  value,
  confirmed,
  saving,
  error,
  onChange,
  onToggleConfirmation,
  onCancel,
  onConfirm,
}) {
  const panelRef = useRef(null);
  useModalA11y({ onClose: onCancel, busy: saving, panelRef });
  const title = workflowAction === 'newVersion'
    ? 'Relire la nouvelle version complète'
    : 'Relire l’adaptation proposée';
  return (
    <div
      className="jpe-modal-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="reviewed-adaptation-title"
      onClick={(event) => { if (event.target === event.currentTarget && !saving) onCancel(); }}
    >
      <div ref={panelRef} className="jpe-modal jpe-modal--xl">
        <header className="jpe-modal__header">
          <div>
            <p className="jrn-step-eyebrow">Brouillon en mémoire · non publié</p>
            <h3 id="reviewed-adaptation-title" className="jpe-modal__title">{title}</h3>
          </div>
          <button type="button" onClick={onCancel} disabled={saving} className="jrn-btn jrn-btn--ghost">
            Fermer sans sauvegarder
          </button>
        </header>
        <div className="jpe-modal__body">
          <p className="jrn-meeting-report__notice">
            Relis et modifie librement le plan complet. Aucune version n’existe encore dans le dossier et rien
            n’est envoyé à la cliente. Toute modification retire automatiquement ta confirmation.
          </p>
          <textarea
            className="jrn-textarea"
            rows={24}
            value={value}
            onChange={(event) => onChange(event.target.value)}
            disabled={saving}
            aria-label="Contenu complet de la proposition à relire"
            style={{ minHeight: '52vh', fontSize: 13, lineHeight: 1.55 }}
          />
          <label className="jrn-meeting-report__notice" style={{ display: 'flex', gap: 8, alignItems: 'flex-start', marginTop: 12 }}>
            <input
              type="checkbox"
              checked={confirmed}
              onChange={(event) => onToggleConfirmation(event.target.checked)}
              disabled={saving || !value.trim()}
            />
            <span>Je confirme avoir relu ce contenu exact et je souhaite créer une version à valider.</span>
          </label>
          {error && <div className="jrn-error" role="alert">{error}</div>}
          <div className="jrn-actions" style={{ marginTop: 18 }}>
            <button
              type="button"
              className="jrn-btn jrn-btn--primary"
              disabled={saving || !confirmed || !value.trim()}
              onClick={onConfirm}
            >
              {saving ? 'Création de la version…' : 'Créer la version à valider'}
            </button>
            <button type="button" className="jrn-btn jrn-btn--ghost" disabled={saving} onClick={onCancel}>
              Annuler
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// « Préparer le rendez-vous » — panneau briefing pré-consultation.
// Résume la période DEPUIS la dernière consultation : synthèse IA (brouillon
// éditable, jamais auto-appliquée), évolution du poids, tendances des ressentis
// par axe, commentaires de la cliente. Design sobre et fonctionnel (Fable
// embellira après) mais sections clairement séparées.
function ConsultationBriefingModal({
  clientId,
  prenom,
  feedbacks,
  weightEntries,
  consultationsLog,
  planPublishedAt,
  packStartedAt,
  nextRdvAt,
  // 2026-07-26 — notes d'appel résultats (journey_state.results_call_notes) :
  // la dernière remonte ici pour qu'Anissa retrouve ce qu'elle a expliqué.
  resultsCallNotes = [],
  syncError,
  syncStatus = 'loading',
  loading = true,
  safetySnapshot = null,
  safetySnapshotIssue = null,
  programme = null,
  cycleReviewContext = null,
  onClose,
  onContinue,
}) {
  const nowISO = useMemo(() => new Date().toISOString(), []);
  const sinceISO = useMemo(
    () => resolveBriefingSince({ consultationsLog, planPublishedAt, packStartedAt, now: nowISO }),
    [consultationsLog, planPublishedAt, packStartedAt, nowISO],
  );
  const data = useMemo(
    () => ({
      ...buildBriefingData({ feedbacks, weightEntries, sinceISO, nowISO }),
      programme,
    }),
    [feedbacks, weightEntries, sinceISO, nowISO, programme],
  );
  const programFacts = useMemo(() => buildProgramMeetingFacts(programme), [programme]);

  const enough = hasEnoughForBriefing(data);
  const [draft, setDraft] = useState(() => cycleReviewContext?.meetingSummary || '');
  const [questions, setQuestions] = useState([]);
  const [summarySource, setSummarySource] = useState('manual');
  const [manualQuestion, setManualQuestion] = useState('');
  const [genBusy, setGenBusy] = useState(false);
  const [genErr, setGenErr] = useState(null);
  const generationRequestRef = useRef(0);
  const panelRef = useRef(null);
  useModalA11y({ onClose, busy: genBusy, panelRef });

  const safetySnapshotFingerprint = useMemo(
    () => JSON.stringify(safetySnapshot),
    [safetySnapshot],
  );
  const requestDataFingerprint = useMemo(
    () => buildConsultationMeetingAuthorityFingerprint(clientId, data),
    [clientId, data],
  );
  const meetingAiSnapshotReady = isMeetingAiSnapshotReady(syncStatus, loading, safetySnapshot);
  const aiAuthorityFingerprint = `${syncStatus}:${loading === true}:${safetySnapshotFingerprint}:${requestDataFingerprint}`;
  const latestAiAuthorityRef = useRef(aiAuthorityFingerprint);
  latestAiAuthorityRef.current = aiAuthorityFingerprint;
  const previousAiAuthorityRef = useRef(aiAuthorityFingerprint);

  // Toute resynchronisation ou modification du snapshot sécurité invalide les éléments IA construits depuis le
  // snapshot précédent. Les questions saisies manuellement par Anissa restent.
  useEffect(() => {
    if (previousAiAuthorityRef.current === aiAuthorityFingerprint) return;
    previousAiAuthorityRef.current = aiAuthorityFingerprint;
    generationRequestRef.current += 1;
    setGenBusy(false);
    const next = invalidateGeneratedMeetingWorkspace({
      summary: draft,
      summarySource,
      questions,
    });
    setDraft(next.summary);
    setSummarySource(next.summarySource);
    setQuestions(next.questions);
    setGenErr(null);
  // L'effet ne doit se déclencher que lors d'un changement d'autorité du
  // snapshot. `draft/questions` sont volontairement lus à cet instant précis.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aiAuthorityFingerprint]);

  const handleGenerate = async () => {
    if (!meetingAiSnapshotReady) {
      setGenErr(
        syncStatus !== 'ok' || loading === true
          ? 'Attends la fin de la synchronisation des ressentis avant de relancer la préparation.'
          : 'Contexte maternel ou profil à risque — à valider Anissa. Prépare ce rendez-vous manuellement.',
      );
      return;
    }
    setGenBusy(true);
    setGenErr(null);
    const requestId = generationRequestRef.current + 1;
    generationRequestRef.current = requestId;
    const requestAuthority = aiAuthorityFingerprint;
    try {
      const proposal = await suggestConsultationMeeting({ ...data, safetySnapshot });
      if (generationRequestRef.current !== requestId || latestAiAuthorityRef.current !== requestAuthority) return;
      setDraft(proposal.summary);
      setSummarySource('ia');
      setQuestions((current) => [
        ...proposal.questions,
        ...current.filter((question) => question.source === 'anissa'),
      ].slice(0, MEETING_WORKSPACE_LIMITS.maxQuestions));
    } catch (e) {
      if (generationRequestRef.current !== requestId || latestAiAuthorityRef.current !== requestAuthority) return;
      setGenErr(e?.message || 'La préparation de la proposition a échoué.');
    } finally {
      if (generationRequestRef.current === requestId) setGenBusy(false);
    }
  };

  const toggleQuestion = (id) => {
    setQuestions((current) => current.map((question) => (
      question.id === id ? { ...question, included: !question.included } : question
    )));
  };

  const addManualQuestion = () => {
    if (questions.length >= MEETING_WORKSPACE_LIMITS.maxQuestions) return;
    const question = createManualMeetingQuestion(manualQuestion);
    if (!question) return;
    setQuestions((current) => [...current, question]);
    setManualQuestion('');
  };

  const selectedQuestionCount = questions.filter((question) => question.included === true).length;
  const questionLimitReached = questions.length >= MEETING_WORKSPACE_LIMITS.maxQuestions;
  const hasAiContent = summarySource === 'ia' || questions.some((question) => question.source === 'ia');
  const aiSnapshotUnavailable = !meetingAiSnapshotReady;
  const appFeedbackUnavailable = syncStatus === 'unavailable';
  const safetyAiUnavailable = !isMeetingAiSnapshotReady('ok', false, safetySnapshot);
  const workspaceReadiness = getMeetingWorkspaceReadiness({ summary: draft, questions });
  const staleAiContent = aiSnapshotUnavailable && hasAiContent;
  const continueToReport = () => {
    if (!workspaceReadiness.ready || staleAiContent) return;
    onContinue?.({
      summary: draft.trim(),
      periodLabel: data.periodLabel,
      questions,
    });
  };

  const sectionHead = (txt) => (
    <div className="jrn-brief-section__head">
      <span className="jrn-brief-section__label">{txt}</span>
      <span className="jrn-brief-section__rule" aria-hidden="true" />
    </div>
  );

  const singleWeighing = data.weight && data.weight.points.length === 1;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`Préparer le rendez-vous — ${prenom}`}
      onClick={onClose}
      className="jrn-brief-overlay"
    >
      <div ref={panelRef} onClick={(e) => e.stopPropagation()} className="jrn-brief-panel">
        {/* En-tête : le nom de la cliente porte le moment. */}
        <div className="jrn-brief__header">
          <div>
            <p className="jrn-brief__eyebrow">Préparer le rendez-vous</p>
            <h2 className="jrn-brief__title">{prenom}</h2>
            <p className="jrn-brief__meta">
              {data.periodLabel}
              {nextRdvAt && (
                <>
                  <span className="jrn-brief__meta-dot">·</span>
                  RDV <strong>{formatRdvLong(nextRdvAt)}</strong>
                </>
              )}
            </p>
          </div>
          <button className="jrn-btn jrn-btn--ghost" onClick={onClose} style={{ flexShrink: 0 }}>
            Fermer
          </button>
        </div>

        {/* Corps défilant */}
        <div className="jrn-brief__body">
          {(syncError || syncStatus === 'error') && (
            <div className="jrn-brief-warn">
              La dernière synchro des ressentis a échoué. Les anciennes propositions ont été retirées.
              Tu peux continuer avec une saisie entièrement manuelle ou réessayer la synchronisation.
            </div>
          )}
          {syncStatus === 'loading' && (
            <div className="jrn-brief-warn">
              Synchronisation des ressentis en cours. Les propositions restent verrouillées jusqu'à confirmation.
            </div>
          )}
          {appFeedbackUnavailable && (
            <div className="jrn-brief-warn">
              <strong>Suivi sans app.</strong>{' '}
              Aucun ressenti n’est synchronisé depuis une application cliente. Prépare le rendez-vous manuellement avec les éléments du dossier SaaS.
            </div>
          )}
          {safetyAiUnavailable && (
            <div className="jrn-brief-warn">
              <strong>À valider Anissa.</strong>{' '}
              {safetySnapshotIssue
                ? `${safetySnapshotIssue} La préparation manuelle reste disponible.`
                : 'Contexte maternel ou profil à risque : la préparation assistée reste fermée. La préparation manuelle reste disponible.'}
            </div>
          )}

          {cycleReviewContext?.current && (
            <div className="jrn-brief-section" style={{ '--brief-i': 0 }}>
              {sectionHead(`Bilan ${cycleReviewContext.currentLabel || 'de cycle'} relu`)}
              <ul className="jrn-meeting-program-facts">
                {cycleReviewContext.takeaways.map((item, index) => (
                  <li key={`cycle-review-takeaway-${index}`}>{item}</li>
                ))}
              </ul>
              <p className="jrn-brief-syn__hint">
                Ce bilan sert uniquement de contexte factuel. La décision persistante est choisie et confirmée dans le compte rendu du rendez-vous.
              </p>
            </div>
          )}

          {programFacts.length > 0 && (
            <div className="jrn-brief-section" style={{ '--brief-i': 0 }}>
              {sectionHead('Repères du programme utilisés')}
              <ul className="jrn-meeting-program-facts">
                {programFacts.map((fact) => <li key={fact.ref}>{fact.label}</li>)}
              </ul>
              <p className="jrn-brief-syn__hint">
                Seuls ces repères logistiques sont transmis : jamais le texte du plan, le nom d’une phase,
                une dose ou une recommandation.
              </p>
            </div>
          )}

          {/* ── Dernier appel résultats (2026-07-26) : ce qu'Anissa a
              expliqué au téléphone, pour reprendre le fil au RDV. ── */}
          {resultsCallNotes.length > 0 && (
            <div className="jrn-brief-section" style={{ '--brief-i': 0 }}>
              {sectionHead('Dernier appel résultats')}
              <p style={{ margin: 0, fontSize: '.76rem', color: 'rgba(255,255,255,.5)' }}>
                {(() => {
                  const last = resultsCallNotes[resultsCallNotes.length - 1];
                  let when = last.date;
                  try {
                    when = new Date(last.date).toLocaleDateString('fr-CH', { day: 'numeric', month: 'long' });
                  } catch { /* date brute */ }
                  return `${when} — `;
                })()}
                <span style={{ color: 'rgba(255,255,255,.78)', whiteSpace: 'pre-wrap' }}>
                  {resultsCallNotes[resultsCallNotes.length - 1].text}
                </span>
              </p>
            </div>
          )}

          {/* ── Synthèse IA (brouillon éditable) ── */}
          <div className="jrn-brief-section" style={{ '--brief-i': 0 }}>
            {sectionHead('Synthèse')}
            <div className="jrn-brief-syn">
              <p className="jrn-brief-syn__hint">
                Le SaaS prépare une synthèse factuelle à partir de références structurées et de
                questions neutres. Aucun conseil ni aucune interprétation n’est ajouté. Les
                commentaires libres restent dans le SaaS.
              </p>
              <button
                className="jrn-btn jrn-btn--soft"
                onClick={handleGenerate}
                disabled={genBusy || !enough || aiSnapshotUnavailable}
                title={aiSnapshotUnavailable
                  ? (appFeedbackUnavailable
                    ? 'Suivi sans app : la préparation manuelle reste disponible.'
                    : safetyAiUnavailable
                    ? 'Contexte de sécurité à valider par Anissa. Préparation manuelle disponible.'
                    : 'La synchronisation doit réussir avant de demander une proposition.')
                  : (!enough ? "Pas encore assez d'éléments sur la période." : 'Proposer une synthèse et des questions.')}
              >
                {genBusy ? 'Préparation…' : 'Proposer des questions'}
              </button>
              {genErr && (
                <div className="jrn-brief-syn__error">{genErr}</div>
              )}
              <textarea
                className="jrn-brief-syn__textarea"
                value={draft}
                onChange={(e) => {
                  setDraft(e.target.value);
                  if (!e.target.value.trim()) setSummarySource('manual');
                }}
                placeholder="La synthèse proposée s'affiche ici. Tu peux aussi l'écrire toi-même."
                rows={6}
                maxLength={1600}
              />
            </div>
          </div>

          <div className="jrn-brief-section" style={{ '--brief-i': 1 }}>
            {sectionHead('Questions du rendez-vous')}
            <div className="jrn-meeting-questions">
              <p className="jrn-brief-syn__hint">
                Les suggestions sont décochées par défaut. Anissa choisit les questions utiles ;
                aucune question ni réponse ne modifie le plan automatiquement.
              </p>
              {questions.length > 0 ? (
                <div className="jrn-meeting-questions__list">
                  {questions.map((question) => (
                    <label
                      key={question.id}
                      className={`jrn-meeting-question${question.included === true ? ' is-selected' : ''}`}
                    >
                      <input
                        type="checkbox"
                        checked={question.included === true}
                        onChange={() => toggleQuestion(question.id)}
                      />
                      <span>
                        <strong>{question.text}</strong>
                        <small>{question.evidence}</small>
                      </span>
                    </label>
                  ))}
                </div>
              ) : (
                <div className="jrn-brief-empty">
                  Préparez des questions ou ajoutez-en une manuellement.
                </div>
              )}
              <div className="jrn-meeting-questions__add">
                <input
                  type="text"
                  value={manualQuestion}
                  onChange={(event) => setManualQuestion(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      event.preventDefault();
                      addManualQuestion();
                    }
                  }}
                  placeholder="Ajouter une question d'Anissa…"
                  maxLength={320}
                />
                <button
                  type="button"
                  className="jrn-btn jrn-btn--ghost"
                  onClick={addManualQuestion}
                  disabled={!manualQuestion.trim() || questionLimitReached}
                  title={questionLimitReached ? `Maximum ${MEETING_WORKSPACE_LIMITS.maxQuestions} questions` : undefined}
                >
                  Ajouter
                </button>
              </div>
            </div>
          </div>

          {/* ── Évolution du poids ── */}
          <div className="jrn-brief-section" style={{ '--brief-i': 1 }}>
            {sectionHead('Évolution du poids')}
            {data.weight ? (
              <div className="jrn-brief-weight">
                <div className="jrn-brief-weight__row">
                  <span className="jrn-brief-weight__value">
                    {data.weight.start}
                    <span className="jrn-brief-weight__unit">kg</span>
                  </span>
                  {!singleWeighing && (
                    <>
                      <span className="jrn-brief-weight__arrow" aria-hidden="true">→</span>
                      <span className="jrn-brief-weight__value">
                        {data.weight.end}
                        <span className="jrn-brief-weight__unit">kg</span>
                      </span>
                      {/* Chip NEUTRE quel que soit le signe : « perdre = bien » est un
                          jugement clinique qui appartient à Anissa, jamais codé en dur
                          (dangereux pour grossesse/TCA/dénutrition). Le signe +/- suffit. */}
                      <span className="jrn-brief-weight__delta">
                        {data.weight.deltaKg > 0 ? '+' : ''}{data.weight.deltaKg} kg
                      </span>
                    </>
                  )}
                </div>
                {data.weight.points.length > 2 && (
                  <WeightSparkline points={data.weight.points} />
                )}
                <div className="jrn-brief-weight__meta">
                  <span>
                    {data.weight.points.length} pesée{data.weight.points.length > 1 ? 's' : ''} sur la période
                  </span>
                  {!singleWeighing && (
                    <span>
                      {formatBriefingDay(data.weight.points[0].date)}
                      {' — '}
                      {formatBriefingDay(data.weight.points[data.weight.points.length - 1].date)}
                    </span>
                  )}
                </div>
              </div>
            ) : (
              <div className="jrn-brief-empty">Aucune pesée sur la période.</div>
            )}
          </div>

          {/* ── Tendances des ressentis ── */}
          <div className="jrn-brief-section" style={{ '--brief-i': 2 }}>
            {sectionHead('Tendances des ressentis')}
            {data.trends.length ? (
              <div className="jrn-brief-trends">
                {data.trends.map((t) => (
                  <div key={t.axis} className="jrn-brief-trend">
                    <span className="jrn-brief-trend__label">{t.label}</span>
                    {/* Part de la dominante — un compte, pas un verdict :
                        teinte constante, aucune valence. */}
                    <span className="jrn-brief-trend__bar" aria-hidden="true">
                      <span
                        className="jrn-brief-trend__bar-fill"
                        style={{ width: `${t.total > 0 ? Math.round((t.count / t.total) * 100) : 0}%` }}
                      />
                    </span>
                    <span className="jrn-brief-trend__value">
                      <span className="jrn-brief-trend__dominant">{t.dominant}</span>
                      <span className="jrn-brief-trend__count">{t.count}/{t.total}</span>
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="jrn-brief-empty">Aucun ressenti sur la période.</div>
            )}
          </div>

          {/* ── Commentaires de la cliente — ses mots, en citation ── */}
          <div className="jrn-brief-section" style={{ '--brief-i': 3 }}>
            {sectionHead(`Commentaires de ${prenom}`)}
            {data.comments.length ? (
              <div className="jrn-brief-quotes">
                {data.comments.map((c, i) => (
                  <figure key={i} className="jrn-brief-quote" style={{ margin: 0 }}>
                    <p className="jrn-brief-quote__date">
                      {c.date ? formatBriefingDay(c.date) : ''}
                    </p>
                    <blockquote className="jrn-brief-quote__text" style={{ margin: 0 }}>
                      {c.text}
                    </blockquote>
                  </figure>
                ))}
              </div>
            ) : (
              <div className="jrn-brief-empty">Aucun commentaire libre sur la période.</div>
            )}
          </div>

          <div className="jrn-meeting-footer">
            <div>
              <strong>
                {workspaceReadiness.ready
                  ? `${selectedQuestionCount} question${selectedQuestionCount > 1 ? 's' : ''} retenue${selectedQuestionCount > 1 ? 's' : ''}`
                  : 'Compte rendu à préparer'}
              </strong>
              <span>
                {workspaceReadiness.issue || 'La synthèse et les réponses restent dans le dossier professionnel.'}
              </span>
            </div>
            <button
              type="button"
              className="jrn-btn jrn-btn--primary"
              onClick={continueToReport}
              disabled={genBusy || staleAiContent || !workspaceReadiness.ready}
              title={staleAiContent
                ? 'La proposition précédente a été invalidée. Utilise une saisie manuelle ou attends la synchronisation.'
                : (workspaceReadiness.issue || undefined)}
            >
              Passer au compte rendu
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Date courte fr-CH pour le briefing (« 8 juillet »). Présentation pure.
function formatBriefingDay(iso) {
  const d = iso ? new Date(iso) : null;
  if (!d || Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('fr-CH', { day: 'numeric', month: 'long' });
}

// Sparkline poids du briefing — présentation pure, dérivée de
// data.weight.points (aucune logique métier).
// GARDE CLINIQUE : trait encre UNIQUE et neutre, pas de zone verte/rouge,
// pas d'objectif, pas de flèche — la lecture du poids appartient à Anissa.
function WeightSparkline({ points }) {
  const geometry = useMemo(() => {
    if (!Array.isArray(points) || points.length < 2) return null;
    const valid = points
      .map((p) => ({ t: new Date(p.date).getTime(), w: Number(p.weight_kg) }))
      .filter((p) => Number.isFinite(p.w));
    if (valid.length < 2) return null;

    const W = 100;
    const H = 32;
    const PAD = 3;

    // Axe X proportionnel au temps si les dates sont exploitables,
    // sinon répartition uniforme (même approche que FeedbacksTrendChart).
    const times = valid.map((p) => p.t);
    const timeOK = times.every((t) => Number.isFinite(t)) && Math.max(...times) > Math.min(...times);
    const tMin = timeOK ? Math.min(...times) : 0;
    const tSpan = timeOK ? Math.max(...times) - tMin : 1;

    const ws = valid.map((p) => p.w);
    const wMin = Math.min(...ws);
    const wSpan = Math.max(...ws) - wMin || 1;

    const coords = valid.map((p, i) => {
      const xr = timeOK ? (p.t - tMin) / tSpan : (valid.length > 1 ? i / (valid.length - 1) : 0);
      return {
        x: PAD + xr * (W - 2 * PAD),
        y: PAD + (1 - (p.w - wMin) / wSpan) * (H - 2 * PAD),
      };
    });

    return {
      W,
      H,
      path: coords.map((c, i) => `${i === 0 ? 'M' : 'L'}${c.x.toFixed(2)},${c.y.toFixed(2)}`).join(' '),
      first: coords[0],
      last: coords[coords.length - 1],
    };
  }, [points]);

  if (!geometry) return null;

  return (
    <svg
      className="jrn-brief-weight__spark"
      viewBox={`0 0 ${geometry.W} ${geometry.H}`}
      preserveAspectRatio="none"
      aria-hidden="true"
    >
      <path
        d={geometry.path}
        fill="none"
        stroke="var(--jrn-text-soft, #2E2C28)"
        strokeWidth="1.4"
        strokeLinejoin="round"
        strokeLinecap="round"
      />
      <circle cx={geometry.first.x} cy={geometry.first.y} r="2" fill="var(--jrn-text-faint, #6E695F)" />
      <circle cx={geometry.last.x} cy={geometry.last.y} r="2.2" fill="var(--jrn-text, #1A1A18)" />
    </svg>
  );
}

// V97.28 — Modal plein écran qui héberge JourneyPlanEditor sans changer
// current_step. Sépare l'édition technique du protocole de la timeline parcours.
function InlineEditorModal({
  client,
  existingConsultationTarget,
  initialTab = 'plan',
  updateScope = null,
  onBeforePersist,
  onClose,
  onPlanSaved,
  savedConsultation = null,
  canPrepareClientApp = false,
  onPrepareClientApp,
}) {
  const prenom = (client?.form?.prenom || client?.prenom || 'la cliente').trim();
  const panelRef = useRef(null);
  const [preparingClientApp, setPreparingClientApp] = useState(false);
  const [clientAppError, setClientAppError] = useState(null);
  const [editorSaveState, setEditorSaveState] = useState('loading');
  const editorVersionReady = ['idle', 'saved'].includes(editorSaveState);
  useModalA11y({ onClose, panelRef });
  const prepareClientApp = async () => {
    if (!savedConsultation?.id || typeof onPrepareClientApp !== 'function') return;
    setPreparingClientApp(true);
    setClientAppError(null);
    try {
      await onPrepareClientApp();
    } catch (error) {
      setClientAppError(error?.message || 'La préparation de la mise à jour de l’app a échoué.');
    } finally {
      setPreparingClientApp(false);
    }
  };
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`Édition manuelle du protocole — ${prenom}`}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(20, 24, 22, 0.55)',
        backdropFilter: 'blur(4px)',
        zIndex: 9000,
        display: 'flex',
        flexDirection: 'column',
        padding: '4vh 4vw',
      }}
    >
      <div ref={panelRef} style={{
        background: 'var(--jrn-bg, #FAF9F6)',
        borderRadius: 12,
        boxShadow: '0 24px 60px rgba(0,0,0,.25)',
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        overflow: 'hidden',
      }}>
        {/* Header */}
        <div style={{
          padding: '14px 20px',
          borderBottom: '1px solid var(--jrn-border, #EFEDE5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 12,
          background: 'white',
        }}>
          <div>
            <p style={{ margin: 0, fontSize: 10.5, fontWeight: 700, letterSpacing: '.12em', textTransform: 'uppercase', color: 'var(--jrn-text-soft, #6b6f6b)' }}>
              Cycle de suivi · Édition manuelle
            </p>
            <h2 style={{ margin: '2px 0 0', fontSize: 18, fontWeight: 700, color: 'var(--jrn-ink, #1A2E1F)' }}>
              Protocole — {prenom}
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="jrn-btn jrn-btn--ghost"
            title="Fermer (l'étape de suivi reste active)"
          >
            Fermer
          </button>
        </div>
        {/* Body : JourneyPlanEditor scrollable */}
        <div style={{ flex: 1, overflow: 'auto', padding: '16px 20px' }}>
          <JourneyPlanEditor
            client={client}
            existingConsultationTarget={existingConsultationTarget}
            initialTab={initialTab}
            updateScope={updateScope}
            onBeforePersist={onBeforePersist}
            onPlanSaved={onPlanSaved}
            onSaveStateChange={setEditorSaveState}
          />
        </div>
        {/* Footer info */}
        <div style={{
          padding: '10px 20px',
          borderTop: '1px solid var(--jrn-border, #EFEDE5)',
          background: 'rgba(46, 78, 56, 0.04)',
          fontSize: 11.5,
          color: 'var(--jrn-text-soft, #6b6f6b)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 12,
          flexWrap: 'wrap',
        }}>
          <div>
            <strong style={{ color: 'var(--jrn-ink, #1A2E1F)' }}>
              {savedConsultation?.id && editorVersionReady
                ? 'Version enregistrée.'
                : 'Enregistrement de la version en attente.'}
            </strong>{' '}
            Aucune publication n’est automatique.
            {clientAppError && <div className="jrn-error" role="alert" style={{ marginTop: 6 }}>{clientAppError}</div>}
          </div>
          {canPrepareClientApp && savedConsultation?.id && (
            <button
              type="button"
              className="jrn-btn jrn-btn--primary"
              onClick={prepareClientApp}
              disabled={preparingClientApp || !editorVersionReady}
              title={!editorVersionReady ? 'Attends la fin de l’enregistrement du plan.' : undefined}
            >
              {preparingClientApp ? 'Ouverture…' : 'Préparer la mise à jour de l’app →'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// Section : Suivi du poids (étape 8 cockpit)
// ═══════════════════════════════════════════════════════════════════

// Composant factorisé : toggles tracking + visible (utilisé étape 7 ET étape 8)
// Source de vérité unique : clientAppConfig (API admin /api/admin/client-config)

/**
 * Une option indisponible parce que l'espace cliente n'existe pas encore n'est
 * pas une erreur : c'est l'ordre normal des choses. On ne l'affiche donc pas
 * en rouge, sinon Anissa croit que quelque chose est cassé.
 */
function MessageOptionCliente({ message }) {
  const attentePublication = /espace cliente n’existe pas encore/i.test(String(message || ''));
  if (attentePublication) {
    return (
      <div className="jrn-inline-card__hint" style={{ marginTop: 6, opacity: 0.9 }}>
        Disponible après la publication du programme.
      </div>
    );
  }
  return <div className="jrn-error" style={{ marginTop: 6 }}>⚠ {message}</div>;
}

function useClientWeightConfig(client) {
  const [config, setConfig] = useState(null);
  const [loadingCfg, setLoadingCfg] = useState(true);
  const [updating, setUpdating] = useState(null);
  const [cfgError, setCfgError] = useState(null);
  const [espaceCliente, setEspaceCliente] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { fetchClientAppConfig } = await import('./services/clientAppConfig');
        const res = await fetchClientAppConfig(client);
        if (cancelled) return;
        // Tant que la cliente n'a pas d'espace, les interrupteurs pilotent le
        // choix retenu dans le SaaS, appliqué à la publication.
        setEspaceCliente(!!res?.found);
        setConfig(res?.found
          ? (res.config || { weight_tracking_enabled: false, weight_visible_to_client: false })
          : readClientAppOptions(client));
      } catch (e) {
        if (!cancelled) {
          setEspaceCliente(false);
          setConfig(readClientAppOptions(client));
        }
      } finally {
        if (!cancelled) setLoadingCfg(false);
      }
    })();
    return () => { cancelled = true; };
  }, [client?.id]);

  const toggleConfig = async (key) => {
    setUpdating(key === 'weight_tracking_enabled' ? 'tracking' : key === 'meal_photos_enabled' ? 'photos' : 'visible');
    setCfgError(null);
    try {
      if (!espaceCliente) {
        const suivant = withClientAppOption(client, key, !config?.[key]);
        await saveClient(suivant);
        setConfig(readClientAppOptions(suivant));
        return;
      }
      const { updateClientAppConfig } = await import('./services/clientAppConfig');
      const updates = { [key]: !config?.[key] };
      if (key === 'weight_tracking_enabled' && config?.[key]) {
        updates.weight_visible_to_client = false;
      }
      const next = await updateClientAppConfig(client, updates);
      setConfig(next);
    } catch (e) {
      setCfgError(e?.message || 'Erreur mise à jour');
    } finally {
      setUpdating(null);
    }
  };

  // V97.13.37 : action atomique pour activer/desactiver les 2 settings ensemble
  // (cas typique etape 7 Activation cliente : un seul toggle = simplicite UX).
  const toggleBothWeight = async () => {
    setUpdating('both');
    setCfgError(null);
    try {
      const bothOn = !!config?.weight_tracking_enabled && !!config?.weight_visible_to_client;
      if (!espaceCliente) {
        const suivant = withBothWeightOptions(client, !bothOn);
        await saveClient(suivant);
        setConfig(readClientAppOptions(suivant));
        return;
      }
      const { updateClientAppConfig } = await import('./services/clientAppConfig');
      const updates = bothOn
        ? { weight_tracking_enabled: false, weight_visible_to_client: false }
        : { weight_tracking_enabled: true, weight_visible_to_client: true };
      const next = await updateClientAppConfig(client, updates);
      setConfig(next);
    } catch (e) {
      setCfgError(e?.message || 'Erreur mise à jour');
    } finally {
      setUpdating(null);
    }
  };

  return { config, loadingCfg, updating, cfgError, espaceCliente, toggleConfig, toggleBothWeight };
}

function WeightTogglesInline({ client, compact = false }) {
  const { config, loadingCfg, updating, cfgError, espaceCliente, toggleBothWeight, toggleConfig } = useClientWeightConfig(client);
  if (loadingCfg) return null;
  const trackingEnabled = !!config?.weight_tracking_enabled;
  const visibleToClient = !!config?.weight_visible_to_client;

  // V97.13.37 : mode compact (etape 7 Activation cliente) = 1 SEUL toggle qui
  // active les 2 settings ensemble (tracking + visible cliente). Cas typique :
  // Anissa veut activer le suivi du poids ET autoriser Camille a saisir. Si
  // elle a besoin du cas avance (tracking sans saisie cliente), elle passe par
  // le drawer ⚙ Configurer de l'etape 8 (mode non-compact ci-dessous).
  if (compact) {
    const bothOn = trackingEnabled && visibleToClient;
    return (
      <>
        <ToggleChip
          label="Suivi du poids"
          checked={bothOn}
          onClick={toggleBothWeight}
          loading={updating === 'both'}
          title="Active le suivi du poids et autorise la cliente a saisir sa pesee dans l'app"
        />
        {!espaceCliente && (
          <div className="jrn-inline-card__hint" style={{ marginTop: 6 }}>
            {bothOn
              ? 'Sera activé à la publication.'
              : 'Tu peux le régler dès maintenant, il sera appliqué à la publication.'}
          </div>
        )}
        {cfgError && <MessageOptionCliente message={cfgError} />}
      </>
    );
  }

  // Mode complet (drawer ⚙ Configurer étape 8) : 2 toggles granulaires pour
  // les cas avancés (suivi privé coach sans saisie cliente, etc.).
  return (
    <>
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
        <ToggleChip
          label="Suivi activé"
          checked={trackingEnabled}
          onClick={() => toggleConfig('weight_tracking_enabled')}
          loading={updating === 'tracking'}
          title="Active le suivi du poids pour cette cliente"
        />
        {trackingEnabled && (
          <ToggleChip
            label="Camille saisit dans l'app"
            checked={visibleToClient}
            onClick={() => toggleConfig('weight_visible_to_client')}
            loading={updating === 'visible'}
            title="Affiche le champ poids dans l'app cliente (sinon Anissa saisit en consultation)"
          />
        )}
      </div>
      {cfgError && <MessageOptionCliente message={cfgError} />}
    </>
  );
}

// 2026-07-21 — Toggle « Photos de repas » (opt-in par cliente). Réutilise le
// hook de config app (toggleConfig est générique) et le style ToggleChip du
// poids. OFF par défaut : l'activation est un jugement d'Anissa par profil.
function MealPhotosToggleInline({ client }) {
  const { config, loadingCfg, updating, cfgError, espaceCliente, toggleConfig } = useClientWeightConfig(client);
  if (loadingCfg) return null;
  const enabled = !!config?.meal_photos_enabled;
  return (
    <>
      <ToggleChip
        label="Photos de repas"
        checked={enabled}
        onClick={() => toggleConfig('meal_photos_enabled')}
        loading={updating === 'photos'}
        title="La cliente peut photographier ses repas depuis son app — visibles dans ton flux Suivi"
      />
      {!espaceCliente && (
        <div className="jrn-inline-card__hint" style={{ marginTop: 6 }}>
          {enabled
            ? 'Sera activé à la publication.'
            : 'Tu peux le régler dès maintenant, il sera appliqué à la publication.'}
        </div>
      )}
      {cfgError && <MessageOptionCliente message={cfgError} />}
    </>
  );
}

function WeightTrackingSection({ client, entries, loading, syncError }) {
  // Hook factorisé pour la section étape 8 cockpit
  const { config, loadingCfg } = useClientWeightConfig(client);
  const trackingEnabled = !!config?.weight_tracking_enabled;
  const visibleToClient = !!config?.weight_visible_to_client;

  // Si tracking off ET pas de pesées historiques, on affiche quand même (pour permettre activation)
  const last = entries[0] || null;
  const oldest = entries[entries.length - 1] || null;
  const delta = last && oldest && entries.length > 1
    ? Number((last.weight_kg - oldest.weight_kg).toFixed(1))
    : null;

  // V97.13.20 — densité progressive : si 0 pesée + tracking désactivé,
  // le bloc se replie en 1 ligne avec lien 'Configurer'.
  const isFullyEmpty = !loading && !loadingCfg && entries.length === 0 && !trackingEnabled;
  const [showConfig, setShowConfig] = useState(false);

  if (isFullyEmpty && !showConfig) {
    return (
      <p className="jrn-cockpit-empty-row">
        Aucune pesée — suivi du poids inactif ·{' '}
        <button
          type="button"
          onClick={() => setShowConfig(true)}
          className="jrn-cockpit-empty-row__link"
        >
          Configurer
        </button>
      </p>
    );
  }

  return (
    <div style={{ marginBottom: 'var(--jrn-6)' }}>
      {/* V97.13.21 : toggles config sortis du flux clinique principal.
          Bouton '⚙ Configurer' discret en haut à droite, drawer inline si cliqué.
          V97.17.2 : badge etat ACTIVE/INACTIF clair (feedback Anissa : on ne
          voyait pas l'etat sur la card).
          V97.17.6.3 : titre interne 'Suivi du poids' supprime — le titre
          externe de la card (h3.jrn-block__title) le contient deja. Plus de
          doublon visuel. */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--jrn-3)', flexWrap: 'wrap', gap: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          {!loadingCfg && (
            <span style={{
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: '.08em',
              textTransform: 'uppercase',
              padding: '3px 8px',
              borderRadius: 999,
              background: trackingEnabled
                ? 'rgba(26, 46, 31, 0.08)'
                : 'rgba(120, 120, 120, 0.08)',
              color: trackingEnabled ? '#1A2E1F' : '#8a8a8a',
              border: trackingEnabled
                ? '1px solid rgba(26, 46, 31, 0.25)'
                : '1px solid rgba(120, 120, 120, 0.20)',
            }}>
              {trackingEnabled
                ? (visibleToClient ? '✓ Activé · cliente saisit' : '✓ Activé · mode coach')
                : '○ Désactivé'}
            </span>
          )}
        </div>
        <button
          type="button"
          onClick={() => setShowConfig(!showConfig)}
          className="jrn-cockpit-config-btn"
          title="Configurer le suivi du poids"
        >
          ⚙ Configurer
        </button>
      </div>

      {showConfig && (
        <div className="jrn-cockpit-config-drawer">
          <WeightTogglesInline client={client} />
        </div>
      )}

      {(loading || loadingCfg) && (
        <p className="jrn-cockpit-empty-row">Chargement…</p>
      )}

      {/* P2.1 — sous panne synchro, on n'affirme pas « en attente des pesées »
          (on ne sait pas) : état distinct de l'absence réelle. */}
      {!loading && !loadingCfg && syncError && trackingEnabled && (
        <div className="jrn-surface" style={{ padding: 'var(--jrn-5)', textAlign: 'center' }}>
          <p style={{ margin: 0, fontSize: 12, color: '#785a1a', fontStyle: 'italic' }}>
            Pesées indisponibles — synchro app cliente échouée. Donnée non récupérée (≠ absence de pesée).
          </p>
        </div>
      )}

      {/* V97.17.2 — placeholder graphique si activé sans data (au lieu d'une
          ligne de texte sobre). Permet a Anissa de visualiser l'absence de
          courbe en plus du badge etat ci-dessus. */}
      {!loading && !loadingCfg && !syncError && entries.length === 0 && trackingEnabled && (
        <div className="jrn-surface" style={{ padding: 'var(--jrn-5)', textAlign: 'center' }}>
          <svg
            width="100%"
            height="60"
            viewBox="0 0 200 60"
            preserveAspectRatio="none"
            style={{ display: 'block', opacity: 0.35 }}
            aria-hidden="true"
          >
            <line x1="0" y1="50" x2="200" y2="50" stroke="rgba(26,46,31,.2)" strokeWidth="1" />
            <line
              x1="0" y1="30" x2="200" y2="30"
              stroke="rgba(26,46,31,.4)"
              strokeWidth="1.5"
              strokeDasharray="4 4"
            />
            <circle cx="10" cy="30" r="3" fill="rgba(26,46,31,.4)" />
            <circle cx="100" cy="30" r="3" fill="rgba(26,46,31,.4)" />
            <circle cx="190" cy="30" r="3" fill="rgba(26,46,31,.4)" />
          </svg>
          <p style={{ marginTop: 8, fontSize: 12, color: 'var(--jrn-text-muted)', fontStyle: 'italic' }}>
            {visibleToClient
              ? 'En attente des premières pesées de la cliente depuis l\'app.'
              : 'Mode coach · aucune pesée enregistrée pour l\'instant.'}
          </p>
        </div>
      )}

      {/* V97.17.2 — placeholder "tracking desactive" plus visuel que la ligne
          de texte precedente (qui etait dans le cas isFullyEmpty plus haut). */}
      {!loading && !loadingCfg && entries.length === 0 && !trackingEnabled && (
        <div className="jrn-surface" style={{ padding: 'var(--jrn-5)' }}>
          <p style={{ margin: 0, fontSize: 12, color: 'var(--jrn-text-muted)' }}>
            Le suivi du poids est désactivé pour cette cliente. Active-le via le bouton ⚙ Configurer ci-dessus.
          </p>
        </div>
      )}

      {!loading && entries.length > 0 && (
        <div className="jrn-surface" style={{ padding: 'var(--jrn-5)' }}>
          {/* Header : dernière valeur + delta */}
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 16, flexWrap: 'wrap', marginBottom: 'var(--jrn-4)' }}>
            <div>
              <div style={{ fontFamily: 'var(--jrn-font-display)', fontStyle: 'italic', fontSize: 36, fontWeight: 600, color: 'var(--jrn-text)', lineHeight: 1 }}>
                {last.weight_kg.toFixed(1)} <span style={{ fontSize: 18, color: 'var(--jrn-text-muted)', fontStyle: 'normal', fontWeight: 400 }}>kg</span>
              </div>
              <div style={{ fontSize: 11, color: 'var(--jrn-text-muted)', marginTop: 4, textTransform: 'uppercase', letterSpacing: '.04em' }}>
                Dernière pesée — {last.date ? new Date(last.date).toLocaleDateString('fr-CH', { day: '2-digit', month: 'long' }) : ''}
              </div>
            </div>
            {delta !== null && (
              <div style={{
                padding: '6px 12px',
                borderRadius: 999,
                background: delta > 0 ? 'rgba(184, 134, 38, 0.10)' : delta < 0 ? 'var(--jrn-accent-soft)' : 'rgba(0,0,0,0.04)',
                color: delta > 0 ? '#8a6722' : delta < 0 ? 'var(--jrn-accent)' : 'var(--jrn-text-muted)',
                fontSize: 12,
                fontWeight: 600,
              }}>
                {delta > 0 ? '↗' : delta < 0 ? '↘' : '→'} {delta > 0 ? '+' : ''}{delta} kg
                <span style={{ marginLeft: 6, fontWeight: 400, opacity: 0.8 }}>
                  sur {entries.length > 1 ? `${entries.length} pesées` : '1 pesée'}
                </span>
              </div>
            )}
          </div>

          {/* Sparkline SVG simple */}
          {entries.length >= 2 && (
            <Sparkline entries={entries} />
          )}

          {/* Liste des 6 dernières pesées */}
          <div style={{ marginTop: 'var(--jrn-4)' }}>
            <div style={{ fontSize: 10, color: 'var(--jrn-text-muted)', textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 8 }}>
              Historique récent
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(110px, 1fr))', gap: 8 }}>
              {entries.slice(0, 6).map((e, i) => (
                <div key={i} style={{
                  padding: '8px 10px',
                  background: 'var(--jrn-surface-alt)',
                  border: '1px solid var(--jrn-border)',
                  borderRadius: 6,
                  fontSize: 12,
                }}>
                  <div style={{ color: 'var(--jrn-text-muted)', fontSize: 10, textTransform: 'uppercase', letterSpacing: '.04em' }}>
                    {e.date ? new Date(e.date).toLocaleDateString('fr-CH', { day: '2-digit', month: 'short' }) : ''}
                  </div>
                  <div style={{ color: 'var(--jrn-text)', fontWeight: 500, marginTop: 2 }}>
                    {e.weight_kg.toFixed(1)} kg
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// AR.2 : switch iOS-like premium (rail + bouton glissant), au lieu d'une
// chip avec checkmark. Plus reconnaissable visuellement comme "toggle".
function ToggleChip({ label, checked, onClick, loading, title }) {
  return (
    <button
      onClick={onClick}
      disabled={loading}
      title={title}
      style={{
        fontFamily: 'var(--jrn-font-ui)',
        fontSize: 12,
        fontWeight: 500,
        padding: '6px 12px 6px 8px',
        borderRadius: 999,
        border: '1px solid transparent',
        background: 'transparent',
        color: checked ? 'var(--jrn-text)' : 'var(--jrn-text-muted)',
        cursor: loading ? 'wait' : 'pointer',
        display: 'inline-flex',
        alignItems: 'center',
        gap: 10,
        whiteSpace: 'nowrap',
        opacity: loading ? 0.6 : 1,
        transition: 'all 160ms var(--jrn-ease)',
      }}
    >
      {/* Switch iOS-like : rail 36×20 + thumb 16×16 qui glisse */}
      <span style={{
        position: 'relative',
        width: 34,
        height: 20,
        borderRadius: 999,
        background: checked ? 'var(--jrn-accent)' : 'rgba(40, 32, 20, 0.18)',
        boxShadow: checked
          ? 'inset 0 1px 2px rgba(15, 25, 18, 0.2)'
          : 'inset 0 1px 2px rgba(40, 32, 20, 0.1)',
        transition: 'background 160ms var(--jrn-ease), box-shadow 160ms var(--jrn-ease)',
        flexShrink: 0,
      }}>
        <span style={{
          position: 'absolute',
          top: 2,
          left: checked ? 16 : 2,
          width: 16,
          height: 16,
          borderRadius: '50%',
          background: '#fff',
          boxShadow: '0 1px 2px rgba(0,0,0,0.2), 0 1px 4px rgba(0,0,0,0.1)',
          transition: 'left 200ms var(--jrn-ease)',
        }} />
      </span>
      {label}
    </button>
  );
}

function Sparkline({ entries }) {
  // Tri chronologique ascendant pour le graphique
  const sorted = [...entries].sort((a, b) => new Date(a.date) - new Date(b.date));
  const w = 600;
  const h = 80;
  const pad = 8;
  const values = sorted.map((e) => e.weight_kg);
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min || 1;
  const stepX = (w - 2 * pad) / Math.max(1, sorted.length - 1);

  const points = sorted.map((e, i) => {
    const x = pad + i * stepX;
    const y = h - pad - ((e.weight_kg - min) / range) * (h - 2 * pad);
    return `${x},${y}`;
  }).join(' ');

  const areaPath = `M ${pad},${h - pad} L ${points.split(' ').join(' L ')} L ${w - pad},${h - pad} Z`;

  return (
    <div style={{ marginTop: 'var(--jrn-3)' }}>
      <svg viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" style={{ width: '100%', height: 80, display: 'block' }}>
        <path d={areaPath} fill="var(--jrn-accent-soft)" />
        <polyline points={points} fill="none" stroke="var(--jrn-accent)" strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
        {sorted.map((e, i) => {
          const x = pad + i * stepX;
          const y = h - pad - ((e.weight_kg - min) / range) * (h - 2 * pad);
          return <circle key={i} cx={x} cy={y} r="2.5" fill="var(--jrn-accent)" />;
        })}
      </svg>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: 'var(--jrn-text-muted)', marginTop: 4 }}>
        <span>{sorted[0]?.date ? new Date(sorted[0].date).toLocaleDateString('fr-CH', { day: '2-digit', month: 'short' }) : ''}</span>
        <span style={{ color: 'var(--jrn-text-soft)' }}>min {min.toFixed(1)} · max {max.toFixed(1)}</span>
        <span>{sorted[sorted.length - 1]?.date ? new Date(sorted[sorted.length - 1].date).toLocaleDateString('fr-CH', { day: '2-digit', month: 'short' }) : ''}</span>
      </div>
    </div>
  );
}

// AY : section Bilan pack 4 semaines — migrée du menu Plus du dashboard.
// Affiche le statut du prochain bilan dû selon le pack (S4 / S8 / S12 / S24)
// + bouton d'envoi + résumé du bilan reçu si déjà soumis.
export function PackReviewSection({
  client,
  reviews = [],
  feedbacks = [],
  feedbackStatus = 'ok',
  loadStatus = 'ready',
  loadError = null,
  onReload = null,
  onSendPackReview,
  onPrepareDecision = null,
  decisionDisabled = false,
  phaseChangeAvailable = false,
}) {
  const [sending, setSending] = useState(false);
  const [err, setErr] = useState(null);
  const digest = useMemo(() => buildCycleReviewDigest({
    client,
    reviews,
    feedbacks,
    feedbackStatus,
  }), [client, feedbackStatus, feedbacks, reviews]);
  const sentReview = useMemo(
    () => selectCurrentSentCycleReview(reviews, client),
    [client, reviews],
  );
  const nextStep = buildPackFollowupSchedule(client)
    .filter((step) => step.type === 'review' && step.status !== 'done')
    .sort((left, right) => left.weekOffset - right.weekOffset)[0] || null;

  const handleSend = async () => {
    if (!nextStep || !canSendPackReview(nextStep) || !onSendPackReview) return;
    setSending(true);
    setErr(null);
    try {
      await onSendPackReview(client, nextStep);
      onReload?.();
    } catch (e) {
      setErr(e?.message || 'Erreur envoi bilan');
    } finally {
      setSending(false);
    }
  };

  const isFollowupPack = isContinuousFollowupPack(client?.packType);
  if (!isFollowupPack) return null;
  if (loadStatus === 'loading') return <p className="jrn-cockpit-empty-row">Chargement des bilans…</p>;
  if (loadStatus === 'error') {
    return (
      <div className="jrn-error" role="alert">
        {loadError || 'Les bilans de cycle sont indisponibles.'}
        {onReload && <button type="button" className="jrn-btn jrn-btn--ghost" onClick={onReload}>Réessayer</button>}
      </div>
    );
  }

  return (
    <div>
      {/* V97.13.20 : label 'Bilan 4 semaines' retiré — doublon avec titre bloc cockpit */}
      <div className="jrn-surface" style={{ padding: 'var(--jrn-5)' }}>
        {digest.requiresValidation && (
          <div className="jrn-error" role="alert">
            Bilan à valider Anissa. Une réponse ou le modèle du questionnaire ne correspond pas au contrat attendu.
            Aucun détail, comparaison ou choix de suite n’est utilisé tant que ce point n’est pas corrigé.
          </div>
        )}
        {digest.current && (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
              <span style={{
                fontSize: 11,
                fontWeight: 600,
                padding: '4px 10px',
                borderRadius: 999,
                background: 'var(--jrn-accent-soft)',
                color: 'var(--jrn-accent)',
                textTransform: 'uppercase',
                letterSpacing: '.04em',
              }}>Bilan reçu</span>
              <span style={{ fontSize: 12, color: 'var(--jrn-text-muted)' }}>
                {digest.current.submitted_at ? new Date(digest.current.submitted_at).toLocaleDateString('fr-CH') : ''}
              </span>
            </div>
            <h4 style={{ margin: '14px 0 8px', color: 'var(--jrn-text)' }}>{digest.currentLabel}</h4>
            <dl style={{ margin: 0, display: 'grid', gap: 6 }}>
              {digest.currentRows.map((row) => (
                <div key={row.field} style={{ display: 'flex', justifyContent: 'space-between', gap: 16, fontSize: 12.5 }}>
                  <dt style={{ color: 'var(--jrn-text-muted)' }}>{row.label}</dt>
                  <dd style={{ margin: 0, color: 'var(--jrn-text)', fontWeight: 600, textAlign: 'right' }}>{row.value}</dd>
                </div>
              ))}
            </dl>

            <div style={{ marginTop: 18, paddingTop: 14, borderTop: '1px solid var(--jrn-border)' }}>
              <h4 style={{ margin: '0 0 8px', color: 'var(--jrn-text)' }}>Comparaison</h4>
              {!digest.comparison ? (
                <p style={{ margin: 0, fontSize: 12.5, color: 'var(--jrn-text-muted)' }}>
                  Premier bilan reçu pour cette période. Aucune comparaison antérieure.
                </p>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                    <thead>
                      <tr style={{ color: 'var(--jrn-text-muted)', textAlign: 'left' }}>
                        <th style={{ padding: '6px 8px 6px 0' }}>Axe</th>
                        <th style={{ padding: '6px 8px' }}>{digest.previousLabel}</th>
                        <th style={{ padding: '6px 8px' }}>{digest.currentLabel}</th>
                        <th style={{ padding: '6px 0 6px 8px' }}>Lecture</th>
                      </tr>
                    </thead>
                    <tbody>
                      {digest.comparison.rows.map((row) => (
                        <tr key={row.field} style={{ borderTop: '1px solid var(--jrn-border)' }}>
                          <td style={{ padding: '7px 8px 7px 0', color: 'var(--jrn-text-muted)' }}>{row.label}</td>
                          <td style={{ padding: '7px 8px' }}>{row.previous}</td>
                          <td style={{ padding: '7px 8px', fontWeight: 600 }}>{row.current}</td>
                          <td style={{ padding: '7px 0 7px 8px', fontWeight: 600 }}>{row.directionLabel}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            <div style={{ marginTop: 18, paddingTop: 14, borderTop: '1px solid var(--jrn-border)' }}>
              <h4 style={{ margin: '0 0 8px', color: 'var(--jrn-text)' }}>Ressentis · 7 derniers jours</h4>
              {!digest.feedbackContext.available ? (
                <p style={{ margin: 0, fontSize: 12.5, color: 'var(--jrn-text-muted)' }}>Données indisponibles.</p>
              ) : !digest.feedbackContext.hasData ? (
                <p style={{ margin: 0, fontSize: 12.5, color: 'var(--jrn-text-muted)' }}>Aucun ressenti reçu sur cette période.</p>
              ) : (
                <ul style={{ margin: 0, paddingLeft: 18, color: 'var(--jrn-text-soft)', fontSize: 12.5 }}>
                  {digest.feedbackContext.axes.map((axis) => <li key={axis.axis}>{axis.summary}</li>)}
                </ul>
              )}
            </div>

            <div style={{ marginTop: 18, padding: 12, borderRadius: 8, background: 'var(--jrn-accent-soft)' }}>
              <h4 style={{ margin: '0 0 8px', color: 'var(--jrn-accent)' }}>À retenir · résumé automatique factuel</h4>
              <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12.5, color: 'var(--jrn-text-soft)' }}>
                {digest.takeaways.map((item, index) => <li key={`takeaway-${index}`}>{item}</li>)}
              </ul>
            </div>

            <div style={{ marginTop: 18, paddingTop: 14, borderTop: '1px solid var(--jrn-border)' }}>
              <h4 style={{ margin: '0 0 6px', color: 'var(--jrn-text)' }}>Suite à décider</h4>
              <p style={{ margin: '0 0 10px', fontSize: 12.5, color: 'var(--jrn-text-muted)' }}>
                Le bilan ne modifie rien seul. Une décision persistante nécessite un vrai rendez-vous et son compte rendu relu.
              </p>
              <div className="jrn-actions" style={{ marginTop: 0 }}>
                <button
                  type="button"
                  className="jrn-btn jrn-btn--soft"
                  onClick={() => onPrepareDecision?.('continue')}
                  disabled={decisionDisabled || typeof onPrepareDecision !== 'function'}
                >
                  Continuer sans changement
                </button>
                <button
                  type="button"
                  className="jrn-btn jrn-btn--soft"
                  onClick={() => onPrepareDecision?.('adapt')}
                  disabled={decisionDisabled || typeof onPrepareDecision !== 'function'}
                >
                  Adapter le programme
                </button>
                <button
                  type="button"
                  className="jrn-btn jrn-btn--soft"
                  onClick={() => onPrepareDecision?.(null)}
                  disabled={decisionDisabled || typeof onPrepareDecision !== 'function'}
                >
                  Préparer un rendez-vous
                </button>
                <button
                  type="button"
                  className="jrn-btn jrn-btn--soft"
                  onClick={() => onPrepareDecision?.('changePhase')}
                  disabled={
                    decisionDisabled
                    || !phaseChangeAvailable
                    || typeof onPrepareDecision !== 'function'
                  }
                  title={!phaseChangeAvailable ? 'Aucune phase suivante disponible.' : undefined}
                >
                  Changer de phase
                </button>
              </div>
            </div>
          </>
        )}

        {sentReview && (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
              <span style={{
                fontSize: 11,
                fontWeight: 600,
                padding: '4px 10px',
                borderRadius: 999,
                background: 'rgba(184, 134, 38, 0.10)',
                color: '#8a6722',
                textTransform: 'uppercase',
                letterSpacing: '.04em',
              }}>Bilan en attente</span>
              {sentReview.created_at && (
                <span style={{ fontSize: 12, color: 'var(--jrn-text-muted)' }}>
                  envoyé le {new Date(sentReview.created_at).toLocaleDateString('fr-CH')}
                </span>
              )}
            </div>
            <p style={{ fontSize: 13, color: 'var(--jrn-text-soft)', margin: 0 }}>
              Le bilan a été envoyé à la cliente. En attente de sa soumission.
            </p>
          </>
        )}

        {!sentReview && nextStep && (
          <>
            <p style={{ fontSize: 13, color: 'var(--jrn-text-soft)', margin: '0 0 12px' }}>
              Prochain bilan : <strong>{nextStep.label || `S${nextStep.stepNumber}`}</strong>
              {nextStep.dueDate && (
                <span style={{ color: 'var(--jrn-text-muted)' }}> · à envoyer {new Date(nextStep.dueDate).toLocaleDateString('fr-CH')}</span>
              )}
            </p>
            <button
              onClick={handleSend}
              disabled={sending || !onSendPackReview || !canSendPackReview(nextStep)}
              className="jrn-btn jrn-btn--primary"
            >
              {sending ? 'Envoi…' : `Envoyer bilan ${nextStep.label || `S${nextStep.stepNumber}`}`}
            </button>
          </>
        )}

        {!digest.current && !digest.requiresValidation && !sentReview && !nextStep && (
          <p style={{ fontSize: 13, color: 'var(--jrn-text-muted)', margin: 0, fontStyle: 'italic' }}>
            Aucun bilan en attente pour ce cycle.
          </p>
        )}

        {err && <div className="jrn-error" style={{ marginTop: 10 }}>{err}</div>}
      </div>
    </div>
  );
}

// Le compte rendu nouveau ne stocke aucun niveau clinique. Anissa conserve les
// faits dans les réponses et la note, puis choisit uniquement l'action
// administrative qui doit suivre l'enregistrement.
const WORKFLOW_ACTION_VALUES = [
  { value: 'continue',     label: 'Poursuivre la phase actuelle',  hint: 'Consultation enregistrée · la version actuelle reste active dans l’app' },
  { value: 'adapt',        label: 'Adapter le protocole',          hint: 'Brouillon relu → version enregistrée → vérification puis publication manuelle dans l’app' },
  { value: 'transition',   label: 'Transition vers phase suivante', hint: 'À l\'enregistrement → demande confirmation pour avancer dans le parcours' },
  { value: 'newVersion',   label: 'Nouvelle version complète',     hint: 'Proposition complète relue → vérification puis publication manuelle dans l’app' },
];

function LogConsultationModal({
  consultationNumber,
  totalIncluded,
  initialWorkflowAction = null,
  phaseChangeAvailable = false,
  requireAppointmentOccurredConfirmation = false,
  pastAppointmentAt = null,
  closableReviewStep = null,
  cycleReviewProof = null,
  onCancel,
  onConfirm,
  saving,
  recentFeedbacks = [],
  meetingWorkspace = null,
  clientId,
  sourceConsultationId,
  consultationOrdinal,
}) {
  const [workflowAction, setWorkflowAction] = useState(() => (
    WORKFLOW_ACTION_VALUES.some((item) => item.value === initialWorkflowAction)
      && (initialWorkflowAction !== 'transition' || phaseChangeAvailable)
      ? initialWorkflowAction
      : null
  ));
  const [note, setNote] = useState('');
  const [meetingReviewConfirmation, setMeetingReviewConfirmation] = useState(null);
  const [appointmentOccurredConfirmed, setAppointmentOccurredConfirmed] = useState(false);
  const [pastAppointmentConfirmed, setPastAppointmentConfirmed] = useState(false);
  const [completeReviewStep, setCompleteReviewStep] = useState(false);
  const [meetingReviewError, setMeetingReviewError] = useState(null);
  const [confirmingMeetingReview, setConfirmingMeetingReview] = useState(false);
  const [meetingQuestions, setMeetingQuestions] = useState(() => (
    Array.isArray(meetingWorkspace?.questions)
      ? meetingWorkspace.questions.filter((question) => question?.included === true)
      : []
  ));
  const panelRef = useRef(null);
  const modalBusy = saving || confirmingMeetingReview;
  useModalA11y({ onClose: onCancel, busy: modalBusy, panelRef });

  const meetingRecordInput = {
    clientId,
    sourceConsultationId,
    consultationOrdinal,
    summary: meetingWorkspace?.summary,
    periodLabel: meetingWorkspace?.periodLabel,
    practitionerNote: note,
    questions: meetingQuestions,
    cycleReview: requireAppointmentOccurredConfirmation ? cycleReviewProof : null,
    occurredConfirmed: requireAppointmentOccurredConfirmation
      ? appointmentOccurredConfirmed
      : false,
  };
  const workspaceReadiness = getMeetingWorkspaceReadiness({
    summary: meetingWorkspace?.summary,
    questions: meetingQuestions,
  });
  let meetingReviewFingerprint = null;
  let meetingRecord = null;
  let meetingBuildError = null;
  try {
    meetingReviewFingerprint = buildMeetingReviewFingerprint(meetingRecordInput);
    meetingRecord = buildMeetingRecord(meetingRecordInput);
  } catch (error) {
    meetingBuildError = error?.message || 'Le compte rendu ne correspond plus à ce dossier.';
  }
  const meetingReviewIsCurrent = meetingReviewConfirmation !== null
    && meetingReviewConfirmation === meetingReviewFingerprint;
  const meetingNeedsReview = meetingRecord !== null && !meetingReviewIsCurrent;
  const saveBlockReason = meetingBuildError
    || (requireAppointmentOccurredConfirmation && appointmentOccurredConfirmed !== true
      ? 'Confirme que le rendez-vous a réellement eu lieu.'
      : null)
    || (pastAppointmentAt && pastAppointmentConfirmed !== true
      ? 'Confirme que le rendez-vous planifié a bien été effectué.'
      : null)
    || (!workspaceReadiness.ready ? workspaceReadiness.issue : null)
    || (!meetingRecord ? 'Prépare d’abord le rendez-vous et son compte rendu.' : null)
    || (meetingNeedsReview ? 'Relis et confirme le compte rendu exact.' : null)
    || (!workflowAction ? 'Choisis l’action après le rendez-vous.' : null);
  const canSaveConsultation = !saveBlockReason;
  const handleConfirm = async () => {
    if (!canSaveConsultation) {
      setMeetingReviewError(saveBlockReason);
      return;
    }
    setMeetingReviewError(null);
    setConfirmingMeetingReview(true);
    try {
      const reviewedMeetingRecord = meetingRecord
        ? await buildReviewedMeetingRecord({
          ...meetingRecordInput,
          reviewConfirmation: meetingReviewConfirmation,
        })
        : null;
      const clinical = {
        appointment: reviewedMeetingRecord,
        workflow_action: workflowAction,
      };
      await onConfirm({
        note: reviewedMeetingRecord.practitioner_note,
        clinical,
        followupCompletion: {
          completePastAppointment: Boolean(pastAppointmentAt && pastAppointmentConfirmed),
          expectedNextRdvAt: pastAppointmentAt || null,
          reviewStepNumber: completeReviewStep ? closableReviewStep?.stepNumber ?? null : null,
        },
      });
    } catch (error) {
      setMeetingReviewError(error?.message || 'Le compte rendu exact doit être confirmé avant l’enregistrement.');
    } finally {
      setConfirmingMeetingReview(false);
    }
  };

  return (
    <div className="jpe-modal-overlay" onClick={(e) => { if (e.target === e.currentTarget && !modalBusy) onCancel(); }}>
      <div ref={panelRef} className="jpe-modal jpe-modal--xl">
        <header className="jpe-modal__header">
          <div>
            <p className="jrn-step-eyebrow">Suivi clinique</p>
            <h3 className="jpe-modal__title">
              Rendez-vous n°{consultationNumber}
              {totalIncluded > 0 && <span style={{ fontSize: 14, color: 'var(--jrn-text-muted)', fontStyle: 'normal', marginLeft: 8 }}>/ {totalIncluded}</span>}
            </h3>
          </div>
          <button onClick={onCancel} disabled={modalBusy} className="jrn-btn jrn-btn--ghost">Fermer</button>
        </header>
        <div className="jpe-modal__body">
          <p style={{ fontSize: 13, color: 'var(--jrn-text-soft)', marginTop: 0, marginBottom: 16, lineHeight: 1.5 }}>
            Complète les réponses et la note factuelle, puis relis le compte rendu exact.
            L’action choisie ensuite organise uniquement le workflow du SaaS.
          </p>

          {requireAppointmentOccurredConfirmation && (
            <label className="jrn-meeting-report__notice" style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 16 }}>
              <input
                type="checkbox"
                checked={appointmentOccurredConfirmed}
                onChange={(event) => {
                  setAppointmentOccurredConfirmed(event.target.checked);
                  setMeetingReviewError(null);
                }}
                disabled={modalBusy}
              />
              <span>Je confirme que ce rendez-vous a réellement eu lieu.</span>
            </label>
          )}

          {(pastAppointmentAt || closableReviewStep) && (
            <ClinicalSection title="Clôture des actions" eyebrow="Après l’enregistrement">
              {pastAppointmentAt && (
                <label className="jrn-meeting-report__notice" style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 8 }}>
                  <input
                    type="checkbox"
                    checked={pastAppointmentConfirmed}
                    onChange={(event) => {
                      setPastAppointmentConfirmed(event.target.checked);
                      setMeetingReviewError(null);
                    }}
                    disabled={modalBusy}
                  />
                  <span>
                    Je confirme que le rendez-vous du {formatRdvLong(pastAppointmentAt)} a bien été effectué.
                    La date sera archivée après l’enregistrement du compte rendu.
                  </span>
                </label>
              )}
              {closableReviewStep && (
                <label className="jrn-meeting-report__notice" style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                  <input
                    type="checkbox"
                    checked={completeReviewStep}
                    onChange={(event) => {
                      setCompleteReviewStep(event.target.checked);
                      setMeetingReviewError(null);
                    }}
                    disabled={modalBusy}
                  />
                  <span>
                    Clôturer aussi {closableReviewStep.label}.
                    À cocher uniquement si ce point a bien été traité pendant ce rendez-vous.
                  </span>
                </label>
              )}
            </ClinicalSection>
          )}

          {meetingRecord && (
            <ClinicalSection title="Questions préparées" eyebrow="Fil conducteur du rendez-vous">
              {meetingRecord.summary && (
                <div className="jrn-meeting-report__summary">
                  <strong>Synthèse relue</strong>
                  <p>{meetingRecord.summary}</p>
                </div>
              )}
              {meetingQuestions.length > 0 && (
                <div className="jrn-meeting-report__questions">
                  {meetingQuestions.map((question, index) => (
                    <label key={question.id || index} className="jrn-meeting-report__question">
                      <span>{question.text}</span>
                      <textarea
                        rows={2}
                        value={question.answer || ''}
                        onChange={(event) => {
                          const answer = event.target.value;
                          setMeetingReviewError(null);
                          setMeetingQuestions((current) => current.map((item, itemIndex) => (
                            itemIndex === index ? { ...item, answer } : item
                          )));
                        }}
                        maxLength={2400}
                        placeholder="Réponse ou précision donnée pendant le rendez-vous…"
                        disabled={saving}
                      />
                    </label>
                  ))}
                </div>
              )}
              <p className="jrn-meeting-report__notice">
                Ces notes restent internes. Elles n'adaptent pas le plan et ne changent pas de phase sans une action explicite ci-dessous.
              </p>
              <label className="jrn-meeting-report__notice" style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                <input
                  type="checkbox"
                  checked={meetingReviewConfirmation === meetingReviewFingerprint}
                  onChange={(event) => {
                    setMeetingReviewConfirmation(event.target.checked ? meetingReviewFingerprint : null);
                    setMeetingReviewError(null);
                  }}
                  disabled={modalBusy || !meetingReviewFingerprint}
                />
                <span>
                  Je confirme avoir relu ce compte rendu exact.
                  {meetingNeedsReview && <strong>{' '}À valider Anissa.</strong>}
                </span>
              </label>
            </ClinicalSection>
          )}

          {/* V97.17.23 — Contexte ressentis cliente 7 derniers jours */}
          <FeedbackContextHelper recentFeedbacks={recentFeedbacks} />

          <ClinicalSection title="Action après le rendez-vous" eyebrow="Quelle étape ouvrir dans le SaaS ?">
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {WORKFLOW_ACTION_VALUES.map((d) => {
                const isSel = workflowAction === d.value;
                const isUnavailableTransition = d.value === 'transition' && !phaseChangeAvailable;
                return (
                  <button
                    key={d.value}
                    type="button"
                    onClick={() => {
                      if (isUnavailableTransition) return;
                      setWorkflowAction(isSel ? null : d.value);
                      setMeetingReviewError(null);
                    }}
                    disabled={modalBusy || isUnavailableTransition}
                    title={isUnavailableTransition
                      ? 'Aucune phase active et suivante n’est configurée pour cette cliente.'
                      : undefined}
                    style={{
                      textAlign: 'left',
                      padding: '10px 12px',
                      borderRadius: 7,
                      border: isSel ? '1.5px solid var(--jrn-accent, #1A2E1F)' : '1px solid rgba(26,46,31,.15)',
                      background: isSel ? 'rgba(26,46,31,.06)' : 'white',
                      cursor: (modalBusy || isUnavailableTransition) ? 'not-allowed' : 'pointer',
                      opacity: isUnavailableTransition ? 0.58 : 1,
                      transition: 'all 120ms ease',
                      fontFamily: 'var(--jrn-font-ui, system-ui)',
                    }}
                  >
                    <div style={{ fontSize: 13, fontWeight: 600, color: '#1A2E1F' }}>
                      {isSel && '✓ '}{d.label}
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--jrn-text-muted, #6b6f6b)', marginTop: 2 }}>
                      {isUnavailableTransition
                        ? 'Indisponible : aucun parcours avec une phase active et une phase suivante n’est configuré.'
                        : d.hint}
                    </div>
                  </button>
                );
              })}
            </div>
          </ClinicalSection>

          {/* Note libre (minimaliste) */}
          <ClinicalSection title="Note interne (optionnelle)" eyebrow="Précisions complémentaires">
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              maxLength={2400}
              rows={3}
              className="jrn-textarea"
              placeholder="Précision rapide si besoin. Reste interne — non envoyée à la cliente."
              disabled={saving}
              style={{ fontSize: 13 }}
            />
            <div style={{ marginTop: 4, textAlign: 'right', fontSize: 11, color: 'var(--jrn-text-muted, #6b6f6b)' }}>
              {note.length} / 2400 caractères
            </div>
          </ClinicalSection>

          <div
            className="jrn-meeting-report__notice"
            role="status"
            aria-live="polite"
            style={{ marginTop: 16 }}
          >
            <strong>{canSaveConsultation ? 'Prêt à enregistrer' : 'Avant d’enregistrer'}</strong>
            <p style={{ margin: '5px 0 0' }}>
              {canSaveConsultation
                ? 'Le compte rendu est relu et l’action suivante est définie.'
                : saveBlockReason}
            </p>
          </div>
          {meetingReviewError && (
            <div className="jrn-error" role="alert" style={{ marginTop: 10 }}>{meetingReviewError}</div>
          )}

          <div className="jrn-actions" style={{ marginTop: 18 }}>
            <button
              onClick={handleConfirm}
              disabled={modalBusy || !canSaveConsultation}
              className="jrn-btn jrn-btn--primary"
              title={saveBlockReason || undefined}
            >
              {modalBusy ? 'Enregistrement…' : '✅ Enregistrer la consultation'}
            </button>
            <button onClick={onCancel} disabled={modalBusy} className="jrn-btn jrn-btn--ghost">
              Annuler
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Helpers internes pour LogConsultationModal ──────────────────────────

// V97.17.23 — Helper : resume des ressentis cliente sur 7 derniers jours.
// Affiche en haut de la modal clinique un encart de contexte (Anissa decide
// en connaissance de cause sans biais d'auto-fill).

const FEEDBACK_DIMS = [
  { key: 'digestion', label: 'Digestion', values: { better: 'Mieux', same: 'Pareil', worse: 'Pire' } },
  { key: 'fatigue',   label: 'Fatigue',   values: { better: 'Mieux', same: 'Pareil', worse: 'Pire' } },
  { key: 'energie',   label: 'Energie',   values: { good: 'Bonne', ok: 'OK', low: 'Basse' } },
  { key: 'faim',      label: 'Faim',      values: { low: 'Pas assez', ok: 'OK', high: 'Trop' } },
];

function FeedbackContextHelper({ recentFeedbacks }) {
  if (!Array.isArray(recentFeedbacks) || recentFeedbacks.length === 0) return null;

  // Pour chaque dimension : compter occurrences par valeur, trouver dominante
  const summaries = FEEDBACK_DIMS.map((dim) => {
    const counts = {};
    for (const f of recentFeedbacks) {
      const v = f[dim.key];
      if (!v) continue;
      counts[v] = (counts[v] || 0) + 1;
    }
    const entries = Object.entries(counts).sort((a, b) => b[1] - a[1]);
    if (entries.length === 0) return null;
    const topCount = entries[0][1];
    const tiedLabels = entries
      .filter(([, count]) => count === topCount)
      .map(([value]) => dim.values[value] || value);
    return {
      dim,
      label: tiedLabels.length === 1 ? tiedLabels[0] : tiedLabels.join(' / '),
      tied: tiedLabels.length > 1,
      count: topCount,
      total: recentFeedbacks.length,
    };
  }).filter(Boolean);

  if (summaries.length === 0) {
    return (
      <div style={{
        padding: '10px 12px',
        background: 'rgba(26, 46, 31, 0.04)',
        border: '1px dashed rgba(26, 46, 31, 0.15)',
        borderRadius: 7,
        marginBottom: 16,
        fontSize: 11.5,
        color: 'var(--jrn-text-muted, #6b6f6b)',
        fontStyle: 'italic',
      }}>
        Aucun ressenti cliente reçu sur les 7 derniers jours.
      </div>
    );
  }

  return (
    <div style={{
      padding: '10px 12px',
      background: 'rgba(26, 46, 31, 0.04)',
      border: '1px solid rgba(26, 46, 31, 0.10)',
      borderRadius: 7,
      marginBottom: 16,
    }}>
      <div style={{
        fontSize: 9.5,
        fontWeight: 700,
        letterSpacing: '.12em',
        textTransform: 'uppercase',
        color: 'var(--jrn-text-muted, #6b6f6b)',
        marginBottom: 6,
      }}>
        Contexte ressentis · 7 derniers jours
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        {summaries.map((s) => (
          <span
            key={s.dim.key}
            style={{
              fontSize: 11,
              padding: '3px 9px',
              borderRadius: 999,
              background: 'white',
              color: '#1A2E1F',
              border: '1px solid rgba(26, 46, 31, 0.10)',
              fontFamily: 'var(--jrn-font-ui, system-ui)',
            }}
            title={s.tied ? `Réponses ex æquo sur ${s.total} ressentis` : `Dominante sur ${s.total} ressentis`}
          >
            <strong>{s.dim.label}</strong>{' '}
            <span style={{ opacity: 0.7 }}>· {s.tied ? 'ex æquo' : 'dominante'} {s.label}</span>{' '}
            <span style={{ fontSize: 10, color: 'var(--jrn-text-muted, #6b6f6b)' }}>
              ({s.count}/{s.total})
            </span>
          </span>
        ))}
      </div>
    </div>
  );
}

function ClinicalSection({ title, eyebrow, children }) {
  return (
    <div style={{ marginBottom: 18 }}>
      <div style={{ marginBottom: 8 }}>
        <p style={{ margin: 0, fontSize: 10, fontWeight: 700, letterSpacing: '.1em', textTransform: 'uppercase', color: 'var(--jrn-text-muted, #6b6f6b)' }}>
          {eyebrow}
        </p>
        <h4 style={{ margin: '2px 0 0 0', fontSize: 14, color: '#1A2E1F', fontWeight: 600 }}>{title}</h4>
      </div>
      <div>{children}</div>
    </div>
  );
}

// Mini modale d'aperçu lecture seule d'une version du plan
function PlanVersionPreviewModal({ version, onClose }) {
  const text = version.nutritionPlan || version.nutrition_plan || '';
  const date = version.createdAt ? new Date(version.createdAt).toLocaleDateString('fr-CH', { day: '2-digit', month: 'long', year: 'numeric' }) : '';
  const panelRef = useRef(null);
  useModalA11y({ onClose, panelRef });
  return (
    <div className="jpe-modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div ref={panelRef} className="jpe-modal jpe-modal--xl">
        <header className="jpe-modal__header">
          <div>
            <p className="jrn-step-eyebrow">Version archivée</p>
            <h3 className="jpe-modal__title">{version.label || 'Plan'}</h3>
            <p style={{ fontSize: 11, color: 'var(--jrn-text-muted)', marginTop: 4 }}>{date}</p>
          </div>
          <button onClick={onClose} className="jrn-btn jrn-btn--ghost">Fermer</button>
        </header>
        <div className="jpe-modal__body">
          <div className="jpe-preview">
            <pre style={{
              whiteSpace: 'pre-wrap',
              fontFamily: 'inherit',
              fontSize: 13,
              lineHeight: 1.65,
              color: 'var(--jrn-text)',
              margin: 0,
            }}>{text}</pre>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Modale « Renouveler le suivi » ─────────────────────────────────────
// Démarre une nouvelle période de suivi sur la MÊME fiche : nouveau pack,
// nouvelle date de départ, planning des bilans recalculé, compteur de
// consultations remis à zéro pour cette période. L'historique (versions,
// consultations, poids, ressentis) reste intact. Admin/planning uniquement,
// aucune IA. Seuls les packs de suivi (avec planning) sont proposés.
function RenewFollowupModal({ currentPackType, prenom, saving, onCancel, onConfirm }) {
  const followupPacks = getActivePacks().filter((p) => p.steps.length > 0);
  const [selected, setSelected] = useState(null);
  const panelRef = useRef(null);
  useModalA11y({ onClose: onCancel, busy: saving, panelRef });

  return (
    <div className="jpe-modal-overlay" onClick={(e) => { if (e.target === e.currentTarget && !saving) onCancel(); }}>
      <div ref={panelRef} className="jpe-modal">
        <header className="jpe-modal__header">
          <div>
            <p className="jrn-step-eyebrow">Suivi</p>
            <h3 className="jpe-modal__title">Renouveler le suivi</h3>
          </div>
          <button onClick={onCancel} disabled={saving} className="jrn-btn jrn-btn--ghost">Fermer</button>
        </header>
        <div className="jpe-modal__body">
          <p style={{ fontSize: 13, color: 'var(--jrn-text-soft)', marginTop: 0, marginBottom: 16, lineHeight: 1.55 }}>
            Garde tout l'historique de {prenom} (versions, consultations passées, poids,
            ressentis). Démarre une nouvelle période : nouvelle date de départ, planning
            des bilans recalculé, compteur de consultations remis à zéro pour cette période.
          </p>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {followupPacks.map((p) => {
              const isSel = selected === p.key;
              const isCurrent = p.key === currentPackType;
              return (
                <button
                  key={p.key}
                  type="button"
                  onClick={() => setSelected(isSel ? null : p.key)}
                  disabled={saving}
                  style={{
                    textAlign: 'left',
                    padding: '12px 14px',
                    borderRadius: 8,
                    border: isSel ? '1.5px solid var(--jrn-accent, #1A2E1F)' : '1px solid rgba(26,46,31,.15)',
                    background: isSel ? 'rgba(26,46,31,.06)' : 'white',
                    cursor: saving ? 'default' : 'pointer',
                    display: 'flex',
                    alignItems: 'baseline',
                    justifyContent: 'space-between',
                    gap: 12,
                  }}
                >
                  <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--jrn-text)' }}>
                    {p.label}
                    {isCurrent && (
                      <span style={{ fontSize: 11, fontWeight: 400, color: 'var(--jrn-text-muted)', marginLeft: 8 }}>
                        pack actuel
                      </span>
                    )}
                  </span>
                  <span style={{ fontSize: 13, color: 'var(--jrn-text-soft)', whiteSpace: 'nowrap' }}>
                    {p.price} CHF
                  </span>
                </button>
              );
            })}
          </div>

          <div className="jrn-actions" style={{ marginTop: 18 }}>
            <button
              onClick={() => onConfirm(selected)}
              disabled={saving || !selected}
              className="jrn-btn jrn-btn--primary"
              title={!selected ? 'Choisis d\'abord le nouveau pack' : 'Démarrer la nouvelle période'}
            >
              {saving ? 'Renouvellement…' : 'Confirmer le renouvellement'}
            </button>
            <button onClick={onCancel} disabled={saving} className="jrn-btn jrn-btn--ghost">
              Annuler
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
